import { createNode } from '@glueapp/graph-types';
import { GithubIntegration } from './GithubIntegration';

export const GeneratedNodes_Github = [
    createNode({
        id: 'Github: GitHub API Root',
        name: 'Github: GitHub API Root',
        description: "Get Hypermedia links to resources accessible in GitHub's REST API",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    current_user_url: { kind: 'string' },
                    current_user_authorizations_html_url: { kind: 'string' },
                    authorizations_url: { kind: 'string' },
                    code_search_url: { kind: 'string' },
                    commit_search_url: { kind: 'string' },
                    emails_url: { kind: 'string' },
                    emojis_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    feeds_url: { kind: 'string' },
                    followers_url: { kind: 'string' },
                    following_url: { kind: 'string' },
                    gists_url: { kind: 'string' },
                    hub_url: { kind: 'string' },
                    issue_search_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    label_search_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    organization_url: { kind: 'string' },
                    organization_repositories_url: { kind: 'string' },
                    organization_teams_url: { kind: 'string' },
                    public_gists_url: { kind: 'string' },
                    rate_limit_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    repository_search_url: { kind: 'string' },
                    current_user_repositories_url: { kind: 'string' },
                    starred_url: { kind: 'string' },
                    starred_gists_url: { kind: 'string' },
                    topic_search_url: { kind: 'string' },
                    user_url: { kind: 'string' },
                    user_organizations_url: { kind: 'string' },
                    user_repositories_url: { kind: 'string' },
                    user_search_url: { kind: 'string' },
                },
                optionalProperties: ['topic_search_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', ''].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the authenticated app',
        name: 'Github: Get the authenticated app',
        description:
            'Returns the GitHub App associated with the authentication credentials used. To see how many app installations are associated with this GitHub App, see the `installations_count` in the response. For more details about your app\'s installations, see the "[List installations for the authenticated app](https://docs.github.com/rest/apps/apps#list-installations-for-the-authenticated-app)" endpoint.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    slug: { kind: 'string' },
                    node_id: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    name: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    external_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            issues: { kind: 'string' },
                            checks: { kind: 'string' },
                            metadata: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    installations_count: { kind: 'number' },
                    client_id: { kind: 'string' },
                    client_secret: { kind: 'string' },
                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pem: { kind: 'string' },
                },
                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a GitHub App from a manifest',
        name: 'Github: Create a GitHub App from a manifest',
        description:
            "Use this endpoint to complete the handshake necessary when implementing the [GitHub App Manifest flow](https://docs.github.com/apps/building-github-apps/creating-github-apps-from-a-manifest/). When you create a GitHub App with the manifest flow, you receive a temporary `code` used to retrieve the GitHub App's `id`, `pem` (private key), and `webhook_secret`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            code: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            slug: { kind: 'string' },
                            node_id: { kind: 'string' },
                            owner: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            name: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            external_url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    issues: { kind: 'string' },
                                    checks: { kind: 'string' },
                                    metadata: { kind: 'string' },
                                    contents: { kind: 'string' },
                                    deployments: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            events: { kind: 'array', subType: { kind: 'string' } },
                            installations_count: { kind: 'number' },
                            client_id: { kind: 'string' },
                            client_secret: { kind: 'string' },
                            webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            pem: { kind: 'string' },
                        },
                        optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: {
                            client_id: { kind: 'string' },
                            client_secret: { kind: 'string' },
                            webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            pem: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app-manifests', input.inPorts.code, 'conversions'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a webhook configuration for an app',
        name: 'Github: Get a webhook configuration for an app',
        description:
            'Returns the webhook configuration for a GitHub App. For more information about configuring a webhook for your app, see "[Creating a GitHub App](/developers/apps/creating-a-github-app)."\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'hook', 'config'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a webhook configuration for an app',
        name: 'Github: Update a webhook configuration for an app',
        description:
            'Updates the webhook configuration for a GitHub App. For more information about configuring a webhook for your app, see "[Creating a GitHub App](/developers/apps/creating-a-github-app)."\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'hook', 'config'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deliveries for an app webhook',
        name: 'Github: List deliveries for an app webhook',
        description:
            'Returns a list of webhook deliveries for the webhook configured for a GitHub App.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            cursor: { kind: 'string' },
            redelivery: { kind: 'boolean' },
        },
        optionalInPorts: ['per_page', 'cursor', 'redelivery'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        guid: { kind: 'string' },
                        delivered_at: { kind: 'string' },
                        redelivery: { kind: 'boolean' },
                        duration: { kind: 'number' },
                        status: { kind: 'string' },
                        status_code: { kind: 'number' },
                        event: { kind: 'string' },
                        action: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        installation_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        repository_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'hook', 'deliveries'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            cursor: input.inPorts.cursor,
                            redelivery: input.inPorts.redelivery,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a delivery for an app webhook',
        name: 'Github: Get a delivery for an app webhook',
        description:
            'Returns a delivery for the webhook configured for a GitHub App.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            delivery_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    guid: { kind: 'string' },
                    delivered_at: { kind: 'string' },
                    redelivery: { kind: 'boolean' },
                    duration: { kind: 'number' },
                    status: { kind: 'string' },
                    status_code: { kind: 'number' },
                    event: { kind: 'string' },
                    action: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    installation_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    repository_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    request: {
                        kind: 'object',
                        properties: {
                            headers: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                            payload: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    response: {
                        kind: 'object',
                        properties: {
                            headers: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'hook', 'deliveries', input.inPorts.delivery_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Redeliver a delivery for an app webhook',
        name: 'Github: Redeliver a delivery for an app webhook',
        description:
            'Redeliver a delivery for the webhook configured for a GitHub App.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            delivery_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'hook', 'deliveries', input.inPorts.delivery_id, 'attempts'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List installation requests for the authenticated app',
        name: 'Github: List installation requests for the authenticated app',
        description: 'Lists all the pending installation requests for the authenticated GitHub App.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        account: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: {
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        html_url: { kind: 'string' },
                                        website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        avatar_url: { kind: 'string' },
                                    },
                                    optionalProperties: ['description', 'website_url'],
                                    restriction: 'open',
                                },
                            ],
                        },
                        requester: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: ['node_id'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installation-requests'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List installations for the authenticated app',
        name: 'Github: List installations for the authenticated app',
        description:
            'You must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.\n\nThe permissions the installation has are included under the `permissions` key.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            since: { kind: 'string' },
            outdated: { kind: 'string' },
        },
        optionalInPorts: ['per_page', 'page', 'since', 'outdated'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        account: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        {
                                            kind: 'object',
                                            properties: {
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'string' },
                                                website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                avatar_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['description', 'website_url'],
                                            restriction: 'open',
                                        },
                                    ],
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository_selection: { kind: 'string' },
                        access_tokens_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        app_id: { kind: 'number' },
                        target_id: { kind: 'number' },
                        target_type: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                actions: { kind: 'string' },
                                administration: { kind: 'string' },
                                checks: { kind: 'string' },
                                contents: { kind: 'string' },
                                deployments: { kind: 'string' },
                                environments: { kind: 'string' },
                                issues: { kind: 'string' },
                                metadata: { kind: 'string' },
                                packages: { kind: 'string' },
                                pages: { kind: 'string' },
                                pull_requests: { kind: 'string' },
                                repository_hooks: { kind: 'string' },
                                repository_projects: { kind: 'string' },
                                secret_scanning_alerts: { kind: 'string' },
                                secrets: { kind: 'string' },
                                security_events: { kind: 'string' },
                                single_file: { kind: 'string' },
                                statuses: { kind: 'string' },
                                vulnerability_alerts: { kind: 'string' },
                                workflows: { kind: 'string' },
                                members: { kind: 'string' },
                                organization_administration: { kind: 'string' },
                                organization_custom_roles: { kind: 'string' },
                                organization_announcement_banners: { kind: 'string' },
                                organization_hooks: { kind: 'string' },
                                organization_personal_access_tokens: { kind: 'string' },
                                organization_personal_access_token_requests: { kind: 'string' },
                                organization_plan: { kind: 'string' },
                                organization_projects: { kind: 'string' },
                                organization_packages: { kind: 'string' },
                                organization_secrets: { kind: 'string' },
                                organization_self_hosted_runners: { kind: 'string' },
                                organization_user_blocking: { kind: 'string' },
                                team_discussions: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        has_multiple_single_files: { kind: 'boolean' },
                        single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                        app_slug: { kind: 'string' },
                        suspended_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            since: input.inPorts.since,
                            outdated: input.inPorts.outdated,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an installation for the authenticated app',
        name: 'Github: Get an installation for the authenticated app',
        description:
            "Enables an authenticated GitHub App to find an installation's information using the installation id.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    account: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    {
                                        kind: 'object',
                                        properties: {
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            avatar_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['description', 'website_url'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository_selection: { kind: 'string' },
                    access_tokens_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    app_id: { kind: 'number' },
                    target_id: { kind: 'number' },
                    target_type: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    has_multiple_single_files: { kind: 'boolean' },
                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                    app_slug: { kind: 'string' },
                    suspended_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installations', input.inPorts.installation_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an installation for the authenticated app',
        name: 'Github: Delete an installation for the authenticated app',
        description:
            'Uninstalls a GitHub App on a user, organization, or business account. If you prefer to temporarily suspend an app\'s access to your account\'s resources, then we recommend the "[Suspend an app installation](https://docs.github.com/rest/reference/apps/#suspend-an-app-installation)" endpoint.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installations', input.inPorts.installation_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an installation access token for an app',
        name: 'Github: Create an installation access token for an app',
        description:
            "Creates an installation access token that enables a GitHub App to make authenticated API requests for the app's installation on an organization or individual account. Installation tokens expire one hour from the time you create them. Using an expired token produces a status code of `401 - Unauthorized`, and requires creating a new installation token. By default the installation token has access to all repositories that the installation can access. To restrict the access to specific repositories, you can provide the `repository_ids` when creating the token. When you omit `repository_ids`, the response does not contain the `repositories` key.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    repositories: { kind: 'array', subType: { kind: 'string' } },
                    repository_ids: { kind: 'array', subType: { kind: 'number' } },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    token: { kind: 'string' },
                    expires_at: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    repository_selection: { kind: 'string' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    single_file: { kind: 'string' },
                    has_multiple_single_files: { kind: 'boolean' },
                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [
                    'permissions',
                    'repository_selection',
                    'repositories',
                    'single_file',
                    'has_multiple_single_files',
                    'single_file_paths',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installations', input.inPorts.installation_id, 'access_tokens'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Suspend an app installation',
        name: 'Github: Suspend an app installation',
        description:
            "Suspends a GitHub App on a user, organization, or business account, which blocks the app from accessing the account's resources. When a GitHub App is suspended, the app's access to the GitHub API or webhook events is blocked for that account.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installations', input.inPorts.installation_id, 'suspended'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unsuspend an app installation',
        name: 'Github: Unsuspend an app installation',
        description:
            'Removes a GitHub App installation suspension.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'app', 'installations', input.inPorts.installation_id, 'suspended'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an app authorization',
        name: 'Github: Delete an app authorization',
        description:
            "OAuth and GitHub application owners can revoke a grant for their application and a specific user. You must use [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) when accessing this endpoint, using the OAuth application's `client_id` and `client_secret` as the username and password. You must also provide a valid OAuth `access_token` as an input parameter and the grant for the token's owner will be deleted.\nDeleting an application's grant will also delete all OAuth tokens associated with the application for the user. Once deleted, the application will have no access to the user's account and will no longer be listed on [the application authorizations settings screen within GitHub](https://github.com/settings/applications#authorized).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            client_id: { kind: 'string' },
            body: { kind: 'object', properties: { access_token: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'applications', input.inPorts.client_id, 'grant'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check a token',
        name: 'Github: Check a token',
        description:
            'OAuth applications and GitHub applications with OAuth authorizations can use this API method for checking OAuth token validity without exceeding the normal rate limits for failed login attempts. Authentication works differently with this particular endpoint. You must use [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) to use this endpoint, where the username is the application `client_id` and the password is its `client_secret`. Invalid tokens will return `404 NOT FOUND`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            client_id: { kind: 'string' },
            body: { kind: 'object', properties: { access_token: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    scopes: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    token: { kind: 'string' },
                    token_last_eight: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hashed_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    app: {
                        kind: 'object',
                        properties: { client_id: { kind: 'string' }, name: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    note_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    fingerprint: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    installation: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            actions: { kind: 'string' },
                                            administration: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                            environments: { kind: 'string' },
                                            issues: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            packages: { kind: 'string' },
                                            pages: { kind: 'string' },
                                            pull_requests: { kind: 'string' },
                                            repository_hooks: { kind: 'string' },
                                            repository_projects: { kind: 'string' },
                                            secret_scanning_alerts: { kind: 'string' },
                                            secrets: { kind: 'string' },
                                            security_events: { kind: 'string' },
                                            single_file: { kind: 'string' },
                                            statuses: { kind: 'string' },
                                            vulnerability_alerts: { kind: 'string' },
                                            workflows: { kind: 'string' },
                                            members: { kind: 'string' },
                                            organization_administration: { kind: 'string' },
                                            organization_custom_roles: { kind: 'string' },
                                            organization_announcement_banners: { kind: 'string' },
                                            organization_hooks: { kind: 'string' },
                                            organization_personal_access_tokens: { kind: 'string' },
                                            organization_personal_access_token_requests: { kind: 'string' },
                                            organization_plan: { kind: 'string' },
                                            organization_projects: { kind: 'string' },
                                            organization_packages: { kind: 'string' },
                                            organization_secrets: { kind: 'string' },
                                            organization_self_hosted_runners: { kind: 'string' },
                                            organization_user_blocking: { kind: 'string' },
                                            team_discussions: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    repository_selection: { kind: 'string' },
                                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    has_multiple_single_files: { kind: 'boolean' },
                                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                                    repositories_url: { kind: 'string' },
                                    account: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['has_multiple_single_files', 'single_file_paths'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['user', 'installation'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'applications', input.inPorts.client_id, 'token'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an app token',
        name: 'Github: Delete an app token',
        description:
            "OAuth  or GitHub application owners can revoke a single token for an OAuth application or a GitHub application with an OAuth authorization. You must use [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) when accessing this endpoint, using the application's `client_id` and `client_secret` as the username and password.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            client_id: { kind: 'string' },
            body: { kind: 'object', properties: { access_token: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'applications', input.inPorts.client_id, 'token'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Reset a token',
        name: 'Github: Reset a token',
        description:
            'OAuth applications and GitHub applications with OAuth authorizations can use this API method to reset a valid OAuth token without end-user involvement. Applications must save the "token" property in the response because changes take effect immediately. You must use [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) when accessing this endpoint, using the application\'s `client_id` and `client_secret` as the username and password. Invalid tokens will return `404 NOT FOUND`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            client_id: { kind: 'string' },
            body: { kind: 'object', properties: { access_token: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    scopes: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    token: { kind: 'string' },
                    token_last_eight: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hashed_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    app: {
                        kind: 'object',
                        properties: { client_id: { kind: 'string' }, name: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    note_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    fingerprint: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    installation: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            actions: { kind: 'string' },
                                            administration: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                            environments: { kind: 'string' },
                                            issues: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            packages: { kind: 'string' },
                                            pages: { kind: 'string' },
                                            pull_requests: { kind: 'string' },
                                            repository_hooks: { kind: 'string' },
                                            repository_projects: { kind: 'string' },
                                            secret_scanning_alerts: { kind: 'string' },
                                            secrets: { kind: 'string' },
                                            security_events: { kind: 'string' },
                                            single_file: { kind: 'string' },
                                            statuses: { kind: 'string' },
                                            vulnerability_alerts: { kind: 'string' },
                                            workflows: { kind: 'string' },
                                            members: { kind: 'string' },
                                            organization_administration: { kind: 'string' },
                                            organization_custom_roles: { kind: 'string' },
                                            organization_announcement_banners: { kind: 'string' },
                                            organization_hooks: { kind: 'string' },
                                            organization_personal_access_tokens: { kind: 'string' },
                                            organization_personal_access_token_requests: { kind: 'string' },
                                            organization_plan: { kind: 'string' },
                                            organization_projects: { kind: 'string' },
                                            organization_packages: { kind: 'string' },
                                            organization_secrets: { kind: 'string' },
                                            organization_self_hosted_runners: { kind: 'string' },
                                            organization_user_blocking: { kind: 'string' },
                                            team_discussions: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    repository_selection: { kind: 'string' },
                                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    has_multiple_single_files: { kind: 'boolean' },
                                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                                    repositories_url: { kind: 'string' },
                                    account: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['has_multiple_single_files', 'single_file_paths'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['user', 'installation'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'applications', input.inPorts.client_id, 'token'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a scoped access token',
        name: 'Github: Create a scoped access token',
        description:
            'Use a non-scoped user access token to create a repository scoped and/or permission scoped user access token. You can specify which repositories the token can access and which permissions are granted to the token. You must use [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) when accessing this endpoint, using the `client_id` and `client_secret` of the GitHub App as the username and password. Invalid tokens will return `404 NOT FOUND`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            client_id: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    access_token: { kind: 'string' },
                    target: { kind: 'string' },
                    target_id: { kind: 'number' },
                    repositories: { kind: 'array', subType: { kind: 'string' } },
                    repository_ids: { kind: 'array', subType: { kind: 'number' } },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['target', 'target_id', 'repositories', 'repository_ids', 'permissions'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    scopes: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    token: { kind: 'string' },
                    token_last_eight: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hashed_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    app: {
                        kind: 'object',
                        properties: { client_id: { kind: 'string' }, name: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    note_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    fingerprint: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    installation: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            actions: { kind: 'string' },
                                            administration: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                            environments: { kind: 'string' },
                                            issues: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            packages: { kind: 'string' },
                                            pages: { kind: 'string' },
                                            pull_requests: { kind: 'string' },
                                            repository_hooks: { kind: 'string' },
                                            repository_projects: { kind: 'string' },
                                            secret_scanning_alerts: { kind: 'string' },
                                            secrets: { kind: 'string' },
                                            security_events: { kind: 'string' },
                                            single_file: { kind: 'string' },
                                            statuses: { kind: 'string' },
                                            vulnerability_alerts: { kind: 'string' },
                                            workflows: { kind: 'string' },
                                            members: { kind: 'string' },
                                            organization_administration: { kind: 'string' },
                                            organization_custom_roles: { kind: 'string' },
                                            organization_announcement_banners: { kind: 'string' },
                                            organization_hooks: { kind: 'string' },
                                            organization_personal_access_tokens: { kind: 'string' },
                                            organization_personal_access_token_requests: { kind: 'string' },
                                            organization_plan: { kind: 'string' },
                                            organization_projects: { kind: 'string' },
                                            organization_packages: { kind: 'string' },
                                            organization_secrets: { kind: 'string' },
                                            organization_self_hosted_runners: { kind: 'string' },
                                            organization_user_blocking: { kind: 'string' },
                                            team_discussions: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    repository_selection: { kind: 'string' },
                                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    has_multiple_single_files: { kind: 'boolean' },
                                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                                    repositories_url: { kind: 'string' },
                                    account: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['has_multiple_single_files', 'single_file_paths'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['user', 'installation'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'applications', input.inPorts.client_id, 'token', 'scoped'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an app',
        name: 'Github: Get an app',
        description:
            '**Note**: The `:app_slug` is just the URL-friendly name of your GitHub App. You can find this on the settings page for your GitHub App (e.g., `https://github.com/settings/apps/:app_slug`).\n\nIf the GitHub App you specify is public, you can access this endpoint without authenticating. If the GitHub App you specify is private, you must authenticate with a [personal access token](https://docs.github.com/articles/creating-a-personal-access-token-for-the-command-line/) or an [installation access token](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-an-installation) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            app_slug: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    slug: { kind: 'string' },
                    node_id: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    name: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    external_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            issues: { kind: 'string' },
                            checks: { kind: 'string' },
                            metadata: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    installations_count: { kind: 'number' },
                    client_id: { kind: 'string' },
                    client_secret: { kind: 'string' },
                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pem: { kind: 'string' },
                },
                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'apps', input.inPorts.app_slug].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all codes of conduct',
        name: 'Github: Get all codes of conduct',
        description: "Returns array of all GitHub's codes of conduct.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        key: { kind: 'string' },
                        name: { kind: 'string' },
                        url: { kind: 'string' },
                        body: { kind: 'string' },
                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['body'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'codes_of_conduct'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a code of conduct',
        name: 'Github: Get a code of conduct',
        description: 'Returns information about the specified GitHub code of conduct.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            key: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key: { kind: 'string' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['body'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'codes_of_conduct', input.inPorts.key].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get emojis',
        name: 'Github: Get emojis',
        description: 'Lists all the emojis available to use on GitHub.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'emojis'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List Dependabot alerts for an enterprise',
        name: 'Github: List Dependabot alerts for an enterprise',
        description:
            'Lists Dependabot alerts for repositories that are owned by the specified enterprise.\nTo use this endpoint, you must be a member of the enterprise, and you must use an\naccess token with the `repo` scope or `security_events` scope.\nAlerts are only returned for organizations in the enterprise for which you are an organization owner or a security manager. For more information about security managers, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            enterprise: { kind: 'string' },
            state: { kind: 'string' },
            severity: { kind: 'string' },
            ecosystem: { kind: 'string' },
            package: { kind: 'string' },
            scope: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            before: { kind: 'string' },
            after: { kind: 'string' },
            first: { kind: 'number' },
            last: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: [
            'state',
            'severity',
            'ecosystem',
            'package',
            'scope',
            'sort',
            'direction',
            'before',
            'after',
            'first',
            'last',
            'per_page',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        dependency: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                manifest_path: { kind: 'string' },
                                scope: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        security_advisory: {
                            kind: 'object',
                            properties: {
                                ghsa_id: { kind: 'string' },
                                cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                summary: { kind: 'string' },
                                description: { kind: 'string' },
                                vulnerabilities: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            package: {
                                                kind: 'object',
                                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            severity: { kind: 'string' },
                                            vulnerable_version_range: { kind: 'string' },
                                            first_patched_version: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: { identifier: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                severity: { kind: 'string' },
                                cvss: {
                                    kind: 'object',
                                    properties: {
                                        score: { kind: 'number' },
                                        vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                cwes: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                identifiers: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                references: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                published_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        security_vulnerability: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                severity: { kind: 'string' },
                                vulnerable_version_range: { kind: 'string' },
                                first_patched_version: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { identifier: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        auto_dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['auto_dismissed_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'enterprises', input.inPorts.enterprise, 'dependabot', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            severity: input.inPorts.severity,
                            ecosystem: input.inPorts.ecosystem,
                            package: input.inPorts.package,
                            scope: input.inPorts.scope,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                            first: input.inPorts.first,
                            last: input.inPorts.last,
                            per_page: input.inPorts.per_page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List secret scanning alerts for an enterprise',
        name: 'Github: List secret scanning alerts for an enterprise',
        description:
            'Lists secret scanning alerts for eligible repositories in an enterprise, from newest to oldest.\nTo use this endpoint, you must be a member of the enterprise, and you must use an access token with the `repo` scope or `security_events` scope. Alerts are only returned for organizations in the enterprise for which you are an organization owner or a [security manager](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            enterprise: { kind: 'string' },
            state: { kind: 'string' },
            secret_type: { kind: 'string' },
            resolution: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            before: { kind: 'string' },
            after: { kind: 'string' },
        },
        optionalInPorts: ['state', 'secret_type', 'resolution', 'sort', 'direction', 'per_page', 'before', 'after'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        locations_url: { kind: 'string' },
                        state: { kind: 'string' },
                        resolution: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolved_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolved_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        secret_type: { kind: 'string' },
                        secret_type_display_name: { kind: 'string' },
                        secret: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        push_protection_bypassed: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                        push_protection_bypassed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        push_protection_bypassed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolution_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'enterprises', input.inPorts.enterprise, 'secret-scanning', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            secret_type: input.inPorts.secret_type,
                            resolution: input.inPorts.resolution,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public events',
        name: 'Github: List public events',
        description:
            'We delay the public events feed by five minutes, which means the most recent event returned by the public events API actually occurred at least five minutes ago.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get feeds',
        name: 'Github: Get feeds',
        description:
            'GitHub provides several timeline resources in [Atom](http://en.wikipedia.org/wiki/Atom_(standard)) format. The Feeds API lists all the feeds available to the authenticated user:\n\n*   **Timeline**: The GitHub global public timeline\n*   **User**: The public timeline for any user, using [URI template](https://docs.github.com/rest/overview/resources-in-the-rest-api#hypermedia)\n*   **Current user public**: The public timeline for the authenticated user\n*   **Current user**: The private timeline for the authenticated user\n*   **Current user actor**: The private timeline for activity created by the authenticated user\n*   **Current user organizations**: The private timeline for the organizations the authenticated user is a member of.\n*   **Security advisories**: A collection of public announcements that provide information about security-related vulnerabilities in software on GitHub.\n\n**Note**: Private feeds are only returned when [authenticating via Basic Auth](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) since current feed URIs use the older, non revocable auth tokens.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    timeline_url: { kind: 'string' },
                    user_url: { kind: 'string' },
                    current_user_public_url: { kind: 'string' },
                    current_user_url: { kind: 'string' },
                    current_user_actor_url: { kind: 'string' },
                    current_user_organization_url: { kind: 'string' },
                    current_user_organization_urls: { kind: 'array', subType: { kind: 'string' } },
                    security_advisories_url: { kind: 'string' },
                    repository_discussions_url: { kind: 'string' },
                    repository_discussions_category_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            timeline: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            user: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            security_advisories: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            current_user: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            current_user_public: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            current_user_actor: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            current_user_organization: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            current_user_organizations: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            repository_discussions: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            repository_discussions_category: {
                                kind: 'object',
                                properties: { href: { kind: 'string' }, type: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [
                            'security_advisories',
                            'current_user',
                            'current_user_public',
                            'current_user_actor',
                            'current_user_organization',
                            'current_user_organizations',
                            'repository_discussions',
                            'repository_discussions_category',
                        ],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'current_user_public_url',
                    'current_user_url',
                    'current_user_actor_url',
                    'current_user_organization_url',
                    'current_user_organization_urls',
                    'security_advisories_url',
                    'repository_discussions_url',
                    'repository_discussions_category_url',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'feeds'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List gists for the authenticated user',
        name: 'Github: List gists for the authenticated user',
        description: "Lists the authenticated user's gists or if called anonymously, this endpoint returns all public gists:",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        id: { kind: 'string' },
                        node_id: { kind: 'string' },
                        git_pull_url: { kind: 'string' },
                        git_push_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        files: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        comments_url: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        truncated: { kind: 'boolean' },
                        forks: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                        history: {
                            kind: 'array',
                            subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                    },
                    optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a gist',
        name: 'Github: Create a gist',
        description:
            'Allows you to add a new gist with one or more files.\n\n**Note:** Don\'t name your files "gistfile" with a numerical suffix. This is the format of the automatic naming scheme that Gist uses internally.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    description: { kind: 'string' },
                    files: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            required: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'string' }] },
                },
                optionalProperties: ['description', 'public'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    forks: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                                bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                public_repos: { kind: 'number' },
                                                public_gists: { kind: 'number' },
                                                followers: { kind: 'number' },
                                                following: { kind: 'number' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                plan: {
                                                    kind: 'object',
                                                    properties: {
                                                        collaborators: { kind: 'number' },
                                                        name: { kind: 'string' },
                                                        space: { kind: 'number' },
                                                        private_repos: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                private_gists: { kind: 'number' },
                                                total_private_repos: { kind: 'number' },
                                                owned_private_repos: { kind: 'number' },
                                                disk_usage: { kind: 'number' },
                                                collaborators: { kind: 'number' },
                                            },
                                            optionalProperties: [
                                                'twitter_username',
                                                'plan',
                                                'suspended_at',
                                                'private_gists',
                                                'total_private_repos',
                                                'owned_private_repos',
                                                'disk_usage',
                                                'collaborators',
                                            ],
                                            restriction: 'open',
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    history: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        version: { kind: 'string' },
                                        committed_at: { kind: 'string' },
                                        change_status: {
                                            kind: 'object',
                                            properties: {
                                                total: { kind: 'number' },
                                                additions: { kind: 'number' },
                                                deletions: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    fork_of: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    id: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    git_pull_url: { kind: 'string' },
                                    git_push_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    files: {
                                        kind: 'object',
                                        properties: {
                                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    public: { kind: 'boolean' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    comments: { kind: 'number' },
                                    user: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    comments_url: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    truncated: { kind: 'boolean' },
                                    forks: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    history: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                },
                                optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    id: { kind: 'string' },
                    node_id: { kind: 'string' },
                    git_pull_url: { kind: 'string' },
                    git_push_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    files: {
                        kind: 'object',
                        properties: {
                            nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    user: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments_url: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    truncated: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public gists',
        name: 'Github: List public gists',
        description:
            'List public gists sorted by most recently updated to least recently updated.\n\nNote: With [pagination](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination), you can fetch up to 3000 gists. For example, you can fetch 100 pages with 30 gists per page or 30 pages with 100 gists per page.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        id: { kind: 'string' },
                        node_id: { kind: 'string' },
                        git_pull_url: { kind: 'string' },
                        git_push_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        files: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        comments_url: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        truncated: { kind: 'boolean' },
                        forks: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                        history: {
                            kind: 'array',
                            subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                    },
                    optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', 'public'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List starred gists',
        name: 'Github: List starred gists',
        description: "List the authenticated user's starred gists:",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        id: { kind: 'string' },
                        node_id: { kind: 'string' },
                        git_pull_url: { kind: 'string' },
                        git_push_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        files: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        comments_url: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        truncated: { kind: 'boolean' },
                        forks: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                        history: {
                            kind: 'array',
                            subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                    },
                    optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', 'starred'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a gist',
        name: 'Github: Get a gist',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    forks: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                                bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                public_repos: { kind: 'number' },
                                                public_gists: { kind: 'number' },
                                                followers: { kind: 'number' },
                                                following: { kind: 'number' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                plan: {
                                                    kind: 'object',
                                                    properties: {
                                                        collaborators: { kind: 'number' },
                                                        name: { kind: 'string' },
                                                        space: { kind: 'number' },
                                                        private_repos: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                private_gists: { kind: 'number' },
                                                total_private_repos: { kind: 'number' },
                                                owned_private_repos: { kind: 'number' },
                                                disk_usage: { kind: 'number' },
                                                collaborators: { kind: 'number' },
                                            },
                                            optionalProperties: [
                                                'twitter_username',
                                                'plan',
                                                'suspended_at',
                                                'private_gists',
                                                'total_private_repos',
                                                'owned_private_repos',
                                                'disk_usage',
                                                'collaborators',
                                            ],
                                            restriction: 'open',
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    history: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        version: { kind: 'string' },
                                        committed_at: { kind: 'string' },
                                        change_status: {
                                            kind: 'object',
                                            properties: {
                                                total: { kind: 'number' },
                                                additions: { kind: 'number' },
                                                deletions: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    fork_of: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    id: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    git_pull_url: { kind: 'string' },
                                    git_push_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    files: {
                                        kind: 'object',
                                        properties: {
                                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    public: { kind: 'boolean' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    comments: { kind: 'number' },
                                    user: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    comments_url: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    truncated: { kind: 'boolean' },
                                    forks: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    history: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                },
                                optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    id: { kind: 'string' },
                    node_id: { kind: 'string' },
                    git_pull_url: { kind: 'string' },
                    git_push_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    files: {
                        kind: 'object',
                        properties: {
                            nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    user: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments_url: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    truncated: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a gist',
        name: 'Github: Delete a gist',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a gist',
        name: 'Github: Update a gist',
        description:
            "Allows you to update a gist's description and to update, delete, or rename gist files. Files from the previous version of the gist that aren't explicitly changed during an edit are unchanged.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            description: { kind: 'string' },
                            files: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    anyOf: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    forks: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                                bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                public_repos: { kind: 'number' },
                                                public_gists: { kind: 'number' },
                                                followers: { kind: 'number' },
                                                following: { kind: 'number' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                plan: {
                                                    kind: 'object',
                                                    properties: {
                                                        collaborators: { kind: 'number' },
                                                        name: { kind: 'string' },
                                                        space: { kind: 'number' },
                                                        private_repos: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                private_gists: { kind: 'number' },
                                                total_private_repos: { kind: 'number' },
                                                owned_private_repos: { kind: 'number' },
                                                disk_usage: { kind: 'number' },
                                                collaborators: { kind: 'number' },
                                            },
                                            optionalProperties: [
                                                'twitter_username',
                                                'plan',
                                                'suspended_at',
                                                'private_gists',
                                                'total_private_repos',
                                                'owned_private_repos',
                                                'disk_usage',
                                                'collaborators',
                                            ],
                                            restriction: 'open',
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    history: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        version: { kind: 'string' },
                                        committed_at: { kind: 'string' },
                                        change_status: {
                                            kind: 'object',
                                            properties: {
                                                total: { kind: 'number' },
                                                additions: { kind: 'number' },
                                                deletions: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    fork_of: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    id: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    git_pull_url: { kind: 'string' },
                                    git_push_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    files: {
                                        kind: 'object',
                                        properties: {
                                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    public: { kind: 'boolean' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    comments: { kind: 'number' },
                                    user: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    comments_url: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    truncated: { kind: 'boolean' },
                                    forks: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    history: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                },
                                optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    id: { kind: 'string' },
                    node_id: { kind: 'string' },
                    git_pull_url: { kind: 'string' },
                    git_push_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    files: {
                        kind: 'object',
                        properties: {
                            nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    user: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments_url: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    truncated: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List gist comments',
        name: 'Github: List gist comments',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        body: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        author_association: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a gist comment',
        name: 'Github: Create a gist comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'comments'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a gist comment',
        name: 'Github: Get a gist comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'comments', input.inPorts.comment_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a gist comment',
        name: 'Github: Delete a gist comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'comments', input.inPorts.comment_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a gist comment',
        name: 'Github: Update a gist comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'comments', input.inPorts.comment_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List gist commits',
        name: 'Github: List gist commits',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        version: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        change_status: {
                            kind: 'object',
                            properties: { total: { kind: 'number' }, additions: { kind: 'number' }, deletions: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        committed_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'commits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List gist forks',
        name: 'Github: List gist forks',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        forks: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            user: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                                    bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    public_repos: { kind: 'number' },
                                                    public_gists: { kind: 'number' },
                                                    followers: { kind: 'number' },
                                                    following: { kind: 'number' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    plan: {
                                                        kind: 'object',
                                                        properties: {
                                                            collaborators: { kind: 'number' },
                                                            name: { kind: 'string' },
                                                            space: { kind: 'number' },
                                                            private_repos: { kind: 'number' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    private_gists: { kind: 'number' },
                                                    total_private_repos: { kind: 'number' },
                                                    owned_private_repos: { kind: 'number' },
                                                    disk_usage: { kind: 'number' },
                                                    collaborators: { kind: 'number' },
                                                },
                                                optionalProperties: [
                                                    'twitter_username',
                                                    'plan',
                                                    'suspended_at',
                                                    'private_gists',
                                                    'total_private_repos',
                                                    'owned_private_repos',
                                                    'disk_usage',
                                                    'collaborators',
                                                ],
                                                restriction: 'open',
                                            },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        history: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            user: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            version: { kind: 'string' },
                                            committed_at: { kind: 'string' },
                                            change_status: {
                                                kind: 'object',
                                                properties: {
                                                    total: { kind: 'number' },
                                                    additions: { kind: 'number' },
                                                    deletions: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        fork_of: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        id: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        git_pull_url: { kind: 'string' },
                                        git_push_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        files: {
                                            kind: 'object',
                                            properties: {
                                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        public: { kind: 'boolean' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        comments_url: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        truncated: { kind: 'boolean' },
                                        forks: {
                                            kind: 'array',
                                            subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                        history: {
                                            kind: 'array',
                                            subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                    },
                                    optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        id: { kind: 'string' },
                        node_id: { kind: 'string' },
                        git_pull_url: { kind: 'string' },
                        git_push_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        files: {
                            kind: 'object',
                            properties: {
                                nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        user: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments_url: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        truncated: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'forks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Fork a gist',
        name: 'Github: Fork a gist',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    id: { kind: 'string' },
                    node_id: { kind: 'string' },
                    git_pull_url: { kind: 'string' },
                    git_push_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    files: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    comments_url: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    truncated: { kind: 'boolean' },
                    forks: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    history: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                },
                optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'forks'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a gist is starred',
        name: 'Github: Check if a gist is starred',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'star'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Star a gist',
        name: 'Github: Star a gist',
        description:
            'Note that you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'star'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unstar a gist',
        name: 'Github: Unstar a gist',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, 'star'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a gist revision',
        name: 'Github: Get a gist revision',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gist_id: { kind: 'string' },
            sha: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    forks: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                                bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                public_repos: { kind: 'number' },
                                                public_gists: { kind: 'number' },
                                                followers: { kind: 'number' },
                                                following: { kind: 'number' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                plan: {
                                                    kind: 'object',
                                                    properties: {
                                                        collaborators: { kind: 'number' },
                                                        name: { kind: 'string' },
                                                        space: { kind: 'number' },
                                                        private_repos: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                private_gists: { kind: 'number' },
                                                total_private_repos: { kind: 'number' },
                                                owned_private_repos: { kind: 'number' },
                                                disk_usage: { kind: 'number' },
                                                collaborators: { kind: 'number' },
                                            },
                                            optionalProperties: [
                                                'twitter_username',
                                                'plan',
                                                'suspended_at',
                                                'private_gists',
                                                'total_private_repos',
                                                'owned_private_repos',
                                                'disk_usage',
                                                'collaborators',
                                            ],
                                            restriction: 'open',
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    history: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        version: { kind: 'string' },
                                        committed_at: { kind: 'string' },
                                        change_status: {
                                            kind: 'object',
                                            properties: {
                                                total: { kind: 'number' },
                                                additions: { kind: 'number' },
                                                deletions: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    fork_of: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    id: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    git_pull_url: { kind: 'string' },
                                    git_push_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    files: {
                                        kind: 'object',
                                        properties: {
                                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    public: { kind: 'boolean' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    comments: { kind: 'number' },
                                    user: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    comments_url: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    truncated: { kind: 'boolean' },
                                    forks: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    history: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                },
                                optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    id: { kind: 'string' },
                    node_id: { kind: 'string' },
                    git_pull_url: { kind: 'string' },
                    git_push_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    files: {
                        kind: 'object',
                        properties: {
                            nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    user: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments_url: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    truncated: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gists', input.inPorts.gist_id, input.inPorts.sha].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all gitignore templates',
        name: 'Github: Get all gitignore templates',
        description:
            'List all templates available to pass as an option when [creating a repository](https://docs.github.com/rest/repos/repos#create-a-repository-for-the-authenticated-user).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: { Response: { kind: 'array', subType: { kind: 'string' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gitignore', 'templates'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a gitignore template',
        name: 'Github: Get a gitignore template',
        description:
            'The API also allows fetching the source of a single template.\nUse the raw [media type](https://docs.github.com/rest/overview/media-types/) to get the raw contents.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { name: { kind: 'string' }, source: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'gitignore', 'templates', input.inPorts.name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories accessible to the app installation',
        name: 'Github: List repositories accessible to the app installation',
        description:
            'List repositories that an app installation can access.\n\nYou must use an [installation access token](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-an-installation) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    repository_selection: { kind: 'string' },
                },
                optionalProperties: ['repository_selection'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'installation', 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Revoke an installation access token',
        name: 'Github: Revoke an installation access token',
        description:
            'Revokes the installation token you\'re using to authenticate as an installation and access this endpoint.\n\nOnce an installation token is revoked, the token is invalidated and cannot be used. Other endpoints that require the revoked installation token must have a new installation token to work. You can create a new token using the "[Create an installation access token for an app](https://docs.github.com/rest/reference/apps#create-an-installation-access-token-for-an-app)" endpoint.\n\nYou must use an [installation access token](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-an-installation) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'installation', 'token'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List issues assigned to the authenticated user',
        name: 'Github: List issues assigned to the authenticated user',
        description:
            'List issues assigned to the authenticated user across all visible repositories including owned repositories, member\nrepositories, and organization repositories. You can use the `filter` query parameter to fetch issues that are not\nnecessarily assigned to you.\n\n\n**Note**: GitHub\'s REST API considers every pull request an issue, but not every issue is a pull request. For this\nreason, "Issues" endpoints may return both issues and pull requests in the response. You can identify pull requests by\nthe `pull_request` key. Be aware that the `id` of a pull request returned from "Issues" endpoints will be an _issue id_. To find out the pull\nrequest id, use the "[List pull requests](https://docs.github.com/rest/pulls/pulls#list-pull-requests)" endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            filter: { kind: 'string' },
            state: { kind: 'string' },
            labels: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            collab: { kind: 'boolean' },
            orgs: { kind: 'boolean' },
            owned: { kind: 'boolean' },
            pulls: { kind: 'boolean' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: [
            'filter',
            'state',
            'labels',
            'sort',
            'direction',
            'since',
            'collab',
            'orgs',
            'owned',
            'pulls',
            'per_page',
            'page',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        title: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        labels: {
                            kind: 'array',
                            subType: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'string' },
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            default: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                        },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assignees: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        milestone: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        title: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        creator: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        open_issues: { kind: 'number' },
                                        closed_issues: { kind: 'number' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        locked: { kind: 'boolean' },
                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        pull_request: {
                            kind: 'object',
                            properties: {
                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['merged_at'],
                            restriction: 'open',
                        },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        draft: { kind: 'boolean' },
                        closed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                        timeline_url: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        author_association: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [
                        'state_reason',
                        'body',
                        'assignees',
                        'active_lock_reason',
                        'pull_request',
                        'draft',
                        'closed_by',
                        'body_html',
                        'body_text',
                        'timeline_url',
                        'repository',
                        'performed_via_github_app',
                        'reactions',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'issues'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            filter: input.inPorts.filter,
                            state: input.inPorts.state,
                            labels: input.inPorts.labels,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            collab: input.inPorts.collab,
                            orgs: input.inPorts.orgs,
                            owned: input.inPorts.owned,
                            pulls: input.inPorts.pulls,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all commonly used licenses',
        name: 'Github: Get all commonly used licenses',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            featured: { kind: 'boolean' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['featured', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        key: { kind: 'string' },
                        name: { kind: 'string' },
                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        node_id: { kind: 'string' },
                        html_url: { kind: 'string' },
                    },
                    optionalProperties: ['html_url'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'licenses'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            featured: input.inPorts.featured,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a license',
        name: 'Github: Get a license',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            license: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key: { kind: 'string' },
                    name: { kind: 'string' },
                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    description: { kind: 'string' },
                    implementation: { kind: 'string' },
                    permissions: { kind: 'array', subType: { kind: 'string' } },
                    conditions: { kind: 'array', subType: { kind: 'string' } },
                    limitations: { kind: 'array', subType: { kind: 'string' } },
                    body: { kind: 'string' },
                    featured: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'licenses', input.inPorts.license].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Render a Markdown document',
        name: 'Github: Render a Markdown document',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { text: { kind: 'string' }, mode: { kind: 'string' }, context: { kind: 'string' } },
                optionalProperties: ['mode', 'context'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'markdown'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Render a Markdown document in raw mode',
        name: 'Github: Render a Markdown document in raw mode',
        description:
            'You must send Markdown as plain text (using a `Content-Type` header of `text/plain` or `text/x-markdown`) to this endpoint, rather than using JSON format. In raw mode, [GitHub Flavored Markdown](https://github.github.com/gfm/) is not supported and Markdown will be rendered in plain format like a README.md file. Markdown content must be 400 KB or less.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'markdown', 'raw'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a subscription plan for an account',
        name: 'Github: Get a subscription plan for an account',
        description:
            "Shows whether the user or organization account actively subscribes to a plan listed by the authenticated GitHub App. When someone submits a plan change that won't be processed until the end of their billing cycle, you will also see the upcoming pending change.\n\nGitHub Apps must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint. OAuth apps must use [basic authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) with their client ID and client secret to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            account_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    type: { kind: 'string' },
                    id: { kind: 'number' },
                    login: { kind: 'string' },
                    organization_billing_email: { kind: 'string' },
                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    marketplace_pending_change: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    is_installed: { kind: 'boolean' },
                                    effective_date: { kind: 'string' },
                                    unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                    id: { kind: 'number' },
                                    plan: {
                                        kind: 'object',
                                        properties: {
                                            url: { kind: 'string' },
                                            accounts_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            number: { kind: 'number' },
                                            name: { kind: 'string' },
                                            description: { kind: 'string' },
                                            monthly_price_in_cents: { kind: 'number' },
                                            yearly_price_in_cents: { kind: 'number' },
                                            price_model: { kind: 'string' },
                                            has_free_trial: { kind: 'boolean' },
                                            unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            state: { kind: 'string' },
                                            bullets: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    marketplace_purchase: {
                        kind: 'object',
                        properties: {
                            billing_cycle: { kind: 'string' },
                            next_billing_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_installed: { kind: 'boolean' },
                            unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            on_free_trial: { kind: 'boolean' },
                            free_trial_ends_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'string' },
                            plan: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    accounts_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    number: { kind: 'number' },
                                    name: { kind: 'string' },
                                    description: { kind: 'string' },
                                    monthly_price_in_cents: { kind: 'number' },
                                    yearly_price_in_cents: { kind: 'number' },
                                    price_model: { kind: 'string' },
                                    has_free_trial: { kind: 'boolean' },
                                    unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    state: { kind: 'string' },
                                    bullets: { kind: 'array', subType: { kind: 'string' } },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['organization_billing_email', 'email', 'marketplace_pending_change'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'marketplace_listing', 'accounts', input.inPorts.account_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List plans',
        name: 'Github: List plans',
        description:
            'Lists all plans that are part of your GitHub Marketplace listing.\n\nGitHub Apps must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint. OAuth apps must use [basic authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) with their client ID and client secret to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        accounts_url: { kind: 'string' },
                        id: { kind: 'number' },
                        number: { kind: 'number' },
                        name: { kind: 'string' },
                        description: { kind: 'string' },
                        monthly_price_in_cents: { kind: 'number' },
                        yearly_price_in_cents: { kind: 'number' },
                        price_model: { kind: 'string' },
                        has_free_trial: { kind: 'boolean' },
                        unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        state: { kind: 'string' },
                        bullets: { kind: 'array', subType: { kind: 'string' } },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'marketplace_listing', 'plans'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List accounts for a plan',
        name: 'Github: List accounts for a plan',
        description:
            "Returns user and organization accounts associated with the specified plan, including free plans. For per-seat pricing, you see the list of accounts that have purchased the plan, including the number of seats purchased. When someone submits a plan change that won't be processed until the end of their billing cycle, you will also see the upcoming pending change.\n\nGitHub Apps must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint. OAuth apps must use [basic authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) with their client ID and client secret to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            plan_id: { kind: 'number' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        type: { kind: 'string' },
                        id: { kind: 'number' },
                        login: { kind: 'string' },
                        organization_billing_email: { kind: 'string' },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        marketplace_pending_change: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        is_installed: { kind: 'boolean' },
                                        effective_date: { kind: 'string' },
                                        unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                        id: { kind: 'number' },
                                        plan: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                accounts_url: { kind: 'string' },
                                                id: { kind: 'number' },
                                                number: { kind: 'number' },
                                                name: { kind: 'string' },
                                                description: { kind: 'string' },
                                                monthly_price_in_cents: { kind: 'number' },
                                                yearly_price_in_cents: { kind: 'number' },
                                                price_model: { kind: 'string' },
                                                has_free_trial: { kind: 'boolean' },
                                                unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                state: { kind: 'string' },
                                                bullets: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        marketplace_purchase: {
                            kind: 'object',
                            properties: {
                                billing_cycle: { kind: 'string' },
                                next_billing_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                is_installed: { kind: 'boolean' },
                                unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                on_free_trial: { kind: 'boolean' },
                                free_trial_ends_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'string' },
                                plan: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        accounts_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        number: { kind: 'number' },
                                        name: { kind: 'string' },
                                        description: { kind: 'string' },
                                        monthly_price_in_cents: { kind: 'number' },
                                        yearly_price_in_cents: { kind: 'number' },
                                        price_model: { kind: 'string' },
                                        has_free_trial: { kind: 'boolean' },
                                        unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        state: { kind: 'string' },
                                        bullets: { kind: 'array', subType: { kind: 'string' } },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['organization_billing_email', 'email', 'marketplace_pending_change'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'marketplace_listing', 'plans', input.inPorts.plan_id, 'accounts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a subscription plan for an account (stubbed)',
        name: 'Github: Get a subscription plan for an account (stubbed)',
        description:
            "Shows whether the user or organization account actively subscribes to a plan listed by the authenticated GitHub App. When someone submits a plan change that won't be processed until the end of their billing cycle, you will also see the upcoming pending change.\n\nGitHub Apps must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint. OAuth apps must use [basic authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) with their client ID and client secret to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            account_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    type: { kind: 'string' },
                    id: { kind: 'number' },
                    login: { kind: 'string' },
                    organization_billing_email: { kind: 'string' },
                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    marketplace_pending_change: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    is_installed: { kind: 'boolean' },
                                    effective_date: { kind: 'string' },
                                    unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                    id: { kind: 'number' },
                                    plan: {
                                        kind: 'object',
                                        properties: {
                                            url: { kind: 'string' },
                                            accounts_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            number: { kind: 'number' },
                                            name: { kind: 'string' },
                                            description: { kind: 'string' },
                                            monthly_price_in_cents: { kind: 'number' },
                                            yearly_price_in_cents: { kind: 'number' },
                                            price_model: { kind: 'string' },
                                            has_free_trial: { kind: 'boolean' },
                                            unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            state: { kind: 'string' },
                                            bullets: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    marketplace_purchase: {
                        kind: 'object',
                        properties: {
                            billing_cycle: { kind: 'string' },
                            next_billing_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_installed: { kind: 'boolean' },
                            unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            on_free_trial: { kind: 'boolean' },
                            free_trial_ends_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'string' },
                            plan: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    accounts_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    number: { kind: 'number' },
                                    name: { kind: 'string' },
                                    description: { kind: 'string' },
                                    monthly_price_in_cents: { kind: 'number' },
                                    yearly_price_in_cents: { kind: 'number' },
                                    price_model: { kind: 'string' },
                                    has_free_trial: { kind: 'boolean' },
                                    unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    state: { kind: 'string' },
                                    bullets: { kind: 'array', subType: { kind: 'string' } },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['organization_billing_email', 'email', 'marketplace_pending_change'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'marketplace_listing', 'stubbed', 'accounts', input.inPorts.account_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List plans (stubbed)',
        name: 'Github: List plans (stubbed)',
        description:
            'Lists all plans that are part of your GitHub Marketplace listing.\n\nGitHub Apps must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint. OAuth apps must use [basic authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) with their client ID and client secret to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        accounts_url: { kind: 'string' },
                        id: { kind: 'number' },
                        number: { kind: 'number' },
                        name: { kind: 'string' },
                        description: { kind: 'string' },
                        monthly_price_in_cents: { kind: 'number' },
                        yearly_price_in_cents: { kind: 'number' },
                        price_model: { kind: 'string' },
                        has_free_trial: { kind: 'boolean' },
                        unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        state: { kind: 'string' },
                        bullets: { kind: 'array', subType: { kind: 'string' } },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'marketplace_listing', 'stubbed', 'plans'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List accounts for a plan (stubbed)',
        name: 'Github: List accounts for a plan (stubbed)',
        description:
            "Returns repository and organization accounts associated with the specified plan, including free plans. For per-seat pricing, you see the list of accounts that have purchased the plan, including the number of seats purchased. When someone submits a plan change that won't be processed until the end of their billing cycle, you will also see the upcoming pending change.\n\nGitHub Apps must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint. OAuth apps must use [basic authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication) with their client ID and client secret to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            plan_id: { kind: 'number' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        type: { kind: 'string' },
                        id: { kind: 'number' },
                        login: { kind: 'string' },
                        organization_billing_email: { kind: 'string' },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        marketplace_pending_change: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        is_installed: { kind: 'boolean' },
                                        effective_date: { kind: 'string' },
                                        unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                        id: { kind: 'number' },
                                        plan: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                accounts_url: { kind: 'string' },
                                                id: { kind: 'number' },
                                                number: { kind: 'number' },
                                                name: { kind: 'string' },
                                                description: { kind: 'string' },
                                                monthly_price_in_cents: { kind: 'number' },
                                                yearly_price_in_cents: { kind: 'number' },
                                                price_model: { kind: 'string' },
                                                has_free_trial: { kind: 'boolean' },
                                                unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                state: { kind: 'string' },
                                                bullets: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        marketplace_purchase: {
                            kind: 'object',
                            properties: {
                                billing_cycle: { kind: 'string' },
                                next_billing_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                is_installed: { kind: 'boolean' },
                                unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                on_free_trial: { kind: 'boolean' },
                                free_trial_ends_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'string' },
                                plan: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        accounts_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        number: { kind: 'number' },
                                        name: { kind: 'string' },
                                        description: { kind: 'string' },
                                        monthly_price_in_cents: { kind: 'number' },
                                        yearly_price_in_cents: { kind: 'number' },
                                        price_model: { kind: 'string' },
                                        has_free_trial: { kind: 'boolean' },
                                        unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        state: { kind: 'string' },
                                        bullets: { kind: 'array', subType: { kind: 'string' } },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['organization_billing_email', 'email', 'marketplace_pending_change'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'marketplace_listing', 'stubbed', 'plans', input.inPorts.plan_id, 'accounts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub meta information',
        name: 'Github: Get GitHub meta information',
        description:
            "Returns meta information about GitHub, including a list of GitHub's IP addresses. For more information, see \"[About GitHub's IP addresses](https://docs.github.com/articles/about-github-s-ip-addresses/).\"\n\nThe API's response also includes a list of GitHub's domain names.\n\nThe values shown in the documentation's response are example values. You must always query the API directly to get the latest values.\n\n**Note:** This endpoint returns both IPv4 and IPv6 addresses. However, not all features support IPv6. You should refer to the specific documentation for each feature to determine if IPv6 is supported.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    verifiable_password_authentication: { kind: 'boolean' },
                    ssh_key_fingerprints: {
                        kind: 'object',
                        properties: {
                            SHA256_RSA: { kind: 'string' },
                            SHA256_DSA: { kind: 'string' },
                            SHA256_ECDSA: { kind: 'string' },
                            SHA256_ED25519: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    ssh_keys: { kind: 'array', subType: { kind: 'string' } },
                    hooks: { kind: 'array', subType: { kind: 'string' } },
                    web: { kind: 'array', subType: { kind: 'string' } },
                    api: { kind: 'array', subType: { kind: 'string' } },
                    git: { kind: 'array', subType: { kind: 'string' } },
                    packages: { kind: 'array', subType: { kind: 'string' } },
                    pages: { kind: 'array', subType: { kind: 'string' } },
                    importer: { kind: 'array', subType: { kind: 'string' } },
                    actions: { kind: 'array', subType: { kind: 'string' } },
                    dependabot: { kind: 'array', subType: { kind: 'string' } },
                    domains: {
                        kind: 'object',
                        properties: {
                            website: { kind: 'array', subType: { kind: 'string' } },
                            codespaces: { kind: 'array', subType: { kind: 'string' } },
                            copilot: { kind: 'array', subType: { kind: 'string' } },
                            packages: { kind: 'array', subType: { kind: 'string' } },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'ssh_key_fingerprints',
                    'ssh_keys',
                    'hooks',
                    'web',
                    'api',
                    'git',
                    'packages',
                    'pages',
                    'importer',
                    'actions',
                    'dependabot',
                    'domains',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'meta'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public events for a network of repositories',
        name: 'Github: List public events for a network of repositories',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'networks', input.inPorts.owner, input.inPorts.repo, 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List notifications for the authenticated user',
        name: 'Github: List notifications for the authenticated user',
        description: 'List all notifications for the current user, sorted by most recently updated.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            all: { kind: 'boolean' },
            participating: { kind: 'boolean' },
            since: { kind: 'string' },
            before: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['all', 'participating', 'since', 'before', 'page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                        subject: {
                            kind: 'object',
                            properties: {
                                title: { kind: 'string' },
                                url: { kind: 'string' },
                                latest_comment_url: { kind: 'string' },
                                type: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        reason: { kind: 'string' },
                        unread: { kind: 'boolean' },
                        updated_at: { kind: 'string' },
                        last_read_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            all: input.inPorts.all,
                            participating: input.inPorts.participating,
                            since: input.inPorts.since,
                            before: input.inPorts.before,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Mark notifications as read',
        name: 'Github: Mark notifications as read',
        description:
            'Marks all notifications as "read" for the current user. If the number of notifications is too large to complete in one request, you will receive a `202 Accepted` status and GitHub will run an asynchronous process to mark notifications as "read." To check whether any "unread" notifications remain, you can use the [List notifications for the authenticated user](https://docs.github.com/rest/activity/notifications#list-notifications-for-the-authenticated-user) endpoint and pass the query parameter `all=false`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { last_read_at: { kind: 'string' }, read: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: { kind: 'object', properties: { message: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a thread',
        name: 'Github: Get a thread',
        description: 'Gets information about a notification thread.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            thread_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'string' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    subject: {
                        kind: 'object',
                        properties: {
                            title: { kind: 'string' },
                            url: { kind: 'string' },
                            latest_comment_url: { kind: 'string' },
                            type: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    reason: { kind: 'string' },
                    unread: { kind: 'boolean' },
                    updated_at: { kind: 'string' },
                    last_read_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications', 'threads', input.inPorts.thread_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Mark a thread as read',
        name: 'Github: Mark a thread as read',
        description:
            'Marks a thread as "read." Marking a thread as "read" is equivalent to clicking a notification in your notification inbox on GitHub: https://github.com/notifications.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            thread_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications', 'threads', input.inPorts.thread_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a thread subscription for the authenticated user',
        name: 'Github: Get a thread subscription for the authenticated user',
        description:
            "This checks to see if the current user is subscribed to a thread. You can also [get a repository subscription](https://docs.github.com/rest/activity/watching#get-a-repository-subscription).\n\nNote that subscriptions are only generated if a user is participating in a conversation--for example, they've replied to the thread, were **@mentioned**, or manually subscribe to a thread.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            thread_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    subscribed: { kind: 'boolean' },
                    ignored: { kind: 'boolean' },
                    reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    thread_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                },
                optionalProperties: ['thread_url', 'repository_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications', 'threads', input.inPorts.thread_id, 'subscription'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set a thread subscription',
        name: 'Github: Set a thread subscription',
        description:
            'If you are watching a repository, you receive notifications for all threads by default. Use this endpoint to ignore future notifications for threads until you comment on the thread or get an **@mention**.\n\nYou can also use this endpoint to subscribe to threads that you are currently not receiving notifications for or to subscribed to threads that you have previously ignored.\n\nUnsubscribing from a conversation in a repository that you are not watching is functionally equivalent to the [Delete a thread subscription](https://docs.github.com/rest/activity/notifications#delete-a-thread-subscription) endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            thread_id: { kind: 'number' },
            body: { kind: 'object', properties: { ignored: { kind: 'boolean' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    subscribed: { kind: 'boolean' },
                    ignored: { kind: 'boolean' },
                    reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    thread_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                },
                optionalProperties: ['thread_url', 'repository_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications', 'threads', input.inPorts.thread_id, 'subscription'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a thread subscription',
        name: 'Github: Delete a thread subscription',
        description:
            'Mutes all future notifications for a conversation until you comment on the thread or get an **@mention**. If you are watching the repository of the thread, you will still receive notifications. To ignore future notifications for a repository you are watching, use the [Set a thread subscription](https://docs.github.com/rest/activity/notifications#set-a-thread-subscription) endpoint and set `ignore` to `true`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            thread_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'notifications', 'threads', input.inPorts.thread_id, 'subscription'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get Octocat',
        name: 'Github: Get Octocat',
        description: 'Get the octocat as ASCII art',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            s: { kind: 'string' },
        },
        optionalInPorts: ['s'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'octocat'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ s: input.inPorts.s }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organizations',
        name: 'Github: List organizations',
        description:
            'Lists all organizations, in the order that they were created on GitHub.\n\n**Note:** Pagination is powered exclusively by the `since` parameter. Use the [Link header](https://docs.github.com/rest/guides/using-pagination-in-the-rest-api#using-link-headers) to get the URL for the next page of organizations.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            since: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        hooks_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        public_members_url: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'organizations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization',
        name: 'Github: Get an organization',
        description:
            'To see many of the organization response values, you need to be an authenticated organization owner with the `admin:org` scope. When the value of `two_factor_requirement_enabled` is `true`, the organization requires all members, billing managers, and outside collaborators to enable [two-factor authentication](https://docs.github.com/articles/securing-your-account-with-two-factor-authentication-2fa/).\n\nGitHub Apps with the `Organization plan` permission can use this endpoint to retrieve information about an organization\'s GitHub plan. See "[Authenticating with GitHub Apps](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/)" for details. For an example response, see \'Response with GitHub plan information\' below."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    login: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repos_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    hooks_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    members_url: { kind: 'string' },
                    public_members_url: { kind: 'string' },
                    avatar_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    name: { kind: 'string' },
                    company: { kind: 'string' },
                    blog: { kind: 'string' },
                    location: { kind: 'string' },
                    email: { kind: 'string' },
                    twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    is_verified: { kind: 'boolean' },
                    has_organization_projects: { kind: 'boolean' },
                    has_repository_projects: { kind: 'boolean' },
                    public_repos: { kind: 'number' },
                    public_gists: { kind: 'number' },
                    followers: { kind: 'number' },
                    following: { kind: 'number' },
                    html_url: { kind: 'string' },
                    type: { kind: 'string' },
                    total_private_repos: { kind: 'number' },
                    owned_private_repos: { kind: 'number' },
                    private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    plan: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'string' },
                            space: { kind: 'number' },
                            private_repos: { kind: 'number' },
                            filled_seats: { kind: 'number' },
                            seats: { kind: 'number' },
                        },
                        optionalProperties: ['filled_seats', 'seats'],
                        restriction: 'open',
                    },
                    default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    members_allowed_repository_creation_type: { kind: 'string' },
                    members_can_create_public_repositories: { kind: 'boolean' },
                    members_can_create_private_repositories: { kind: 'boolean' },
                    members_can_create_internal_repositories: { kind: 'boolean' },
                    members_can_create_pages: { kind: 'boolean' },
                    members_can_create_public_pages: { kind: 'boolean' },
                    members_can_create_private_pages: { kind: 'boolean' },
                    members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    web_commit_signoff_required: { kind: 'boolean' },
                    advanced_security_enabled_for_new_repositories: { kind: 'boolean' },
                    dependabot_alerts_enabled_for_new_repositories: { kind: 'boolean' },
                    dependabot_security_updates_enabled_for_new_repositories: { kind: 'boolean' },
                    dependency_graph_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_push_protection_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_push_protection_custom_link_enabled: { kind: 'boolean' },
                    secret_scanning_push_protection_custom_link: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'name',
                    'company',
                    'blog',
                    'location',
                    'email',
                    'twitter_username',
                    'is_verified',
                    'total_private_repos',
                    'owned_private_repos',
                    'private_gists',
                    'disk_usage',
                    'collaborators',
                    'billing_email',
                    'plan',
                    'default_repository_permission',
                    'members_can_create_repositories',
                    'two_factor_requirement_enabled',
                    'members_allowed_repository_creation_type',
                    'members_can_create_public_repositories',
                    'members_can_create_private_repositories',
                    'members_can_create_internal_repositories',
                    'members_can_create_pages',
                    'members_can_create_public_pages',
                    'members_can_create_private_pages',
                    'members_can_fork_private_repositories',
                    'web_commit_signoff_required',
                    'advanced_security_enabled_for_new_repositories',
                    'dependabot_alerts_enabled_for_new_repositories',
                    'dependabot_security_updates_enabled_for_new_repositories',
                    'dependency_graph_enabled_for_new_repositories',
                    'secret_scanning_enabled_for_new_repositories',
                    'secret_scanning_push_protection_enabled_for_new_repositories',
                    'secret_scanning_push_protection_custom_link_enabled',
                    'secret_scanning_push_protection_custom_link',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an organization',
        name: 'Github: Delete an organization',
        description:
            'Deletes an organization and all its repositories.\n\nThe organization login will be unavailable for 90 days after deletion.\n\nPlease review the Terms of Service regarding account deletion before using this endpoint:\n\nhttps://docs.github.com/site-policy/github-terms/github-terms-of-service',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an organization',
        name: 'Github: Update an organization',
        description:
            "**Parameter Deprecation Notice:** GitHub will replace and discontinue `members_allowed_repository_creation_type` in favor of more granular permissions. The new input parameters are `members_can_create_public_repositories`, `members_can_create_private_repositories` for all organizations and `members_can_create_internal_repositories` for organizations associated with an enterprise account using GitHub Enterprise Cloud or GitHub Enterprise Server 2.20+. For more information, see the [blog post](https://developer.github.com/changes/2019-12-03-internal-visibility-changes).\n\nEnables an authenticated organization owner with the `admin:org` scope or the `repo` scope to update the organization's profile and member privileges.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    billing_email: { kind: 'string' },
                    company: { kind: 'string' },
                    email: { kind: 'string' },
                    twitter_username: { kind: 'string' },
                    location: { kind: 'string' },
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    has_organization_projects: { kind: 'boolean' },
                    has_repository_projects: { kind: 'boolean' },
                    default_repository_permission: { kind: 'string' },
                    members_can_create_repositories: { kind: 'boolean' },
                    members_can_create_internal_repositories: { kind: 'boolean' },
                    members_can_create_private_repositories: { kind: 'boolean' },
                    members_can_create_public_repositories: { kind: 'boolean' },
                    members_allowed_repository_creation_type: { kind: 'string' },
                    members_can_create_pages: { kind: 'boolean' },
                    members_can_create_public_pages: { kind: 'boolean' },
                    members_can_create_private_pages: { kind: 'boolean' },
                    members_can_fork_private_repositories: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    blog: { kind: 'string' },
                    advanced_security_enabled_for_new_repositories: { kind: 'boolean' },
                    dependabot_alerts_enabled_for_new_repositories: { kind: 'boolean' },
                    dependabot_security_updates_enabled_for_new_repositories: { kind: 'boolean' },
                    dependency_graph_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_push_protection_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_push_protection_custom_link_enabled: { kind: 'boolean' },
                    secret_scanning_push_protection_custom_link: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    login: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repos_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    hooks_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    members_url: { kind: 'string' },
                    public_members_url: { kind: 'string' },
                    avatar_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    name: { kind: 'string' },
                    company: { kind: 'string' },
                    blog: { kind: 'string' },
                    location: { kind: 'string' },
                    email: { kind: 'string' },
                    twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    is_verified: { kind: 'boolean' },
                    has_organization_projects: { kind: 'boolean' },
                    has_repository_projects: { kind: 'boolean' },
                    public_repos: { kind: 'number' },
                    public_gists: { kind: 'number' },
                    followers: { kind: 'number' },
                    following: { kind: 'number' },
                    html_url: { kind: 'string' },
                    type: { kind: 'string' },
                    total_private_repos: { kind: 'number' },
                    owned_private_repos: { kind: 'number' },
                    private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    plan: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'string' },
                            space: { kind: 'number' },
                            private_repos: { kind: 'number' },
                            filled_seats: { kind: 'number' },
                            seats: { kind: 'number' },
                        },
                        optionalProperties: ['filled_seats', 'seats'],
                        restriction: 'open',
                    },
                    default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    members_allowed_repository_creation_type: { kind: 'string' },
                    members_can_create_public_repositories: { kind: 'boolean' },
                    members_can_create_private_repositories: { kind: 'boolean' },
                    members_can_create_internal_repositories: { kind: 'boolean' },
                    members_can_create_pages: { kind: 'boolean' },
                    members_can_create_public_pages: { kind: 'boolean' },
                    members_can_create_private_pages: { kind: 'boolean' },
                    members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    web_commit_signoff_required: { kind: 'boolean' },
                    advanced_security_enabled_for_new_repositories: { kind: 'boolean' },
                    dependabot_alerts_enabled_for_new_repositories: { kind: 'boolean' },
                    dependabot_security_updates_enabled_for_new_repositories: { kind: 'boolean' },
                    dependency_graph_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_push_protection_enabled_for_new_repositories: { kind: 'boolean' },
                    secret_scanning_push_protection_custom_link_enabled: { kind: 'boolean' },
                    secret_scanning_push_protection_custom_link: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'name',
                    'company',
                    'blog',
                    'location',
                    'email',
                    'twitter_username',
                    'is_verified',
                    'total_private_repos',
                    'owned_private_repos',
                    'private_gists',
                    'disk_usage',
                    'collaborators',
                    'billing_email',
                    'plan',
                    'default_repository_permission',
                    'members_can_create_repositories',
                    'two_factor_requirement_enabled',
                    'members_allowed_repository_creation_type',
                    'members_can_create_public_repositories',
                    'members_can_create_private_repositories',
                    'members_can_create_internal_repositories',
                    'members_can_create_pages',
                    'members_can_create_public_pages',
                    'members_can_create_private_pages',
                    'members_can_fork_private_repositories',
                    'web_commit_signoff_required',
                    'advanced_security_enabled_for_new_repositories',
                    'dependabot_alerts_enabled_for_new_repositories',
                    'dependabot_security_updates_enabled_for_new_repositories',
                    'dependency_graph_enabled_for_new_repositories',
                    'secret_scanning_enabled_for_new_repositories',
                    'secret_scanning_push_protection_enabled_for_new_repositories',
                    'secret_scanning_push_protection_custom_link_enabled',
                    'secret_scanning_push_protection_custom_link',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Actions cache usage for an organization',
        name: 'Github: Get GitHub Actions cache usage for an organization',
        description:
            'Gets the total GitHub Actions cache usage for an organization.\nThe data fetched using this API is refreshed approximately every 5 minutes, so values returned from this endpoint may take at least 5 minutes to get updated.\nYou must authenticate using an access token with the `read:org` scope to use this endpoint. GitHub Apps must have the `organization_admistration:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { total_active_caches_count: { kind: 'number' }, total_active_caches_size_in_bytes: { kind: 'number' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'cache', 'usage'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories with GitHub Actions cache usage for an organization',
        name: 'Github: List repositories with GitHub Actions cache usage for an organization',
        description:
            'Lists repositories and their GitHub Actions cache usage for an organization.\nThe data fetched using this API is refreshed approximately every 5 minutes, so values returned from this endpoint may take at least 5 minutes to get updated.\nYou must authenticate using an access token with the `read:org` scope to use this endpoint. GitHub Apps must have the `organization_admistration:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repository_cache_usages: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                full_name: { kind: 'string' },
                                active_caches_size_in_bytes: { kind: 'number' },
                                active_caches_count: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'cache', 'usage-by-repository'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the customization template for an OIDC subject claim for an organization',
        name: 'Github: Get the customization template for an OIDC subject claim for an organization',
        description:
            'Gets the customization template for an OpenID Connect (OIDC) subject claim.\nYou must authenticate using an access token with the `read:org` scope to use this endpoint.\nGitHub Apps must have the `organization_administration:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { include_claim_keys: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'oidc', 'customization', 'sub'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set the customization template for an OIDC subject claim for an organization',
        name: 'Github: Set the customization template for an OIDC subject claim for an organization',
        description:
            'Creates or updates the customization template for an OpenID Connect (OIDC) subject claim.\nYou must authenticate using an access token with the `write:org` scope to use this endpoint.\nGitHub Apps must have the `admin:org` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { include_claim_keys: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'oidc', 'customization', 'sub'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Actions permissions for an organization',
        name: 'Github: Get GitHub Actions permissions for an organization',
        description:
            'Gets the GitHub Actions permissions policy for repositories and allowed actions and reusable workflows in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    enabled_repositories: { kind: 'string' },
                    selected_repositories_url: { kind: 'string' },
                    allowed_actions: { kind: 'string' },
                    selected_actions_url: { kind: 'string' },
                },
                optionalProperties: ['selected_repositories_url', 'allowed_actions', 'selected_actions_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set GitHub Actions permissions for an organization',
        name: 'Github: Set GitHub Actions permissions for an organization',
        description:
            'Sets the GitHub Actions permissions policy for repositories and allowed actions and reusable workflows in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { enabled_repositories: { kind: 'string' }, allowed_actions: { kind: 'string' } },
                optionalProperties: ['allowed_actions'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List selected repositories enabled for GitHub Actions in an organization',
        name: 'Github: List selected repositories enabled for GitHub Actions in an organization',
        description:
            'Lists the selected repositories that are enabled for GitHub Actions in an organization. To use this endpoint, the organization permission policy for `enabled_repositories` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for an organization](#set-github-actions-permissions-for-an-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set selected repositories enabled for GitHub Actions in an organization',
        name: 'Github: Set selected repositories enabled for GitHub Actions in an organization',
        description:
            'Replaces the list of selected repositories that are enabled for GitHub Actions in an organization. To use this endpoint, the organization permission policy for `enabled_repositories` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for an organization](#set-github-actions-permissions-for-an-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_repository_ids: { kind: 'array', subType: { kind: 'number' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'repositories'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Enable a selected repository for GitHub Actions in an organization',
        name: 'Github: Enable a selected repository for GitHub Actions in an organization',
        description:
            'Adds a repository to the list of selected repositories that are enabled for GitHub Actions in an organization. To use this endpoint, the organization permission policy for `enabled_repositories` must be must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for an organization](#set-github-actions-permissions-for-an-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'repositories', input.inPorts.repository_id].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Disable a selected repository for GitHub Actions in an organization',
        name: 'Github: Disable a selected repository for GitHub Actions in an organization',
        description:
            'Removes a repository from the list of selected repositories that are enabled for GitHub Actions in an organization. To use this endpoint, the organization permission policy for `enabled_repositories` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for an organization](#set-github-actions-permissions-for-an-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'repositories', input.inPorts.repository_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get allowed actions and reusable workflows for an organization',
        name: 'Github: Get allowed actions and reusable workflows for an organization',
        description:
            'Gets the selected actions and reusable workflows that are allowed in an organization. To use this endpoint, the organization permission policy for `allowed_actions` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for an organization](#set-github-actions-permissions-for-an-organization).""\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    github_owned_allowed: { kind: 'boolean' },
                    verified_allowed: { kind: 'boolean' },
                    patterns_allowed: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'selected-actions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set allowed actions and reusable workflows for an organization',
        name: 'Github: Set allowed actions and reusable workflows for an organization',
        description:
            'Sets the actions and reusable workflows that are allowed in an organization. To use this endpoint, the organization permission policy for `allowed_actions` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for an organization](#set-github-actions-permissions-for-an-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    github_owned_allowed: { kind: 'boolean' },
                    verified_allowed: { kind: 'boolean' },
                    patterns_allowed: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'selected-actions'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get default workflow permissions for an organization',
        name: 'Github: Get default workflow permissions for an organization',
        description:
            'Gets the default workflow permissions granted to the `GITHUB_TOKEN` when running workflows in an organization,\nas well as whether GitHub Actions can submit approving pull request reviews. For more information, see\n"[Setting the permissions of the GITHUB_TOKEN for your organization](https://docs.github.com/organizations/managing-organization-settings/disabling-or-limiting-github-actions-for-your-organization#setting-the-permissions-of-the-github_token-for-your-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { default_workflow_permissions: { kind: 'string' }, can_approve_pull_request_reviews: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'workflow'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set default workflow permissions for an organization',
        name: 'Github: Set default workflow permissions for an organization',
        description:
            'Sets the default workflow permissions granted to the `GITHUB_TOKEN` when running workflows in an organization, and sets if GitHub Actions\ncan submit approving pull request reviews. For more information, see\n"[Setting the permissions of the GITHUB_TOKEN for your organization](https://docs.github.com/organizations/managing-organization-settings/disabling-or-limiting-github-actions-for-your-organization#setting-the-permissions-of-the-github_token-for-your-organization)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. GitHub Apps must have the `administration` organization permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { default_workflow_permissions: { kind: 'string' }, can_approve_pull_request_reviews: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'permissions', 'workflow'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List self-hosted runners for an organization',
        name: 'Github: List self-hosted runners for an organization',
        description:
            'Lists all self-hosted runners configured in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    runners: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                runner_group_id: { kind: 'number' },
                                name: { kind: 'string' },
                                os: { kind: 'string' },
                                status: { kind: 'string' },
                                busy: { kind: 'boolean' },
                                labels: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                                        optionalProperties: ['id', 'type'],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: ['runner_group_id'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List runner applications for an organization',
        name: 'Github: List runner applications for an organization',
        description:
            'Lists binaries for the runner application that you can download and run.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        os: { kind: 'string' },
                        architecture: { kind: 'string' },
                        download_url: { kind: 'string' },
                        filename: { kind: 'string' },
                        temp_download_token: { kind: 'string' },
                        sha256_checksum: { kind: 'string' },
                    },
                    optionalProperties: ['temp_download_token', 'sha256_checksum'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', 'downloads'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create configuration for a just-in-time runner for an organization',
        name: 'Github: Create configuration for a just-in-time runner for an organization',
        description:
            'Generates a configuration that can be passed to the runner application at startup.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    runner_group_id: { kind: 'number' },
                    labels: { kind: 'array', subType: { kind: 'string' } },
                    work_folder: { kind: 'string' },
                },
                optionalProperties: ['work_folder'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    runner: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            runner_group_id: { kind: 'number' },
                            name: { kind: 'string' },
                            os: { kind: 'string' },
                            status: { kind: 'string' },
                            busy: { kind: 'boolean' },
                            labels: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: ['id', 'type'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['runner_group_id'],
                        restriction: 'open',
                    },
                    encoded_jit_config: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', 'generate-jitconfig'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a registration token for an organization',
        name: 'Github: Create a registration token for an organization',
        description:
            'Returns a token that you can pass to the `config` script. The token expires after one hour.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.\n\nExample using registration token: \n\nConfigure your self-hosted runner, replacing `TOKEN` with the registration token provided by this endpoint.\n\n```\n./config.sh --url https://github.com/octo-org --token TOKEN\n```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    token: { kind: 'string' },
                    expires_at: { kind: 'string' },
                    permissions: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    single_file: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    repository_selection: { kind: 'string' },
                },
                optionalProperties: ['permissions', 'repositories', 'single_file', 'repository_selection'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', 'registration-token'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a remove token for an organization',
        name: 'Github: Create a remove token for an organization',
        description:
            'Returns a token that you can pass to the `config` script to remove a self-hosted runner from an organization. The token expires after one hour.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.\n\nExample using remove token:\n\nTo remove your self-hosted runner from an organization, replace `TOKEN` with the remove token provided by this\nendpoint.\n\n```\n./config.sh remove --token TOKEN\n```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    token: { kind: 'string' },
                    expires_at: { kind: 'string' },
                    permissions: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    single_file: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    repository_selection: { kind: 'string' },
                },
                optionalProperties: ['permissions', 'repositories', 'single_file', 'repository_selection'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', 'remove-token'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a self-hosted runner for an organization',
        name: 'Github: Get a self-hosted runner for an organization',
        description:
            'Gets a specific self-hosted runner configured in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    runner_group_id: { kind: 'number' },
                    name: { kind: 'string' },
                    os: { kind: 'string' },
                    status: { kind: 'string' },
                    busy: { kind: 'boolean' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: ['runner_group_id'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a self-hosted runner from an organization',
        name: 'Github: Delete a self-hosted runner from an organization',
        description:
            'Forces the removal of a self-hosted runner from an organization. You can use this endpoint to completely remove the runner when the machine you were using no longer exists.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List labels for a self-hosted runner for an organization',
        name: 'Github: List labels for a self-hosted runner for an organization',
        description:
            'Lists all labels for a self-hosted runner configured in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id, 'labels'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add custom labels to a self-hosted runner for an organization',
        name: 'Github: Add custom labels to a self-hosted runner for an organization',
        description:
            'Add custom labels to a self-hosted runner configured in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { labels: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id, 'labels'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set custom labels for a self-hosted runner for an organization',
        name: 'Github: Set custom labels for a self-hosted runner for an organization',
        description:
            'Remove all previous custom labels and set the new custom labels for a specific\nself-hosted runner configured in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { labels: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id, 'labels'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove all custom labels from a self-hosted runner for an organization',
        name: 'Github: Remove all custom labels from a self-hosted runner for an organization',
        description:
            'Remove all custom labels from a self-hosted runner configured in an\norganization. Returns the remaining read-only labels from the runner.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id, 'labels'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a custom label from a self-hosted runner for an organization',
        name: 'Github: Remove a custom label from a self-hosted runner for an organization',
        description:
            'Remove a custom label from a self-hosted runner configured\nin an organization. Returns the remaining labels from the runner.\n\nThis endpoint returns a `404 Not Found` status if the custom label is not\npresent on the runner.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            runner_id: { kind: 'number' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'runners', input.inPorts.runner_id, 'labels', input.inPorts.name].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization secrets',
        name: 'Github: List organization secrets',
        description:
            'Lists all secrets available in an organization without revealing their\nencrypted values.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    secrets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                visibility: { kind: 'string' },
                                selected_repositories_url: { kind: 'string' },
                            },
                            optionalProperties: ['selected_repositories_url'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization public key',
        name: 'Github: Get an organization public key',
        description:
            'Gets your public key, which you need to encrypt secrets. You need to\nencrypt a secret before you can create or update secrets.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key_id: { kind: 'string' },
                    key: { kind: 'string' },
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: ['id', 'url', 'title', 'created_at'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets', 'public-key'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization secret',
        name: 'Github: Get an organization secret',
        description:
            'Gets a single organization secret without revealing its encrypted value.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    visibility: { kind: 'string' },
                    selected_repositories_url: { kind: 'string' },
                },
                optionalProperties: ['selected_repositories_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create or update an organization secret',
        name: 'Github: Create or update an organization secret',
        description:
            'Creates or updates an organization secret with an encrypted value. Encrypt your secret using\n[LibSodium](https://libsodium.gitbook.io/doc/bindings_for_other_languages).\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.\n\n**Example encrypting a secret using Node.js*\n\nEncrypt your secret using the [libsodium-wrappers](https://www.npmjs.com/package/libsodium-wrappers) library.\n\n```\nconst sodium = require(\'libsodium-wrappers\')\nconst secret = \'plain-text-secret\' // replace with the secret you want to encrypt\nconst key = \'base64-encoded-public-key\' // replace with the Base64 encoded public key\n\n//Check if libsodium is ready and then proceed.\nsodium.ready.then(() => {\n  // Convert Secret & Base64 key to Uint8Array.\n  let binkey = sodium.from_base64(key, sodium.base64_variants.ORIGINAL)\n  let binsec = sodium.from_string(secret)\n\n  //Encrypt the secret using LibSodium\n  let encBytes = sodium.crypto_box_seal(binsec, binkey)\n\n  // Convert encrypted Uint8Array to Base64\n  let output = sodium.to_base64(encBytes, sodium.base64_variants.ORIGINAL)\n\n  console.log(output)\n});\n```\n\n**Example encrypting a secret using Python**\n\nEncrypt your secret using [pynacl](https://pynacl.readthedocs.io/en/latest/public/#nacl-public-sealedbox) with Python 3.\n\n```\nfrom base64 import b64encode\nfrom nacl import encoding, public\n\ndef encrypt(public_key: str, secret_value: str) -> str:\n  """Encrypt a Unicode string using the public key."""\n  public_key = public.PublicKey(public_key.encode("utf-8"), encoding.Base64Encoder())\n  sealed_box = public.SealedBox(public_key)\n  encrypted = sealed_box.encrypt(secret_value.encode("utf-8"))\n  return b64encode(encrypted).decode("utf-8")\n```\n\n**Example encrypting a secret using C#**\n\nEncrypt your secret using the [Sodium.Core](https://www.nuget.org/packages/Sodium.Core/) package.\n\n```\nvar secretValue = System.Text.Encoding.UTF8.GetBytes("mySecret");\nvar publicKey = Convert.FromBase64String("2Sg8iYjAxxmI2LvUXpJjkYrMxURPc8r+dB7TJyvvcCU=");\n\nvar sealedPublicKeyBox = Sodium.SealedPublicKeyBox.Create(secretValue, publicKey);\n\nConsole.WriteLine(Convert.ToBase64String(sealedPublicKeyBox));\n```\n\n**Example encrypting a secret using Ruby**\n\nEncrypt your secret using the [rbnacl](https://github.com/RubyCrypto/rbnacl) gem.\n\n```ruby\nrequire "rbnacl"\nrequire "base64"\n\nkey = Base64.decode64("+ZYvJDZMHUfBkJdyq5Zm9SKqeuBQ4sj+6sfjlH4CgG0=")\npublic_key = RbNaCl::PublicKey.new(key)\n\nbox = RbNaCl::Boxes::Sealed.from_public_key(public_key)\nencrypted_secret = box.encrypt("my_secret")\n\n# Print the base64 encoded secret\nputs Base64.strict_encode64(encrypted_secret)\n```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    encrypted_value: { kind: 'string' },
                    key_id: { kind: 'string' },
                    visibility: { kind: 'string' },
                    selected_repository_ids: { kind: 'array', subType: { kind: 'number' } },
                },
                optionalProperties: ['encrypted_value', 'key_id', 'selected_repository_ids'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an organization secret',
        name: 'Github: Delete an organization secret',
        description:
            'Deletes a secret in an organization using the secret name.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List selected repositories for an organization secret',
        name: 'Github: List selected repositories for an organization secret',
        description:
            'Lists all repositories that have been selected when the `visibility`\nfor repository access to a secret is set to `selected`.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets', input.inPorts.secret_name, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set selected repositories for an organization secret',
        name: 'Github: Set selected repositories for an organization secret',
        description:
            'Replaces all repositories for an organization secret when the `visibility`\nfor repository access is set to `selected`. The visibility is set when you [Create\nor update an organization secret](https://docs.github.com/rest/actions/secrets#create-or-update-an-organization-secret).\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_repository_ids: { kind: 'array', subType: { kind: 'number' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'secrets', input.inPorts.secret_name, 'repositories'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add selected repository to an organization secret',
        name: 'Github: Add selected repository to an organization secret',
        description:
            'Adds a repository to an organization secret when the `visibility` for\nrepository access is set to `selected`. The visibility is set when you [Create or\nupdate an organization secret](https://docs.github.com/rest/actions/secrets#create-or-update-an-organization-secret).\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'actions',
                        'secrets',
                        input.inPorts.secret_name,
                        'repositories',
                        input.inPorts.repository_id,
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove selected repository from an organization secret',
        name: 'Github: Remove selected repository from an organization secret',
        description:
            'Removes a repository from an organization secret when the `visibility`\nfor repository access is set to `selected`. The visibility is set when you [Create\nor update an organization secret](https://docs.github.com/rest/actions/secrets#create-or-update-an-organization-secret).\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            secret_name: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'actions',
                        'secrets',
                        input.inPorts.secret_name,
                        'repositories',
                        input.inPorts.repository_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization variables',
        name: 'Github: List organization variables',
        description:
            'Lists all organization variables.\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `organization_actions_variables:read` organization permission to use this endpoint. Authenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    variables: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                value: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                visibility: { kind: 'string' },
                                selected_repositories_url: { kind: 'string' },
                            },
                            optionalProperties: ['selected_repositories_url'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an organization variable',
        name: 'Github: Create an organization variable',
        description:
            'Creates an organization variable that you can reference in a GitHub Actions workflow.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:write` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    value: { kind: 'string' },
                    visibility: { kind: 'string' },
                    selected_repository_ids: { kind: 'array', subType: { kind: 'number' } },
                },
                optionalProperties: ['selected_repository_ids'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization variable',
        name: 'Github: Get an organization variable',
        description:
            'Gets a specific variable in an organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:read` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    value: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    visibility: { kind: 'string' },
                    selected_repositories_url: { kind: 'string' },
                },
                optionalProperties: ['selected_repositories_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables', input.inPorts.name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an organization variable',
        name: 'Github: Delete an organization variable',
        description:
            'Deletes an organization variable using the variable name.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:write` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables', input.inPorts.name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an organization variable',
        name: 'Github: Update an organization variable',
        description:
            'Updates an organization variable that you can reference in a GitHub Actions workflow.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:write` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    value: { kind: 'string' },
                    visibility: { kind: 'string' },
                    selected_repository_ids: { kind: 'array', subType: { kind: 'number' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables', input.inPorts.name].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List selected repositories for an organization variable',
        name: 'Github: List selected repositories for an organization variable',
        description:
            'Lists all repositories that can access an organization variable\nthat is available to selected repositories.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:read` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables', input.inPorts.name, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set selected repositories for an organization variable',
        name: 'Github: Set selected repositories for an organization variable',
        description:
            'Replaces all repositories for an organization variable that is available\nto selected repositories. Organization variables that are available to selected\nrepositories have their `visibility` field set to `selected`.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:write` organization permission to use this\nendpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_repository_ids: { kind: 'array', subType: { kind: 'number' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'actions', 'variables', input.inPorts.name, 'repositories'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add selected repository to an organization variable',
        name: 'Github: Add selected repository to an organization variable',
        description:
            'Adds a repository to an organization variable that is available to selected repositories.\nOrganization variables that are available to selected repositories have their `visibility` field set to `selected`.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:write` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'actions',
                        'variables',
                        input.inPorts.name,
                        'repositories',
                        input.inPorts.repository_id,
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove selected repository from an organization variable',
        name: 'Github: Remove selected repository from an organization variable',
        description:
            'Removes a repository from an organization variable that is\navailable to selected repositories. Organization variables that are available to\nselected repositories have their `visibility` field set to `selected`.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `organization_actions_variables:write` organization permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            name: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'actions',
                        'variables',
                        input.inPorts.name,
                        'repositories',
                        input.inPorts.repository_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List users blocked by an organization',
        name: 'Github: List users blocked by an organization',
        description: 'List the users blocked by an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'blocks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a user is blocked by an organization',
        name: 'Github: Check if a user is blocked by an organization',
        description:
            'Returns a 204 if the given user is blocked by the given organization. Returns a 404 if the organization is not blocking the user, or if the user account has been identified as spam by GitHub.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'blocks', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Block a user from an organization',
        name: 'Github: Block a user from an organization',
        description:
            'Blocks the given user on behalf of the specified organization and returns a 204. If the organization cannot block the given user a 422 is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'blocks', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unblock a user from an organization',
        name: 'Github: Unblock a user from an organization',
        description: 'Unblocks the given user on behalf of the specified organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'blocks', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List code scanning alerts for an organization',
        name: 'Github: List code scanning alerts for an organization',
        description:
            'Lists code scanning alerts for the default branch for all eligible repositories in an organization. Eligible repositories are repositories that are owned by organizations that you own or for which you are a security manager. For more information, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization)."\n\nTo use this endpoint, you must be an owner or security manager for the organization, and you must use an access token with the `repo` scope or `security_events` scope.\n\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have the `security_events` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            tool_name: { kind: 'string' },
            tool_guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
            before: { kind: 'string' },
            after: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            direction: { kind: 'string' },
            state: { kind: 'string' },
            sort: { kind: 'string' },
            severity: { kind: 'string' },
        },
        optionalInPorts: ['tool_name', 'tool_guid', 'before', 'after', 'page', 'per_page', 'direction', 'state', 'sort', 'severity'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        instances_url: { kind: 'string' },
                        state: { kind: 'string' },
                        fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        rule: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                name: { kind: 'string' },
                                severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                security_severity_level: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                description: { kind: 'string' },
                                full_description: { kind: 'string' },
                                tags: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                                help: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                help_uri: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        tool: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                version: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        most_recent_instance: {
                            kind: 'object',
                            properties: {
                                ref: { kind: 'string' },
                                analysis_key: { kind: 'string' },
                                environment: { kind: 'string' },
                                category: { kind: 'string' },
                                state: { kind: 'string' },
                                commit_sha: { kind: 'string' },
                                message: {
                                    kind: 'object',
                                    properties: { text: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                location: {
                                    kind: 'object',
                                    properties: {
                                        path: { kind: 'string' },
                                        start_line: { kind: 'number' },
                                        end_line: { kind: 'number' },
                                        start_column: { kind: 'number' },
                                        end_column: { kind: 'number' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html_url: { kind: 'string' },
                                classifications: {
                                    kind: 'array',
                                    subType: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['updated_at', 'fixed_at', 'dismissed_comment'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'code-scanning', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            tool_name: input.inPorts.tool_name,
                            tool_guid: input.inPorts.tool_guid,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                            direction: input.inPorts.direction,
                            state: input.inPorts.state,
                            sort: input.inPorts.sort,
                            severity: input.inPorts.severity,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List codespaces for the organization',
        name: 'Github: List codespaces for the organization',
        description:
            'Lists the codespaces associated to a specified organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    codespaces: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                name: { kind: 'string' },
                                display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                billable_owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                machine: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'string' },
                                                display_name: { kind: 'string' },
                                                operating_system: { kind: 'string' },
                                                storage_in_bytes: { kind: 'number' },
                                                memory_in_bytes: { kind: 'number' },
                                                cpus: { kind: 'number' },
                                                prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                last_used_at: { kind: 'string' },
                                state: { kind: 'string' },
                                url: { kind: 'string' },
                                git_status: {
                                    kind: 'object',
                                    properties: {
                                        ahead: { kind: 'number' },
                                        behind: { kind: 'number' },
                                        has_unpushed_changes: { kind: 'boolean' },
                                        has_uncommitted_changes: { kind: 'boolean' },
                                        ref: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                location: { kind: 'string' },
                                idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                web_url: { kind: 'string' },
                                machines_url: { kind: 'string' },
                                start_url: { kind: 'string' },
                                stop_url: { kind: 'string' },
                                publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                recent_folders: { kind: 'array', subType: { kind: 'string' } },
                                runtime_constraints: {
                                    kind: 'object',
                                    properties: {
                                        allowed_port_privacy_settings: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [
                                'display_name',
                                'devcontainer_path',
                                'publish_url',
                                'runtime_constraints',
                                'pending_operation',
                                'pending_operation_disabled_reason',
                                'idle_timeout_notice',
                                'retention_period_minutes',
                                'retention_expires_at',
                                'last_known_stop_notice',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'codespaces'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Manage access control for organization codespaces',
        name: 'Github: Manage access control for organization codespaces',
        description:
            'Sets which users can access codespaces in an organization. This is synonymous with granting or revoking codespaces billing permissions for users according to the visibility.\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { visibility: { kind: 'string' }, selected_usernames: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: ['selected_usernames'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'codespaces', 'billing'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add users to Codespaces billing for an organization',
        name: 'Github: Add users to Codespaces billing for an organization',
        description:
            'Codespaces for the specified users will be billed to the organization.\n\nTo use this endpoint, the billing settings for the organization must be set to `selected_members`.\nFor information on how to change this setting, see "[Manage access control for organization codespaces](https://docs.github.com/rest/codespaces/organizations#manage-access-control-for-organization-codespaces)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_usernames: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'codespaces', 'billing', 'selected_users'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove users from Codespaces billing for an organization',
        name: 'Github: Remove users from Codespaces billing for an organization',
        description:
            'Codespaces for the specified users will no longer be billed to the organization.\n\nTo use this endpoint, the billing settings for the organization must be set to `selected_members`.\nFor information on how to change this setting, see "[Manage access control for organization codespaces](https://docs.github.com/rest/codespaces/organizations#manage-access-control-for-organization-codespaces)."\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_usernames: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'codespaces', 'billing', 'selected_users'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get Copilot for Business seat information and settings for an organization',
        name: 'Github: Get Copilot for Business seat information and settings for an organization',
        description:
            "**Note**: This endpoint is in beta and is subject to change.\n\nGets information about an organization's Copilot for Business subscription, including seat breakdown\nand code matching policies. To configure these settings, go to your organization's settings on GitHub.com.\nFor more information, see \"[Configuring GitHub Copilot settings in your organization](https://docs.github.com/copilot/configuring-github-copilot/configuring-github-copilot-settings-in-your-organization)\".\n\nOnly organization owners and members with admin permissions can configure and view details about the organization's Copilot for Business subscription. You must\nauthenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    seat_breakdown: {
                        kind: 'object',
                        properties: {
                            total: { kind: 'number' },
                            added_this_cycle: { kind: 'number' },
                            pending_cancellation: { kind: 'number' },
                            pending_invitation: { kind: 'number' },
                            active_this_cycle: { kind: 'number' },
                            inactive_this_cycle: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public_code_suggestions: { kind: 'string' },
                    seat_management_setting: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'copilot', 'billing'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List all Copilot for Business seat assignments for an organization',
        name: 'Github: List all Copilot for Business seat assignments for an organization',
        description:
            "**Note**: This endpoint is in beta and is subject to change.\n\nLists all Copilot for Business seat assignments for an organization that are currently being billed (either active or pending cancellation at the start of the next billing cycle).\n\nOnly organization owners and members with admin permissions can configure and view details about the organization's Copilot for Business subscription. You must\nauthenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_seats: { kind: 'number' },
                    seats: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                assignee: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                assigning_team: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        permission: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                pull: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        parent: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        members_url: { kind: 'string' },
                                                                        name: { kind: 'string' },
                                                                        description: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        permission: { kind: 'string' },
                                                                        privacy: { kind: 'string' },
                                                                        notification_setting: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        repositories_url: { kind: 'string' },
                                                                        slug: { kind: 'string' },
                                                                        ldap_dn: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                    restriction: 'open',
                                                },
                                            ],
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                pending_cancellation_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_activity_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_activity_editor: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                            },
                            optionalProperties: [
                                'assigning_team',
                                'pending_cancellation_date',
                                'last_activity_at',
                                'last_activity_editor',
                                'updated_at',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'copilot', 'billing', 'seats'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add teams to the Copilot for Business subscription for an organization',
        name: 'Github: Add teams to the Copilot for Business subscription for an organization',
        description:
            '**Note**: This endpoint is in beta and is subject to change.\n\n Purchases a GitHub Copilot for Business seat for all users within each specified team.\n The organization will be billed accordingly. For more information about Copilot for Business pricing, see "[About billing for GitHub Copilot for Business](https://docs.github.com/billing/managing-billing-for-github-copilot/about-billing-for-github-copilot#pricing-for-github-copilot-for-business)".\n\n Only organization owners and members with admin permissions can configure GitHub Copilot in their organization. You must\n authenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.\n\n In order for an admin to use this endpoint, the organization must have a Copilot for Business subscription and a configured suggestion matching policy.\n For more information about setting up a Copilot for Business subscription, see "[Setting up a Copilot for Business subscription for your organization](https://docs.github.com/billing/managing-billing-for-github-copilot/managing-your-github-copilot-subscription-for-your-organization-or-enterprise#setting-up-a-copilot-for-business-subscription-for-your-organization)".\n For more information about setting a suggestion matching policy, see "[Configuring suggestion matching policies for GitHub Copilot in your organization](https://docs.github.com/copilot/configuring-github-copilot/configuring-github-copilot-settings-in-your-organization#configuring-suggestion-matching-policies-for-github-copilot-in-your-organization)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_teams: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { seats_created: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'copilot', 'billing', 'selected_teams'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove teams from the Copilot for Business subscription for an organization',
        name: 'Github: Remove teams from the Copilot for Business subscription for an organization',
        description:
            '**Note**: This endpoint is in beta and is subject to change.\n\nCancels the Copilot for Business seat assignment for all members of each team specified.\nThis will cause the members of the specified team(s) to lose access to GitHub Copilot at the end of the current billing cycle, and the organization will not be billed further for those users.\n\nFor more information about Copilot for Business pricing, see "[About billing for GitHub Copilot for Business](https://docs.github.com/billing/managing-billing-for-github-copilot/about-billing-for-github-copilot#pricing-for-github-copilot-for-business)".\n\nFor more information about disabling access to Copilot for Business, see "[Disabling access to GitHub Copilot for specific users in your organization](https://docs.github.com/copilot/configuring-github-copilot/configuring-github-copilot-settings-in-your-organization#disabling-access-to-github-copilot-for-specific-users-in-your-organization)".\n\nOnly organization owners and members with admin permissions can configure GitHub Copilot in their organization. You must\nauthenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_teams: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { seats_cancelled: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'copilot', 'billing', 'selected_teams'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add users to the Copilot for Business subscription for an organization',
        name: 'Github: Add users to the Copilot for Business subscription for an organization',
        description:
            '**Note**: This endpoint is in beta and is subject to change.\n\nPurchases a GitHub Copilot for Business seat for each user specified.\nThe organization will be billed accordingly. For more information about Copilot for Business pricing, see "[About billing for GitHub Copilot for Business](https://docs.github.com/billing/managing-billing-for-github-copilot/about-billing-for-github-copilot#pricing-for-github-copilot-for-business)".\n\nOnly organization owners and members with admin permissions can configure GitHub Copilot in their organization. You must\nauthenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.\n\nIn order for an admin to use this endpoint, the organization must have a Copilot for Business subscription and a configured suggestion matching policy.\nFor more information about setting up a Copilot for Business subscription, see "[Setting up a Copilot for Business subscription for your organization](https://docs.github.com/billing/managing-billing-for-github-copilot/managing-your-github-copilot-subscription-for-your-organization-or-enterprise#setting-up-a-copilot-for-business-subscription-for-your-organization)".\nFor more information about setting a suggestion matching policy, see "[Configuring suggestion matching policies for GitHub Copilot in your organization](https://docs.github.com/copilot/configuring-github-copilot/configuring-github-copilot-settings-in-your-organization#configuring-suggestion-matching-policies-for-github-copilot-in-your-organization)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_usernames: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { seats_created: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'copilot', 'billing', 'selected_users'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove users from the Copilot for Business subscription for an organization',
        name: 'Github: Remove users from the Copilot for Business subscription for an organization',
        description:
            '**Note**: This endpoint is in beta and is subject to change.\n\nCancels the Copilot for Business seat assignment for each user specified.\nThis will cause the specified users to lose access to GitHub Copilot at the end of the current billing cycle, and the organization will not be billed further for those users.\n\nFor more information about Copilot for Business pricing, see "[About billing for GitHub Copilot for Business](https://docs.github.com/billing/managing-billing-for-github-copilot/about-billing-for-github-copilot#pricing-for-github-copilot-for-business)"\n\nFor more information about disabling access to Copilot for Business, see "[Disabling access to GitHub Copilot for specific users in your organization](https://docs.github.com/copilot/configuring-github-copilot/configuring-github-copilot-settings-in-your-organization#disabling-access-to-github-copilot-for-specific-users-in-your-organization)".\n\nOnly organization owners and members with admin permissions can configure GitHub Copilot in their organization. You must\nauthenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_usernames: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { seats_cancelled: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'copilot', 'billing', 'selected_users'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List Dependabot alerts for an organization',
        name: 'Github: List Dependabot alerts for an organization',
        description:
            'Lists Dependabot alerts for an organization.\n\nTo use this endpoint, you must be an owner or security manager for the organization, and you must use an access token with the `repo` scope or `security_events` scope.\n\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have **Dependabot alerts** read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            state: { kind: 'string' },
            severity: { kind: 'string' },
            ecosystem: { kind: 'string' },
            package: { kind: 'string' },
            scope: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            before: { kind: 'string' },
            after: { kind: 'string' },
            first: { kind: 'number' },
            last: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: [
            'state',
            'severity',
            'ecosystem',
            'package',
            'scope',
            'sort',
            'direction',
            'before',
            'after',
            'first',
            'last',
            'per_page',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        dependency: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                manifest_path: { kind: 'string' },
                                scope: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        security_advisory: {
                            kind: 'object',
                            properties: {
                                ghsa_id: { kind: 'string' },
                                cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                summary: { kind: 'string' },
                                description: { kind: 'string' },
                                vulnerabilities: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            package: {
                                                kind: 'object',
                                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            severity: { kind: 'string' },
                                            vulnerable_version_range: { kind: 'string' },
                                            first_patched_version: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: { identifier: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                severity: { kind: 'string' },
                                cvss: {
                                    kind: 'object',
                                    properties: {
                                        score: { kind: 'number' },
                                        vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                cwes: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                identifiers: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                references: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                published_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        security_vulnerability: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                severity: { kind: 'string' },
                                vulnerable_version_range: { kind: 'string' },
                                first_patched_version: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { identifier: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        auto_dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['auto_dismissed_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'dependabot', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            severity: input.inPorts.severity,
                            ecosystem: input.inPorts.ecosystem,
                            package: input.inPorts.package,
                            scope: input.inPorts.scope,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                            first: input.inPorts.first,
                            last: input.inPorts.last,
                            per_page: input.inPorts.per_page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get list of conflicting packages during Docker migration for organization',
        name: 'Github: Get list of conflicting packages during Docker migration for organization',
        description:
            'Lists all packages that are in a specific organization, are readable by the requesting user, and that encountered a conflict during a Docker migration.\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        package_type: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        version_count: { kind: 'number' },
                        visibility: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: ['owner', 'repository'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'docker', 'conflicts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public organization events',
        name: 'Github: List public organization events',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List failed organization invitations',
        name: 'Github: List failed organization invitations',
        description:
            'The return hash contains `failed_at` and `failed_reason` fields which represent the time at which the invitation failed and the reason for the failure.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        login: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        role: { kind: 'string' },
                        created_at: { kind: 'string' },
                        failed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        failed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        inviter: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        team_count: { kind: 'number' },
                        node_id: { kind: 'string' },
                        invitation_teams_url: { kind: 'string' },
                        invitation_source: { kind: 'string' },
                    },
                    optionalProperties: ['failed_at', 'failed_reason', 'invitation_source'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'failed_invitations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization webhooks',
        name: 'Github: List organization webhooks',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        url: { kind: 'string' },
                        ping_url: { kind: 'string' },
                        deliveries_url: { kind: 'string' },
                        name: { kind: 'string' },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        active: { kind: 'boolean' },
                        config: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                insecure_ssl: { kind: 'string' },
                                content_type: { kind: 'string' },
                                secret: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        updated_at: { kind: 'string' },
                        created_at: { kind: 'string' },
                        type: { kind: 'string' },
                    },
                    optionalProperties: ['deliveries_url'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an organization webhook',
        name: 'Github: Create an organization webhook',
        description: "Here's how you can create a hook that posts payloads in JSON format:",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    config: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            content_type: { kind: 'string' },
                            secret: { kind: 'string' },
                            insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                            username: { kind: 'string' },
                            password: { kind: 'string' },
                        },
                        optionalProperties: ['content_type', 'secret', 'insecure_ssl', 'username', 'password'],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    active: { kind: 'boolean' },
                },
                optionalProperties: ['events', 'active'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    ping_url: { kind: 'string' },
                    deliveries_url: { kind: 'string' },
                    name: { kind: 'string' },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    active: { kind: 'boolean' },
                    config: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            insecure_ssl: { kind: 'string' },
                            content_type: { kind: 'string' },
                            secret: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: ['deliveries_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization webhook',
        name: 'Github: Get an organization webhook',
        description:
            'Returns a webhook configured in an organization. To get only the webhook `config` properties, see "[Get a webhook configuration for an organization](/rest/orgs/webhooks#get-a-webhook-configuration-for-an-organization)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    ping_url: { kind: 'string' },
                    deliveries_url: { kind: 'string' },
                    name: { kind: 'string' },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    active: { kind: 'boolean' },
                    config: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            insecure_ssl: { kind: 'string' },
                            content_type: { kind: 'string' },
                            secret: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: ['deliveries_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an organization webhook',
        name: 'Github: Delete an organization webhook',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an organization webhook',
        name: 'Github: Update an organization webhook',
        description:
            'Updates a webhook configured in an organization. When you update a webhook, the `secret` will be overwritten. If you previously had a `secret` set, you must provide the same `secret` or set a new `secret` or the secret will be removed. If you are only updating individual webhook `config` properties, use "[Update a webhook configuration for an organization](/rest/orgs/webhooks#update-a-webhook-configuration-for-an-organization)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    config: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            content_type: { kind: 'string' },
                            secret: { kind: 'string' },
                            insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                        },
                        optionalProperties: ['content_type', 'secret', 'insecure_ssl'],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    active: { kind: 'boolean' },
                    name: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    ping_url: { kind: 'string' },
                    deliveries_url: { kind: 'string' },
                    name: { kind: 'string' },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    active: { kind: 'boolean' },
                    config: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            insecure_ssl: { kind: 'string' },
                            content_type: { kind: 'string' },
                            secret: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    type: { kind: 'string' },
                },
                optionalProperties: ['deliveries_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a webhook configuration for an organization',
        name: 'Github: Get a webhook configuration for an organization',
        description:
            'Returns the webhook configuration for an organization. To get more information about the webhook, including the `active` state and `events`, use "[Get an organization webhook ](/rest/orgs/webhooks#get-an-organization-webhook)."\n\nAccess tokens must have the `admin:org_hook` scope, and GitHub Apps must have the `organization_hooks:read` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id, 'config'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a webhook configuration for an organization',
        name: 'Github: Update a webhook configuration for an organization',
        description:
            'Updates the webhook configuration for an organization. To update more information about the webhook, including the `active` state and `events`, use "[Update an organization webhook ](/rest/orgs/webhooks#update-an-organization-webhook)."\n\nAccess tokens must have the `admin:org_hook` scope, and GitHub Apps must have the `organization_hooks:write` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id, 'config'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deliveries for an organization webhook',
        name: 'Github: List deliveries for an organization webhook',
        description: 'Returns a list of webhook deliveries for a webhook configured in an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
            per_page: { kind: 'number' },
            cursor: { kind: 'string' },
            redelivery: { kind: 'boolean' },
        },
        optionalInPorts: ['per_page', 'cursor', 'redelivery'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        guid: { kind: 'string' },
                        delivered_at: { kind: 'string' },
                        redelivery: { kind: 'boolean' },
                        duration: { kind: 'number' },
                        status: { kind: 'string' },
                        status_code: { kind: 'number' },
                        event: { kind: 'string' },
                        action: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        installation_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        repository_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id, 'deliveries'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            cursor: input.inPorts.cursor,
                            redelivery: input.inPorts.redelivery,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a webhook delivery for an organization webhook',
        name: 'Github: Get a webhook delivery for an organization webhook',
        description: 'Returns a delivery for a webhook configured in an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
            delivery_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    guid: { kind: 'string' },
                    delivered_at: { kind: 'string' },
                    redelivery: { kind: 'boolean' },
                    duration: { kind: 'number' },
                    status: { kind: 'string' },
                    status_code: { kind: 'number' },
                    event: { kind: 'string' },
                    action: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    installation_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    repository_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    request: {
                        kind: 'object',
                        properties: {
                            headers: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                            payload: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    response: {
                        kind: 'object',
                        properties: {
                            headers: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id, 'deliveries', input.inPorts.delivery_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Redeliver a delivery for an organization webhook',
        name: 'Github: Redeliver a delivery for an organization webhook',
        description: 'Redeliver a delivery for a webhook configured in an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
            delivery_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'hooks',
                        input.inPorts.hook_id,
                        'deliveries',
                        input.inPorts.delivery_id,
                        'attempts',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Ping an organization webhook',
        name: 'Github: Ping an organization webhook',
        description: 'This will trigger a [ping event](https://docs.github.com/webhooks/#ping-event) to be sent to the hook.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'hooks', input.inPorts.hook_id, 'pings'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization installation for the authenticated app',
        name: 'Github: Get an organization installation for the authenticated app',
        description:
            "Enables an authenticated GitHub App to find the organization's installation information.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    account: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    {
                                        kind: 'object',
                                        properties: {
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            avatar_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['description', 'website_url'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository_selection: { kind: 'string' },
                    access_tokens_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    app_id: { kind: 'number' },
                    target_id: { kind: 'number' },
                    target_type: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    has_multiple_single_files: { kind: 'boolean' },
                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                    app_slug: { kind: 'string' },
                    suspended_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'installation'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List app installations for an organization',
        name: 'Github: List app installations for an organization',
        description:
            'Lists all GitHub Apps in an organization. The installation count includes all GitHub Apps installed on repositories in the organization. You must be an organization owner with `admin:read` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    installations: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                account: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        html_url: { kind: 'string' },
                                                        website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        avatar_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['description', 'website_url'],
                                                    restriction: 'open',
                                                },
                                            ],
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                repository_selection: { kind: 'string' },
                                access_tokens_url: { kind: 'string' },
                                repositories_url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                app_id: { kind: 'number' },
                                target_id: { kind: 'number' },
                                target_type: { kind: 'string' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        actions: { kind: 'string' },
                                        administration: { kind: 'string' },
                                        checks: { kind: 'string' },
                                        contents: { kind: 'string' },
                                        deployments: { kind: 'string' },
                                        environments: { kind: 'string' },
                                        issues: { kind: 'string' },
                                        metadata: { kind: 'string' },
                                        packages: { kind: 'string' },
                                        pages: { kind: 'string' },
                                        pull_requests: { kind: 'string' },
                                        repository_hooks: { kind: 'string' },
                                        repository_projects: { kind: 'string' },
                                        secret_scanning_alerts: { kind: 'string' },
                                        secrets: { kind: 'string' },
                                        security_events: { kind: 'string' },
                                        single_file: { kind: 'string' },
                                        statuses: { kind: 'string' },
                                        vulnerability_alerts: { kind: 'string' },
                                        workflows: { kind: 'string' },
                                        members: { kind: 'string' },
                                        organization_administration: { kind: 'string' },
                                        organization_custom_roles: { kind: 'string' },
                                        organization_announcement_banners: { kind: 'string' },
                                        organization_hooks: { kind: 'string' },
                                        organization_personal_access_tokens: { kind: 'string' },
                                        organization_personal_access_token_requests: { kind: 'string' },
                                        organization_plan: { kind: 'string' },
                                        organization_projects: { kind: 'string' },
                                        organization_packages: { kind: 'string' },
                                        organization_secrets: { kind: 'string' },
                                        organization_self_hosted_runners: { kind: 'string' },
                                        organization_user_blocking: { kind: 'string' },
                                        team_discussions: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                events: { kind: 'array', subType: { kind: 'string' } },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                has_multiple_single_files: { kind: 'boolean' },
                                single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                                app_slug: { kind: 'string' },
                                suspended_by: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'installations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get interaction restrictions for an organization',
        name: 'Github: Get interaction restrictions for an organization',
        description:
            'Shows which type of GitHub user can interact with this organization and when the restriction expires. If there is no restrictions, you will see an empty response.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { limit: { kind: 'string' }, origin: { kind: 'string' }, expires_at: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'interaction-limits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set interaction restrictions for an organization',
        name: 'Github: Set interaction restrictions for an organization',
        description:
            'Temporarily restricts interactions to a certain type of GitHub user in any public repository in the given organization. You must be an organization owner to set these restrictions. Setting the interaction limit at the organization level will overwrite any interaction limits that are set for individual repositories owned by the organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { limit: { kind: 'string' }, expiry: { kind: 'string' } },
                optionalProperties: ['expiry'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { limit: { kind: 'string' }, origin: { kind: 'string' }, expires_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'interaction-limits'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove interaction restrictions for an organization',
        name: 'Github: Remove interaction restrictions for an organization',
        description:
            'Removes all interaction restrictions from public repositories in the given organization. You must be an organization owner to remove restrictions.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'interaction-limits'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List pending organization invitations',
        name: 'Github: List pending organization invitations',
        description:
            'The return hash contains a `role` field which refers to the Organization Invitation role and will be one of the following values: `direct_member`, `admin`, `billing_manager`, or `hiring_manager`. If the invitee is not a GitHub member, the `login` field in the return hash will be `null`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            role: { kind: 'string' },
            invitation_source: { kind: 'string' },
        },
        optionalInPorts: ['per_page', 'page', 'role', 'invitation_source'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        login: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        role: { kind: 'string' },
                        created_at: { kind: 'string' },
                        failed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        failed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        inviter: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        team_count: { kind: 'number' },
                        node_id: { kind: 'string' },
                        invitation_teams_url: { kind: 'string' },
                        invitation_source: { kind: 'string' },
                    },
                    optionalProperties: ['failed_at', 'failed_reason', 'invitation_source'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'invitations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            role: input.inPorts.role,
                            invitation_source: input.inPorts.invitation_source,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an organization invitation',
        name: 'Github: Create an organization invitation',
        description:
            'Invite people to an organization by using their GitHub user ID or their email address. In order to create invitations in an organization, the authenticated user must be an organization owner.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    invitee_id: { kind: 'number' },
                    email: { kind: 'string' },
                    role: { kind: 'string' },
                    team_ids: { kind: 'array', subType: { kind: 'number' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    login: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    role: { kind: 'string' },
                    created_at: { kind: 'string' },
                    failed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    failed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    inviter: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    team_count: { kind: 'number' },
                    node_id: { kind: 'string' },
                    invitation_teams_url: { kind: 'string' },
                    invitation_source: { kind: 'string' },
                },
                optionalProperties: ['failed_at', 'failed_reason', 'invitation_source'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'invitations'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Cancel an organization invitation',
        name: 'Github: Cancel an organization invitation',
        description:
            'Cancel an organization invitation. In order to cancel an organization invitation, the authenticated user must be an organization owner.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            invitation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'invitations', input.inPorts.invitation_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization invitation teams',
        name: 'Github: List organization invitation teams',
        description:
            'List all teams associated with an invitation. In order to see invitations in an organization, the authenticated user must be an organization owner.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            invitation_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'invitations', input.inPorts.invitation_id, 'teams'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization issues assigned to the authenticated user',
        name: 'Github: List organization issues assigned to the authenticated user',
        description:
            'List issues in an organization assigned to the authenticated user.\n\n**Note**: GitHub\'s REST API considers every pull request an issue, but not every issue is a pull request. For this\nreason, "Issues" endpoints may return both issues and pull requests in the response. You can identify pull requests by\nthe `pull_request` key. Be aware that the `id` of a pull request returned from "Issues" endpoints will be an _issue id_. To find out the pull\nrequest id, use the "[List pull requests](https://docs.github.com/rest/pulls/pulls#list-pull-requests)" endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            filter: { kind: 'string' },
            state: { kind: 'string' },
            labels: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['filter', 'state', 'labels', 'sort', 'direction', 'since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        title: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        labels: {
                            kind: 'array',
                            subType: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'string' },
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            default: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                        },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assignees: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        milestone: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        title: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        creator: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        open_issues: { kind: 'number' },
                                        closed_issues: { kind: 'number' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        locked: { kind: 'boolean' },
                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        pull_request: {
                            kind: 'object',
                            properties: {
                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['merged_at'],
                            restriction: 'open',
                        },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        draft: { kind: 'boolean' },
                        closed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                        timeline_url: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        author_association: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [
                        'state_reason',
                        'body',
                        'assignees',
                        'active_lock_reason',
                        'pull_request',
                        'draft',
                        'closed_by',
                        'body_html',
                        'body_text',
                        'timeline_url',
                        'repository',
                        'performed_via_github_app',
                        'reactions',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'issues'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            filter: input.inPorts.filter,
                            state: input.inPorts.state,
                            labels: input.inPorts.labels,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization members',
        name: 'Github: List organization members',
        description:
            'List all users who are members of an organization. If the authenticated user is also a member of this organization then both concealed and public members will be returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            filter: { kind: 'string' },
            role: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['filter', 'role', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'members'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            filter: input.inPorts.filter,
                            role: input.inPorts.role,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check organization membership for a user',
        name: 'Github: Check organization membership for a user',
        description: 'Check if a user is, publicly or privately, a member of the organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'members', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove an organization member',
        name: 'Github: Remove an organization member',
        description:
            "Removing a user from this list will remove them from all teams and they will no longer have any access to the organization's repositories.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'members', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List codespaces for a user in organization',
        name: 'Github: List codespaces for a user in organization',
        description:
            'Lists the codespaces that a member of an organization has for repositories in that organization.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    codespaces: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                name: { kind: 'string' },
                                display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                billable_owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                machine: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'string' },
                                                display_name: { kind: 'string' },
                                                operating_system: { kind: 'string' },
                                                storage_in_bytes: { kind: 'number' },
                                                memory_in_bytes: { kind: 'number' },
                                                cpus: { kind: 'number' },
                                                prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                last_used_at: { kind: 'string' },
                                state: { kind: 'string' },
                                url: { kind: 'string' },
                                git_status: {
                                    kind: 'object',
                                    properties: {
                                        ahead: { kind: 'number' },
                                        behind: { kind: 'number' },
                                        has_unpushed_changes: { kind: 'boolean' },
                                        has_uncommitted_changes: { kind: 'boolean' },
                                        ref: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                location: { kind: 'string' },
                                idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                web_url: { kind: 'string' },
                                machines_url: { kind: 'string' },
                                start_url: { kind: 'string' },
                                stop_url: { kind: 'string' },
                                publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                recent_folders: { kind: 'array', subType: { kind: 'string' } },
                                runtime_constraints: {
                                    kind: 'object',
                                    properties: {
                                        allowed_port_privacy_settings: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [
                                'display_name',
                                'devcontainer_path',
                                'publish_url',
                                'runtime_constraints',
                                'pending_operation',
                                'pending_operation_disabled_reason',
                                'idle_timeout_notice',
                                'retention_period_minutes',
                                'retention_expires_at',
                                'last_known_stop_notice',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'members', input.inPorts.username, 'codespaces'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a codespace from the organization',
        name: 'Github: Delete a codespace from the organization',
        description:
            "Deletes a user's codespace.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'members',
                        input.inPorts.username,
                        'codespaces',
                        input.inPorts.codespace_name,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Stop a codespace for an organization user',
        name: 'Github: Stop a codespace for an organization user',
        description:
            "Stops a user's codespace.\n\nYou must authenticate using an access token with the `admin:org` scope to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'members',
                        input.inPorts.username,
                        'codespaces',
                        input.inPorts.codespace_name,
                        'stop',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get Copilot for Business seat assignment details for a user',
        name: 'Github: Get Copilot for Business seat assignment details for a user',
        description:
            '**Note**: This endpoint is in beta and is subject to change.\n\nGets the GitHub Copilot for Business seat assignment details for a member of an organization who currently has access to GitHub Copilot.\n\nOrganization owners and members with admin permissions can view GitHub Copilot seat assignment details for members in their organization. You must authenticate using an access token with the `manage_billing:copilot` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    assignee: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    assigning_team: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            privacy: { kind: 'string' },
                                            notification_setting: { kind: 'string' },
                                            permission: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    pull: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            members_url: { kind: 'string' },
                                            repositories_url: { kind: 'string' },
                                            parent: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            permission: { kind: 'string' },
                                                            privacy: { kind: 'string' },
                                                            notification_setting: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            repositories_url: { kind: 'string' },
                                                            slug: { kind: 'string' },
                                                            ldap_dn: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    pending_cancellation_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_activity_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_activity_editor: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'assigning_team',
                    'pending_cancellation_date',
                    'last_activity_at',
                    'last_activity_editor',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'members', input.inPorts.username, 'copilot'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get organization membership for a user',
        name: 'Github: Get organization membership for a user',
        description:
            "In order to get a user's membership with an organization, the authenticated user must be an organization member. The `state` parameter in the response can be used to identify the user's membership status.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    state: { kind: 'string' },
                    role: { kind: 'string' },
                    organization_url: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    permissions: {
                        kind: 'object',
                        properties: { can_create_repository: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['permissions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'memberships', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set organization membership for a user',
        name: 'Github: Set organization membership for a user',
        description:
            "Only authenticated organization owners can add a member to the organization or update the member's role.\n\n*   If the authenticated user is _adding_ a member to the organization, the invited user will receive an email inviting them to the organization. The user's [membership status](https://docs.github.com/rest/orgs/members#get-organization-membership-for-a-user) will be `pending` until they accept the invitation.\n    \n*   Authenticated users can _update_ a user's membership by passing the `role` parameter. If the authenticated user changes a member's role to `admin`, the affected user will receive an email notifying them that they've been made an organization owner. If the authenticated user changes an owner's role to `member`, no email will be sent.\n\n**Rate limits**\n\nTo prevent abuse, the authenticated user is limited to 50 organization invitations per 24 hour period. If the organization is more than one month old or on a paid plan, the limit is 500 invitations per 24 hour period.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
            body: { kind: 'object', properties: { role: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    state: { kind: 'string' },
                    role: { kind: 'string' },
                    organization_url: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    permissions: {
                        kind: 'object',
                        properties: { can_create_repository: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['permissions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'memberships', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove organization membership for a user',
        name: 'Github: Remove organization membership for a user',
        description:
            "In order to remove a user's membership with an organization, the authenticated user must be an organization owner.\n\nIf the specified user is an active member of the organization, this will remove them from the organization. If the specified user has been invited to the organization, this will cancel their invitation. The specified user will receive an email notification in both cases.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'memberships', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization migrations',
        name: 'Github: List organization migrations',
        description:
            'Lists the most recent migrations, including both exports (which can be started through the REST API) and imports (which cannot be started using the REST API).\n\nA list of `repositories` is only returned for export migrations.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            exclude: { kind: 'array', subType: { kind: 'string' } },
        },
        optionalInPorts: ['per_page', 'page', 'exclude'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        guid: { kind: 'string' },
                        state: { kind: 'string' },
                        lock_repositories: { kind: 'boolean' },
                        exclude_metadata: { kind: 'boolean' },
                        exclude_git_data: { kind: 'boolean' },
                        exclude_attachments: { kind: 'boolean' },
                        exclude_releases: { kind: 'boolean' },
                        exclude_owner_projects: { kind: 'boolean' },
                        org_metadata_only: { kind: 'boolean' },
                        repositories: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                        },
                        url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        node_id: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        exclude: { kind: 'array', subType: { kind: 'string' } },
                    },
                    optionalProperties: ['archive_url', 'exclude'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'migrations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            exclude: input.inPorts.exclude,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Start an organization migration',
        name: 'Github: Start an organization migration',
        description: 'Initiates the generation of a migration archive.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    repositories: { kind: 'array', subType: { kind: 'string' } },
                    lock_repositories: { kind: 'boolean' },
                    exclude_metadata: { kind: 'boolean' },
                    exclude_git_data: { kind: 'boolean' },
                    exclude_attachments: { kind: 'boolean' },
                    exclude_releases: { kind: 'boolean' },
                    exclude_owner_projects: { kind: 'boolean' },
                    org_metadata_only: { kind: 'boolean' },
                    exclude: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [
                    'lock_repositories',
                    'exclude_metadata',
                    'exclude_git_data',
                    'exclude_attachments',
                    'exclude_releases',
                    'exclude_owner_projects',
                    'org_metadata_only',
                    'exclude',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    guid: { kind: 'string' },
                    state: { kind: 'string' },
                    lock_repositories: { kind: 'boolean' },
                    exclude_metadata: { kind: 'boolean' },
                    exclude_git_data: { kind: 'boolean' },
                    exclude_attachments: { kind: 'boolean' },
                    exclude_releases: { kind: 'boolean' },
                    exclude_owner_projects: { kind: 'boolean' },
                    org_metadata_only: { kind: 'boolean' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    node_id: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    exclude: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['archive_url', 'exclude'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'migrations'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization migration status',
        name: 'Github: Get an organization migration status',
        description:
            "Fetches the status of a migration.\n\nThe `state` of a migration can be one of the following values:\n\n*   `pending`, which means the migration hasn't started yet.\n*   `exporting`, which means the migration is in progress.\n*   `exported`, which means the migration finished successfully.\n*   `failed`, which means the migration failed.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            migration_id: { kind: 'number' },
            exclude: { kind: 'array', subType: { kind: 'string' } },
        },
        optionalInPorts: ['exclude'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    guid: { kind: 'string' },
                    state: { kind: 'string' },
                    lock_repositories: { kind: 'boolean' },
                    exclude_metadata: { kind: 'boolean' },
                    exclude_git_data: { kind: 'boolean' },
                    exclude_attachments: { kind: 'boolean' },
                    exclude_releases: { kind: 'boolean' },
                    exclude_owner_projects: { kind: 'boolean' },
                    org_metadata_only: { kind: 'boolean' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    node_id: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    exclude: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['archive_url', 'exclude'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'migrations', input.inPorts.migration_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ exclude: input.inPorts.exclude }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an organization migration archive',
        name: 'Github: Delete an organization migration archive',
        description: 'Deletes a previous migration archive. Migration archives are automatically deleted after seven days.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            migration_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'migrations', input.inPorts.migration_id, 'archive'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unlock an organization repository',
        name: 'Github: Unlock an organization repository',
        description:
            'Unlocks a repository that was locked for migration. You should unlock each migrated repository and [delete them](https://docs.github.com/rest/repos/repos#delete-a-repository) when the migration is complete and you no longer need the source data.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            migration_id: { kind: 'number' },
            repo_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'migrations',
                        input.inPorts.migration_id,
                        'repos',
                        input.inPorts.repo_name,
                        'lock',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories in an organization migration',
        name: 'Github: List repositories in an organization migration',
        description: 'List all the repositories for this organization migration.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            migration_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'migrations', input.inPorts.migration_id, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List outside collaborators for an organization',
        name: 'Github: List outside collaborators for an organization',
        description: 'List all users who are outside collaborators of an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            filter: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['filter', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'outside_collaborators'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ filter: input.inPorts.filter, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Convert an organization member to outside collaborator',
        name: 'Github: Convert an organization member to outside collaborator',
        description:
            'When an organization member is converted to an outside collaborator, they\'ll only have access to the repositories that their current team membership allows. The user will no longer be a member of the organization. For more information, see "[Converting an organization member to an outside collaborator](https://docs.github.com/articles/converting-an-organization-member-to-an-outside-collaborator/)". Converting an organization member to an outside collaborator may be restricted by enterprise administrators. For more information, see "[Enforcing repository management policies in your enterprise](https://docs.github.com/admin/policies/enforcing-policies-for-your-enterprise/enforcing-repository-management-policies-in-your-enterprise#enforcing-a-policy-for-inviting-outside-collaborators-to-repositories)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
            body: { kind: 'object', properties: { async: { kind: 'boolean' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'outside_collaborators', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove outside collaborator from an organization',
        name: 'Github: Remove outside collaborator from an organization',
        description: "Removing a user from this list will remove them from all the organization's repositories.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'outside_collaborators', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List packages for an organization',
        name: 'Github: List packages for an organization',
        description:
            'Lists packages in an organization readable by the user.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            package_type: { kind: 'string' },
            visibility: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['visibility', 'page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        package_type: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        version_count: { kind: 'number' },
                        visibility: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: ['owner', 'repository'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'packages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            package_type: input.inPorts.package_type,
                            visibility: input.inPorts.visibility,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a package for an organization',
        name: 'Github: Get a package for an organization',
        description:
            'Gets a specific package in an organization.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    package_type: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    version_count: { kind: 'number' },
                    visibility: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    role_name: { kind: 'string' },
                                    temp_clone_token: { kind: 'string' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    code_of_conduct: {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'string' },
                                            body: { kind: 'string' },
                                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: ['body'],
                                        restriction: 'open',
                                    },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    spdx_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    node_id: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    security_and_analysis: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    advanced_security: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    dependabot_security_updates: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    secret_scanning: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    secret_scanning_push_protection: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                },
                                optionalProperties: [
                                    'git_url',
                                    'ssh_url',
                                    'clone_url',
                                    'mirror_url',
                                    'svn_url',
                                    'homepage',
                                    'language',
                                    'forks_count',
                                    'stargazers_count',
                                    'watchers_count',
                                    'size',
                                    'default_branch',
                                    'open_issues_count',
                                    'is_template',
                                    'topics',
                                    'has_issues',
                                    'has_projects',
                                    'has_wiki',
                                    'has_pages',
                                    'has_downloads',
                                    'has_discussions',
                                    'archived',
                                    'disabled',
                                    'visibility',
                                    'pushed_at',
                                    'created_at',
                                    'updated_at',
                                    'permissions',
                                    'role_name',
                                    'temp_clone_token',
                                    'delete_branch_on_merge',
                                    'subscribers_count',
                                    'network_count',
                                    'code_of_conduct',
                                    'license',
                                    'forks',
                                    'open_issues',
                                    'watchers',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'security_and_analysis',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: ['owner', 'repository'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'packages', input.inPorts.package_type, input.inPorts.package_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a package for an organization',
        name: 'Github: Delete a package for an organization',
        description:
            'Deletes an entire package in an organization. You cannot delete a public package if any version of the package has more than 5,000 downloads. In this scenario, contact GitHub support for further assistance.\n\nTo use this endpoint, you must have admin permissions in the organization and authenticate using an access token with the `read:packages` and `delete:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package you want to delete. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'packages', input.inPorts.package_type, input.inPorts.package_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Restore a package for an organization',
        name: 'Github: Restore a package for an organization',
        description:
            'Restores an entire package in an organization.\n\nYou can restore a deleted package under the following conditions:\n  - The package was deleted within the last 30 days.\n  - The same package namespace and version is still available and not reused for a new package. If the same package namespace is not available, you will not be able to restore your package. In this scenario, to restore the deleted package, you must delete the new package that uses the deleted package\'s namespace first.\n\nTo use this endpoint, you must have admin permissions in the organization and authenticate using an access token with the `read:packages` and `write:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package you want to restore. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
            token: { kind: 'string' },
        },
        optionalInPorts: ['token'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'restore',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ token: input.inPorts.token }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List package versions for a package owned by an organization',
        name: 'Github: List package versions for a package owned by an organization',
        description:
            'Lists package versions for a package owned by an organization.\n\nIf the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            state: { kind: 'string' },
        },
        optionalInPorts: ['page', 'per_page', 'state'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        url: { kind: 'string' },
                        package_html_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        license: { kind: 'string' },
                        description: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        deleted_at: { kind: 'string' },
                        metadata: {
                            kind: 'object',
                            properties: {
                                package_type: { kind: 'string' },
                                container: {
                                    kind: 'object',
                                    properties: { tags: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                docker: {
                                    kind: 'object',
                                    properties: { tag: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: ['tag'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['container', 'docker'],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['html_url', 'license', 'description', 'deleted_at', 'metadata'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page, state: input.inPorts.state }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a package version for an organization',
        name: 'Github: Get a package version for an organization',
        description:
            'Gets a specific package version in an organization.\n\nYou must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    package_html_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    license: { kind: 'string' },
                    description: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    deleted_at: { kind: 'string' },
                    metadata: {
                        kind: 'object',
                        properties: {
                            package_type: { kind: 'string' },
                            container: {
                                kind: 'object',
                                properties: { tags: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            docker: {
                                kind: 'object',
                                properties: { tag: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: ['tag'],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: ['container', 'docker'],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['html_url', 'license', 'description', 'deleted_at', 'metadata'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete package version for an organization',
        name: 'Github: Delete package version for an organization',
        description:
            'Deletes a specific package version in an organization. If the package is public and the package version has more than 5,000 downloads, you cannot delete the package version. In this scenario, contact GitHub support for further assistance.\n\nTo use this endpoint, you must have admin permissions in the organization and authenticate using an access token with the `read:packages` and `delete:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package whose version you want to delete. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Restore package version for an organization',
        name: 'Github: Restore package version for an organization',
        description:
            'Restores a specific package version in an organization.\n\nYou can restore a deleted package under the following conditions:\n  - The package was deleted within the last 30 days.\n  - The same package namespace and version is still available and not reused for a new package. If the same package namespace is not available, you will not be able to restore your package. In this scenario, to restore the deleted package, you must delete the new package that uses the deleted package\'s namespace first.\n\nTo use this endpoint, you must have admin permissions in the organization and authenticate using an access token with the `read:packages` and `write:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package whose version you want to restore. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            org: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                        'restore',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List requests to access organization resources with fine-grained personal access tokens',
        name: 'Github: List requests to access organization resources with fine-grained personal access tokens',
        description:
            'Lists requests from organization members to access organization resources with a fine-grained personal access token. Only GitHub Apps can call this API,\nusing the `organization_personal_access_token_requests: read` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            owner: { kind: 'array', subType: { kind: 'string' } },
            repository: { kind: 'string' },
            permission: { kind: 'string' },
            last_used_before: { kind: 'string' },
            last_used_after: { kind: 'string' },
        },
        optionalInPorts: [
            'per_page',
            'page',
            'sort',
            'direction',
            'owner',
            'repository',
            'permission',
            'last_used_before',
            'last_used_after',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        repository_selection: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                organization: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                repository: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                other: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        created_at: { kind: 'string' },
                        token_expired: { kind: 'boolean' },
                        token_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        token_last_used_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-token-requests'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            owner: input.inPorts.owner,
                            repository: input.inPorts.repository,
                            permission: input.inPorts.permission,
                            last_used_before: input.inPorts.last_used_before,
                            last_used_after: input.inPorts.last_used_after,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Review requests to access organization resources with fine-grained personal access tokens',
        name: 'Github: Review requests to access organization resources with fine-grained personal access tokens',
        description:
            'Approves or denies multiple pending requests to access organization resources via a fine-grained personal access token. Only GitHub Apps can call this API,\nusing the `organization_personal_access_token_requests: write` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    pat_request_ids: { kind: 'array', subType: { kind: 'number' } },
                    action: { kind: 'string' },
                    reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['pat_request_ids', 'reason'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-token-requests'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Review a request to access organization resources with a fine-grained personal access token',
        name: 'Github: Review a request to access organization resources with a fine-grained personal access token',
        description:
            'Approves or denies a pending request to access organization resources via a fine-grained personal access token. Only GitHub Apps can call this API,\nusing the `organization_personal_access_token_requests: write` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            pat_request_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { action: { kind: 'string' }, reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                optionalProperties: ['reason'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-token-requests', input.inPorts.pat_request_id].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories requested to be accessed by a fine-grained personal access token',
        name: 'Github: List repositories requested to be accessed by a fine-grained personal access token',
        description:
            'Lists the repositories a fine-grained personal access token request is requesting access to. Only GitHub Apps can call this API,\nusing the `organization_personal_access_token_requests: read` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            pat_request_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'personal-access-token-requests',
                        input.inPorts.pat_request_id,
                        'repositories',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List fine-grained personal access tokens with access to organization resources',
        name: 'Github: List fine-grained personal access tokens with access to organization resources',
        description:
            'Lists approved fine-grained personal access tokens owned by organization members that can access organization resources. Only GitHub Apps can call this API,\nusing the `organization_personal_access_tokens: read` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            owner: { kind: 'array', subType: { kind: 'string' } },
            repository: { kind: 'string' },
            permission: { kind: 'string' },
            last_used_before: { kind: 'string' },
            last_used_after: { kind: 'string' },
        },
        optionalInPorts: [
            'per_page',
            'page',
            'sort',
            'direction',
            'owner',
            'repository',
            'permission',
            'last_used_before',
            'last_used_after',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        repository_selection: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                organization: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                repository: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                other: {
                                    kind: 'object',
                                    properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        access_granted_at: { kind: 'string' },
                        token_expired: { kind: 'boolean' },
                        token_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        token_last_used_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-tokens'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            owner: input.inPorts.owner,
                            repository: input.inPorts.repository,
                            permission: input.inPorts.permission,
                            last_used_before: input.inPorts.last_used_before,
                            last_used_after: input.inPorts.last_used_after,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update the access to organization resources via fine-grained personal access tokens',
        name: 'Github: Update the access to organization resources via fine-grained personal access tokens',
        description:
            "Updates the access organization members have to organization resources via fine-grained personal access tokens. Limited to revoking a token's existing access. Only GitHub Apps can call this API,\nusing the `organization_personal_access_tokens: write` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { action: { kind: 'string' }, pat_ids: { kind: 'array', subType: { kind: 'number' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-tokens'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update the access a fine-grained personal access token has to organization resources',
        name: 'Github: Update the access a fine-grained personal access token has to organization resources',
        description:
            "Updates the access an organization member has to organization resources via a fine-grained personal access token. Limited to revoking the token's existing access. Limited to revoking a token's existing access. Only GitHub Apps can call this API,\nusing the `organization_personal_access_tokens: write` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            pat_id: { kind: 'number' },
            body: { kind: 'object', properties: { action: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-tokens', input.inPorts.pat_id].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories a fine-grained personal access token has access to',
        name: 'Github: List repositories a fine-grained personal access token has access to',
        description:
            'Lists the repositories a fine-grained personal access token has access to. Only GitHub Apps can call this API,\nusing the `organization_personal_access_tokens: read` permission.\n\n**Note**: Fine-grained PATs are in public beta. Related APIs, events, and functionality are subject to change.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            pat_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'personal-access-tokens', input.inPorts.pat_id, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization projects',
        name: 'Github: List organization projects',
        description:
            'Lists the projects in an organization. Returns a `404 Not Found` status if projects are disabled in the organization. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            state: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['state', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        owner_url: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        columns_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        organization_permission: { kind: 'string' },
                        private: { kind: 'boolean' },
                    },
                    optionalProperties: ['organization_permission', 'private'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'projects'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ state: input.inPorts.state, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an organization project',
        name: 'Github: Create an organization project',
        description:
            'Creates an organization project board. Returns a `410 Gone` status if projects are disabled in the organization or if the organization does not have existing classic projects. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, body: { kind: 'string' } },
                optionalProperties: ['body'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'projects'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public organization members',
        name: 'Github: List public organization members',
        description: 'Members of an organization can choose to have their membership publicized or not.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'public_members'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check public organization membership for a user',
        name: 'Github: Check public organization membership for a user',
        description: 'Check if the provided user is a public member of the organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'public_members', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set public organization membership for the authenticated user',
        name: 'Github: Set public organization membership for the authenticated user',
        description:
            'The user can publicize their own membership. (A user cannot publicize the membership for another user.)\n\nNote that you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'public_members', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove public organization membership for the authenticated user',
        name: 'Github: Remove public organization membership for the authenticated user',
        description:
            'Removes the public membership for the authenticated user from the specified organization, unless public visibility is enforced by default.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'public_members', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization repositories',
        name: 'Github: List organization repositories',
        description:
            'Lists repositories for the specified organization.\n\n**Note:** In order to see the `security_and_analysis` block for a repository you must have admin permissions for the repository or be an owner or security manager for the organization that owns the repository. For more information, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            type: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['type', 'sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'repos'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            type: input.inPorts.type,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an organization repository',
        name: 'Github: Create an organization repository',
        description:
            'Creates a new repository in the specified organization. The authenticated user must be a member of the organization.\n\n**OAuth scope requirements**\n\nWhen using [OAuth](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/), authorizations must include:\n\n*   `public_repo` scope or `repo` scope to create a public repository. Note: For GitHub AE, use `repo` scope to create an internal repository.\n*   `repo` scope to create a private repository',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    homepage: { kind: 'string' },
                    private: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    is_template: { kind: 'boolean' },
                    team_id: { kind: 'number' },
                    auto_init: { kind: 'boolean' },
                    gitignore_template: { kind: 'string' },
                    license_template: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_rebase_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                },
                optionalProperties: [
                    'description',
                    'homepage',
                    'private',
                    'visibility',
                    'has_issues',
                    'has_projects',
                    'has_wiki',
                    'has_downloads',
                    'is_template',
                    'team_id',
                    'auto_init',
                    'gitignore_template',
                    'license_template',
                    'allow_squash_merge',
                    'allow_merge_commit',
                    'allow_rebase_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    organization: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    forks: { kind: 'number' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                        },
                        optionalProperties: ['triage', 'maintain'],
                        restriction: 'open',
                    },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'string' },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'string' },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'string' },
                                    language: { kind: 'string' },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    master_branch: { kind: 'string' },
                    starred_at: { kind: 'string' },
                    anonymous_access_enabled: { kind: 'boolean' },
                },
                optionalProperties: [
                    'organization',
                    'permissions',
                    'is_template',
                    'topics',
                    'has_discussions',
                    'visibility',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_update_branch',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'allow_merge_commit',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'subscribers_count',
                    'network_count',
                    'master_branch',
                    'starred_at',
                    'anonymous_access_enabled',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'repos'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all organization repository rulesets',
        name: 'Github: Get all organization repository rulesets',
        description: 'Get all the repository rulesets for an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        target: { kind: 'string' },
                        source_type: { kind: 'string' },
                        source: { kind: 'string' },
                        enforcement: { kind: 'string' },
                        bypass_actors: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    actor_id: { kind: 'number' },
                                    actor_type: { kind: 'string' },
                                    bypass_mode: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        current_user_can_bypass: { kind: 'string' },
                        node_id: { kind: 'string' },
                        _links: {
                            kind: 'object',
                            properties: {
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        conditions: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        ref_name: {
                                            kind: 'object',
                                            properties: {
                                                include: { kind: 'array', subType: { kind: 'string' } },
                                                exclude: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            ],
                        },
                        rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: [
                        'target',
                        'source_type',
                        'bypass_actors',
                        'current_user_can_bypass',
                        'node_id',
                        '_links',
                        'conditions',
                        'rules',
                        'created_at',
                        'updated_at',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'rulesets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an organization repository ruleset',
        name: 'Github: Create an organization repository ruleset',
        description: 'Create a repository ruleset for an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    conditions: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                },
                optionalProperties: ['target', 'bypass_actors', 'conditions', 'rules'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    source_type: { kind: 'string' },
                    source: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    current_user_can_bypass: { kind: 'string' },
                    node_id: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    ref_name: {
                                        kind: 'object',
                                        properties: {
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        ],
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'target',
                    'source_type',
                    'bypass_actors',
                    'current_user_can_bypass',
                    'node_id',
                    '_links',
                    'conditions',
                    'rules',
                    'created_at',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'rulesets'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization repository ruleset',
        name: 'Github: Get an organization repository ruleset',
        description: 'Get a repository ruleset for an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            ruleset_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    source_type: { kind: 'string' },
                    source: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    current_user_can_bypass: { kind: 'string' },
                    node_id: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    ref_name: {
                                        kind: 'object',
                                        properties: {
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        ],
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'target',
                    'source_type',
                    'bypass_actors',
                    'current_user_can_bypass',
                    'node_id',
                    '_links',
                    'conditions',
                    'rules',
                    'created_at',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'rulesets', input.inPorts.ruleset_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an organization repository ruleset',
        name: 'Github: Update an organization repository ruleset',
        description: 'Update a ruleset for an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            ruleset_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    conditions: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    source_type: { kind: 'string' },
                    source: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    current_user_can_bypass: { kind: 'string' },
                    node_id: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    ref_name: {
                                        kind: 'object',
                                        properties: {
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        ],
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'target',
                    'source_type',
                    'bypass_actors',
                    'current_user_can_bypass',
                    'node_id',
                    '_links',
                    'conditions',
                    'rules',
                    'created_at',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'rulesets', input.inPorts.ruleset_id].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an organization repository ruleset',
        name: 'Github: Delete an organization repository ruleset',
        description: 'Delete a ruleset for an organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            ruleset_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'rulesets', input.inPorts.ruleset_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List secret scanning alerts for an organization',
        name: 'Github: List secret scanning alerts for an organization',
        description:
            'Lists secret scanning alerts for eligible repositories in an organization, from newest to oldest.\nTo use this endpoint, you must be an administrator or security manager for the organization, and you must use an access token with the `repo` scope or `security_events` scope.\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have the `secret_scanning_alerts` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            state: { kind: 'string' },
            secret_type: { kind: 'string' },
            resolution: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            before: { kind: 'string' },
            after: { kind: 'string' },
        },
        optionalInPorts: ['state', 'secret_type', 'resolution', 'sort', 'direction', 'page', 'per_page', 'before', 'after'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        locations_url: { kind: 'string' },
                        state: { kind: 'string' },
                        resolution: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolved_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolved_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        secret_type: { kind: 'string' },
                        secret_type_display_name: { kind: 'string' },
                        secret: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        push_protection_bypassed: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                        push_protection_bypassed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        push_protection_bypassed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolution_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'secret-scanning', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            secret_type: input.inPorts.secret_type,
                            resolution: input.inPorts.resolution,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List security manager teams',
        name: 'Github: List security manager teams',
        description:
            'Lists teams that are security managers for an organization. For more information, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization)."\n\nTo use this endpoint, you must be an administrator or security manager for the organization, and you must use an access token with the `read:org` scope.\n\nGitHub Apps must have the `administration` organization read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permission: { kind: 'string' },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        html_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        slug: { kind: 'string' },
                        ldap_dn: { kind: 'string' },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'security-managers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add a security manager team',
        name: 'Github: Add a security manager team',
        description:
            'Adds a team as a security manager for an organization. For more information, see "[Managing security for an organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization) for an organization."\n\nTo use this endpoint, you must be an administrator for the organization, and you must use an access token with the `write:org` scope.\n\nGitHub Apps must have the `administration` organization read-write permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'security-managers', 'teams', input.inPorts.team_slug].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a security manager team',
        name: 'Github: Remove a security manager team',
        description:
            'Removes the security manager role from a team for an organization. For more information, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization) team from an organization."\n\nTo use this endpoint, you must be an administrator for the organization, and you must use an access token with the `admin:org` scope.\n\nGitHub Apps must have the `administration` organization read-write permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'security-managers', 'teams', input.inPorts.team_slug].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Actions billing for an organization',
        name: 'Github: Get GitHub Actions billing for an organization',
        description:
            'Gets the summary of the free and paid GitHub Actions minutes used.\n\nPaid minutes only apply to workflows in private repositories that use GitHub-hosted runners. Minutes used is listed for each GitHub-hosted runner operating system. Any job re-runs are also included in the usage. The usage returned includes any minute multipliers for macOS and Windows runners, and is rounded up to the nearest whole minute. For more information, see "[Managing billing for GitHub Actions](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-actions)".\n\nAccess tokens must have the `repo` or `admin:org` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_minutes_used: { kind: 'number' },
                    total_paid_minutes_used: { kind: 'number' },
                    included_minutes: { kind: 'number' },
                    minutes_used_breakdown: {
                        kind: 'object',
                        properties: {
                            UBUNTU: { kind: 'number' },
                            MACOS: { kind: 'number' },
                            WINDOWS: { kind: 'number' },
                            ubuntu_4_core: { kind: 'number' },
                            ubuntu_8_core: { kind: 'number' },
                            ubuntu_16_core: { kind: 'number' },
                            ubuntu_32_core: { kind: 'number' },
                            ubuntu_64_core: { kind: 'number' },
                            windows_4_core: { kind: 'number' },
                            windows_8_core: { kind: 'number' },
                            windows_16_core: { kind: 'number' },
                            windows_32_core: { kind: 'number' },
                            windows_64_core: { kind: 'number' },
                            macos_12_core: { kind: 'number' },
                            total: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'settings', 'billing', 'actions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Packages billing for an organization',
        name: 'Github: Get GitHub Packages billing for an organization',
        description:
            'Gets the free and paid storage used for GitHub Packages in gigabytes.\n\nPaid minutes only apply to packages stored for private repositories. For more information, see "[Managing billing for GitHub Packages](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-packages)."\n\nAccess tokens must have the `repo` or `admin:org` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_gigabytes_bandwidth_used: { kind: 'number' },
                    total_paid_gigabytes_bandwidth_used: { kind: 'number' },
                    included_gigabytes_bandwidth: { kind: 'number' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'settings', 'billing', 'packages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get shared storage billing for an organization',
        name: 'Github: Get shared storage billing for an organization',
        description:
            'Gets the estimated paid and estimated total storage used for GitHub Actions and GitHub Packages.\n\nPaid minutes only apply to packages stored for private repositories. For more information, see "[Managing billing for GitHub Packages](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-packages)."\n\nAccess tokens must have the `repo` or `admin:org` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    days_left_in_billing_cycle: { kind: 'number' },
                    estimated_paid_storage_for_month: { kind: 'number' },
                    estimated_storage_for_month: { kind: 'number' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'settings', 'billing', 'shared-storage'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List teams',
        name: 'Github: List teams',
        description: 'Lists all teams in an organization that are visible to the authenticated user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a team',
        name: 'Github: Create a team',
        description:
            'To create a team, the authenticated user must be a member or owner of `{org}`. By default, organization members can create teams. Organization owners can limit team creation to organization owners. For more information, see "[Setting team creation permissions](https://docs.github.com/articles/setting-team-creation-permissions-in-your-organization)."\n\nWhen you create a new team, you automatically become a team maintainer without explicitly adding yourself to the optional array of `maintainers`. For more information, see "[About teams](https://docs.github.com/github/setting-up-and-managing-organizations-and-teams/about-teams)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    maintainers: { kind: 'array', subType: { kind: 'string' } },
                    repo_names: { kind: 'array', subType: { kind: 'string' } },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    parent_team_id: { kind: 'number' },
                },
                optionalProperties: [
                    'description',
                    'maintainers',
                    'repo_names',
                    'privacy',
                    'notification_setting',
                    'permission',
                    'parent_team_id',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    name: { kind: 'string' },
                    slug: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    members_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    parent: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    members_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permission: { kind: 'string' },
                                    privacy: { kind: 'string' },
                                    notification_setting: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    repositories_url: { kind: 'string' },
                                    slug: { kind: 'string' },
                                    ldap_dn: { kind: 'string' },
                                },
                                optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    members_count: { kind: 'number' },
                    repos_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            company: { kind: 'string' },
                            blog: { kind: 'string' },
                            location: { kind: 'string' },
                            email: { kind: 'string' },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_verified: { kind: 'boolean' },
                            has_organization_projects: { kind: 'boolean' },
                            has_repository_projects: { kind: 'boolean' },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            html_url: { kind: 'string' },
                            created_at: { kind: 'string' },
                            type: { kind: 'string' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            plan: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                    filled_seats: { kind: 'number' },
                                    seats: { kind: 'number' },
                                },
                                optionalProperties: ['filled_seats', 'seats'],
                                restriction: 'open',
                            },
                            default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            members_allowed_repository_creation_type: { kind: 'string' },
                            members_can_create_public_repositories: { kind: 'boolean' },
                            members_can_create_private_repositories: { kind: 'boolean' },
                            members_can_create_internal_repositories: { kind: 'boolean' },
                            members_can_create_pages: { kind: 'boolean' },
                            members_can_create_public_pages: { kind: 'boolean' },
                            members_can_create_private_pages: { kind: 'boolean' },
                            members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            web_commit_signoff_required: { kind: 'boolean' },
                            updated_at: { kind: 'string' },
                            archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [
                            'name',
                            'company',
                            'blog',
                            'location',
                            'email',
                            'twitter_username',
                            'is_verified',
                            'total_private_repos',
                            'owned_private_repos',
                            'private_gists',
                            'disk_usage',
                            'collaborators',
                            'billing_email',
                            'plan',
                            'default_repository_permission',
                            'members_can_create_repositories',
                            'two_factor_requirement_enabled',
                            'members_allowed_repository_creation_type',
                            'members_can_create_public_repositories',
                            'members_can_create_private_repositories',
                            'members_can_create_internal_repositories',
                            'members_can_create_pages',
                            'members_can_create_public_pages',
                            'members_can_create_private_pages',
                            'members_can_fork_private_repositories',
                            'web_commit_signoff_required',
                        ],
                        restriction: 'open',
                    },
                    ldap_dn: { kind: 'string' },
                },
                optionalProperties: ['privacy', 'notification_setting', 'parent', 'ldap_dn'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a team by name',
        name: 'Github: Get a team by name',
        description:
            'Gets a team using the team\'s `slug`. To create the `slug`, GitHub replaces special characters in the `name` string, changes all words to lowercase, and replaces spaces with a `-` separator. For example, `"My TEam N\u00E4me"` would become `my-team-name`.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    name: { kind: 'string' },
                    slug: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    members_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    parent: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    members_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permission: { kind: 'string' },
                                    privacy: { kind: 'string' },
                                    notification_setting: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    repositories_url: { kind: 'string' },
                                    slug: { kind: 'string' },
                                    ldap_dn: { kind: 'string' },
                                },
                                optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    members_count: { kind: 'number' },
                    repos_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            company: { kind: 'string' },
                            blog: { kind: 'string' },
                            location: { kind: 'string' },
                            email: { kind: 'string' },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_verified: { kind: 'boolean' },
                            has_organization_projects: { kind: 'boolean' },
                            has_repository_projects: { kind: 'boolean' },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            html_url: { kind: 'string' },
                            created_at: { kind: 'string' },
                            type: { kind: 'string' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            plan: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                    filled_seats: { kind: 'number' },
                                    seats: { kind: 'number' },
                                },
                                optionalProperties: ['filled_seats', 'seats'],
                                restriction: 'open',
                            },
                            default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            members_allowed_repository_creation_type: { kind: 'string' },
                            members_can_create_public_repositories: { kind: 'boolean' },
                            members_can_create_private_repositories: { kind: 'boolean' },
                            members_can_create_internal_repositories: { kind: 'boolean' },
                            members_can_create_pages: { kind: 'boolean' },
                            members_can_create_public_pages: { kind: 'boolean' },
                            members_can_create_private_pages: { kind: 'boolean' },
                            members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            web_commit_signoff_required: { kind: 'boolean' },
                            updated_at: { kind: 'string' },
                            archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [
                            'name',
                            'company',
                            'blog',
                            'location',
                            'email',
                            'twitter_username',
                            'is_verified',
                            'total_private_repos',
                            'owned_private_repos',
                            'private_gists',
                            'disk_usage',
                            'collaborators',
                            'billing_email',
                            'plan',
                            'default_repository_permission',
                            'members_can_create_repositories',
                            'two_factor_requirement_enabled',
                            'members_allowed_repository_creation_type',
                            'members_can_create_public_repositories',
                            'members_can_create_private_repositories',
                            'members_can_create_internal_repositories',
                            'members_can_create_pages',
                            'members_can_create_public_pages',
                            'members_can_create_private_pages',
                            'members_can_fork_private_repositories',
                            'web_commit_signoff_required',
                        ],
                        restriction: 'open',
                    },
                    ldap_dn: { kind: 'string' },
                },
                optionalProperties: ['privacy', 'notification_setting', 'parent', 'ldap_dn'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a team',
        name: 'Github: Delete a team',
        description:
            'To delete a team, the authenticated user must be an organization owner or team maintainer.\n\nIf you are an organization owner, deleting a parent team will delete all of its child teams as well.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `DELETE /organizations/{org_id}/team/{team_id}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a team',
        name: 'Github: Update a team',
        description:
            'To edit a team, the authenticated user must either be an organization owner or a team maintainer.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `PATCH /organizations/{org_id}/team/{team_id}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    parent_team_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    name: { kind: 'string' },
                    slug: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    members_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    parent: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    members_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permission: { kind: 'string' },
                                    privacy: { kind: 'string' },
                                    notification_setting: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    repositories_url: { kind: 'string' },
                                    slug: { kind: 'string' },
                                    ldap_dn: { kind: 'string' },
                                },
                                optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    members_count: { kind: 'number' },
                    repos_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            company: { kind: 'string' },
                            blog: { kind: 'string' },
                            location: { kind: 'string' },
                            email: { kind: 'string' },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_verified: { kind: 'boolean' },
                            has_organization_projects: { kind: 'boolean' },
                            has_repository_projects: { kind: 'boolean' },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            html_url: { kind: 'string' },
                            created_at: { kind: 'string' },
                            type: { kind: 'string' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            plan: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                    filled_seats: { kind: 'number' },
                                    seats: { kind: 'number' },
                                },
                                optionalProperties: ['filled_seats', 'seats'],
                                restriction: 'open',
                            },
                            default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            members_allowed_repository_creation_type: { kind: 'string' },
                            members_can_create_public_repositories: { kind: 'boolean' },
                            members_can_create_private_repositories: { kind: 'boolean' },
                            members_can_create_internal_repositories: { kind: 'boolean' },
                            members_can_create_pages: { kind: 'boolean' },
                            members_can_create_public_pages: { kind: 'boolean' },
                            members_can_create_private_pages: { kind: 'boolean' },
                            members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            web_commit_signoff_required: { kind: 'boolean' },
                            updated_at: { kind: 'string' },
                            archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [
                            'name',
                            'company',
                            'blog',
                            'location',
                            'email',
                            'twitter_username',
                            'is_verified',
                            'total_private_repos',
                            'owned_private_repos',
                            'private_gists',
                            'disk_usage',
                            'collaborators',
                            'billing_email',
                            'plan',
                            'default_repository_permission',
                            'members_can_create_repositories',
                            'two_factor_requirement_enabled',
                            'members_allowed_repository_creation_type',
                            'members_can_create_public_repositories',
                            'members_can_create_private_repositories',
                            'members_can_create_internal_repositories',
                            'members_can_create_pages',
                            'members_can_create_public_pages',
                            'members_can_create_private_pages',
                            'members_can_fork_private_repositories',
                            'web_commit_signoff_required',
                        ],
                        restriction: 'open',
                    },
                    ldap_dn: { kind: 'string' },
                },
                optionalProperties: ['privacy', 'notification_setting', 'parent', 'ldap_dn'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List discussions',
        name: 'Github: List discussions',
        description:
            "List all discussions on a team's page. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/discussions`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            pinned: { kind: 'string' },
        },
        optionalInPorts: ['direction', 'per_page', 'page', 'pinned'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'string' },
                        body_html: { kind: 'string' },
                        body_version: { kind: 'string' },
                        comments_count: { kind: 'number' },
                        comments_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        html_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                        number: { kind: 'number' },
                        pinned: { kind: 'boolean' },
                        private: { kind: 'boolean' },
                        team_url: { kind: 'string' },
                        title: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'discussions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            pinned: input.inPorts.pinned,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a discussion',
        name: 'Github: Create a discussion',
        description:
            'Creates a new discussion post on a team\'s page. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `POST /organizations/{org_id}/team/{team_id}/discussions`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, body: { kind: 'string' }, private: { kind: 'boolean' } },
                optionalProperties: ['private'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    comments_count: { kind: 'number' },
                    comments_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    pinned: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                    team_url: { kind: 'string' },
                    title: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'discussions'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a discussion',
        name: 'Github: Get a discussion',
        description:
            "Get a specific discussion on a team's page. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    comments_count: { kind: 'number' },
                    comments_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    pinned: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                    team_url: { kind: 'string' },
                    title: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a discussion',
        name: 'Github: Delete a discussion',
        description:
            "Delete a discussion from a team's page. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `DELETE /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a discussion',
        name: 'Github: Update a discussion',
        description:
            'Edits the title and body text of a discussion post. Only the parameters you provide are updated. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `PATCH /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, body: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    comments_count: { kind: 'number' },
                    comments_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    pinned: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                    team_url: { kind: 'string' },
                    title: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List discussion comments',
        name: 'Github: List discussion comments',
        description:
            'List all comments on a team discussion. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}/comments`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'string' },
                        body_html: { kind: 'string' },
                        body_version: { kind: 'string' },
                        created_at: { kind: 'string' },
                        last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        discussion_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                        number: { kind: 'number' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a discussion comment',
        name: 'Github: Create a discussion comment',
        description:
            'Creates a new comment on a team discussion. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `POST /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}/comments`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    discussion_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a discussion comment',
        name: 'Github: Get a discussion comment',
        description:
            'Get a specific comment on a team discussion. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}/comments/{comment_number}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    discussion_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a discussion comment',
        name: 'Github: Delete a discussion comment',
        description:
            'Deletes a comment on a team discussion. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `DELETE /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}/comments/{comment_number}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a discussion comment',
        name: 'Github: Update a discussion comment',
        description:
            'Edits the body text of a discussion comment. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `PATCH /organizations/{org_id}/team/{team_id}/discussions/{discussion_number}/comments/{comment_number}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    discussion_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a team discussion comment',
        name: 'Github: List reactions for a team discussion comment',
        description:
            'List the reactions to a [team discussion comment](https://docs.github.com/rest/reference/teams#discussion-comments/). OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/:org_id/team/:team_id/discussions/:discussion_number/comments/:comment_number/reactions`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                        'reactions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a team discussion comment',
        name: 'Github: Create reaction for a team discussion comment',
        description:
            'Create a reaction to a [team discussion comment](https://docs.github.com/rest/reference/teams#discussion-comments). OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/). A response with an HTTP `200` status means that you already added the reaction type to this team discussion comment.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `POST /organizations/:org_id/team/:team_id/discussions/:discussion_number/comments/:comment_number/reactions`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                        'reactions',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete team discussion comment reaction',
        name: 'Github: Delete team discussion comment reaction',
        description:
            '**Note:** You can also specify a team or organization with `team_id` and `org_id` using the route `DELETE /organizations/:org_id/team/:team_id/discussions/:discussion_number/comments/:comment_number/reactions/:reaction_id`.\n\nDelete a reaction to a [team discussion comment](https://docs.github.com/rest/reference/teams#discussion-comments). OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a team discussion',
        name: 'Github: List reactions for a team discussion',
        description:
            'List the reactions to a [team discussion](https://docs.github.com/rest/reference/teams#discussions). OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/:org_id/team/:team_id/discussions/:discussion_number/reactions`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'reactions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a team discussion',
        name: 'Github: Create reaction for a team discussion',
        description:
            'Create a reaction to a [team discussion](https://docs.github.com/rest/reference/teams#discussions). OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/). A response with an HTTP `200` status means that you already added the reaction type to this team discussion.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `POST /organizations/:org_id/team/:team_id/discussions/:discussion_number/reactions`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'reactions',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete team discussion reaction',
        name: 'Github: Delete team discussion reaction',
        description:
            '**Note:** You can also specify a team or organization with `team_id` and `org_id` using the route `DELETE /organizations/:org_id/team/:team_id/discussions/:discussion_number/reactions/:reaction_id`.\n\nDelete a reaction to a [team discussion](https://docs.github.com/rest/reference/teams#discussions). OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            discussion_number: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'discussions',
                        input.inPorts.discussion_number,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List pending team invitations',
        name: 'Github: List pending team invitations',
        description:
            'The return hash contains a `role` field which refers to the Organization Invitation role and will be one of the following values: `direct_member`, `admin`, `billing_manager`, `hiring_manager`, or `reinstate`. If the invitee is not a GitHub member, the `login` field in the return hash will be `null`.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/invitations`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        login: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        role: { kind: 'string' },
                        created_at: { kind: 'string' },
                        failed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        failed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        inviter: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        team_count: { kind: 'number' },
                        node_id: { kind: 'string' },
                        invitation_teams_url: { kind: 'string' },
                        invitation_source: { kind: 'string' },
                    },
                    optionalProperties: ['failed_at', 'failed_reason', 'invitation_source'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'invitations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List team members',
        name: 'Github: List team members',
        description:
            'Team members will include the members of child teams.\n\nTo list members in a team, the team must be visible to the authenticated user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            role: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['role', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'members'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ role: input.inPorts.role, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get team membership for a user',
        name: 'Github: Get team membership for a user',
        description:
            "Team members will include the members of child teams.\n\nTo get a user's membership with a team, the team must be visible to the authenticated user.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/memberships/{username}`.\n\n**Note:**\nThe response contains the `state` of the membership and the member's `role`.\n\nThe `role` for organization owners is set to `maintainer`. For more information about `maintainer` roles, see [Create a team](https://docs.github.com/rest/teams/teams#create-a-team).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, role: { kind: 'string' }, state: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'memberships', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add or update team membership for a user',
        name: 'Github: Add or update team membership for a user',
        description:
            'Adds an organization member to a team. An authenticated organization owner or team maintainer can add organization members to a team.\n\nTeam synchronization is available for organizations using GitHub Enterprise Cloud. For more information, see [GitHub\'s products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\n**Note:** When you have team synchronization set up for a team with your organization\'s identity provider (IdP), you will see an error if you attempt to use the API for making changes to the team\'s membership. If you have access to manage group membership in your IdP, you can manage GitHub team membership through your identity provider, which automatically adds and removes team members in an organization. For more information, see "[Synchronizing teams between your identity provider and GitHub](https://docs.github.com/articles/synchronizing-teams-between-your-identity-provider-and-github/)."\n\nAn organization owner can add someone who is not part of the team\'s organization to a team. When an organization owner adds someone to a team who is not an organization member, this endpoint will send an invitation to the person via email. This newly-created membership will be in the "pending" state until the person accepts the invitation, at which point the membership will transition to the "active" state and the user will be added as a member of the team.\n\nIf the user is already a member of the team, this endpoint will update the role of the team member\'s role. To update the membership of a team member, the authenticated user must be an organization owner or a team maintainer.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `PUT /organizations/{org_id}/team/{team_id}/memberships/{username}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            username: { kind: 'string' },
            body: { kind: 'object', properties: { role: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, role: { kind: 'string' }, state: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'memberships', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove team membership for a user',
        name: 'Github: Remove team membership for a user',
        description:
            "To remove a membership between a user and a team, the authenticated user must have 'admin' permissions to the team or be an owner of the organization that the team is associated with. Removing team membership does not delete the user, it just removes their membership from the team.\n\nTeam synchronization is available for organizations using GitHub Enterprise Cloud. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\n**Note:** When you have team synchronization set up for a team with your organization's identity provider (IdP), you will see an error if you attempt to use the API for making changes to the team's membership. If you have access to manage group membership in your IdP, you can manage GitHub team membership through your identity provider, which automatically adds and removes team members in an organization. For more information, see \"[Synchronizing teams between your identity provider and GitHub](https://docs.github.com/articles/synchronizing-teams-between-your-identity-provider-and-github/).\"\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `DELETE /organizations/{org_id}/team/{team_id}/memberships/{username}`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'memberships', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List team projects',
        name: 'Github: List team projects',
        description:
            'Lists the organization projects for a team.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/projects`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        owner_url: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        columns_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        creator: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        organization_permission: { kind: 'string' },
                        private: { kind: 'boolean' },
                        permissions: {
                            kind: 'object',
                            properties: { read: { kind: 'boolean' }, write: { kind: 'boolean' }, admin: { kind: 'boolean' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['organization_permission', 'private'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'projects'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check team permissions for a project',
        name: 'Github: Check team permissions for a project',
        description:
            'Checks whether a team has `read`, `write`, or `admin` permissions for an organization project. The response includes projects inherited from a parent team.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/projects/{project_id}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            project_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                    permissions: {
                        kind: 'object',
                        properties: { read: { kind: 'boolean' }, write: { kind: 'boolean' }, admin: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'projects', input.inPorts.project_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add or update team project permissions',
        name: 'Github: Add or update team project permissions',
        description:
            "Adds an organization project to a team. To add a project to a team or update the team's permission on a project, the authenticated user must have `admin` permissions for the project. The project and team must be part of the same organization.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `PUT /organizations/{org_id}/team/{team_id}/projects/{project_id}`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            project_id: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    { kind: 'object', properties: { permission: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'projects', input.inPorts.project_id].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a project from a team',
        name: 'Github: Remove a project from a team',
        description:
            'Removes an organization project from a team. An organization owner or a team maintainer can remove any project from the team. To remove a project from a team as an organization member, the authenticated user must have `read` access to both the team and project, or `admin` access to the team or project. This endpoint removes the project from the team, but does not delete the project.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `DELETE /organizations/{org_id}/team/{team_id}/projects/{project_id}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            project_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'projects', input.inPorts.project_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List team repositories',
        name: 'Github: List team repositories',
        description:
            "Lists a team's repositories visible to the authenticated user.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/repos`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'repos'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check team permissions for a repository',
        name: 'Github: Check team permissions for a repository',
        description:
            "Checks whether a team has `admin`, `push`, `maintain`, `triage`, or `pull` permission for a repository. Repositories inherited through a parent team will also be checked.\n\nYou can also get information about the specified repository, including what permissions the team grants on it, by passing the following custom [media type](https://docs.github.com/rest/overview/media-types/) via the `application/vnd.github.v3.repository+json` accept header.\n\nIf a team doesn't have permission for the repository, you will receive a `404 Not Found` response status.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/repos/{owner}/{repo}`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    forks: { kind: 'number' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                        },
                        optionalProperties: ['triage', 'maintain'],
                        restriction: 'open',
                    },
                    role_name: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    master_branch: { kind: 'string' },
                },
                optionalProperties: [
                    'permissions',
                    'role_name',
                    'is_template',
                    'topics',
                    'visibility',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_merge_commit',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'subscribers_count',
                    'network_count',
                    'master_branch',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add or update team repository permissions',
        name: 'Github: Add or update team repository permissions',
        description:
            'To add a repository to a team or update the team\'s permission on a repository, the authenticated user must have admin access to the repository, and must be able to see the team. The repository must be owned by the organization, or a direct fork of a repository owned by the organization. You will get a `422 Unprocessable Entity` status if you attempt to add a repository to a team that is not owned by the organization. Note that, if you choose not to pass any parameters, you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `PUT /organizations/{org_id}/team/{team_id}/repos/{owner}/{repo}`.\n\nFor more information about the permission levels, see "[Repository permission levels for an organization](https://docs.github.com/github/setting-up-and-managing-organizations-and-teams/repository-permission-levels-for-an-organization#permission-levels-for-repositories-owned-by-an-organization)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { permission: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a repository from a team',
        name: 'Github: Remove a repository from a team',
        description:
            'If the authenticated user is an organization owner or a team maintainer, they can remove any repositories from the team. To remove a repository from a team as an organization member, the authenticated user must have admin access to the repository and must be able to see the team. This does not delete the repository, it just removes it from the team.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `DELETE /organizations/{org_id}/team/{team_id}/repos/{owner}/{repo}`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'orgs',
                        input.inPorts.org,
                        'teams',
                        input.inPorts.team_slug,
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List child teams',
        name: 'Github: List child teams',
        description:
            'Lists the child teams of the team specified by `{team_slug}`.\n\n**Note:** You can also specify a team by `org_id` and `team_id` using the route `GET /organizations/{org_id}/team/{team_id}/teams`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            team_slug: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, 'teams', input.inPorts.team_slug, 'teams'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Enable or disable a security feature for an organization',
        name: 'Github: Enable or disable a security feature for an organization',
        description:
            'Enables or disables the specified security feature for all eligible repositories in an organization.\n\nTo use this endpoint, you must be an organization owner or be member of a team with the security manager role.\nA token with the \'write:org\' scope is also required.\n\nGitHub Apps must have the `organization_administration:write` permission to use this endpoint.\n\nFor more information, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            security_product: { kind: 'string' },
            enablement: { kind: 'string' },
            body: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'orgs', input.inPorts.org, input.inPorts.security_product, input.inPorts.enablement].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a project card',
        name: 'Github: Get a project card',
        description: 'Gets information about a project card.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            card_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    archived: { kind: 'boolean' },
                    column_name: { kind: 'string' },
                    project_id: { kind: 'string' },
                    column_url: { kind: 'string' },
                    content_url: { kind: 'string' },
                    project_url: { kind: 'string' },
                },
                optionalProperties: ['archived', 'column_name', 'project_id', 'content_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', 'cards', input.inPorts.card_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a project card',
        name: 'Github: Delete a project card',
        description: 'Deletes a project card',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            card_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', 'cards', input.inPorts.card_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an existing project card',
        name: 'Github: Update an existing project card',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            card_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] }, archived: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    archived: { kind: 'boolean' },
                    column_name: { kind: 'string' },
                    project_id: { kind: 'string' },
                    column_url: { kind: 'string' },
                    content_url: { kind: 'string' },
                    project_url: { kind: 'string' },
                },
                optionalProperties: ['archived', 'column_name', 'project_id', 'content_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', 'cards', input.inPorts.card_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Move a project card',
        name: 'Github: Move a project card',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            card_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { position: { kind: 'string' }, column_id: { kind: 'number' } },
                optionalProperties: ['column_id'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', 'cards', input.inPorts.card_id, 'moves'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a project column',
        name: 'Github: Get a project column',
        description: 'Gets information about a project column.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            column_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    project_url: { kind: 'string' },
                    cards_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', input.inPorts.column_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a project column',
        name: 'Github: Delete a project column',
        description: 'Deletes a project column.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            column_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', input.inPorts.column_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an existing project column',
        name: 'Github: Update an existing project column',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            column_id: { kind: 'number' },
            body: { kind: 'object', properties: { name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    project_url: { kind: 'string' },
                    cards_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', input.inPorts.column_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List project cards',
        name: 'Github: List project cards',
        description: 'Lists the project cards in a project.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            column_id: { kind: 'number' },
            archived_state: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['archived_state', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        archived: { kind: 'boolean' },
                        column_name: { kind: 'string' },
                        project_id: { kind: 'string' },
                        column_url: { kind: 'string' },
                        content_url: { kind: 'string' },
                        project_url: { kind: 'string' },
                    },
                    optionalProperties: ['archived', 'column_name', 'project_id', 'content_url'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', input.inPorts.column_id, 'cards'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            archived_state: input.inPorts.archived_state,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a project card',
        name: 'Github: Create a project card',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            column_id: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: { content_id: { kind: 'number' }, content_type: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    note: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    archived: { kind: 'boolean' },
                    column_name: { kind: 'string' },
                    project_id: { kind: 'string' },
                    column_url: { kind: 'string' },
                    content_url: { kind: 'string' },
                    project_url: { kind: 'string' },
                },
                optionalProperties: ['archived', 'column_name', 'project_id', 'content_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', input.inPorts.column_id, 'cards'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Move a project column',
        name: 'Github: Move a project column',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            column_id: { kind: 'number' },
            body: { kind: 'object', properties: { position: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', 'columns', input.inPorts.column_id, 'moves'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a project',
        name: 'Github: Get a project',
        description:
            'Gets a project by its `id`. Returns a `404 Not Found` status if projects are disabled. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a project',
        name: 'Github: Delete a project',
        description: 'Deletes a project board. Returns a `404 Not Found` status if projects are disabled.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a project',
        name: 'Github: Update a project',
        description:
            "Updates a project board's information. Returns a `404 Not Found` status if projects are disabled. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    state: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List project collaborators',
        name: 'Github: List project collaborators',
        description:
            'Lists the collaborators for an organization project. For a project, the list of collaborators includes outside collaborators, organization members that are direct collaborators, organization members with access through team memberships, organization members with access through default organization permissions, and organization owners. You must be an organization owner or a project `admin` to list collaborators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            affiliation: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['affiliation', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id, 'collaborators'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            affiliation: input.inPorts.affiliation,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add project collaborator',
        name: 'Github: Add project collaborator',
        description:
            'Adds a collaborator to an organization project and sets their permission level. You must be an organization owner or a project `admin` to add a collaborator.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            username: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    { kind: 'object', properties: { permission: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id, 'collaborators', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove user as a collaborator',
        name: 'Github: Remove user as a collaborator',
        description:
            'Removes a collaborator from an organization project. You must be an organization owner or a project `admin` to remove a collaborator.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id, 'collaborators', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get project permission for a user',
        name: 'Github: Get project permission for a user',
        description:
            "Returns the collaborator's permission level for an organization project. Possible values for the `permission` key: `admin`, `write`, `read`, `none`. You must be an organization owner or a project `admin` to review a user's permission level.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    permission: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id, 'collaborators', input.inPorts.username, 'permission'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List project columns',
        name: 'Github: List project columns',
        description: 'Lists the project columns in a project.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        project_url: { kind: 'string' },
                        cards_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id, 'columns'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a project column',
        name: 'Github: Create a project column',
        description: 'Creates a new project column.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            project_id: { kind: 'number' },
            body: { kind: 'object', properties: { name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    project_url: { kind: 'string' },
                    cards_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'projects', input.inPorts.project_id, 'columns'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get rate limit status for the authenticated user',
        name: 'Github: Get rate limit status for the authenticated user',
        description:
            "**Note:** Accessing this endpoint does not count against your REST API rate limit.\n\n**Note:** The `rate` object is deprecated. If you're writing new API client code or updating existing code, you should use the `core` object instead of the `rate` object. The `core` object contains the same information that is present in the `rate` object.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    resources: {
                        kind: 'object',
                        properties: {
                            core: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            graphql: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            search: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            code_search: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            source_import: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            integration_manifest: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            code_scanning_upload: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            actions_runner_registration: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            scim: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            dependency_snapshots: {
                                kind: 'object',
                                properties: {
                                    limit: { kind: 'number' },
                                    remaining: { kind: 'number' },
                                    reset: { kind: 'number' },
                                    used: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [
                            'graphql',
                            'code_search',
                            'source_import',
                            'integration_manifest',
                            'code_scanning_upload',
                            'actions_runner_registration',
                            'scim',
                            'dependency_snapshots',
                        ],
                        restriction: 'open',
                    },
                    rate: {
                        kind: 'object',
                        properties: {
                            limit: { kind: 'number' },
                            remaining: { kind: 'number' },
                            reset: { kind: 'number' },
                            used: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'rate_limit'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository',
        name: 'Github: Get a repository',
        description:
            'The `parent` and `source` objects are present when the repository is a fork. `parent` is the repository this repository was forked from, `source` is the ultimate source for the network.\n\n**Note:** In order to see the `security_and_analysis` block for a repository you must have admin permissions for the repository or be an owner or security manager for the organization that owns the repository. For more information, see "[Managing security managers in your organization](https://docs.github.com/organizations/managing-peoples-access-to-your-organization-with-roles/managing-security-managers-in-your-organization)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                        },
                        optionalProperties: ['maintain', 'triage'],
                        restriction: 'open',
                    },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    organization: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    parent: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    source: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    forks: { kind: 'number' },
                    master_branch: { kind: 'string' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    anonymous_access_enabled: { kind: 'boolean' },
                    code_of_conduct: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            key: { kind: 'string' },
                            name: { kind: 'string' },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_and_analysis: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    advanced_security: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dependabot_security_updates: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning_push_protection: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [
                    'is_template',
                    'topics',
                    'visibility',
                    'permissions',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_merge_commit',
                    'allow_update_branch',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'organization',
                    'parent',
                    'source',
                    'master_branch',
                    'anonymous_access_enabled',
                    'code_of_conduct',
                    'security_and_analysis',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository',
        name: 'Github: Delete a repository',
        description:
            'Deleting a repository requires admin access. If OAuth is used, the `delete_repo` scope is required.\n\nIf an organization owner has configured the organization to prevent members from deleting organization-owned\nrepositories, you will get a `403 Forbidden` response.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a repository',
        name: 'Github: Update a repository',
        description:
            "**Note**: To edit a repository's topics, use the [Replace all repository topics](https://docs.github.com/rest/repos/repos#replace-all-repository-topics) endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    homepage: { kind: 'string' },
                    private: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    security_and_analysis: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    advanced_security: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning_push_protection: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    is_template: { kind: 'boolean' },
                    default_branch: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_rebase_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    archived: { kind: 'boolean' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                        },
                        optionalProperties: ['maintain', 'triage'],
                        restriction: 'open',
                    },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    organization: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    parent: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    source: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    forks: { kind: 'number' },
                    master_branch: { kind: 'string' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    anonymous_access_enabled: { kind: 'boolean' },
                    code_of_conduct: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            key: { kind: 'string' },
                            name: { kind: 'string' },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_and_analysis: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    advanced_security: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dependabot_security_updates: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning_push_protection: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [
                    'is_template',
                    'topics',
                    'visibility',
                    'permissions',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_merge_commit',
                    'allow_update_branch',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'organization',
                    'parent',
                    'source',
                    'master_branch',
                    'anonymous_access_enabled',
                    'code_of_conduct',
                    'security_and_analysis',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List artifacts for a repository',
        name: 'Github: List artifacts for a repository',
        description:
            'Lists all artifacts for a repository. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            name: { kind: 'string' },
        },
        optionalInPorts: ['per_page', 'page', 'name'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    artifacts: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                size_in_bytes: { kind: 'number' },
                                url: { kind: 'string' },
                                archive_download_url: { kind: 'string' },
                                expired: { kind: 'boolean' },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_run: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                repository_id: { kind: 'number' },
                                                head_repository_id: { kind: 'number' },
                                                head_branch: { kind: 'string' },
                                                head_sha: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: ['workflow_run'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'artifacts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page, name: input.inPorts.name }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an artifact',
        name: 'Github: Get an artifact',
        description:
            'Gets a specific artifact for a workflow run. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            artifact_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    size_in_bytes: { kind: 'number' },
                    url: { kind: 'string' },
                    archive_download_url: { kind: 'string' },
                    expired: { kind: 'boolean' },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    workflow_run: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    repository_id: { kind: 'number' },
                                    head_repository_id: { kind: 'number' },
                                    head_branch: { kind: 'string' },
                                    head_sha: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['workflow_run'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'artifacts', input.inPorts.artifact_id].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an artifact',
        name: 'Github: Delete an artifact',
        description:
            'Deletes an artifact for a workflow run. You must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            artifact_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'artifacts', input.inPorts.artifact_id].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Actions cache usage for a repository',
        name: 'Github: Get GitHub Actions cache usage for a repository',
        description:
            'Gets GitHub Actions cache usage for a repository.\nThe data fetched using this API is refreshed approximately every 5 minutes, so values returned from this endpoint may take at least 5 minutes to get updated.\nAnyone with read access to the repository can use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    full_name: { kind: 'string' },
                    active_caches_size_in_bytes: { kind: 'number' },
                    active_caches_count: { kind: 'number' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'cache', 'usage'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List GitHub Actions caches for a repository',
        name: 'Github: List GitHub Actions caches for a repository',
        description:
            'Lists the GitHub Actions caches for a repository.\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            ref: { kind: 'string' },
            key: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
        },
        optionalInPorts: ['per_page', 'page', 'ref', 'key', 'sort', 'direction'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    actions_caches: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                ref: { kind: 'string' },
                                key: { kind: 'string' },
                                version: { kind: 'string' },
                                last_accessed_at: { kind: 'string' },
                                created_at: { kind: 'string' },
                                size_in_bytes: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'caches'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            ref: input.inPorts.ref,
                            key: input.inPorts.key,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete GitHub Actions caches for a repository (using a cache key)',
        name: 'Github: Delete GitHub Actions caches for a repository (using a cache key)',
        description:
            'Deletes one or more GitHub Actions caches for a repository, using a complete cache key. By default, all caches that match the provided key are deleted, but you can optionally provide a Git ref to restrict deletions to caches that match both the provided key and the Git ref.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\n\nGitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            key: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: ['ref'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    actions_caches: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                ref: { kind: 'string' },
                                key: { kind: 'string' },
                                version: { kind: 'string' },
                                last_accessed_at: { kind: 'string' },
                                created_at: { kind: 'string' },
                                size_in_bytes: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'caches'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(
                        Object.entries({ key: input.inPorts.key, ref: input.inPorts.ref }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a GitHub Actions cache for a repository (using a cache ID)',
        name: 'Github: Delete a GitHub Actions cache for a repository (using a cache ID)',
        description:
            'Deletes a GitHub Actions cache for a repository, using a cache ID.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\n\nGitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            cache_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'caches', input.inPorts.cache_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a job for a workflow run',
        name: 'Github: Get a job for a workflow run',
        description:
            'Gets a specific job in a workflow run. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            job_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    run_id: { kind: 'number' },
                    run_url: { kind: 'string' },
                    run_attempt: { kind: 'number' },
                    node_id: { kind: 'string' },
                    head_sha: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    status: { kind: 'string' },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    started_at: { kind: 'string' },
                    completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    name: { kind: 'string' },
                    steps: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                status: { kind: 'string' },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                name: { kind: 'string' },
                                number: { kind: 'number' },
                                started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['started_at', 'completed_at'],
                            restriction: 'open',
                        },
                    },
                    check_run_url: { kind: 'string' },
                    labels: { kind: 'array', subType: { kind: 'string' } },
                    runner_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    runner_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    runner_group_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    runner_group_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    workflow_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['run_attempt', 'steps'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'jobs', input.inPorts.job_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Re-run a job from a workflow run',
        name: 'Github: Re-run a job from a workflow run',
        description:
            'Re-run a job and its dependent jobs in a workflow run.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            job_id: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { enable_debug_logging: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'jobs', input.inPorts.job_id, 'rerun'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the customization template for an OIDC subject claim for a repository',
        name: 'Github: Get the customization template for an OIDC subject claim for a repository',
        description:
            'Gets the customization template for an OpenID Connect (OIDC) subject claim.\nYou must authenticate using an access token with the `repo` scope to use this\nendpoint. GitHub Apps must have the `organization_administration:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { use_default: { kind: 'boolean' }, include_claim_keys: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: ['include_claim_keys'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'oidc', 'customization', 'sub'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set the customization template for an OIDC subject claim for a repository',
        name: 'Github: Set the customization template for an OIDC subject claim for a repository',
        description:
            'Sets the customization template and `opt-in` or `opt-out` flag for an OpenID Connect (OIDC) subject claim for a repository.\nYou must authenticate using an access token with the `repo` scope to use this\nendpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { use_default: { kind: 'boolean' }, include_claim_keys: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: ['include_claim_keys'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'oidc', 'customization', 'sub'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository organization secrets',
        name: 'Github: List repository organization secrets',
        description:
            'Lists all organization secrets shared with a repository without revealing their encrypted\nvalues.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    secrets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { name: { kind: 'string' }, created_at: { kind: 'string' }, updated_at: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'organization-secrets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository organization variables',
        name: 'Github: List repository organization variables',
        description:
            'Lists all organiation variables shared with a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions_variables:read` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    variables: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                value: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'organization-variables'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Actions permissions for a repository',
        name: 'Github: Get GitHub Actions permissions for a repository',
        description:
            'Gets the GitHub Actions permissions policy for a repository, including whether GitHub Actions is enabled and the actions and reusable workflows allowed to run in the repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration` repository permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { enabled: { kind: 'boolean' }, allowed_actions: { kind: 'string' }, selected_actions_url: { kind: 'string' } },
                optionalProperties: ['allowed_actions', 'selected_actions_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set GitHub Actions permissions for a repository',
        name: 'Github: Set GitHub Actions permissions for a repository',
        description:
            'Sets the GitHub Actions permissions policy for enabling GitHub Actions and allowed actions and reusable workflows in the repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration` repository permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { enabled: { kind: 'boolean' }, allowed_actions: { kind: 'string' } },
                optionalProperties: ['allowed_actions'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the level of access for workflows outside of the repository',
        name: 'Github: Get the level of access for workflows outside of the repository',
        description:
            'Gets the level of access that workflows outside of the repository have to actions and reusable workflows in the repository.\nThis endpoint only applies to private repositories.\nFor more information, see "[Allowing access to components in a private repository](https://docs.github.com/repositories/managing-your-repositorys-settings-and-features/enabling-features-for-your-repository/managing-github-actions-settings-for-a-repository#allowing-access-to-components-in-a-private-repository)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the\nrepository `administration` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: { kind: 'object', properties: { access_level: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions', 'access'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set the level of access for workflows outside of the repository',
        name: 'Github: Set the level of access for workflows outside of the repository',
        description:
            'Sets the level of access that workflows outside of the repository have to actions and reusable workflows in the repository.\nThis endpoint only applies to private repositories.\nFor more information, see "[Allowing access to components in a private repository](https://docs.github.com/repositories/managing-your-repositorys-settings-and-features/enabling-features-for-your-repository/managing-github-actions-settings-for-a-repository#allowing-access-to-components-in-a-private-repository)".\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the\nrepository `administration` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { access_level: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions', 'access'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get allowed actions and reusable workflows for a repository',
        name: 'Github: Get allowed actions and reusable workflows for a repository',
        description:
            'Gets the settings for selected actions and reusable workflows that are allowed in a repository. To use this endpoint, the repository policy for `allowed_actions` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for a repository](#set-github-actions-permissions-for-a-repository)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration` repository permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    github_owned_allowed: { kind: 'boolean' },
                    verified_allowed: { kind: 'boolean' },
                    patterns_allowed: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions', 'selected-actions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set allowed actions and reusable workflows for a repository',
        name: 'Github: Set allowed actions and reusable workflows for a repository',
        description:
            'Sets the actions and reusable workflows that are allowed in a repository. To use this endpoint, the repository permission policy for `allowed_actions` must be configured to `selected`. For more information, see "[Set GitHub Actions permissions for a repository](#set-github-actions-permissions-for-a-repository)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration` repository permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    github_owned_allowed: { kind: 'boolean' },
                    verified_allowed: { kind: 'boolean' },
                    patterns_allowed: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions', 'selected-actions'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get default workflow permissions for a repository',
        name: 'Github: Get default workflow permissions for a repository',
        description:
            'Gets the default workflow permissions granted to the `GITHUB_TOKEN` when running workflows in a repository,\nas well as if GitHub Actions can submit approving pull request reviews.\nFor more information, see "[Setting the permissions of the GITHUB_TOKEN for your repository](https://docs.github.com/repositories/managing-your-repositorys-settings-and-features/enabling-features-for-your-repository/managing-github-actions-settings-for-a-repository#setting-the-permissions-of-the-github_token-for-your-repository)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the repository `administration` permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { default_workflow_permissions: { kind: 'string' }, can_approve_pull_request_reviews: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions', 'workflow'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set default workflow permissions for a repository',
        name: 'Github: Set default workflow permissions for a repository',
        description:
            'Sets the default workflow permissions granted to the `GITHUB_TOKEN` when running workflows in a repository, and sets if GitHub Actions\ncan submit approving pull request reviews.\nFor more information, see "[Setting the permissions of the GITHUB_TOKEN for your repository](https://docs.github.com/repositories/managing-your-repositorys-settings-and-features/enabling-features-for-your-repository/managing-github-actions-settings-for-a-repository#setting-the-permissions-of-the-github_token-for-your-repository)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the repository `administration` permission to use this API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { default_workflow_permissions: { kind: 'string' }, can_approve_pull_request_reviews: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'permissions', 'workflow'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List self-hosted runners for a repository',
        name: 'Github: List self-hosted runners for a repository',
        description:
            'Lists all self-hosted runners configured in a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    runners: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                runner_group_id: { kind: 'number' },
                                name: { kind: 'string' },
                                os: { kind: 'string' },
                                status: { kind: 'string' },
                                busy: { kind: 'boolean' },
                                labels: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                                        optionalProperties: ['id', 'type'],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: ['runner_group_id'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List runner applications for a repository',
        name: 'Github: List runner applications for a repository',
        description:
            'Lists binaries for the runner application that you can download and run.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        os: { kind: 'string' },
                        architecture: { kind: 'string' },
                        download_url: { kind: 'string' },
                        filename: { kind: 'string' },
                        temp_download_token: { kind: 'string' },
                        sha256_checksum: { kind: 'string' },
                    },
                    optionalProperties: ['temp_download_token', 'sha256_checksum'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners', 'downloads'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create configuration for a just-in-time runner for a repository',
        name: 'Github: Create configuration for a just-in-time runner for a repository',
        description:
            'Generates a configuration that can be passed to the runner application at startup.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    runner_group_id: { kind: 'number' },
                    labels: { kind: 'array', subType: { kind: 'string' } },
                    work_folder: { kind: 'string' },
                },
                optionalProperties: ['work_folder'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    runner: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            runner_group_id: { kind: 'number' },
                            name: { kind: 'string' },
                            os: { kind: 'string' },
                            status: { kind: 'string' },
                            busy: { kind: 'boolean' },
                            labels: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: ['id', 'type'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['runner_group_id'],
                        restriction: 'open',
                    },
                    encoded_jit_config: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners', 'generate-jitconfig'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a registration token for a repository',
        name: 'Github: Create a registration token for a repository',
        description:
            'Returns a token that you can pass to the `config` script. The token\nexpires after one hour.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.\n\nExample using registration token: \n\nConfigure your self-hosted runner, replacing `TOKEN` with the registration token provided\nby this endpoint.\n\n```config.sh --url https://github.com/octo-org/octo-repo-artifacts --token TOKEN```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    token: { kind: 'string' },
                    expires_at: { kind: 'string' },
                    permissions: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    single_file: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    repository_selection: { kind: 'string' },
                },
                optionalProperties: ['permissions', 'repositories', 'single_file', 'repository_selection'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners', 'registration-token'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a remove token for a repository',
        name: 'Github: Create a remove token for a repository',
        description:
            'Returns a token that you can pass to remove a self-hosted runner from\na repository. The token expires after one hour.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.\n\nExample using remove token:\n\nTo remove your self-hosted runner from a repository, replace TOKEN with\nthe remove token provided by this endpoint.\n\n```config.sh remove --token TOKEN```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    token: { kind: 'string' },
                    expires_at: { kind: 'string' },
                    permissions: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    single_file: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    repository_selection: { kind: 'string' },
                },
                optionalProperties: ['permissions', 'repositories', 'single_file', 'repository_selection'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners', 'remove-token'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a self-hosted runner for a repository',
        name: 'Github: Get a self-hosted runner for a repository',
        description:
            'Gets a specific self-hosted runner configured in a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    runner_group_id: { kind: 'number' },
                    name: { kind: 'string' },
                    os: { kind: 'string' },
                    status: { kind: 'string' },
                    busy: { kind: 'boolean' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: ['runner_group_id'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners', input.inPorts.runner_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a self-hosted runner from a repository',
        name: 'Github: Delete a self-hosted runner from a repository',
        description:
            'Forces the removal of a self-hosted runner from a repository. You can use this endpoint to completely remove the runner when the machine you were using no longer exists.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runners', input.inPorts.runner_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List labels for a self-hosted runner for a repository',
        name: 'Github: List labels for a self-hosted runner for a repository',
        description:
            'Lists all labels for a self-hosted runner configured in a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runners',
                        input.inPorts.runner_id,
                        'labels',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add custom labels to a self-hosted runner for a repository',
        name: 'Github: Add custom labels to a self-hosted runner for a repository',
        description:
            'Add custom labels to a self-hosted runner configured in a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { labels: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runners',
                        input.inPorts.runner_id,
                        'labels',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set custom labels for a self-hosted runner for a repository',
        name: 'Github: Set custom labels for a self-hosted runner for a repository',
        description:
            'Remove all previous custom labels and set the new custom labels for a specific\nself-hosted runner configured in a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { labels: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runners',
                        input.inPorts.runner_id,
                        'labels',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove all custom labels from a self-hosted runner for a repository',
        name: 'Github: Remove all custom labels from a self-hosted runner for a repository',
        description:
            'Remove all custom labels from a self-hosted runner configured in a\nrepository. Returns the remaining read-only labels from the runner.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runners',
                        input.inPorts.runner_id,
                        'labels',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a custom label from a self-hosted runner for a repository',
        name: 'Github: Remove a custom label from a self-hosted runner for a repository',
        description:
            'Remove a custom label from a self-hosted runner configured\nin a repository. Returns the remaining labels from the runner.\n\nThis endpoint returns a `404 Not Found` status if the custom label is not\npresent on the runner.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `administration` permission for repositories and the `organization_self_hosted_runners` permission for organizations.\nAuthenticated users must have admin access to repositories or organizations, or the `manage_runners:enterprise` scope for enterprises, to use these endpoints.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            runner_id: { kind: 'number' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, type: { kind: 'string' } },
                            optionalProperties: ['id', 'type'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runners',
                        input.inPorts.runner_id,
                        'labels',
                        input.inPorts.name,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List workflow runs for a repository',
        name: 'Github: List workflow runs for a repository',
        description:
            'Lists all workflow runs for a repository. You can use parameters to narrow the list of results. For more information about using parameters, see [Parameters](https://docs.github.com/rest/overview/resources-in-the-rest-api#parameters).\n\nAnyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            actor: { kind: 'string' },
            branch: { kind: 'string' },
            event: { kind: 'string' },
            status: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            created: { kind: 'string' },
            exclude_pull_requests: { kind: 'boolean' },
            check_suite_id: { kind: 'number' },
            head_sha: { kind: 'string' },
        },
        optionalInPorts: [
            'actor',
            'branch',
            'event',
            'status',
            'per_page',
            'page',
            'created',
            'exclude_pull_requests',
            'check_suite_id',
            'head_sha',
        ],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    workflow_runs: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                node_id: { kind: 'string' },
                                check_suite_id: { kind: 'number' },
                                check_suite_node_id: { kind: 'string' },
                                head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                head_sha: { kind: 'string' },
                                path: { kind: 'string' },
                                run_number: { kind: 'number' },
                                run_attempt: { kind: 'number' },
                                referenced_workflows: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: { path: { kind: 'string' }, sha: { kind: 'string' }, ref: { kind: 'string' } },
                                                optionalProperties: ['ref'],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                event: { kind: 'string' },
                                status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_id: { kind: 'number' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                pull_requests: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    number: { kind: 'number' },
                                                    url: { kind: 'string' },
                                                    head: {
                                                        kind: 'object',
                                                        properties: {
                                                            ref: { kind: 'string' },
                                                            sha: { kind: 'string' },
                                                            repo: {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    base: {
                                                        kind: 'object',
                                                        properties: {
                                                            ref: { kind: 'string' },
                                                            sha: { kind: 'string' },
                                                            repo: {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                triggering_actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                run_started_at: { kind: 'string' },
                                jobs_url: { kind: 'string' },
                                logs_url: { kind: 'string' },
                                check_suite_url: { kind: 'string' },
                                artifacts_url: { kind: 'string' },
                                cancel_url: { kind: 'string' },
                                rerun_url: { kind: 'string' },
                                previous_attempt_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_url: { kind: 'string' },
                                head_commit: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'string' },
                                                tree_id: { kind: 'string' },
                                                message: { kind: 'string' },
                                                timestamp: { kind: 'string' },
                                                author: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                committer: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                head_repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                head_repository_id: { kind: 'number' },
                                display_title: { kind: 'string' },
                            },
                            optionalProperties: [
                                'name',
                                'check_suite_id',
                                'check_suite_node_id',
                                'run_attempt',
                                'referenced_workflows',
                                'actor',
                                'triggering_actor',
                                'run_started_at',
                                'previous_attempt_url',
                                'head_repository_id',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            actor: input.inPorts.actor,
                            branch: input.inPorts.branch,
                            event: input.inPorts.event,
                            status: input.inPorts.status,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            created: input.inPorts.created,
                            exclude_pull_requests: input.inPorts.exclude_pull_requests,
                            check_suite_id: input.inPorts.check_suite_id,
                            head_sha: input.inPorts.head_sha,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a workflow run',
        name: 'Github: Get a workflow run',
        description:
            'Gets a specific workflow run. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            exclude_pull_requests: { kind: 'boolean' },
        },
        optionalInPorts: ['exclude_pull_requests'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    node_id: { kind: 'string' },
                    check_suite_id: { kind: 'number' },
                    check_suite_node_id: { kind: 'string' },
                    head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_sha: { kind: 'string' },
                    path: { kind: 'string' },
                    run_number: { kind: 'number' },
                    run_attempt: { kind: 'number' },
                    referenced_workflows: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { path: { kind: 'string' }, sha: { kind: 'string' }, ref: { kind: 'string' } },
                                    optionalProperties: ['ref'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    event: { kind: 'string' },
                    status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    workflow_id: { kind: 'number' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_requests: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        number: { kind: 'number' },
                                        url: { kind: 'string' },
                                        head: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        base: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    actor: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    triggering_actor: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    run_started_at: { kind: 'string' },
                    jobs_url: { kind: 'string' },
                    logs_url: { kind: 'string' },
                    check_suite_url: { kind: 'string' },
                    artifacts_url: { kind: 'string' },
                    cancel_url: { kind: 'string' },
                    rerun_url: { kind: 'string' },
                    previous_attempt_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    workflow_url: { kind: 'string' },
                    head_commit: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'string' },
                                    tree_id: { kind: 'string' },
                                    message: { kind: 'string' },
                                    timestamp: { kind: 'string' },
                                    author: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    committer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    head_repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    head_repository_id: { kind: 'number' },
                    display_title: { kind: 'string' },
                },
                optionalProperties: [
                    'name',
                    'check_suite_id',
                    'check_suite_node_id',
                    'run_attempt',
                    'referenced_workflows',
                    'actor',
                    'triggering_actor',
                    'run_started_at',
                    'previous_attempt_url',
                    'head_repository_id',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ exclude_pull_requests: input.inPorts.exclude_pull_requests }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a workflow run',
        name: 'Github: Delete a workflow run',
        description:
            'Delete a specific workflow run. Anyone with write access to the repository can use this endpoint. If the repository is\nprivate you must use an access token with the `repo` scope. GitHub Apps must have the `actions:write` permission to use\nthis endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the review history for a workflow run',
        name: 'Github: Get the review history for a workflow run',
        description:
            'Anyone with read access to the repository can use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        environments: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        state: { kind: 'string' },
                        user: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        comment: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'approvals'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Approve a workflow run for a fork pull request',
        name: 'Github: Approve a workflow run for a fork pull request',
        description:
            'Approves a workflow run for a pull request from a public fork of a first time contributor. For more information, see ["Approving workflow runs from public forks](https://docs.github.com/actions/managing-workflow-runs/approving-workflow-runs-from-public-forks)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'approve'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List workflow run artifacts',
        name: 'Github: List workflow run artifacts',
        description:
            'Lists artifacts for a workflow run. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    artifacts: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                size_in_bytes: { kind: 'number' },
                                url: { kind: 'string' },
                                archive_download_url: { kind: 'string' },
                                expired: { kind: 'boolean' },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_run: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                repository_id: { kind: 'number' },
                                                head_repository_id: { kind: 'number' },
                                                head_branch: { kind: 'string' },
                                                head_sha: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: ['workflow_run'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'artifacts'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a workflow run attempt',
        name: 'Github: Get a workflow run attempt',
        description:
            'Gets a specific workflow run attempt. Anyone with read access to the repository\ncan use this endpoint. If the repository is private you must use an access token\nwith the `repo` scope. GitHub Apps must have the `actions:read` permission to\nuse this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            attempt_number: { kind: 'number' },
            exclude_pull_requests: { kind: 'boolean' },
        },
        optionalInPorts: ['exclude_pull_requests'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    node_id: { kind: 'string' },
                    check_suite_id: { kind: 'number' },
                    check_suite_node_id: { kind: 'string' },
                    head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_sha: { kind: 'string' },
                    path: { kind: 'string' },
                    run_number: { kind: 'number' },
                    run_attempt: { kind: 'number' },
                    referenced_workflows: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { path: { kind: 'string' }, sha: { kind: 'string' }, ref: { kind: 'string' } },
                                    optionalProperties: ['ref'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    event: { kind: 'string' },
                    status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    workflow_id: { kind: 'number' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_requests: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        number: { kind: 'number' },
                                        url: { kind: 'string' },
                                        head: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        base: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    actor: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    triggering_actor: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    run_started_at: { kind: 'string' },
                    jobs_url: { kind: 'string' },
                    logs_url: { kind: 'string' },
                    check_suite_url: { kind: 'string' },
                    artifacts_url: { kind: 'string' },
                    cancel_url: { kind: 'string' },
                    rerun_url: { kind: 'string' },
                    previous_attempt_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    workflow_url: { kind: 'string' },
                    head_commit: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'string' },
                                    tree_id: { kind: 'string' },
                                    message: { kind: 'string' },
                                    timestamp: { kind: 'string' },
                                    author: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    committer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    head_repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    head_repository_id: { kind: 'number' },
                    display_title: { kind: 'string' },
                },
                optionalProperties: [
                    'name',
                    'check_suite_id',
                    'check_suite_node_id',
                    'run_attempt',
                    'referenced_workflows',
                    'actor',
                    'triggering_actor',
                    'run_started_at',
                    'previous_attempt_url',
                    'head_repository_id',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runs',
                        input.inPorts.run_id,
                        'attempts',
                        input.inPorts.attempt_number,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ exclude_pull_requests: input.inPorts.exclude_pull_requests }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List jobs for a workflow run attempt',
        name: 'Github: List jobs for a workflow run attempt',
        description:
            'Lists jobs for a specific workflow run attempt. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint. You can use parameters to narrow the list of results. For more information about using parameters, see [Parameters](https://docs.github.com/rest/overview/resources-in-the-rest-api#parameters).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            attempt_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    jobs: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                run_id: { kind: 'number' },
                                run_url: { kind: 'string' },
                                run_attempt: { kind: 'number' },
                                node_id: { kind: 'string' },
                                head_sha: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                status: { kind: 'string' },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                started_at: { kind: 'string' },
                                completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                name: { kind: 'string' },
                                steps: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            status: { kind: 'string' },
                                            conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            name: { kind: 'string' },
                                            number: { kind: 'number' },
                                            started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: ['started_at', 'completed_at'],
                                        restriction: 'open',
                                    },
                                },
                                check_run_url: { kind: 'string' },
                                labels: { kind: 'array', subType: { kind: 'string' } },
                                runner_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                runner_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                runner_group_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                runner_group_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['run_attempt', 'steps'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runs',
                        input.inPorts.run_id,
                        'attempts',
                        input.inPorts.attempt_number,
                        'jobs',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Cancel a workflow run',
        name: 'Github: Cancel a workflow run',
        description:
            'Cancels a workflow run using its `id`.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'cancel'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Review custom deployment protection rules for a workflow run',
        name: 'Github: Review custom deployment protection rules for a workflow run',
        description:
            'Approve or reject custom deployment protection rules provided by a GitHub App for a workflow run. For more information, see "[Using environments for deployment](https://docs.github.com/actions/deployment/targeting-different-environments/using-environments-for-deployment)."\n\n**Note:** GitHub Apps can only review their own custom deployment protection rules.\nTo approve or reject pending deployments that are waiting for review from a specific person or team, see [`POST /repos/{owner}/{repo}/actions/runs/{run_id}/pending_deployments`](/rest/actions/workflow-runs#review-pending-deployments-for-a-workflow-run).\n\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have read and write permission for **Deployments** to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { environment_name: { kind: 'string' }, comment: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: { environment_name: { kind: 'string' }, state: { kind: 'string' }, comment: { kind: 'string' } },
                        optionalProperties: ['comment'],
                        restriction: 'open',
                    },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runs',
                        input.inPorts.run_id,
                        'deployment_protection_rule',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List jobs for a workflow run',
        name: 'Github: List jobs for a workflow run',
        description:
            'Lists jobs for a workflow run. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint. You can use parameters to narrow the list of results. For more information about using parameters, see [Parameters](https://docs.github.com/rest/overview/resources-in-the-rest-api#parameters).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            filter: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['filter', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    jobs: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                run_id: { kind: 'number' },
                                run_url: { kind: 'string' },
                                run_attempt: { kind: 'number' },
                                node_id: { kind: 'string' },
                                head_sha: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                status: { kind: 'string' },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                started_at: { kind: 'string' },
                                completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                name: { kind: 'string' },
                                steps: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            status: { kind: 'string' },
                                            conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            name: { kind: 'string' },
                                            number: { kind: 'number' },
                                            started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: ['started_at', 'completed_at'],
                                        restriction: 'open',
                                    },
                                },
                                check_run_url: { kind: 'string' },
                                labels: { kind: 'array', subType: { kind: 'string' } },
                                runner_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                runner_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                runner_group_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                runner_group_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['run_attempt', 'steps'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'jobs'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ filter: input.inPorts.filter, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete workflow run logs',
        name: 'Github: Delete workflow run logs',
        description:
            'Deletes all logs for a workflow run. You must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'logs'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get pending deployments for a workflow run',
        name: 'Github: Get pending deployments for a workflow run',
        description:
            'Get all deployment environments for a workflow run that are waiting for protection rules to pass.\n\nAnyone with read access to the repository can use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        environment: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        wait_timer: { kind: 'number' },
                        wait_timer_started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        current_user_can_approve: { kind: 'boolean' },
                        reviewers: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    type: { kind: 'string' },
                                    reviewer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    slug: { kind: 'string' },
                                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    privacy: { kind: 'string' },
                                                    notification_setting: { kind: 'string' },
                                                    permission: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            pull: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    members_url: { kind: 'string' },
                                                    repositories_url: { kind: 'string' },
                                                    parent: {
                                                        kind: 'union',
                                                        subTypes: [
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    url: { kind: 'string' },
                                                                    members_url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                    description: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    permission: { kind: 'string' },
                                                                    privacy: { kind: 'string' },
                                                                    notification_setting: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    repositories_url: { kind: 'string' },
                                                                    slug: { kind: 'string' },
                                                                    ldap_dn: { kind: 'string' },
                                                                },
                                                                optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                restriction: 'open',
                                                            },
                                                            { kind: 'null' },
                                                        ],
                                                    },
                                                },
                                                optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                restriction: 'open',
                                            },
                                        ],
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runs',
                        input.inPorts.run_id,
                        'pending_deployments',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Review pending deployments for a workflow run',
        name: 'Github: Review pending deployments for a workflow run',
        description:
            'Approve or reject pending deployments that are waiting on approval by a required reviewer.\n\nRequired reviewers with read access to the repository contents and deployments can use this endpoint. Required reviewers must authenticate using an access token with the `repo` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    environment_ids: { kind: 'array', subType: { kind: 'number' } },
                    state: { kind: 'string' },
                    comment: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        sha: { kind: 'string' },
                        ref: { kind: 'string' },
                        task: { kind: 'string' },
                        payload: {
                            kind: 'union',
                            subTypes: [{ kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' }, { kind: 'string' }],
                        },
                        original_environment: { kind: 'string' },
                        environment: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        transient_environment: { kind: 'boolean' },
                        production_environment: { kind: 'boolean' },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'original_environment',
                        'transient_environment',
                        'production_environment',
                        'performed_via_github_app',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runs',
                        input.inPorts.run_id,
                        'pending_deployments',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Re-run a workflow',
        name: 'Github: Re-run a workflow',
        description:
            'Re-runs your workflow run using its `id`. You must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { enable_debug_logging: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'rerun'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Re-run failed jobs from a workflow run',
        name: 'Github: Re-run failed jobs from a workflow run',
        description:
            'Re-run all of the failed jobs and their dependent jobs in a workflow run using the `id` of the workflow run. You must authenticate using an access token with the `repo` scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { enable_debug_logging: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'runs',
                        input.inPorts.run_id,
                        'rerun-failed-jobs',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get workflow run usage',
        name: 'Github: Get workflow run usage',
        description:
            'Gets the number of billable minutes and total run time for a specific workflow run. Billable minutes only apply to workflows in private repositories that use GitHub-hosted runners. Usage is listed for each GitHub-hosted runner operating system in milliseconds. Any job re-runs are also included in the usage. The usage does not include the multiplier for macOS and Windows runners and is not rounded up to the nearest whole minute. For more information, see "[Managing billing for GitHub Actions](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-actions)".\n\nAnyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    billable: {
                        kind: 'object',
                        properties: {
                            UBUNTU: {
                                kind: 'object',
                                properties: {
                                    total_ms: { kind: 'number' },
                                    jobs: { kind: 'number' },
                                    job_runs: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { job_id: { kind: 'number' }, duration_ms: { kind: 'number' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: ['job_runs'],
                                restriction: 'open',
                            },
                            MACOS: {
                                kind: 'object',
                                properties: {
                                    total_ms: { kind: 'number' },
                                    jobs: { kind: 'number' },
                                    job_runs: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { job_id: { kind: 'number' }, duration_ms: { kind: 'number' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: ['job_runs'],
                                restriction: 'open',
                            },
                            WINDOWS: {
                                kind: 'object',
                                properties: {
                                    total_ms: { kind: 'number' },
                                    jobs: { kind: 'number' },
                                    job_runs: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { job_id: { kind: 'number' }, duration_ms: { kind: 'number' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: ['job_runs'],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    run_duration_ms: { kind: 'number' },
                },
                optionalProperties: ['run_duration_ms'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'runs', input.inPorts.run_id, 'timing'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository secrets',
        name: 'Github: List repository secrets',
        description:
            'Lists all secrets available in a repository without revealing their encrypted\nvalues.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    secrets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { name: { kind: 'string' }, created_at: { kind: 'string' }, updated_at: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'secrets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository public key',
        name: 'Github: Get a repository public key',
        description:
            'Gets your public key, which you need to encrypt secrets. You need to\nencrypt a secret before you can create or update secrets.\n\nAnyone with read access to the repository can use this endpoint.\nIf the repository is private you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key_id: { kind: 'string' },
                    key: { kind: 'string' },
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: ['id', 'url', 'title', 'created_at'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'secrets', 'public-key'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository secret',
        name: 'Github: Get a repository secret',
        description:
            'Gets a single repository secret without revealing its encrypted value.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { name: { kind: 'string' }, created_at: { kind: 'string' }, updated_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create or update a repository secret',
        name: 'Github: Create or update a repository secret',
        description:
            'Creates or updates a repository secret with an encrypted value. Encrypt your secret using\n[LibSodium](https://libsodium.gitbook.io/doc/bindings_for_other_languages).\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.\n\n**Example encrypting a secret using Node.js**\n\nEncrypt your secret using the [libsodium-wrappers](https://www.npmjs.com/package/libsodium-wrappers) library.\n\n```\nconst sodium = require(\'libsodium-wrappers\')\nconst secret = \'plain-text-secret\' // replace with the secret you want to encrypt\nconst key = \'base64-encoded-public-key\' // replace with the Base64 encoded public key\n\n//Check if libsodium is ready and then proceed.\nsodium.ready.then(() => {\n  // Convert Secret & Base64 key to Uint8Array.\n  let binkey = sodium.from_base64(key, sodium.base64_variants.ORIGINAL)\n  let binsec = sodium.from_string(secret)\n\n  //Encrypt the secret using LibSodium\n  let encBytes = sodium.crypto_box_seal(binsec, binkey)\n\n  // Convert encrypted Uint8Array to Base64\n  let output = sodium.to_base64(encBytes, sodium.base64_variants.ORIGINAL)\n\n  console.log(output)\n});\n```\n\n**Example encrypting a secret using Python**\n\nEncrypt your secret using [pynacl](https://pynacl.readthedocs.io/en/latest/public/#nacl-public-sealedbox) with Python 3.\n\n```\nfrom base64 import b64encode\nfrom nacl import encoding, public\n\ndef encrypt(public_key: str, secret_value: str) -> str:\n  """Encrypt a Unicode string using the public key."""\n  public_key = public.PublicKey(public_key.encode("utf-8"), encoding.Base64Encoder())\n  sealed_box = public.SealedBox(public_key)\n  encrypted = sealed_box.encrypt(secret_value.encode("utf-8"))\n  return b64encode(encrypted).decode("utf-8")\n```\n\n**Example encrypting a secret using C#**\n\nEncrypt your secret using the [Sodium.Core](https://www.nuget.org/packages/Sodium.Core/) package.\n\n```\nvar secretValue = System.Text.Encoding.UTF8.GetBytes("mySecret");\nvar publicKey = Convert.FromBase64String("2Sg8iYjAxxmI2LvUXpJjkYrMxURPc8r+dB7TJyvvcCU=");\n\nvar sealedPublicKeyBox = Sodium.SealedPublicKeyBox.Create(secretValue, publicKey);\n\nConsole.WriteLine(Convert.ToBase64String(sealedPublicKeyBox));\n```\n\n**Example encrypting a secret using Ruby**\n\nEncrypt your secret using the [rbnacl](https://github.com/RubyCrypto/rbnacl) gem.\n\n```ruby\nrequire "rbnacl"\nrequire "base64"\n\nkey = Base64.decode64("+ZYvJDZMHUfBkJdyq5Zm9SKqeuBQ4sj+6sfjlH4CgG0=")\npublic_key = RbNaCl::PublicKey.new(key)\n\nbox = RbNaCl::Boxes::Sealed.from_public_key(public_key)\nencrypted_secret = box.encrypt("my_secret")\n\n# Print the base64 encoded secret\nputs Base64.strict_encode64(encrypted_secret)\n```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            secret_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { encrypted_value: { kind: 'string' }, key_id: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository secret',
        name: 'Github: Delete a repository secret',
        description:
            'Deletes a secret in a repository using the secret name.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository variables',
        name: 'Github: List repository variables',
        description:
            'Lists all repository variables.\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions_variables:read` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    variables: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                value: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'variables'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository variable',
        name: 'Github: Create a repository variable',
        description:
            'Creates a repository variable that you can reference in a GitHub Actions workflow.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions_variables:write` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, value: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'variables'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository variable',
        name: 'Github: Get a repository variable',
        description:
            'Gets a specific variable in a repository.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions_variables:read` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    value: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'variables', input.inPorts.name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository variable',
        name: 'Github: Delete a repository variable',
        description:
            'Deletes a repository variable using the variable name.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions_variables:write` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'variables', input.inPorts.name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a repository variable',
        name: 'Github: Update a repository variable',
        description:
            'Updates a repository variable that you can reference in a GitHub Actions workflow.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `actions_variables:write` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, value: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'variables', input.inPorts.name].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository workflows',
        name: 'Github: List repository workflows',
        description:
            'Lists the workflows in a repository. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    workflows: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                path: { kind: 'string' },
                                state: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                badge_url: { kind: 'string' },
                                deleted_at: { kind: 'string' },
                            },
                            optionalProperties: ['deleted_at'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'workflows'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a workflow',
        name: 'Github: Get a workflow',
        description:
            'Gets a specific workflow. You can replace `workflow_id` with the workflow file name. For example, you could use `main.yaml`. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            workflow_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    path: { kind: 'string' },
                    state: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    badge_url: { kind: 'string' },
                    deleted_at: { kind: 'string' },
                },
                optionalProperties: ['deleted_at'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'actions', 'workflows', input.inPorts.workflow_id].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Disable a workflow',
        name: 'Github: Disable a workflow',
        description:
            'Disables a workflow and sets the `state` of the workflow to `disabled_manually`. You can replace `workflow_id` with the workflow file name. For example, you could use `main.yaml`.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            workflow_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'workflows',
                        input.inPorts.workflow_id,
                        'disable',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a workflow dispatch event',
        name: 'Github: Create a workflow dispatch event',
        description:
            'You can use this endpoint to manually trigger a GitHub Actions workflow run. You can replace `workflow_id` with the workflow file name. For example, you could use `main.yaml`.\n\nYou must configure your GitHub Actions workflow to run when the [`workflow_dispatch` webhook](/developers/webhooks-and-events/webhook-events-and-payloads#workflow_dispatch) event occurs. The `inputs` are configured in the workflow file. For more information about how to configure the `workflow_dispatch` event in the workflow file, see "[Events that trigger workflows](/actions/reference/events-that-trigger-workflows#workflow_dispatch)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint. For more information, see "[Creating a personal access token for the command line](https://docs.github.com/articles/creating-a-personal-access-token-for-the-command-line)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            workflow_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
            body: {
                kind: 'object',
                properties: {
                    ref: { kind: 'string' },
                    inputs: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                },
                optionalProperties: ['inputs'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'workflows',
                        input.inPorts.workflow_id,
                        'dispatches',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Enable a workflow',
        name: 'Github: Enable a workflow',
        description:
            'Enables a workflow and sets the `state` of the workflow to `active`. You can replace `workflow_id` with the workflow file name. For example, you could use `main.yaml`.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `actions:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            workflow_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'workflows',
                        input.inPorts.workflow_id,
                        'enable',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List workflow runs for a workflow',
        name: 'Github: List workflow runs for a workflow',
        description:
            'List all workflow runs for a workflow. You can replace `workflow_id` with the workflow file name. For example, you could use `main.yaml`. You can use parameters to narrow the list of results. For more information about using parameters, see [Parameters](https://docs.github.com/rest/overview/resources-in-the-rest-api#parameters).\n\nAnyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            workflow_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
            actor: { kind: 'string' },
            branch: { kind: 'string' },
            event: { kind: 'string' },
            status: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            created: { kind: 'string' },
            exclude_pull_requests: { kind: 'boolean' },
            check_suite_id: { kind: 'number' },
            head_sha: { kind: 'string' },
        },
        optionalInPorts: [
            'actor',
            'branch',
            'event',
            'status',
            'per_page',
            'page',
            'created',
            'exclude_pull_requests',
            'check_suite_id',
            'head_sha',
        ],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    workflow_runs: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                node_id: { kind: 'string' },
                                check_suite_id: { kind: 'number' },
                                check_suite_node_id: { kind: 'string' },
                                head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                head_sha: { kind: 'string' },
                                path: { kind: 'string' },
                                run_number: { kind: 'number' },
                                run_attempt: { kind: 'number' },
                                referenced_workflows: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: { path: { kind: 'string' }, sha: { kind: 'string' }, ref: { kind: 'string' } },
                                                optionalProperties: ['ref'],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                event: { kind: 'string' },
                                status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_id: { kind: 'number' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                pull_requests: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    number: { kind: 'number' },
                                                    url: { kind: 'string' },
                                                    head: {
                                                        kind: 'object',
                                                        properties: {
                                                            ref: { kind: 'string' },
                                                            sha: { kind: 'string' },
                                                            repo: {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    base: {
                                                        kind: 'object',
                                                        properties: {
                                                            ref: { kind: 'string' },
                                                            sha: { kind: 'string' },
                                                            repo: {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                triggering_actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                run_started_at: { kind: 'string' },
                                jobs_url: { kind: 'string' },
                                logs_url: { kind: 'string' },
                                check_suite_url: { kind: 'string' },
                                artifacts_url: { kind: 'string' },
                                cancel_url: { kind: 'string' },
                                rerun_url: { kind: 'string' },
                                previous_attempt_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                workflow_url: { kind: 'string' },
                                head_commit: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'string' },
                                                tree_id: { kind: 'string' },
                                                message: { kind: 'string' },
                                                timestamp: { kind: 'string' },
                                                author: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                committer: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                head_repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                head_repository_id: { kind: 'number' },
                                display_title: { kind: 'string' },
                            },
                            optionalProperties: [
                                'name',
                                'check_suite_id',
                                'check_suite_node_id',
                                'run_attempt',
                                'referenced_workflows',
                                'actor',
                                'triggering_actor',
                                'run_started_at',
                                'previous_attempt_url',
                                'head_repository_id',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'workflows',
                        input.inPorts.workflow_id,
                        'runs',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            actor: input.inPorts.actor,
                            branch: input.inPorts.branch,
                            event: input.inPorts.event,
                            status: input.inPorts.status,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            created: input.inPorts.created,
                            exclude_pull_requests: input.inPorts.exclude_pull_requests,
                            check_suite_id: input.inPorts.check_suite_id,
                            head_sha: input.inPorts.head_sha,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get workflow usage',
        name: 'Github: Get workflow usage',
        description:
            'Gets the number of billable minutes used by a specific workflow during the current billing cycle. Billable minutes only apply to workflows in private repositories that use GitHub-hosted runners. Usage is listed for each GitHub-hosted runner operating system in milliseconds. Any job re-runs are also included in the usage. The usage does not include the multiplier for macOS and Windows runners and is not rounded up to the nearest whole minute. For more information, see "[Managing billing for GitHub Actions](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-actions)".\n\nYou can replace `workflow_id` with the workflow file name. For example, you could use `main.yaml`. Anyone with read access to the repository can use this endpoint. If the repository is private you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            workflow_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    billable: {
                        kind: 'object',
                        properties: {
                            UBUNTU: {
                                kind: 'object',
                                properties: { total_ms: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            MACOS: {
                                kind: 'object',
                                properties: { total_ms: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            WINDOWS: {
                                kind: 'object',
                                properties: { total_ms: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'actions',
                        'workflows',
                        input.inPorts.workflow_id,
                        'timing',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository activities',
        name: 'Github: List repository activities',
        description:
            'Lists a detailed history of changes to a repository, such as pushes, merges, force pushes, and branch changes, and associates these changes with commits and users.\n\nFor more information about viewing repository activity,\nsee "[Viewing repository activity](https://docs.github.com/repositories/viewing-activity-and-data-for-your-repository/viewing-repository-activity)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            before: { kind: 'string' },
            after: { kind: 'string' },
            ref: { kind: 'string' },
            actor: { kind: 'string' },
            time_period: { kind: 'string' },
            activity_type: { kind: 'string' },
        },
        optionalInPorts: ['direction', 'per_page', 'before', 'after', 'ref', 'actor', 'time_period', 'activity_type'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        before: { kind: 'string' },
                        after: { kind: 'string' },
                        ref: { kind: 'string' },
                        timestamp: { kind: 'string' },
                        activity_type: { kind: 'string' },
                        actor: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'activity'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                            ref: input.inPorts.ref,
                            actor: input.inPorts.actor,
                            time_period: input.inPorts.time_period,
                            activity_type: input.inPorts.activity_type,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List assignees',
        name: 'Github: List assignees',
        description:
            'Lists the [available assignees](https://docs.github.com/articles/assigning-issues-and-pull-requests-to-other-github-users/) for issues in a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'assignees'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a user can be assigned',
        name: 'Github: Check if a user can be assigned',
        description:
            'Checks if a user has permission to be assigned to an issue in this repository.\n\nIf the `assignee` can be assigned to issues in the repository, a `204` header with no content is returned.\n\nOtherwise a `404` status code is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            assignee: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'assignees', input.inPorts.assignee].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List all autolinks of a repository',
        name: 'Github: List all autolinks of a repository',
        description:
            'This returns a list of autolinks configured for the given repository.\n\nInformation about autolinks are only available to repository administrators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        key_prefix: { kind: 'string' },
                        url_template: { kind: 'string' },
                        is_alphanumeric: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'autolinks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ page: input.inPorts.page }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an autolink reference for a repository',
        name: 'Github: Create an autolink reference for a repository',
        description: 'Users with admin access to the repository can create an autolink.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { key_prefix: { kind: 'string' }, url_template: { kind: 'string' }, is_alphanumeric: { kind: 'boolean' } },
                optionalProperties: ['is_alphanumeric'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    key_prefix: { kind: 'string' },
                    url_template: { kind: 'string' },
                    is_alphanumeric: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'autolinks'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an autolink reference of a repository',
        name: 'Github: Get an autolink reference of a repository',
        description:
            'This returns a single autolink reference by ID that was configured for the given repository.\n\nInformation about autolinks are only available to repository administrators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            autolink_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    key_prefix: { kind: 'string' },
                    url_template: { kind: 'string' },
                    is_alphanumeric: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'autolinks', input.inPorts.autolink_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an autolink reference from a repository',
        name: 'Github: Delete an autolink reference from a repository',
        description:
            'This deletes a single autolink reference by ID that was configured for the given repository.\n\nInformation about autolinks are only available to repository administrators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            autolink_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'autolinks', input.inPorts.autolink_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if automated security fixes are enabled for a repository',
        name: 'Github: Check if automated security fixes are enabled for a repository',
        description:
            'Shows whether automated security fixes are enabled, disabled or paused for a repository. The authenticated user must have admin read access to the repository. For more information, see "[Configuring automated security fixes](https://docs.github.com/articles/configuring-automated-security-fixes)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { enabled: { kind: 'boolean' }, paused: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'automated-security-fixes'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Enable automated security fixes',
        name: 'Github: Enable automated security fixes',
        description:
            'Enables automated security fixes for a repository. The authenticated user must have admin access to the repository. For more information, see "[Configuring automated security fixes](https://docs.github.com/articles/configuring-automated-security-fixes)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'automated-security-fixes'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Disable automated security fixes',
        name: 'Github: Disable automated security fixes',
        description:
            'Disables automated security fixes for a repository. The authenticated user must have admin access to the repository. For more information, see "[Configuring automated security fixes](https://docs.github.com/articles/configuring-automated-security-fixes)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'automated-security-fixes'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List branches',
        name: 'Github: List branches',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            protected: { kind: 'boolean' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['protected', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'string' },
                        commit: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        protected: { kind: 'boolean' },
                        protection: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                enabled: { kind: 'boolean' },
                                required_status_checks: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        enforcement_level: { kind: 'string' },
                                        contexts: { kind: 'array', subType: { kind: 'string' } },
                                        checks: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    context: { kind: 'string' },
                                                    app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        contexts_url: { kind: 'string' },
                                        strict: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['url', 'enforcement_level', 'contexts_url', 'strict'],
                                    restriction: 'open',
                                },
                                enforce_admins: {
                                    kind: 'object',
                                    properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                required_pull_request_reviews: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        dismissal_restrictions: {
                                            kind: 'object',
                                            properties: {
                                                users: {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                teams: {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            slug: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            privacy: { kind: 'string' },
                                                            notification_setting: { kind: 'string' },
                                                            permission: { kind: 'string' },
                                                            permissions: {
                                                                kind: 'object',
                                                                properties: {
                                                                    pull: { kind: 'boolean' },
                                                                    triage: { kind: 'boolean' },
                                                                    push: { kind: 'boolean' },
                                                                    maintain: { kind: 'boolean' },
                                                                    admin: { kind: 'boolean' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            repositories_url: { kind: 'string' },
                                                            parent: {
                                                                kind: 'union',
                                                                subTypes: [
                                                                    {
                                                                        kind: 'object',
                                                                        properties: {
                                                                            id: { kind: 'number' },
                                                                            node_id: { kind: 'string' },
                                                                            url: { kind: 'string' },
                                                                            members_url: { kind: 'string' },
                                                                            name: { kind: 'string' },
                                                                            description: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            permission: { kind: 'string' },
                                                                            privacy: { kind: 'string' },
                                                                            notification_setting: { kind: 'string' },
                                                                            html_url: { kind: 'string' },
                                                                            repositories_url: { kind: 'string' },
                                                                            slug: { kind: 'string' },
                                                                            ldap_dn: { kind: 'string' },
                                                                        },
                                                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                        restriction: 'open',
                                                                    },
                                                                    { kind: 'null' },
                                                                ],
                                                            },
                                                        },
                                                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                apps: {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            slug: { kind: 'string' },
                                                            node_id: { kind: 'string' },
                                                            owner: {
                                                                kind: 'union',
                                                                subTypes: [
                                                                    {
                                                                        kind: 'object',
                                                                        properties: {
                                                                            name: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            email: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            login: { kind: 'string' },
                                                                            id: { kind: 'number' },
                                                                            node_id: { kind: 'string' },
                                                                            avatar_url: { kind: 'string' },
                                                                            gravatar_id: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            url: { kind: 'string' },
                                                                            html_url: { kind: 'string' },
                                                                            followers_url: { kind: 'string' },
                                                                            following_url: { kind: 'string' },
                                                                            gists_url: { kind: 'string' },
                                                                            starred_url: { kind: 'string' },
                                                                            subscriptions_url: { kind: 'string' },
                                                                            organizations_url: { kind: 'string' },
                                                                            repos_url: { kind: 'string' },
                                                                            events_url: { kind: 'string' },
                                                                            received_events_url: { kind: 'string' },
                                                                            type: { kind: 'string' },
                                                                            site_admin: { kind: 'boolean' },
                                                                            starred_at: { kind: 'string' },
                                                                        },
                                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                                        restriction: 'open',
                                                                    },
                                                                    { kind: 'null' },
                                                                ],
                                                            },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            external_url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            created_at: { kind: 'string' },
                                                            updated_at: { kind: 'string' },
                                                            permissions: {
                                                                kind: 'object',
                                                                properties: {
                                                                    issues: { kind: 'string' },
                                                                    checks: { kind: 'string' },
                                                                    metadata: { kind: 'string' },
                                                                    contents: { kind: 'string' },
                                                                    deployments: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            events: { kind: 'array', subType: { kind: 'string' } },
                                                            installations_count: { kind: 'number' },
                                                            client_id: { kind: 'string' },
                                                            client_secret: { kind: 'string' },
                                                            webhook_secret: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            pem: { kind: 'string' },
                                                        },
                                                        optionalProperties: [
                                                            'slug',
                                                            'installations_count',
                                                            'client_id',
                                                            'client_secret',
                                                            'webhook_secret',
                                                            'pem',
                                                        ],
                                                        restriction: 'open',
                                                    },
                                                },
                                                url: { kind: 'string' },
                                                users_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        bypass_pull_request_allowances: {
                                            kind: 'object',
                                            properties: {
                                                users: {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                teams: {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            slug: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            privacy: { kind: 'string' },
                                                            notification_setting: { kind: 'string' },
                                                            permission: { kind: 'string' },
                                                            permissions: {
                                                                kind: 'object',
                                                                properties: {
                                                                    pull: { kind: 'boolean' },
                                                                    triage: { kind: 'boolean' },
                                                                    push: { kind: 'boolean' },
                                                                    maintain: { kind: 'boolean' },
                                                                    admin: { kind: 'boolean' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            repositories_url: { kind: 'string' },
                                                            parent: {
                                                                kind: 'union',
                                                                subTypes: [
                                                                    {
                                                                        kind: 'object',
                                                                        properties: {
                                                                            id: { kind: 'number' },
                                                                            node_id: { kind: 'string' },
                                                                            url: { kind: 'string' },
                                                                            members_url: { kind: 'string' },
                                                                            name: { kind: 'string' },
                                                                            description: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            permission: { kind: 'string' },
                                                                            privacy: { kind: 'string' },
                                                                            notification_setting: { kind: 'string' },
                                                                            html_url: { kind: 'string' },
                                                                            repositories_url: { kind: 'string' },
                                                                            slug: { kind: 'string' },
                                                                            ldap_dn: { kind: 'string' },
                                                                        },
                                                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                        restriction: 'open',
                                                                    },
                                                                    { kind: 'null' },
                                                                ],
                                                            },
                                                        },
                                                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                apps: {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            slug: { kind: 'string' },
                                                            node_id: { kind: 'string' },
                                                            owner: {
                                                                kind: 'union',
                                                                subTypes: [
                                                                    {
                                                                        kind: 'object',
                                                                        properties: {
                                                                            name: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            email: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            login: { kind: 'string' },
                                                                            id: { kind: 'number' },
                                                                            node_id: { kind: 'string' },
                                                                            avatar_url: { kind: 'string' },
                                                                            gravatar_id: {
                                                                                kind: 'union',
                                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                            },
                                                                            url: { kind: 'string' },
                                                                            html_url: { kind: 'string' },
                                                                            followers_url: { kind: 'string' },
                                                                            following_url: { kind: 'string' },
                                                                            gists_url: { kind: 'string' },
                                                                            starred_url: { kind: 'string' },
                                                                            subscriptions_url: { kind: 'string' },
                                                                            organizations_url: { kind: 'string' },
                                                                            repos_url: { kind: 'string' },
                                                                            events_url: { kind: 'string' },
                                                                            received_events_url: { kind: 'string' },
                                                                            type: { kind: 'string' },
                                                                            site_admin: { kind: 'boolean' },
                                                                            starred_at: { kind: 'string' },
                                                                        },
                                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                                        restriction: 'open',
                                                                    },
                                                                    { kind: 'null' },
                                                                ],
                                                            },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            external_url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            created_at: { kind: 'string' },
                                                            updated_at: { kind: 'string' },
                                                            permissions: {
                                                                kind: 'object',
                                                                properties: {
                                                                    issues: { kind: 'string' },
                                                                    checks: { kind: 'string' },
                                                                    metadata: { kind: 'string' },
                                                                    contents: { kind: 'string' },
                                                                    deployments: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            events: { kind: 'array', subType: { kind: 'string' } },
                                                            installations_count: { kind: 'number' },
                                                            client_id: { kind: 'string' },
                                                            client_secret: { kind: 'string' },
                                                            webhook_secret: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            pem: { kind: 'string' },
                                                        },
                                                        optionalProperties: [
                                                            'slug',
                                                            'installations_count',
                                                            'client_id',
                                                            'client_secret',
                                                            'webhook_secret',
                                                            'pem',
                                                        ],
                                                        restriction: 'open',
                                                    },
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dismiss_stale_reviews: { kind: 'boolean' },
                                        require_code_owner_reviews: { kind: 'boolean' },
                                        required_approving_review_count: { kind: 'number' },
                                        require_last_push_approval: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'url',
                                        'dismissal_restrictions',
                                        'bypass_pull_request_allowances',
                                        'required_approving_review_count',
                                        'require_last_push_approval',
                                    ],
                                    restriction: 'open',
                                },
                                restrictions: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        users_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        apps_url: { kind: 'string' },
                                        users: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        teams: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    slug: { kind: 'string' },
                                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    privacy: { kind: 'string' },
                                                    notification_setting: { kind: 'string' },
                                                    permission: { kind: 'string' },
                                                    members_url: { kind: 'string' },
                                                    repositories_url: { kind: 'string' },
                                                    parent: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        apps: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    slug: { kind: 'string' },
                                                    node_id: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            hooks_url: { kind: 'string' },
                                                            issues_url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            public_members_url: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            description: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    name: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    external_url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            metadata: { kind: 'string' },
                                                            contents: { kind: 'string' },
                                                            issues: { kind: 'string' },
                                                            single_file: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    events: { kind: 'array', subType: { kind: 'string' } },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                required_linear_history: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                allow_force_pushes: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                allow_deletions: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                block_creations: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                required_conversation_resolution: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                name: { kind: 'string' },
                                protection_url: { kind: 'string' },
                                required_signatures: {
                                    kind: 'object',
                                    properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                lock_branch: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                allow_fork_syncing: {
                                    kind: 'object',
                                    properties: { enabled: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        protection_url: { kind: 'string' },
                    },
                    optionalProperties: ['protection', 'protection_url'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'branches'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            protected: input.inPorts.protected,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a branch',
        name: 'Github: Get a branch',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    commit: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            sha: { kind: 'string' },
                            node_id: { kind: 'string' },
                            html_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commit: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    author: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    committer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    message: { kind: 'string' },
                                    comment_count: { kind: 'number' },
                                    tree: {
                                        kind: 'object',
                                        properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    verification: {
                                        kind: 'object',
                                        properties: {
                                            verified: { kind: 'boolean' },
                                            reason: { kind: 'string' },
                                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['verification'],
                                restriction: 'open',
                            },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            parents: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                    optionalProperties: ['html_url'],
                                    restriction: 'open',
                                },
                            },
                            stats: {
                                kind: 'object',
                                properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            files: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        sha: { kind: 'string' },
                                        filename: { kind: 'string' },
                                        status: { kind: 'string' },
                                        additions: { kind: 'number' },
                                        deletions: { kind: 'number' },
                                        changes: { kind: 'number' },
                                        blob_url: { kind: 'string' },
                                        raw_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        patch: { kind: 'string' },
                                        previous_filename: { kind: 'string' },
                                    },
                                    optionalProperties: ['patch', 'previous_filename'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['stats', 'files'],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: { html: { kind: 'string' }, self: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    protected: { kind: 'boolean' },
                    protection: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            enabled: { kind: 'boolean' },
                            required_status_checks: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    enforcement_level: { kind: 'string' },
                                    contexts: { kind: 'array', subType: { kind: 'string' } },
                                    checks: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                context: { kind: 'string' },
                                                app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    contexts_url: { kind: 'string' },
                                    strict: { kind: 'boolean' },
                                },
                                optionalProperties: ['url', 'enforcement_level', 'contexts_url', 'strict'],
                                restriction: 'open',
                            },
                            enforce_admins: {
                                kind: 'object',
                                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            required_pull_request_reviews: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    dismissal_restrictions: {
                                        kind: 'object',
                                        properties: {
                                            users: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                            },
                                            teams: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        permission: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                pull: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        parent: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        members_url: { kind: 'string' },
                                                                        name: { kind: 'string' },
                                                                        description: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        permission: { kind: 'string' },
                                                                        privacy: { kind: 'string' },
                                                                        notification_setting: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        repositories_url: { kind: 'string' },
                                                                        slug: { kind: 'string' },
                                                                        ldap_dn: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                    restriction: 'open',
                                                },
                                            },
                                            apps: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                            },
                                            url: { kind: 'string' },
                                            users_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    bypass_pull_request_allowances: {
                                        kind: 'object',
                                        properties: {
                                            users: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                            },
                                            teams: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        permission: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                pull: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        parent: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        members_url: { kind: 'string' },
                                                                        name: { kind: 'string' },
                                                                        description: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        permission: { kind: 'string' },
                                                                        privacy: { kind: 'string' },
                                                                        notification_setting: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        repositories_url: { kind: 'string' },
                                                                        slug: { kind: 'string' },
                                                                        ldap_dn: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                    restriction: 'open',
                                                },
                                            },
                                            apps: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dismiss_stale_reviews: { kind: 'boolean' },
                                    require_code_owner_reviews: { kind: 'boolean' },
                                    required_approving_review_count: { kind: 'number' },
                                    require_last_push_approval: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'url',
                                    'dismissal_restrictions',
                                    'bypass_pull_request_allowances',
                                    'required_approving_review_count',
                                    'require_last_push_approval',
                                ],
                                restriction: 'open',
                            },
                            restrictions: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    users_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    apps_url: { kind: 'string' },
                                    users: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    teams: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                permission: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                parent: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    apps: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        public_members_url: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'string' },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        issues: { kind: 'string' },
                                                        single_file: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            required_linear_history: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            allow_force_pushes: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            allow_deletions: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            block_creations: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            required_conversation_resolution: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            name: { kind: 'string' },
                            protection_url: { kind: 'string' },
                            required_signatures: {
                                kind: 'object',
                                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            lock_branch: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            allow_fork_syncing: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    protection_url: { kind: 'string' },
                    pattern: { kind: 'string' },
                    required_approving_review_count: { kind: 'number' },
                },
                optionalProperties: ['pattern', 'required_approving_review_count'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'branches', input.inPorts.branch].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get branch protection',
        name: 'Github: Get branch protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    enabled: { kind: 'boolean' },
                    required_status_checks: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            enforcement_level: { kind: 'string' },
                            contexts: { kind: 'array', subType: { kind: 'string' } },
                            checks: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        context: { kind: 'string' },
                                        app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            contexts_url: { kind: 'string' },
                            strict: { kind: 'boolean' },
                        },
                        optionalProperties: ['url', 'enforcement_level', 'contexts_url', 'strict'],
                        restriction: 'open',
                    },
                    enforce_admins: {
                        kind: 'object',
                        properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    required_pull_request_reviews: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            dismissal_restrictions: {
                                kind: 'object',
                                properties: {
                                    users: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                    },
                                    teams: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                permission: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                parent: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                members_url: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                description: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                permission: { kind: 'string' },
                                                                privacy: { kind: 'string' },
                                                                notification_setting: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                repositories_url: { kind: 'string' },
                                                                slug: { kind: 'string' },
                                                                ldap_dn: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                            },
                                            optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                            restriction: 'open',
                                        },
                                    },
                                    apps: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                    },
                                    url: { kind: 'string' },
                                    users_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            bypass_pull_request_allowances: {
                                kind: 'object',
                                properties: {
                                    users: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                    },
                                    teams: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                permission: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                parent: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                members_url: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                description: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                permission: { kind: 'string' },
                                                                privacy: { kind: 'string' },
                                                                notification_setting: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                repositories_url: { kind: 'string' },
                                                                slug: { kind: 'string' },
                                                                ldap_dn: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                            },
                                            optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                            restriction: 'open',
                                        },
                                    },
                                    apps: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            dismiss_stale_reviews: { kind: 'boolean' },
                            require_code_owner_reviews: { kind: 'boolean' },
                            required_approving_review_count: { kind: 'number' },
                            require_last_push_approval: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'url',
                            'dismissal_restrictions',
                            'bypass_pull_request_allowances',
                            'required_approving_review_count',
                            'require_last_push_approval',
                        ],
                        restriction: 'open',
                    },
                    restrictions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            users_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            apps_url: { kind: 'string' },
                            users: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            teams: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            apps: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                public_members_url: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                gravatar_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'string' },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                issues: { kind: 'string' },
                                                single_file: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    required_linear_history: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    allow_force_pushes: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    allow_deletions: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    block_creations: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    required_conversation_resolution: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    protection_url: { kind: 'string' },
                    required_signatures: {
                        kind: 'object',
                        properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    lock_branch: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    allow_fork_syncing: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'branches', input.inPorts.branch, 'protection'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update branch protection',
        name: 'Github: Update branch protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nProtecting a branch requires admin or owner permissions to the repository.\n\n**Note**: Passing new arrays of `users` and `teams` replaces their previous values.\n\n**Note**: The list of users, apps, and teams in total is limited to 100 items.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    required_status_checks: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    strict: { kind: 'boolean' },
                                    contexts: { kind: 'array', subType: { kind: 'string' } },
                                    checks: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { context: { kind: 'string' }, app_id: { kind: 'number' } },
                                            optionalProperties: ['app_id'],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: ['checks'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    enforce_admins: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    required_pull_request_reviews: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    dismissal_restrictions: {
                                        kind: 'object',
                                        properties: {
                                            users: { kind: 'array', subType: { kind: 'string' } },
                                            teams: { kind: 'array', subType: { kind: 'string' } },
                                            apps: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dismiss_stale_reviews: { kind: 'boolean' },
                                    require_code_owner_reviews: { kind: 'boolean' },
                                    required_approving_review_count: { kind: 'number' },
                                    require_last_push_approval: { kind: 'boolean' },
                                    bypass_pull_request_allowances: {
                                        kind: 'object',
                                        properties: {
                                            users: { kind: 'array', subType: { kind: 'string' } },
                                            teams: { kind: 'array', subType: { kind: 'string' } },
                                            apps: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    restrictions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    users: { kind: 'array', subType: { kind: 'string' } },
                                    teams: { kind: 'array', subType: { kind: 'string' } },
                                    apps: { kind: 'array', subType: { kind: 'string' } },
                                },
                                optionalProperties: ['apps'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    required_linear_history: { kind: 'boolean' },
                    allow_force_pushes: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    allow_deletions: { kind: 'boolean' },
                    block_creations: { kind: 'boolean' },
                    required_conversation_resolution: { kind: 'boolean' },
                    lock_branch: { kind: 'boolean' },
                    allow_fork_syncing: { kind: 'boolean' },
                },
                optionalProperties: [
                    'required_linear_history',
                    'allow_force_pushes',
                    'allow_deletions',
                    'block_creations',
                    'required_conversation_resolution',
                    'lock_branch',
                    'allow_fork_syncing',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    required_status_checks: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            strict: { kind: 'boolean' },
                            contexts: { kind: 'array', subType: { kind: 'string' } },
                            checks: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        context: { kind: 'string' },
                                        app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            contexts_url: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    required_pull_request_reviews: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            dismiss_stale_reviews: { kind: 'boolean' },
                            require_code_owner_reviews: { kind: 'boolean' },
                            required_approving_review_count: { kind: 'number' },
                            require_last_push_approval: { kind: 'boolean' },
                            dismissal_restrictions: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    users_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    users: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                    },
                                    teams: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                permission: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                parent: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                members_url: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                description: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                permission: { kind: 'string' },
                                                                privacy: { kind: 'string' },
                                                                notification_setting: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                repositories_url: { kind: 'string' },
                                                                slug: { kind: 'string' },
                                                                ldap_dn: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                            },
                                            optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                            restriction: 'open',
                                        },
                                    },
                                    apps: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: ['apps'],
                                restriction: 'open',
                            },
                            bypass_pull_request_allowances: {
                                kind: 'object',
                                properties: {
                                    users: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                    },
                                    teams: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                permission: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                parent: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                members_url: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                description: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                permission: { kind: 'string' },
                                                                privacy: { kind: 'string' },
                                                                notification_setting: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                repositories_url: { kind: 'string' },
                                                                slug: { kind: 'string' },
                                                                ldap_dn: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                            },
                                            optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                            restriction: 'open',
                                        },
                                    },
                                    apps: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: ['apps'],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [
                            'dismiss_stale_reviews',
                            'require_code_owner_reviews',
                            'required_approving_review_count',
                            'require_last_push_approval',
                            'dismissal_restrictions',
                            'bypass_pull_request_allowances',
                        ],
                        restriction: 'open',
                    },
                    required_signatures: {
                        kind: 'object',
                        properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    enforce_admins: {
                        kind: 'object',
                        properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    required_linear_history: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    allow_force_pushes: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    allow_deletions: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    restrictions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            users_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            apps_url: { kind: 'string' },
                            users: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            teams: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            apps: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                public_members_url: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                gravatar_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'string' },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                issues: { kind: 'string' },
                                                single_file: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    required_conversation_resolution: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    block_creations: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    lock_branch: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    allow_fork_syncing: {
                        kind: 'object',
                        properties: { enabled: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'required_status_checks',
                    'required_pull_request_reviews',
                    'required_signatures',
                    'enforce_admins',
                    'required_linear_history',
                    'allow_force_pushes',
                    'allow_deletions',
                    'restrictions',
                    'required_conversation_resolution',
                    'block_creations',
                    'lock_branch',
                    'allow_fork_syncing',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'branches', input.inPorts.branch, 'protection'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete branch protection',
        name: 'Github: Delete branch protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'branches', input.inPorts.branch, 'protection'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get admin branch protection',
        name: 'Github: Get admin branch protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'enforce_admins',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set admin branch protection',
        name: 'Github: Set admin branch protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nAdding admin enforcement requires admin or owner permissions to the repository and branch protection to be enabled.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'enforce_admins',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete admin branch protection',
        name: 'Github: Delete admin branch protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nRemoving admin enforcement requires admin or owner permissions to the repository and branch protection to be enabled.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'enforce_admins',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get pull request review protection',
        name: 'Github: Get pull request review protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    dismissal_restrictions: {
                        kind: 'object',
                        properties: {
                            users: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            teams: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            apps: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                            },
                            url: { kind: 'string' },
                            users_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    bypass_pull_request_allowances: {
                        kind: 'object',
                        properties: {
                            users: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            teams: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            apps: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    dismiss_stale_reviews: { kind: 'boolean' },
                    require_code_owner_reviews: { kind: 'boolean' },
                    required_approving_review_count: { kind: 'number' },
                    require_last_push_approval: { kind: 'boolean' },
                },
                optionalProperties: [
                    'url',
                    'dismissal_restrictions',
                    'bypass_pull_request_allowances',
                    'required_approving_review_count',
                    'require_last_push_approval',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_pull_request_reviews',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete pull request review protection',
        name: 'Github: Delete pull request review protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_pull_request_reviews',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update pull request review protection',
        name: 'Github: Update pull request review protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nUpdating pull request review enforcement requires admin or owner permissions to the repository and branch protection to be enabled.\n\n**Note**: Passing new arrays of `users` and `teams` replaces their previous values.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    dismissal_restrictions: {
                        kind: 'object',
                        properties: {
                            users: { kind: 'array', subType: { kind: 'string' } },
                            teams: { kind: 'array', subType: { kind: 'string' } },
                            apps: { kind: 'array', subType: { kind: 'string' } },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    dismiss_stale_reviews: { kind: 'boolean' },
                    require_code_owner_reviews: { kind: 'boolean' },
                    required_approving_review_count: { kind: 'number' },
                    require_last_push_approval: { kind: 'boolean' },
                    bypass_pull_request_allowances: {
                        kind: 'object',
                        properties: {
                            users: { kind: 'array', subType: { kind: 'string' } },
                            teams: { kind: 'array', subType: { kind: 'string' } },
                            apps: { kind: 'array', subType: { kind: 'string' } },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    dismissal_restrictions: {
                        kind: 'object',
                        properties: {
                            users: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            teams: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            apps: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                            },
                            url: { kind: 'string' },
                            users_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    bypass_pull_request_allowances: {
                        kind: 'object',
                        properties: {
                            users: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            teams: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            apps: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    dismiss_stale_reviews: { kind: 'boolean' },
                    require_code_owner_reviews: { kind: 'boolean' },
                    required_approving_review_count: { kind: 'number' },
                    require_last_push_approval: { kind: 'boolean' },
                },
                optionalProperties: [
                    'url',
                    'dismissal_restrictions',
                    'bypass_pull_request_allowances',
                    'required_approving_review_count',
                    'require_last_push_approval',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_pull_request_reviews',
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get commit signature protection',
        name: 'Github: Get commit signature protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nWhen authenticated with admin or owner permissions to the repository, you can use this endpoint to check whether a branch requires signed commits. An enabled status of `true` indicates you must sign commits on this branch. For more information, see [Signing commits with GPG](https://docs.github.com/articles/signing-commits-with-gpg) in GitHub Help.\n\n**Note**: You must enable branch protection to require signed commits.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_signatures',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create commit signature protection',
        name: 'Github: Create commit signature protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nWhen authenticated with admin or owner permissions to the repository, you can use this endpoint to require signed commits on a branch. You must enable branch protection to require signed commits.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_signatures',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete commit signature protection',
        name: 'Github: Delete commit signature protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nWhen authenticated with admin or owner permissions to the repository, you can use this endpoint to disable required signed commits on a branch. You must enable branch protection to require signed commits.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_signatures',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get status checks protection',
        name: 'Github: Get status checks protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    strict: { kind: 'boolean' },
                    contexts: { kind: 'array', subType: { kind: 'string' } },
                    checks: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                context: { kind: 'string' },
                                app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    contexts_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove status check protection',
        name: 'Github: Remove status check protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update status check protection',
        name: 'Github: Update status check protection',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nUpdating required status checks requires admin or owner permissions to the repository and branch protection to be enabled.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    strict: { kind: 'boolean' },
                    contexts: { kind: 'array', subType: { kind: 'string' } },
                    checks: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { context: { kind: 'string' }, app_id: { kind: 'number' } },
                            optionalProperties: ['app_id'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    strict: { kind: 'boolean' },
                    contexts: { kind: 'array', subType: { kind: 'string' } },
                    checks: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                context: { kind: 'string' },
                                app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    contexts_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all status check contexts',
        name: 'Github: Get all status check contexts',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'array', subType: { kind: 'string' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                        'contexts',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add status check contexts',
        name: 'Github: Add status check contexts',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { contexts: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'array', subType: { kind: 'string' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                        'contexts',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set status check contexts',
        name: 'Github: Set status check contexts',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { contexts: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'array', subType: { kind: 'string' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                        'contexts',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove status check contexts',
        name: 'Github: Remove status check contexts',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { contexts: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'array', subType: { kind: 'string' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'required_status_checks',
                        'contexts',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get access restrictions',
        name: 'Github: Get access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nLists who has access to this protected branch.\n\n**Note**: Users, apps, and teams `restrictions` are only available for organization-owned repositories.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    users_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    apps_url: { kind: 'string' },
                    users: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    teams: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                name: { kind: 'string' },
                                slug: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                privacy: { kind: 'string' },
                                notification_setting: { kind: 'string' },
                                permission: { kind: 'string' },
                                members_url: { kind: 'string' },
                                repositories_url: { kind: 'string' },
                                parent: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    apps: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                slug: { kind: 'string' },
                                node_id: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        hooks_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        public_members_url: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        description: { kind: 'string' },
                                        gravatar_id: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                name: { kind: 'string' },
                                description: { kind: 'string' },
                                external_url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        metadata: { kind: 'string' },
                                        contents: { kind: 'string' },
                                        issues: { kind: 'string' },
                                        single_file: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                events: { kind: 'array', subType: { kind: 'string' } },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete access restrictions',
        name: 'Github: Delete access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nDisables the ability to restrict who can push to this branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get apps with access to the protected branch',
        name: 'Github: Get apps with access to the protected branch',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nLists the GitHub Apps that have push access to this branch. Only installed GitHub Apps with `write` access to the `contents` permission can be added as authorized actors on a protected branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        slug: { kind: 'string' },
                        node_id: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        external_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                issues: { kind: 'string' },
                                checks: { kind: 'string' },
                                metadata: { kind: 'string' },
                                contents: { kind: 'string' },
                                deployments: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        installations_count: { kind: 'number' },
                        client_id: { kind: 'string' },
                        client_secret: { kind: 'string' },
                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        pem: { kind: 'string' },
                    },
                    optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'apps',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add app access restrictions',
        name: 'Github: Add app access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nGrants the specified apps push access for this branch. Only installed GitHub Apps with `write` access to the `contents` permission can be added as authorized actors on a protected branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { apps: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        slug: { kind: 'string' },
                        node_id: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        external_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                issues: { kind: 'string' },
                                checks: { kind: 'string' },
                                metadata: { kind: 'string' },
                                contents: { kind: 'string' },
                                deployments: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        installations_count: { kind: 'number' },
                        client_id: { kind: 'string' },
                        client_secret: { kind: 'string' },
                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        pem: { kind: 'string' },
                    },
                    optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'apps',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set app access restrictions',
        name: 'Github: Set app access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nReplaces the list of apps that have push access to this branch. This removes all apps that previously had push access and grants push access to the new list of apps. Only installed GitHub Apps with `write` access to the `contents` permission can be added as authorized actors on a protected branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { apps: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        slug: { kind: 'string' },
                        node_id: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        external_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                issues: { kind: 'string' },
                                checks: { kind: 'string' },
                                metadata: { kind: 'string' },
                                contents: { kind: 'string' },
                                deployments: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        installations_count: { kind: 'number' },
                        client_id: { kind: 'string' },
                        client_secret: { kind: 'string' },
                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        pem: { kind: 'string' },
                    },
                    optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'apps',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove app access restrictions',
        name: 'Github: Remove app access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nRemoves the ability of an app to push to this branch. Only installed GitHub Apps with `write` access to the `contents` permission can be added as authorized actors on a protected branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { apps: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        slug: { kind: 'string' },
                        node_id: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        external_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                issues: { kind: 'string' },
                                checks: { kind: 'string' },
                                metadata: { kind: 'string' },
                                contents: { kind: 'string' },
                                deployments: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        installations_count: { kind: 'number' },
                        client_id: { kind: 'string' },
                        client_secret: { kind: 'string' },
                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        pem: { kind: 'string' },
                    },
                    optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'apps',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get teams with access to the protected branch',
        name: 'Github: Get teams with access to the protected branch',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nLists the teams who have push access to this branch. The list includes child teams.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'teams',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add team access restrictions',
        name: 'Github: Add team access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nGrants the specified teams push access for this branch. You can also give push access to child teams.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { teams: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'teams',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set team access restrictions',
        name: 'Github: Set team access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nReplaces the list of teams that have push access to this branch. This removes all teams that previously had push access and grants push access to the new list of teams. Team restrictions include child teams.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { teams: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'teams',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove team access restrictions',
        name: 'Github: Remove team access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nRemoves the ability of a team to push to this branch. You can also remove push access for child teams.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { teams: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'teams',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get users with access to the protected branch',
        name: 'Github: Get users with access to the protected branch',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nLists the people who have push access to this branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'users',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add user access restrictions',
        name: 'Github: Add user access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nGrants the specified people push access for this branch.\n\n| Type    | Description                                                                                                                   |\n| ------- | ----------------------------------------------------------------------------------------------------------------------------- |\n| `array` | Usernames for people who can have push access. **Note**: The list of users, apps, and teams in total is limited to 100 items. |",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { users: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'users',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set user access restrictions',
        name: 'Github: Set user access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nReplaces the list of people that have push access to this branch. This removes all people that previously had push access and grants push access to the new list of people.\n\n| Type    | Description                                                                                                                   |\n| ------- | ----------------------------------------------------------------------------------------------------------------------------- |\n| `array` | Usernames for people who can have push access. **Note**: The list of users, apps, and teams in total is limited to 100 items. |",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { users: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'users',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove user access restrictions',
        name: 'Github: Remove user access restrictions',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nRemoves the ability of a user to push to this branch.\n\n| Type    | Description                                                                                                                                   |\n| ------- | --------------------------------------------------------------------------------------------------------------------------------------------- |\n| `array` | Usernames of the people who should no longer have push access. **Note**: The list of users, apps, and teams in total is limited to 100 items. |",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { users: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'branches',
                        input.inPorts.branch,
                        'protection',
                        'restrictions',
                        'users',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Rename a branch',
        name: 'Github: Rename a branch',
        description:
            'Renames a branch in a repository.\n\n**Note:** Although the API responds immediately, the branch rename process might take some extra time to complete in the background. You won\'t be able to push to the old branch name while the rename process is in progress. For more information, see "[Renaming a branch](https://docs.github.com/github/administering-a-repository/renaming-a-branch)".\n\nThe permissions required to use this endpoint depends on whether you are renaming the default branch.\n\nTo rename a non-default branch:\n\n* Users must have push access.\n* GitHub Apps must have the `contents:write` repository permission.\n\nTo rename the default branch:\n\n* Users must have admin or owner permissions.\n* GitHub Apps must have the `administration:write` repository permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            body: { kind: 'object', properties: { new_name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    commit: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            sha: { kind: 'string' },
                            node_id: { kind: 'string' },
                            html_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commit: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    author: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    committer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    message: { kind: 'string' },
                                    comment_count: { kind: 'number' },
                                    tree: {
                                        kind: 'object',
                                        properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    verification: {
                                        kind: 'object',
                                        properties: {
                                            verified: { kind: 'boolean' },
                                            reason: { kind: 'string' },
                                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['verification'],
                                restriction: 'open',
                            },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            parents: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                    optionalProperties: ['html_url'],
                                    restriction: 'open',
                                },
                            },
                            stats: {
                                kind: 'object',
                                properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            files: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        sha: { kind: 'string' },
                                        filename: { kind: 'string' },
                                        status: { kind: 'string' },
                                        additions: { kind: 'number' },
                                        deletions: { kind: 'number' },
                                        changes: { kind: 'number' },
                                        blob_url: { kind: 'string' },
                                        raw_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        patch: { kind: 'string' },
                                        previous_filename: { kind: 'string' },
                                    },
                                    optionalProperties: ['patch', 'previous_filename'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['stats', 'files'],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: { html: { kind: 'string' }, self: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    protected: { kind: 'boolean' },
                    protection: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            enabled: { kind: 'boolean' },
                            required_status_checks: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    enforcement_level: { kind: 'string' },
                                    contexts: { kind: 'array', subType: { kind: 'string' } },
                                    checks: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                context: { kind: 'string' },
                                                app_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    contexts_url: { kind: 'string' },
                                    strict: { kind: 'boolean' },
                                },
                                optionalProperties: ['url', 'enforcement_level', 'contexts_url', 'strict'],
                                restriction: 'open',
                            },
                            enforce_admins: {
                                kind: 'object',
                                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            required_pull_request_reviews: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    dismissal_restrictions: {
                                        kind: 'object',
                                        properties: {
                                            users: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                            },
                                            teams: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        permission: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                pull: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        parent: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        members_url: { kind: 'string' },
                                                                        name: { kind: 'string' },
                                                                        description: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        permission: { kind: 'string' },
                                                                        privacy: { kind: 'string' },
                                                                        notification_setting: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        repositories_url: { kind: 'string' },
                                                                        slug: { kind: 'string' },
                                                                        ldap_dn: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                    restriction: 'open',
                                                },
                                            },
                                            apps: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                            },
                                            url: { kind: 'string' },
                                            users_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    bypass_pull_request_allowances: {
                                        kind: 'object',
                                        properties: {
                                            users: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                            },
                                            teams: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        permission: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                pull: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        parent: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        members_url: { kind: 'string' },
                                                                        name: { kind: 'string' },
                                                                        description: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        permission: { kind: 'string' },
                                                                        privacy: { kind: 'string' },
                                                                        notification_setting: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        repositories_url: { kind: 'string' },
                                                                        slug: { kind: 'string' },
                                                                        ldap_dn: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                    restriction: 'open',
                                                },
                                            },
                                            apps: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dismiss_stale_reviews: { kind: 'boolean' },
                                    require_code_owner_reviews: { kind: 'boolean' },
                                    required_approving_review_count: { kind: 'number' },
                                    require_last_push_approval: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'url',
                                    'dismissal_restrictions',
                                    'bypass_pull_request_allowances',
                                    'required_approving_review_count',
                                    'require_last_push_approval',
                                ],
                                restriction: 'open',
                            },
                            restrictions: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    users_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    apps_url: { kind: 'string' },
                                    users: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    teams: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                name: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                permission: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                parent: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    apps: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        public_members_url: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'string' },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        issues: { kind: 'string' },
                                                        single_file: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            required_linear_history: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            allow_force_pushes: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            allow_deletions: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            block_creations: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            required_conversation_resolution: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            name: { kind: 'string' },
                            protection_url: { kind: 'string' },
                            required_signatures: {
                                kind: 'object',
                                properties: { url: { kind: 'string' }, enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            lock_branch: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            allow_fork_syncing: {
                                kind: 'object',
                                properties: { enabled: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    protection_url: { kind: 'string' },
                    pattern: { kind: 'string' },
                    required_approving_review_count: { kind: 'number' },
                },
                optionalProperties: ['pattern', 'required_approving_review_count'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'branches', input.inPorts.branch, 'rename'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a check run',
        name: 'Github: Create a check run',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array.\n\nCreates a new check run for a specific commit in a repository. Your GitHub App must have the `checks:write` permission to create check runs.\n\nIn a check suite, GitHub limits the number of check runs with the same name to 1000. Once these check runs exceed 1000, GitHub will start to automatically delete older check runs.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    head_sha: { kind: 'string' },
                    details_url: { kind: 'string' },
                    external_id: { kind: 'string' },
                    status: { kind: 'string' },
                    started_at: { kind: 'string' },
                    conclusion: { kind: 'string' },
                    completed_at: { kind: 'string' },
                    output: {
                        kind: 'object',
                        properties: {
                            title: { kind: 'string' },
                            summary: { kind: 'string' },
                            text: { kind: 'string' },
                            annotations: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        path: { kind: 'string' },
                                        start_line: { kind: 'number' },
                                        end_line: { kind: 'number' },
                                        start_column: { kind: 'number' },
                                        end_column: { kind: 'number' },
                                        annotation_level: { kind: 'string' },
                                        message: { kind: 'string' },
                                        title: { kind: 'string' },
                                        raw_details: { kind: 'string' },
                                    },
                                    optionalProperties: ['start_column', 'end_column', 'title', 'raw_details'],
                                    restriction: 'open',
                                },
                            },
                            images: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { alt: { kind: 'string' }, image_url: { kind: 'string' }, caption: { kind: 'string' } },
                                    optionalProperties: ['caption'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['text', 'annotations', 'images'],
                        restriction: 'open',
                    },
                    actions: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { label: { kind: 'string' }, description: { kind: 'string' }, identifier: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [
                    'details_url',
                    'external_id',
                    'status',
                    'started_at',
                    'conclusion',
                    'completed_at',
                    'output',
                    'actions',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    head_sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    external_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    details_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    status: { kind: 'string' },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    output: {
                        kind: 'object',
                        properties: {
                            title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            annotations_count: { kind: 'number' },
                            annotations_url: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    check_suite: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { id: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    pull_requests: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                number: { kind: 'number' },
                                url: { kind: 'string' },
                                head: {
                                    kind: 'object',
                                    properties: {
                                        ref: { kind: 'string' },
                                        sha: { kind: 'string' },
                                        repo: {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' }, url: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                base: {
                                    kind: 'object',
                                    properties: {
                                        ref: { kind: 'string' },
                                        sha: { kind: 'string' },
                                        repo: {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' }, url: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    deployment: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            task: { kind: 'string' },
                            original_environment: { kind: 'string' },
                            environment: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            repository_url: { kind: 'string' },
                            transient_environment: { kind: 'boolean' },
                            production_environment: { kind: 'boolean' },
                            performed_via_github_app: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            slug: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                            owner: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            external_url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    issues: { kind: 'string' },
                                                    checks: { kind: 'string' },
                                                    metadata: { kind: 'string' },
                                                    contents: { kind: 'string' },
                                                    deployments: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            events: { kind: 'array', subType: { kind: 'string' } },
                                            installations_count: { kind: 'number' },
                                            client_id: { kind: 'string' },
                                            client_secret: { kind: 'string' },
                                            webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            pem: { kind: 'string' },
                                        },
                                        optionalProperties: [
                                            'slug',
                                            'installations_count',
                                            'client_id',
                                            'client_secret',
                                            'webhook_secret',
                                            'pem',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'original_environment',
                            'transient_environment',
                            'production_environment',
                            'performed_via_github_app',
                        ],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['deployment'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-runs'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a check run',
        name: 'Github: Get a check run',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array.\n\nGets a single check run using its `id`. GitHub Apps must have the `checks:read` permission on a private repository or pull access to a public repository to get check runs. OAuth apps and authenticated users must have the `repo` scope to get check runs in a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    head_sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    external_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    details_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    status: { kind: 'string' },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    output: {
                        kind: 'object',
                        properties: {
                            title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            annotations_count: { kind: 'number' },
                            annotations_url: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    check_suite: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { id: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    pull_requests: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                number: { kind: 'number' },
                                url: { kind: 'string' },
                                head: {
                                    kind: 'object',
                                    properties: {
                                        ref: { kind: 'string' },
                                        sha: { kind: 'string' },
                                        repo: {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' }, url: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                base: {
                                    kind: 'object',
                                    properties: {
                                        ref: { kind: 'string' },
                                        sha: { kind: 'string' },
                                        repo: {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' }, url: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    deployment: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            task: { kind: 'string' },
                            original_environment: { kind: 'string' },
                            environment: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            repository_url: { kind: 'string' },
                            transient_environment: { kind: 'boolean' },
                            production_environment: { kind: 'boolean' },
                            performed_via_github_app: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            slug: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                            owner: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            external_url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    issues: { kind: 'string' },
                                                    checks: { kind: 'string' },
                                                    metadata: { kind: 'string' },
                                                    contents: { kind: 'string' },
                                                    deployments: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            events: { kind: 'array', subType: { kind: 'string' } },
                                            installations_count: { kind: 'number' },
                                            client_id: { kind: 'string' },
                                            client_secret: { kind: 'string' },
                                            webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            pem: { kind: 'string' },
                                        },
                                        optionalProperties: [
                                            'slug',
                                            'installations_count',
                                            'client_id',
                                            'client_secret',
                                            'webhook_secret',
                                            'pem',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'original_environment',
                            'transient_environment',
                            'production_environment',
                            'performed_via_github_app',
                        ],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['deployment'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-runs', input.inPorts.check_run_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a check run',
        name: 'Github: Update a check run',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array.\n\nUpdates a check run for a specific commit in a repository. Your GitHub App must have the `checks:write` permission to edit check runs.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_run_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    details_url: { kind: 'string' },
                    external_id: { kind: 'string' },
                    started_at: { kind: 'string' },
                    status: { kind: 'string' },
                    conclusion: { kind: 'string' },
                    completed_at: { kind: 'string' },
                    output: {
                        kind: 'object',
                        properties: {
                            title: { kind: 'string' },
                            summary: { kind: 'string' },
                            text: { kind: 'string' },
                            annotations: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        path: { kind: 'string' },
                                        start_line: { kind: 'number' },
                                        end_line: { kind: 'number' },
                                        start_column: { kind: 'number' },
                                        end_column: { kind: 'number' },
                                        annotation_level: { kind: 'string' },
                                        message: { kind: 'string' },
                                        title: { kind: 'string' },
                                        raw_details: { kind: 'string' },
                                    },
                                    optionalProperties: ['start_column', 'end_column', 'title', 'raw_details'],
                                    restriction: 'open',
                                },
                            },
                            images: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { alt: { kind: 'string' }, image_url: { kind: 'string' }, caption: { kind: 'string' } },
                                    optionalProperties: ['caption'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['title', 'text', 'annotations', 'images'],
                        restriction: 'open',
                    },
                    actions: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { label: { kind: 'string' }, description: { kind: 'string' }, identifier: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    head_sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    external_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    details_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    status: { kind: 'string' },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    output: {
                        kind: 'object',
                        properties: {
                            title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            annotations_count: { kind: 'number' },
                            annotations_url: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    name: { kind: 'string' },
                    check_suite: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { id: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    pull_requests: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                number: { kind: 'number' },
                                url: { kind: 'string' },
                                head: {
                                    kind: 'object',
                                    properties: {
                                        ref: { kind: 'string' },
                                        sha: { kind: 'string' },
                                        repo: {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' }, url: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                base: {
                                    kind: 'object',
                                    properties: {
                                        ref: { kind: 'string' },
                                        sha: { kind: 'string' },
                                        repo: {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' }, url: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    deployment: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            task: { kind: 'string' },
                            original_environment: { kind: 'string' },
                            environment: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            repository_url: { kind: 'string' },
                            transient_environment: { kind: 'boolean' },
                            production_environment: { kind: 'boolean' },
                            performed_via_github_app: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            slug: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                            owner: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            external_url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    issues: { kind: 'string' },
                                                    checks: { kind: 'string' },
                                                    metadata: { kind: 'string' },
                                                    contents: { kind: 'string' },
                                                    deployments: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            events: { kind: 'array', subType: { kind: 'string' } },
                                            installations_count: { kind: 'number' },
                                            client_id: { kind: 'string' },
                                            client_secret: { kind: 'string' },
                                            webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            pem: { kind: 'string' },
                                        },
                                        optionalProperties: [
                                            'slug',
                                            'installations_count',
                                            'client_id',
                                            'client_secret',
                                            'webhook_secret',
                                            'pem',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'original_environment',
                            'transient_environment',
                            'production_environment',
                            'performed_via_github_app',
                        ],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['deployment'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-runs', input.inPorts.check_run_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List check run annotations',
        name: 'Github: List check run annotations',
        description:
            'Lists annotations for a check run using the annotation `id`. GitHub Apps must have the `checks:read` permission on a private repository or pull access to a public repository to get annotations for a check run. OAuth apps and authenticated users must have the `repo` scope to get annotations for a check run in a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_run_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        path: { kind: 'string' },
                        start_line: { kind: 'number' },
                        end_line: { kind: 'number' },
                        start_column: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        end_column: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        annotation_level: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        raw_details: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        blob_href: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'check-runs',
                        input.inPorts.check_run_id,
                        'annotations',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Rerequest a check run',
        name: 'Github: Rerequest a check run',
        description:
            'Triggers GitHub to rerequest an existing check run, without pushing new code to a repository. This endpoint will trigger the [`check_run` webhook](https://docs.github.com/webhooks/event-payloads/#check_run) event with the action `rerequested`. When a check run is `rerequested`, its `status` is reset to `queued` and the `conclusion` is cleared.\n\nTo rerequest a check run, your GitHub App must have the `checks:read` permission on a private repository or pull access to a public repository.\n\nFor more information about how to re-run GitHub Actions jobs, see "[Re-run a job from a workflow run](https://docs.github.com/rest/actions/workflow-runs#re-run-a-job-from-a-workflow-run)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_run_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-runs', input.inPorts.check_run_id, 'rerequest'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a check suite',
        name: 'Github: Create a check suite',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array and a `null` value for `head_branch`.\n\nBy default, check suites are automatically created when you create a [check run](https://docs.github.com/rest/reference/checks#check-runs). You only need to use this endpoint for manually creating check suites when you\'ve disabled automatic creation using "[Update repository preferences for check suites](https://docs.github.com/rest/checks/suites#update-repository-preferences-for-check-suites)". Your GitHub App must have the `checks:write` permission to create check suites.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { head_sha: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_sha: { kind: 'string' },
                    status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    before: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    after: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pull_requests: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        number: { kind: 'number' },
                                        url: { kind: 'string' },
                                        head: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        base: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_commit: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'string' },
                            tree_id: { kind: 'string' },
                            message: { kind: 'string' },
                            timestamp: { kind: 'string' },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    latest_check_runs_count: { kind: 'number' },
                    check_runs_url: { kind: 'string' },
                    rerequestable: { kind: 'boolean' },
                    runs_rerequestable: { kind: 'boolean' },
                },
                optionalProperties: ['rerequestable', 'runs_rerequestable'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-suites'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update repository preferences for check suites',
        name: 'Github: Update repository preferences for check suites',
        description:
            'Changes the default automatic flow when creating check suites. By default, a check suite is automatically created each time code is pushed to a repository. When you disable the automatic creation of check suites, you can manually [Create a check suite](https://docs.github.com/rest/checks/suites#create-a-check-suite). You must have admin permissions in the repository to set preferences for check suites.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    auto_trigger_checks: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { app_id: { kind: 'number' }, setting: { kind: 'boolean' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    preferences: {
                        kind: 'object',
                        properties: {
                            auto_trigger_checks: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { app_id: { kind: 'number' }, setting: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-suites', 'preferences'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a check suite',
        name: 'Github: Get a check suite',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array and a `null` value for `head_branch`.\n\nGets a single check suite using its `id`. GitHub Apps must have the `checks:read` permission on a private repository or pull access to a public repository to get check suites. OAuth apps and authenticated users must have the `repo` scope to get check suites in a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_suite_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_sha: { kind: 'string' },
                    status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    before: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    after: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pull_requests: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        number: { kind: 'number' },
                                        url: { kind: 'string' },
                                        head: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        base: {
                                            kind: 'object',
                                            properties: {
                                                ref: { kind: 'string' },
                                                sha: { kind: 'string' },
                                                repo: {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    head_commit: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'string' },
                            tree_id: { kind: 'string' },
                            message: { kind: 'string' },
                            timestamp: { kind: 'string' },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    latest_check_runs_count: { kind: 'number' },
                    check_runs_url: { kind: 'string' },
                    rerequestable: { kind: 'boolean' },
                    runs_rerequestable: { kind: 'boolean' },
                },
                optionalProperties: ['rerequestable', 'runs_rerequestable'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'check-suites', input.inPorts.check_suite_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List check runs in a check suite',
        name: 'Github: List check runs in a check suite',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array.\n\nLists check runs for a check suite using its `id`. GitHub Apps must have the `checks:read` permission on a private repository or pull access to a public repository to get check runs. OAuth apps and authenticated users must have the `repo` scope to get check runs in a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_suite_id: { kind: 'number' },
            check_name: { kind: 'string' },
            status: { kind: 'string' },
            filter: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['check_name', 'status', 'filter', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    check_runs: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                head_sha: { kind: 'string' },
                                node_id: { kind: 'string' },
                                external_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                details_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                status: { kind: 'string' },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                output: {
                                    kind: 'object',
                                    properties: {
                                        title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        annotations_count: { kind: 'number' },
                                        annotations_url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                name: { kind: 'string' },
                                check_suite: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                pull_requests: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            number: { kind: 'number' },
                                            url: { kind: 'string' },
                                            head: {
                                                kind: 'object',
                                                properties: {
                                                    ref: { kind: 'string' },
                                                    sha: { kind: 'string' },
                                                    repo: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            base: {
                                                kind: 'object',
                                                properties: {
                                                    ref: { kind: 'string' },
                                                    sha: { kind: 'string' },
                                                    repo: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                deployment: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        task: { kind: 'string' },
                                        original_environment: { kind: 'string' },
                                        environment: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        transient_environment: { kind: 'boolean' },
                                        production_environment: { kind: 'boolean' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'original_environment',
                                        'transient_environment',
                                        'production_environment',
                                        'performed_via_github_app',
                                    ],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['deployment'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'check-suites',
                        input.inPorts.check_suite_id,
                        'check-runs',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            check_name: input.inPorts.check_name,
                            status: input.inPorts.status,
                            filter: input.inPorts.filter,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Rerequest a check suite',
        name: 'Github: Rerequest a check suite',
        description:
            'Triggers GitHub to rerequest an existing check suite, without pushing new code to a repository. This endpoint will trigger the [`check_suite` webhook](https://docs.github.com/webhooks/event-payloads/#check_suite) event with the action `rerequested`. When a check suite is `rerequested`, its `status` is reset to `queued` and the `conclusion` is cleared.\n\nTo rerequest a check suite, your GitHub App must have the `checks:read` permission on a private repository or pull access to a public repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            check_suite_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'check-suites',
                        input.inPorts.check_suite_id,
                        'rerequest',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List code scanning alerts for a repository',
        name: 'Github: List code scanning alerts for a repository',
        description:
            'Lists code scanning alerts.\n\nTo use this endpoint, you must use an access token with the `security_events` scope or, for alerts from public repositories only, an access token with the `public_repo` scope.\n\nGitHub Apps must have the `security_events` read\npermission to use this endpoint.\n\nThe response includes a `most_recent_instance` object.\nThis provides details of the most recent instance of this alert\nfor the default branch (or for the specified Git reference if you used `ref` in the request).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            tool_name: { kind: 'string' },
            tool_guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            ref: { kind: 'string' },
            direction: { kind: 'string' },
            sort: { kind: 'string' },
            state: { kind: 'string' },
            severity: { kind: 'string' },
        },
        optionalInPorts: ['tool_name', 'tool_guid', 'page', 'per_page', 'ref', 'direction', 'sort', 'state', 'severity'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        instances_url: { kind: 'string' },
                        state: { kind: 'string' },
                        fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        rule: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                name: { kind: 'string' },
                                tags: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                                severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                description: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        tool: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                version: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        most_recent_instance: {
                            kind: 'object',
                            properties: {
                                ref: { kind: 'string' },
                                analysis_key: { kind: 'string' },
                                environment: { kind: 'string' },
                                category: { kind: 'string' },
                                state: { kind: 'string' },
                                commit_sha: { kind: 'string' },
                                message: {
                                    kind: 'object',
                                    properties: { text: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                location: {
                                    kind: 'object',
                                    properties: {
                                        path: { kind: 'string' },
                                        start_line: { kind: 'number' },
                                        end_line: { kind: 'number' },
                                        start_column: { kind: 'number' },
                                        end_column: { kind: 'number' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html_url: { kind: 'string' },
                                classifications: {
                                    kind: 'array',
                                    subType: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['updated_at', 'fixed_at', 'dismissed_comment'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            tool_name: input.inPorts.tool_name,
                            tool_guid: input.inPorts.tool_guid,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                            ref: input.inPorts.ref,
                            direction: input.inPorts.direction,
                            sort: input.inPorts.sort,
                            state: input.inPorts.state,
                            severity: input.inPorts.severity,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a code scanning alert',
        name: 'Github: Get a code scanning alert',
        description:
            'Gets a single code scanning alert. You must use an access token with the `security_events` scope to use this endpoint with private repos, the `public_repo` scope also grants permission to read security events on public repos only. GitHub Apps must have the `security_events` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    number: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    instances_url: { kind: 'string' },
                    state: { kind: 'string' },
                    fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    rule: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            security_severity_level: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            description: { kind: 'string' },
                            full_description: { kind: 'string' },
                            tags: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                            help: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            help_uri: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    tool: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'string' },
                            version: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    most_recent_instance: {
                        kind: 'object',
                        properties: {
                            ref: { kind: 'string' },
                            analysis_key: { kind: 'string' },
                            environment: { kind: 'string' },
                            category: { kind: 'string' },
                            state: { kind: 'string' },
                            commit_sha: { kind: 'string' },
                            message: {
                                kind: 'object',
                                properties: { text: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            location: {
                                kind: 'object',
                                properties: {
                                    path: { kind: 'string' },
                                    start_line: { kind: 'number' },
                                    end_line: { kind: 'number' },
                                    start_column: { kind: 'number' },
                                    end_column: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html_url: { kind: 'string' },
                            classifications: {
                                kind: 'array',
                                subType: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['updated_at', 'fixed_at', 'dismissed_comment'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'alerts', input.inPorts.alert_number].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a code scanning alert',
        name: 'Github: Update a code scanning alert',
        description:
            'Updates the status of a single code scanning alert. You must use an access token with the `security_events` scope to use this endpoint with private repositories. You can also use tokens with the `public_repo` scope for public repositories only. GitHub Apps must have the `security_events` write permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['dismissed_reason', 'dismissed_comment'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    number: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    instances_url: { kind: 'string' },
                    state: { kind: 'string' },
                    fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    rule: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            security_severity_level: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            description: { kind: 'string' },
                            full_description: { kind: 'string' },
                            tags: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                            help: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            help_uri: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    tool: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'string' },
                            version: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    most_recent_instance: {
                        kind: 'object',
                        properties: {
                            ref: { kind: 'string' },
                            analysis_key: { kind: 'string' },
                            environment: { kind: 'string' },
                            category: { kind: 'string' },
                            state: { kind: 'string' },
                            commit_sha: { kind: 'string' },
                            message: {
                                kind: 'object',
                                properties: { text: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            location: {
                                kind: 'object',
                                properties: {
                                    path: { kind: 'string' },
                                    start_line: { kind: 'number' },
                                    end_line: { kind: 'number' },
                                    start_column: { kind: 'number' },
                                    end_column: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html_url: { kind: 'string' },
                            classifications: {
                                kind: 'array',
                                subType: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['updated_at', 'fixed_at', 'dismissed_comment'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'alerts', input.inPorts.alert_number].join(
                        '/',
                    ),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List instances of a code scanning alert',
        name: 'Github: List instances of a code scanning alert',
        description:
            'Lists all instances of the specified code scanning alert.\nYou must use an access token with the `security_events` scope to use this endpoint with private repos,\nthe `public_repo` scope also grants permission to read security events on public repos only.\nGitHub Apps must have the `security_events` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            ref: { kind: 'string' },
        },
        optionalInPorts: ['page', 'per_page', 'ref'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        ref: { kind: 'string' },
                        analysis_key: { kind: 'string' },
                        environment: { kind: 'string' },
                        category: { kind: 'string' },
                        state: { kind: 'string' },
                        commit_sha: { kind: 'string' },
                        message: { kind: 'object', properties: { text: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        location: {
                            kind: 'object',
                            properties: {
                                path: { kind: 'string' },
                                start_line: { kind: 'number' },
                                end_line: { kind: 'number' },
                                start_column: { kind: 'number' },
                                end_column: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        html_url: { kind: 'string' },
                        classifications: { kind: 'array', subType: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'code-scanning',
                        'alerts',
                        input.inPorts.alert_number,
                        'instances',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page, ref: input.inPorts.ref }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List code scanning analyses for a repository',
        name: 'Github: List code scanning analyses for a repository',
        description:
            "Lists the details of all code scanning analyses for a repository,\nstarting with the most recent.\nThe response is paginated and you can use the `page` and `per_page` parameters\nto list the analyses you're interested in.\nBy default 30 analyses are listed per page.\n\nThe `rules_count` field in the response give the number of rules\nthat were run in the analysis.\nFor very old analyses this data is not available,\nand `0` is returned in this field.\n\nYou must use an access token with the `security_events` scope to use this endpoint with private repos,\nthe `public_repo` scope also grants permission to read security events on public repos only.\nGitHub Apps must have the `security_events` read permission to use this endpoint.\n\n**Deprecation notice**:\nThe `tool_name` field is deprecated and will, in future, not be included in the response for this endpoint. The example response reflects this change. The tool name can now be found inside the `tool` field.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            tool_name: { kind: 'string' },
            tool_guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            ref: { kind: 'string' },
            sarif_id: { kind: 'string' },
            direction: { kind: 'string' },
            sort: { kind: 'string' },
        },
        optionalInPorts: ['tool_name', 'tool_guid', 'page', 'per_page', 'ref', 'sarif_id', 'direction', 'sort'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        ref: { kind: 'string' },
                        commit_sha: { kind: 'string' },
                        analysis_key: { kind: 'string' },
                        environment: { kind: 'string' },
                        category: { kind: 'string' },
                        error: { kind: 'string' },
                        created_at: { kind: 'string' },
                        results_count: { kind: 'number' },
                        rules_count: { kind: 'number' },
                        id: { kind: 'number' },
                        url: { kind: 'string' },
                        sarif_id: { kind: 'string' },
                        tool: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                version: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        deletable: { kind: 'boolean' },
                        warning: { kind: 'string' },
                    },
                    optionalProperties: ['category'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'analyses'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            tool_name: input.inPorts.tool_name,
                            tool_guid: input.inPorts.tool_guid,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                            ref: input.inPorts.ref,
                            sarif_id: input.inPorts.sarif_id,
                            direction: input.inPorts.direction,
                            sort: input.inPorts.sort,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a code scanning analysis for a repository',
        name: 'Github: Get a code scanning analysis for a repository',
        description:
            'Gets a specified code scanning analysis for a repository.\nYou must use an access token with the `security_events` scope to use this endpoint with private repos,\nthe `public_repo` scope also grants permission to read security events on public repos only.\nGitHub Apps must have the `security_events` read permission to use this endpoint.\n\nThe default JSON response contains fields that describe the analysis.\nThis includes the Git reference and commit SHA to which the analysis relates,\nthe datetime of the analysis, the name of the code scanning tool,\nand the number of alerts.\n\nThe `rules_count` field in the default response give the number of rules\nthat were run in the analysis.\nFor very old analyses this data is not available,\nand `0` is returned in this field.\n\nIf you use the Accept header `application/sarif+json`,\nthe response contains the analysis data that was uploaded.\nThis is formatted as\n[SARIF version 2.1.0](https://docs.oasis-open.org/sarif/sarif/v2.1.0/cs01/sarif-v2.1.0-cs01.html).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            analysis_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ref: { kind: 'string' },
                    commit_sha: { kind: 'string' },
                    analysis_key: { kind: 'string' },
                    environment: { kind: 'string' },
                    category: { kind: 'string' },
                    error: { kind: 'string' },
                    created_at: { kind: 'string' },
                    results_count: { kind: 'number' },
                    rules_count: { kind: 'number' },
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    sarif_id: { kind: 'string' },
                    tool: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'string' },
                            version: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            guid: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    deletable: { kind: 'boolean' },
                    warning: { kind: 'string' },
                },
                optionalProperties: ['category'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'code-scanning',
                        'analyses',
                        input.inPorts.analysis_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a code scanning analysis from a repository',
        name: 'Github: Delete a code scanning analysis from a repository',
        description:
            "Deletes a specified code scanning analysis from a repository. For\nprivate repositories, you must use an access token with the `repo` scope. For public repositories,\nyou must use an access token with `public_repo` scope.\nGitHub Apps must have the `security_events` write permission to use this endpoint.\n\nYou can delete one analysis at a time.\nTo delete a series of analyses, start with the most recent analysis and work backwards.\nConceptually, the process is similar to the undo function in a text editor.\n\nWhen you list the analyses for a repository,\none or more will be identified as deletable in the response:\n\n```\n\"deletable\": true\n```\n\nAn analysis is deletable when it's the most recent in a set of analyses.\nTypically, a repository will have multiple sets of analyses\nfor each enabled code scanning tool,\nwhere a set is determined by a unique combination of analysis values:\n\n* `ref`\n* `tool`\n* `category`\n\nIf you attempt to delete an analysis that is not the most recent in a set,\nyou'll get a 400 response with the message:\n\n```\nAnalysis specified is not deletable.\n```\n\nThe response from a successful `DELETE` operation provides you with\ntwo alternative URLs for deleting the next analysis in the set:\n`next_analysis_url` and `confirm_delete_url`.\nUse the `next_analysis_url` URL if you want to avoid accidentally deleting the final analysis\nin a set. This is a useful option if you want to preserve at least one analysis\nfor the specified tool in your repository.\nUse the `confirm_delete_url` URL if you are content to remove all analyses for a tool.\nWhen you delete the last analysis in a set, the value of `next_analysis_url` and `confirm_delete_url`\nin the 200 response is `null`.\n\nAs an example of the deletion process,\nlet's imagine that you added a workflow that configured a particular code scanning tool\nto analyze the code in a repository. This tool has added 15 analyses:\n10 on the default branch, and another 5 on a topic branch.\nYou therefore have two separate sets of analyses for this tool.\nYou've now decided that you want to remove all of the analyses for the tool.\nTo do this you must make 15 separate deletion requests.\nTo start, you must find an analysis that's identified as deletable.\nEach set of analyses always has one that's identified as deletable.\nHaving found the deletable analysis for one of the two sets,\ndelete this analysis and then continue deleting the next analysis in the set until they're all deleted.\nThen repeat the process for the second set.\nThe procedure therefore consists of a nested loop:\n\n**Outer loop**:\n* List the analyses for the repository, filtered by tool.\n* Parse this list to find a deletable analysis. If found:\n\n  **Inner loop**:\n  * Delete the identified analysis.\n  * Parse the response for the value of `confirm_delete_url` and, if found, use this in the next iteration.\n\nThe above process assumes that you want to remove all trace of the tool's analyses from the GitHub user interface, for the specified repository, and it therefore uses the `confirm_delete_url` value. Alternatively, you could use the `next_analysis_url` value, which would leave the last analysis in each set undeleted to avoid removing a tool's analysis entirely.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            analysis_id: { kind: 'number' },
            confirm_delete: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
        },
        optionalInPorts: ['confirm_delete'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    next_analysis_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    confirm_delete_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'code-scanning',
                        'analyses',
                        input.inPorts.analysis_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(
                        Object.entries({ confirm_delete: input.inPorts.confirm_delete }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List CodeQL databases for a repository',
        name: 'Github: List CodeQL databases for a repository',
        description:
            'Lists the CodeQL databases that are available in a repository.\n\nFor private repositories, you must use an access token with the `security_events` scope.\nFor public repositories, you can use tokens with the `security_events` or `public_repo` scope.\nGitHub Apps must have the `contents` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        language: { kind: 'string' },
                        uploader: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        content_type: { kind: 'string' },
                        size: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'codeql', 'databases'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a CodeQL database for a repository',
        name: 'Github: Get a CodeQL database for a repository',
        description:
            'Gets a CodeQL database for a language in a repository.\n\nBy default this endpoint returns JSON metadata about the CodeQL database. To\ndownload the CodeQL database binary content, set the `Accept` header of the request\nto [`application/zip`](https://docs.github.com/rest/overview/media-types), and make sure\nyour HTTP client is configured to follow redirects or use the `Location` header\nto make a second request to get the redirect URL.\n\nFor private repositories, you must use an access token with the `security_events` scope.\nFor public repositories, you can use tokens with the `security_events` or `public_repo` scope.\nGitHub Apps must have the `contents` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            language: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    language: { kind: 'string' },
                    uploader: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    content_type: { kind: 'string' },
                    size: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'code-scanning',
                        'codeql',
                        'databases',
                        input.inPorts.language,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a code scanning default setup configuration',
        name: 'Github: Get a code scanning default setup configuration',
        description:
            'Gets a code scanning default setup configuration.\nYou must use an access token with the `repo` scope to use this endpoint with private repos or the `public_repo`\nscope for public repos. GitHub Apps must have the `repo` write permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    languages: { kind: 'array', subType: { kind: 'string' } },
                    query_suite: { kind: 'string' },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'default-setup'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a code scanning default setup configuration',
        name: 'Github: Update a code scanning default setup configuration',
        description:
            'Updates a code scanning default setup configuration.\nYou must use an access token with the `repo` scope to use this endpoint with private repos or the `public_repo`\nscope for public repos. GitHub Apps must have the `repo` write permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    query_suite: { kind: 'string' },
                    languages: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['query_suite', 'languages'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'default-setup'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Upload an analysis as SARIF data',
        name: 'Github: Upload an analysis as SARIF data',
        description:
            'Uploads SARIF data containing the results of a code scanning analysis to make the results available in a repository. You must use an access token with the `security_events` scope to use this endpoint for private repositories. You can also use tokens with the `public_repo` scope for public repositories only. GitHub Apps must have the `security_events` write permission to use this endpoint.\n\nThere are two places where you can upload code scanning results.\n - If you upload to a pull request, for example `--ref refs/pull/42/merge` or `--ref refs/pull/42/head`, then the results appear as alerts in a pull request check. For more information, see "[Triaging code scanning alerts in pull requests](/code-security/secure-coding/triaging-code-scanning-alerts-in-pull-requests)."\n - If you upload to a branch, for example `--ref refs/heads/my-branch`, then the results appear in the **Security** tab for your repository. For more information, see "[Managing code scanning alerts for your repository](/code-security/secure-coding/managing-code-scanning-alerts-for-your-repository#viewing-the-alerts-for-a-repository)."\n\nYou must compress the SARIF-formatted analysis data that you want to upload, using `gzip`, and then encode it as a Base64 format string. For example:\n\n```\ngzip -c analysis-data.sarif | base64 -w0\n```\n<br>\nSARIF upload supports a maximum number of entries per the following data objects, and an analysis will be rejected if any of these objects is above its maximum value. For some objects, there are additional values over which the entries will be ignored while keeping the most important entries whenever applicable.\nTo get the most out of your analysis when it includes data above the supported limits, try to optimize the analysis configuration. For example, for the CodeQL tool, identify and remove the most noisy queries.\n\n\n| **SARIF data**                   | **Maximum values** | **Additional limits**                                                            |\n|----------------------------------|:------------------:|----------------------------------------------------------------------------------|\n| Runs per file                    |         20         |                                                                                  |\n| Results per run                  |       25,000       | Only the top 5,000 results will be included, prioritized by severity.            |\n| Rules per run                    |       25,000       |                                                                                  |\n| Tool extensions per run          |        100         |                                                                                  |\n| Thread Flow Locations per result |       10,000       | Only the top 1,000 Thread Flow Locations will be included, using prioritization. |\n| Location per result\t             |       1,000        | Only 100 locations will be included.                                             |\n| Tags per rule\t                   |         20         | Only 10 tags will be included.                                                   |\n\n\nThe `202 Accepted` response includes an `id` value.\nYou can use this ID to check the status of the upload by using it in the `/sarifs/{sarif_id}` endpoint.\nFor more information, see "[Get information about a SARIF upload](/rest/code-scanning/code-scanning#get-information-about-a-sarif-upload)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    commit_sha: { kind: 'string' },
                    ref: { kind: 'string' },
                    sarif: { kind: 'string' },
                    checkout_uri: { kind: 'string' },
                    started_at: { kind: 'string' },
                    tool_name: { kind: 'string' },
                    validate: { kind: 'boolean' },
                },
                optionalProperties: ['checkout_uri', 'started_at', 'tool_name', 'validate'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { id: { kind: 'string' }, url: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'sarifs'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get information about a SARIF upload',
        name: 'Github: Get information about a SARIF upload',
        description:
            'Gets information about a SARIF upload, including the status and the URL of the analysis that was uploaded so that you can retrieve details of the analysis. For more information, see "[Get a code scanning analysis for a repository](/rest/code-scanning/code-scanning#get-a-code-scanning-analysis-for-a-repository)." You must use an access token with the `security_events` scope to use this endpoint with private repos, the `public_repo` scope also grants permission to read security events on public repos only. GitHub Apps must have the `security_events` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sarif_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    processing_status: { kind: 'string' },
                    analyses_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    errors: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'code-scanning', 'sarifs', input.inPorts.sarif_id].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List CODEOWNERS errors',
        name: 'Github: List CODEOWNERS errors',
        description:
            'List any syntax errors that are detected in the CODEOWNERS\nfile.\n\nFor more information about the correct CODEOWNERS syntax,\nsee "[About code owners](https://docs.github.com/repositories/managing-your-repositorys-settings-and-features/customizing-your-repository/about-code-owners)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: ['ref'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    errors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                line: { kind: 'number' },
                                column: { kind: 'number' },
                                source: { kind: 'string' },
                                kind: { kind: 'string' },
                                suggestion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                message: { kind: 'string' },
                                path: { kind: 'string' },
                            },
                            optionalProperties: ['source', 'suggestion'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'codeowners', 'errors'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ ref: input.inPorts.ref }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List codespaces in a repository for the authenticated user',
        name: 'Github: List codespaces in a repository for the authenticated user',
        description:
            'Lists the codespaces associated to a specified repository and the authenticated user.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    codespaces: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                name: { kind: 'string' },
                                display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                billable_owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                machine: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'string' },
                                                display_name: { kind: 'string' },
                                                operating_system: { kind: 'string' },
                                                storage_in_bytes: { kind: 'number' },
                                                memory_in_bytes: { kind: 'number' },
                                                cpus: { kind: 'number' },
                                                prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                last_used_at: { kind: 'string' },
                                state: { kind: 'string' },
                                url: { kind: 'string' },
                                git_status: {
                                    kind: 'object',
                                    properties: {
                                        ahead: { kind: 'number' },
                                        behind: { kind: 'number' },
                                        has_unpushed_changes: { kind: 'boolean' },
                                        has_uncommitted_changes: { kind: 'boolean' },
                                        ref: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                location: { kind: 'string' },
                                idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                web_url: { kind: 'string' },
                                machines_url: { kind: 'string' },
                                start_url: { kind: 'string' },
                                stop_url: { kind: 'string' },
                                publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                recent_folders: { kind: 'array', subType: { kind: 'string' } },
                                runtime_constraints: {
                                    kind: 'object',
                                    properties: {
                                        allowed_port_privacy_settings: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [
                                'display_name',
                                'devcontainer_path',
                                'publish_url',
                                'runtime_constraints',
                                'pending_operation',
                                'pending_operation_disabled_reason',
                                'idle_timeout_notice',
                                'retention_period_minutes',
                                'retention_expires_at',
                                'last_known_stop_notice',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'codespaces'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a codespace in a repository',
        name: 'Github: Create a codespace in a repository',
        description:
            'Creates a codespace owned by the authenticated user in the specified repository.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            ref: { kind: 'string' },
                            location: { kind: 'string' },
                            geo: { kind: 'string' },
                            client_ip: { kind: 'string' },
                            machine: { kind: 'string' },
                            devcontainer_path: { kind: 'string' },
                            multi_repo_permissions_opt_out: { kind: 'boolean' },
                            working_directory: { kind: 'string' },
                            idle_timeout_minutes: { kind: 'number' },
                            display_name: { kind: 'string' },
                            retention_period_minutes: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'codespaces'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List devcontainer configurations in a repository for the authenticated user',
        name: 'Github: List devcontainer configurations in a repository for the authenticated user',
        description:
            'Lists the devcontainer.json files associated with a specified repository and the authenticated user. These files\nspecify launchpoint configurations for codespaces created within the repository.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_metadata` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    devcontainers: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { path: { kind: 'string' }, name: { kind: 'string' }, display_name: { kind: 'string' } },
                            optionalProperties: ['name', 'display_name'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'codespaces', 'devcontainers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List available machine types for a repository',
        name: 'Github: List available machine types for a repository',
        description:
            'List the machine types available for a given repository based on its configuration.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_metadata` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            location: { kind: 'string' },
            client_ip: { kind: 'string' },
        },
        optionalInPorts: ['location', 'client_ip'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    machines: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                display_name: { kind: 'string' },
                                operating_system: { kind: 'string' },
                                storage_in_bytes: { kind: 'number' },
                                memory_in_bytes: { kind: 'number' },
                                cpus: { kind: 'number' },
                                prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'codespaces', 'machines'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ location: input.inPorts.location, client_ip: input.inPorts.client_ip }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get default attributes for a codespace',
        name: 'Github: Get default attributes for a codespace',
        description:
            'Gets the default attributes for codespaces created by the user with the repository.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            client_ip: { kind: 'string' },
        },
        optionalInPorts: ['ref', 'client_ip'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    defaults: {
                        kind: 'object',
                        properties: {
                            location: { kind: 'string' },
                            devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'codespaces', 'new'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ ref: input.inPorts.ref, client_ip: input.inPorts.client_ip }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository collaborators',
        name: 'Github: List repository collaborators',
        description:
            'For organization-owned repositories, the list of collaborators includes outside collaborators, organization members that are direct collaborators, organization members with access through team memberships, organization members with access through default organization permissions, and organization owners.\nOrganization members with write, maintain, or admin privileges on the organization-owned repository can use this endpoint.\n\nTeam members will include the members of child teams.\n\nYou must authenticate using an access token with the `read:org` and `repo` scopes with push access to use this\nendpoint. GitHub Apps must have the `members` organization permission and `metadata` repository permission to use this\nendpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            affiliation: { kind: 'string' },
            permission: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['affiliation', 'permission', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: ['triage', 'maintain'],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                    },
                    optionalProperties: ['email', 'name', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'collaborators'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            affiliation: input.inPorts.affiliation,
                            permission: input.inPorts.permission,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a user is a repository collaborator',
        name: 'Github: Check if a user is a repository collaborator',
        description:
            'For organization-owned repositories, the list of collaborators includes outside collaborators, organization members that are direct collaborators, organization members with access through team memberships, organization members with access through default organization permissions, and organization owners.\n\nTeam members will include the members of child teams.\n\nYou must authenticate using an access token with the `read:org` and `repo` scopes with push access to use this\nendpoint. GitHub Apps must have the `members` organization permission and `metadata` repository permission to use this\nendpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'collaborators', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add a repository collaborator',
        name: 'Github: Add a repository collaborator',
        description:
            'This endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.\n\nAdding an outside collaborator may be restricted by enterprise administrators. For more information, see "[Enforcing repository management policies in your enterprise](https://docs.github.com/admin/policies/enforcing-policies-for-your-enterprise/enforcing-repository-management-policies-in-your-enterprise#enforcing-a-policy-for-inviting-outside-collaborators-to-repositories)."\n\nFor more information on permission levels, see "[Repository permission levels for an organization](https://docs.github.com/github/setting-up-and-managing-organizations-and-teams/repository-permission-levels-for-an-organization#permission-levels-for-repositories-owned-by-an-organization)". There are restrictions on which permissions can be granted to organization members when an organization base role is in place. In this case, the permission being given must be equal to or higher than the org base permission. Otherwise, the request will fail with:\n\n```\nCannot assign {member} permission of {role name}\n```\n\nNote that, if you choose not to pass any parameters, you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."\n\nThe invitee will receive a notification that they have been invited to the repository, which they must accept or decline. They may do this via the notifications page, the email they receive, or by using the [repository invitations API endpoints](https://docs.github.com/rest/reference/repos#invitations).\n\n**Updating an existing collaborator\'s permission level**\n\nThe endpoint can also be used to change the permissions of an existing collaborator without first removing and re-adding the collaborator. To change the permissions, use the same endpoint and pass a different `permission` parameter. The response will be a `204`, with no other indication that the permission level changed.\n\n**Rate limits**\n\nYou are limited to sending 50 invitations to a repository per 24 hour period. Note there is no limit if you are inviting organization members to an organization repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            username: { kind: 'string' },
            body: { kind: 'object', properties: { permission: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    invitee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    inviter: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    permissions: { kind: 'string' },
                    created_at: { kind: 'string' },
                    expired: { kind: 'boolean' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                },
                optionalProperties: ['expired'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'collaborators', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a repository collaborator',
        name: 'Github: Remove a repository collaborator',
        description:
            'Removes a collaborator from a repository.\n\nTo use this endpoint, the authenticated user must either be an administrator of the repository or target themselves for removal.\n\nThis endpoint also:\n- Cancels any outstanding invitations\n- Unasigns the user from any issues\n- Removes access to organization projects if the user is not an organization member and is not a collaborator on any other organization repositories.\n- Unstars the repository\n- Updates access permissions to packages\n\nRemoving a user as a collaborator has the following effects on forks:\n - If the user had access to a fork through their membership to this repository, the user will also be removed from the fork.\n - If the user had their own fork of the repository, the fork will be deleted.\n - If the user still has read access to the repository, open pull requests by this user from a fork will be denied.\n\n**Note**: A user can still have access to the repository through organization permissions like base repository permissions.\n\nAlthough the API responds immediately, the additional permission updates might take some extra time to complete in the background.\n\nFor more information on fork permissions, see "[About permissions and visibility of forks](https://docs.github.com/pull-requests/collaborating-with-pull-requests/working-with-forks/about-permissions-and-visibility-of-forks)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'collaborators', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get repository permissions for a user',
        name: 'Github: Get repository permissions for a user',
        description:
            'Checks the repository permission of a collaborator. The possible repository\npermissions are `admin`, `write`, `read`, and `none`.\n\n*Note*: The `permission` attribute provides the legacy base roles of `admin`, `write`, `read`, and `none`, where the\n`maintain` role is mapped to `write` and the `triage` role is mapped to `read`. To determine the role assigned to the\ncollaborator, see the `role_name` attribute, which will provide the full role name, including custom roles. The\n`permissions` hash can also be used to determine which base level of access the collaborator has to the repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    permission: { kind: 'string' },
                    role_name: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            admin: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    role_name: { kind: 'string' },
                                },
                                optionalProperties: ['email', 'name', 'permissions'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'collaborators', input.inPorts.username, 'permission'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List commit comments for a repository',
        name: 'Github: List commit comments for a repository',
        description:
            'Commit Comments use [these custom media types](https://docs.github.com/rest/reference/repos#custom-media-types). You can read more about the use of media types in the API [here](https://docs.github.com/rest/overview/media-types/).\n\nComments are ordered by ascending ID.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        html_url: { kind: 'string' },
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        body: { kind: 'string' },
                        path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        position: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        commit_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        author_association: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a commit comment',
        name: 'Github: Get a commit comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    html_url: { kind: 'string' },
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    body: { kind: 'string' },
                    path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    position: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'comments', input.inPorts.comment_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a commit comment',
        name: 'Github: Delete a commit comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'comments', input.inPorts.comment_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a commit comment',
        name: 'Github: Update a commit comment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    html_url: { kind: 'string' },
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    body: { kind: 'string' },
                    path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    position: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'comments', input.inPorts.comment_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a commit comment',
        name: 'Github: List reactions for a commit comment',
        description: 'List the reactions to a [commit comment](https://docs.github.com/rest/reference/repos#comments).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'comments', input.inPorts.comment_id, 'reactions'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a commit comment',
        name: 'Github: Create reaction for a commit comment',
        description:
            'Create a reaction to a [commit comment](https://docs.github.com/rest/reference/repos#comments). A response with an HTTP `200` status means that you already added the reaction type to this commit comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'comments', input.inPorts.comment_id, 'reactions'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a commit comment reaction',
        name: 'Github: Delete a commit comment reaction',
        description:
            '**Note:** You can also specify a repository by `repository_id` using the route `DELETE /repositories/:repository_id/comments/:comment_id/reactions/:reaction_id`.\n\nDelete a reaction to a [commit comment](https://docs.github.com/rest/reference/repos#comments).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List commits',
        name: 'Github: List commits',
        description:
            '**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit\'s signature. The following fields are included in the `verification` object:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The "signing" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user\'s account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sha: { kind: 'string' },
            path: { kind: 'string' },
            author: { kind: 'string' },
            committer: { kind: 'string' },
            since: { kind: 'string' },
            until: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sha', 'path', 'author', 'committer', 'since', 'until', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        sha: { kind: 'string' },
                        node_id: { kind: 'string' },
                        html_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commit: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                author: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                committer: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                message: { kind: 'string' },
                                comment_count: { kind: 'number' },
                                tree: {
                                    kind: 'object',
                                    properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                verification: {
                                    kind: 'object',
                                    properties: {
                                        verified: { kind: 'boolean' },
                                        reason: { kind: 'string' },
                                        payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['verification'],
                            restriction: 'open',
                        },
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        committer: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        parents: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                        },
                        stats: {
                            kind: 'object',
                            properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        files: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    sha: { kind: 'string' },
                                    filename: { kind: 'string' },
                                    status: { kind: 'string' },
                                    additions: { kind: 'number' },
                                    deletions: { kind: 'number' },
                                    changes: { kind: 'number' },
                                    blob_url: { kind: 'string' },
                                    raw_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    patch: { kind: 'string' },
                                    previous_filename: { kind: 'string' },
                                },
                                optionalProperties: ['patch', 'previous_filename'],
                                restriction: 'open',
                            },
                        },
                    },
                    optionalProperties: ['stats', 'files'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sha: input.inPorts.sha,
                            path: input.inPorts.path,
                            author: input.inPorts.author,
                            committer: input.inPorts.committer,
                            since: input.inPorts.since,
                            until: input.inPorts.until,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List branches for HEAD commit',
        name: 'Github: List branches for HEAD commit',
        description:
            "Protected branches are available in public repositories with GitHub Free and GitHub Free for organizations, and in public and private repositories with GitHub Pro, GitHub Team, GitHub Enterprise Cloud, and GitHub Enterprise Server. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nReturns all branches where the given commit SHA is the HEAD, or latest commit for the branch.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            commit_sha: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'string' },
                        commit: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        protected: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'commits',
                        input.inPorts.commit_sha,
                        'branches-where-head',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List commit comments',
        name: 'Github: List commit comments',
        description: 'Use the `:commit_sha` to specify the commit that will have its comments listed.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            commit_sha: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        html_url: { kind: 'string' },
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        body: { kind: 'string' },
                        path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        position: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        commit_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        author_association: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.commit_sha, 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a commit comment',
        name: 'Github: Create a commit comment',
        description:
            'Create a comment for a commit using its `:commit_sha`.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            commit_sha: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { body: { kind: 'string' }, path: { kind: 'string' }, position: { kind: 'number' }, line: { kind: 'number' } },
                optionalProperties: ['path', 'position', 'line'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    html_url: { kind: 'string' },
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    body: { kind: 'string' },
                    path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    position: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.commit_sha, 'comments'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List pull requests associated with a commit',
        name: 'Github: List pull requests associated with a commit',
        description:
            'Lists the merged pull request that introduced the commit to the repository. If the commit is not present in the default branch, will only return open pull requests associated with the commit.\n\nTo list the open or merged pull requests associated with a branch, you can set the `commit_sha` parameter to the branch name.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            commit_sha: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        html_url: { kind: 'string' },
                        diff_url: { kind: 'string' },
                        patch_url: { kind: 'string' },
                        issue_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        review_comments_url: { kind: 'string' },
                        review_comment_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        locked: { kind: 'boolean' },
                        title: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        labels: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'string' },
                                    color: { kind: 'string' },
                                    default: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        milestone: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        title: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        creator: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        open_issues: { kind: 'number' },
                                        closed_issues: { kind: 'number' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assignees: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        requested_reviewers: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        requested_teams: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            privacy: { kind: 'string' },
                                            notification_setting: { kind: 'string' },
                                            permission: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    pull: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            members_url: { kind: 'string' },
                                            repositories_url: { kind: 'string' },
                                            parent: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            permission: { kind: 'string' },
                                                            privacy: { kind: 'string' },
                                                            notification_setting: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            repositories_url: { kind: 'string' },
                                                            slug: { kind: 'string' },
                                                            ldap_dn: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        head: {
                            kind: 'object',
                            properties: {
                                label: { kind: 'string' },
                                ref: { kind: 'string' },
                                repo: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        node_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['html_url'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        organization: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                            },
                                            optionalProperties: ['triage', 'maintain'],
                                            restriction: 'open',
                                        },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        template_repository: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        full_name: { kind: 'string' },
                                                        owner: {
                                                            kind: 'object',
                                                            properties: {
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        private: { kind: 'boolean' },
                                                        html_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        fork: { kind: 'boolean' },
                                                        url: { kind: 'string' },
                                                        archive_url: { kind: 'string' },
                                                        assignees_url: { kind: 'string' },
                                                        blobs_url: { kind: 'string' },
                                                        branches_url: { kind: 'string' },
                                                        collaborators_url: { kind: 'string' },
                                                        comments_url: { kind: 'string' },
                                                        commits_url: { kind: 'string' },
                                                        compare_url: { kind: 'string' },
                                                        contents_url: { kind: 'string' },
                                                        contributors_url: { kind: 'string' },
                                                        deployments_url: { kind: 'string' },
                                                        downloads_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        forks_url: { kind: 'string' },
                                                        git_commits_url: { kind: 'string' },
                                                        git_refs_url: { kind: 'string' },
                                                        git_tags_url: { kind: 'string' },
                                                        git_url: { kind: 'string' },
                                                        issue_comment_url: { kind: 'string' },
                                                        issue_events_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        keys_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        languages_url: { kind: 'string' },
                                                        merges_url: { kind: 'string' },
                                                        milestones_url: { kind: 'string' },
                                                        notifications_url: { kind: 'string' },
                                                        pulls_url: { kind: 'string' },
                                                        releases_url: { kind: 'string' },
                                                        ssh_url: { kind: 'string' },
                                                        stargazers_url: { kind: 'string' },
                                                        statuses_url: { kind: 'string' },
                                                        subscribers_url: { kind: 'string' },
                                                        subscription_url: { kind: 'string' },
                                                        tags_url: { kind: 'string' },
                                                        teams_url: { kind: 'string' },
                                                        trees_url: { kind: 'string' },
                                                        clone_url: { kind: 'string' },
                                                        mirror_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        svn_url: { kind: 'string' },
                                                        homepage: { kind: 'string' },
                                                        language: { kind: 'string' },
                                                        forks_count: { kind: 'number' },
                                                        stargazers_count: { kind: 'number' },
                                                        watchers_count: { kind: 'number' },
                                                        size: { kind: 'number' },
                                                        default_branch: { kind: 'string' },
                                                        open_issues_count: { kind: 'number' },
                                                        is_template: { kind: 'boolean' },
                                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                                        has_issues: { kind: 'boolean' },
                                                        has_projects: { kind: 'boolean' },
                                                        has_wiki: { kind: 'boolean' },
                                                        has_pages: { kind: 'boolean' },
                                                        has_downloads: { kind: 'boolean' },
                                                        archived: { kind: 'boolean' },
                                                        disabled: { kind: 'boolean' },
                                                        visibility: { kind: 'string' },
                                                        pushed_at: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                admin: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                pull: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        allow_rebase_merge: { kind: 'boolean' },
                                                        temp_clone_token: { kind: 'string' },
                                                        allow_squash_merge: { kind: 'boolean' },
                                                        allow_auto_merge: { kind: 'boolean' },
                                                        delete_branch_on_merge: { kind: 'boolean' },
                                                        allow_update_branch: { kind: 'boolean' },
                                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                                        squash_merge_commit_title: { kind: 'string' },
                                                        squash_merge_commit_message: { kind: 'string' },
                                                        merge_commit_title: { kind: 'string' },
                                                        merge_commit_message: { kind: 'string' },
                                                        allow_merge_commit: { kind: 'boolean' },
                                                        subscribers_count: { kind: 'number' },
                                                        network_count: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        master_branch: { kind: 'string' },
                                        starred_at: { kind: 'string' },
                                        anonymous_access_enabled: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'organization',
                                        'permissions',
                                        'is_template',
                                        'topics',
                                        'has_discussions',
                                        'visibility',
                                        'allow_rebase_merge',
                                        'template_repository',
                                        'temp_clone_token',
                                        'allow_squash_merge',
                                        'allow_auto_merge',
                                        'delete_branch_on_merge',
                                        'allow_update_branch',
                                        'use_squash_pr_title_as_default',
                                        'squash_merge_commit_title',
                                        'squash_merge_commit_message',
                                        'merge_commit_title',
                                        'merge_commit_message',
                                        'allow_merge_commit',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'subscribers_count',
                                        'network_count',
                                        'master_branch',
                                        'starred_at',
                                        'anonymous_access_enabled',
                                    ],
                                    restriction: 'open',
                                },
                                sha: { kind: 'string' },
                                user: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        base: {
                            kind: 'object',
                            properties: {
                                label: { kind: 'string' },
                                ref: { kind: 'string' },
                                repo: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        node_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['html_url'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        organization: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                            },
                                            optionalProperties: ['triage', 'maintain'],
                                            restriction: 'open',
                                        },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        template_repository: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        full_name: { kind: 'string' },
                                                        owner: {
                                                            kind: 'object',
                                                            properties: {
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        private: { kind: 'boolean' },
                                                        html_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        fork: { kind: 'boolean' },
                                                        url: { kind: 'string' },
                                                        archive_url: { kind: 'string' },
                                                        assignees_url: { kind: 'string' },
                                                        blobs_url: { kind: 'string' },
                                                        branches_url: { kind: 'string' },
                                                        collaborators_url: { kind: 'string' },
                                                        comments_url: { kind: 'string' },
                                                        commits_url: { kind: 'string' },
                                                        compare_url: { kind: 'string' },
                                                        contents_url: { kind: 'string' },
                                                        contributors_url: { kind: 'string' },
                                                        deployments_url: { kind: 'string' },
                                                        downloads_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        forks_url: { kind: 'string' },
                                                        git_commits_url: { kind: 'string' },
                                                        git_refs_url: { kind: 'string' },
                                                        git_tags_url: { kind: 'string' },
                                                        git_url: { kind: 'string' },
                                                        issue_comment_url: { kind: 'string' },
                                                        issue_events_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        keys_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        languages_url: { kind: 'string' },
                                                        merges_url: { kind: 'string' },
                                                        milestones_url: { kind: 'string' },
                                                        notifications_url: { kind: 'string' },
                                                        pulls_url: { kind: 'string' },
                                                        releases_url: { kind: 'string' },
                                                        ssh_url: { kind: 'string' },
                                                        stargazers_url: { kind: 'string' },
                                                        statuses_url: { kind: 'string' },
                                                        subscribers_url: { kind: 'string' },
                                                        subscription_url: { kind: 'string' },
                                                        tags_url: { kind: 'string' },
                                                        teams_url: { kind: 'string' },
                                                        trees_url: { kind: 'string' },
                                                        clone_url: { kind: 'string' },
                                                        mirror_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        svn_url: { kind: 'string' },
                                                        homepage: { kind: 'string' },
                                                        language: { kind: 'string' },
                                                        forks_count: { kind: 'number' },
                                                        stargazers_count: { kind: 'number' },
                                                        watchers_count: { kind: 'number' },
                                                        size: { kind: 'number' },
                                                        default_branch: { kind: 'string' },
                                                        open_issues_count: { kind: 'number' },
                                                        is_template: { kind: 'boolean' },
                                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                                        has_issues: { kind: 'boolean' },
                                                        has_projects: { kind: 'boolean' },
                                                        has_wiki: { kind: 'boolean' },
                                                        has_pages: { kind: 'boolean' },
                                                        has_downloads: { kind: 'boolean' },
                                                        archived: { kind: 'boolean' },
                                                        disabled: { kind: 'boolean' },
                                                        visibility: { kind: 'string' },
                                                        pushed_at: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                admin: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                pull: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        allow_rebase_merge: { kind: 'boolean' },
                                                        temp_clone_token: { kind: 'string' },
                                                        allow_squash_merge: { kind: 'boolean' },
                                                        allow_auto_merge: { kind: 'boolean' },
                                                        delete_branch_on_merge: { kind: 'boolean' },
                                                        allow_update_branch: { kind: 'boolean' },
                                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                                        squash_merge_commit_title: { kind: 'string' },
                                                        squash_merge_commit_message: { kind: 'string' },
                                                        merge_commit_title: { kind: 'string' },
                                                        merge_commit_message: { kind: 'string' },
                                                        allow_merge_commit: { kind: 'boolean' },
                                                        subscribers_count: { kind: 'number' },
                                                        network_count: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        master_branch: { kind: 'string' },
                                        starred_at: { kind: 'string' },
                                        anonymous_access_enabled: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'organization',
                                        'permissions',
                                        'is_template',
                                        'topics',
                                        'has_discussions',
                                        'visibility',
                                        'allow_rebase_merge',
                                        'template_repository',
                                        'temp_clone_token',
                                        'allow_squash_merge',
                                        'allow_auto_merge',
                                        'delete_branch_on_merge',
                                        'allow_update_branch',
                                        'use_squash_pr_title_as_default',
                                        'squash_merge_commit_title',
                                        'squash_merge_commit_message',
                                        'merge_commit_title',
                                        'merge_commit_message',
                                        'allow_merge_commit',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'subscribers_count',
                                        'network_count',
                                        'master_branch',
                                        'starred_at',
                                        'anonymous_access_enabled',
                                    ],
                                    restriction: 'open',
                                },
                                sha: { kind: 'string' },
                                user: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                comments: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                commits: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                statuses: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                issue: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                review_comments: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                review_comment: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        author_association: { kind: 'string' },
                        auto_merge: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        enabled_by: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        merge_method: { kind: 'string' },
                                        commit_title: { kind: 'string' },
                                        commit_message: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        draft: { kind: 'boolean' },
                    },
                    optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.commit_sha, 'pulls'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a commit',
        name: 'Github: Get a commit',
        description:
            "Returns the contents of a single commit reference. You must have `read` access for the repository to use this endpoint.\n\n**Note:** If there are more than 300 files in the commit diff, the response will include pagination link headers for the remaining files, up to a limit of 3000 files. Each page contains the static commit information, and the only changes are to the file listing.\n\nYou can pass the appropriate [media type](https://docs.github.com/rest/overview/media-types/#commits-commit-comparison-and-pull-requests) to  fetch `diff` and `patch` formats. Diffs with binary data will have no `patch` property.\n\nTo return only the SHA-1 hash of the commit reference, you can provide the `sha` custom [media type](https://docs.github.com/rest/overview/media-types/#commits-commit-comparison-and-pull-requests) in the `Accept` header. You can use this endpoint to check if a remote reference's SHA-1 hash is the same as your local reference's SHA-1 hash by providing the local SHA-1 reference as the ETag.\n\n**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit's signature. The following fields are included in the `verification` object:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The \"signing\" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user's account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commit: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            message: { kind: 'string' },
                            comment_count: { kind: 'number' },
                            tree: {
                                kind: 'object',
                                properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            verification: {
                                kind: 'object',
                                properties: {
                                    verified: { kind: 'boolean' },
                                    reason: { kind: 'string' },
                                    payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: ['verification'],
                        restriction: 'open',
                    },
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    committer: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    parents: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                            optionalProperties: ['html_url'],
                            restriction: 'open',
                        },
                    },
                    stats: {
                        kind: 'object',
                        properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    files: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                sha: { kind: 'string' },
                                filename: { kind: 'string' },
                                status: { kind: 'string' },
                                additions: { kind: 'number' },
                                deletions: { kind: 'number' },
                                changes: { kind: 'number' },
                                blob_url: { kind: 'string' },
                                raw_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                patch: { kind: 'string' },
                                previous_filename: { kind: 'string' },
                            },
                            optionalProperties: ['patch', 'previous_filename'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: ['stats', 'files'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.ref].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List check runs for a Git reference',
        name: 'Github: List check runs for a Git reference',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array.\n\nLists check runs for a commit ref. The `ref` can be a SHA, branch name, or a tag name. GitHub Apps must have the `checks:read` permission on a private repository or pull access to a public repository to get check runs. OAuth apps and authenticated users must have the `repo` scope to get check runs in a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            check_name: { kind: 'string' },
            status: { kind: 'string' },
            filter: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            app_id: { kind: 'number' },
        },
        optionalInPorts: ['check_name', 'status', 'filter', 'per_page', 'page', 'app_id'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    check_runs: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                head_sha: { kind: 'string' },
                                node_id: { kind: 'string' },
                                external_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                details_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                status: { kind: 'string' },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                started_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                output: {
                                    kind: 'object',
                                    properties: {
                                        title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        annotations_count: { kind: 'number' },
                                        annotations_url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                name: { kind: 'string' },
                                check_suite: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { id: { kind: 'number' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                pull_requests: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            number: { kind: 'number' },
                                            url: { kind: 'string' },
                                            head: {
                                                kind: 'object',
                                                properties: {
                                                    ref: { kind: 'string' },
                                                    sha: { kind: 'string' },
                                                    repo: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            base: {
                                                kind: 'object',
                                                properties: {
                                                    ref: { kind: 'string' },
                                                    sha: { kind: 'string' },
                                                    repo: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                deployment: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        task: { kind: 'string' },
                                        original_environment: { kind: 'string' },
                                        environment: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        transient_environment: { kind: 'boolean' },
                                        production_environment: { kind: 'boolean' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'original_environment',
                                        'transient_environment',
                                        'production_environment',
                                        'performed_via_github_app',
                                    ],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['deployment'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.ref, 'check-runs'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            check_name: input.inPorts.check_name,
                            status: input.inPorts.status,
                            filter: input.inPorts.filter,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            app_id: input.inPorts.app_id,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List check suites for a Git reference',
        name: 'Github: List check suites for a Git reference',
        description:
            '**Note:** The Checks API only looks for pushes in the repository where the check suite or check run were created. Pushes to a branch in a forked repository are not detected and return an empty `pull_requests` array and a `null` value for `head_branch`.\n\nLists check suites for a commit `ref`. The `ref` can be a SHA, branch name, or a tag name. GitHub Apps must have the `checks:read` permission on a private repository or pull access to a public repository to list check suites. OAuth apps and authenticated users must have the `repo` scope to get check suites in a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            app_id: { kind: 'number' },
            check_name: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['app_id', 'check_name', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    check_suites: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                head_branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                head_sha: { kind: 'string' },
                                status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                conclusion: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                before: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                after: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                pull_requests: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    number: { kind: 'number' },
                                                    url: { kind: 'string' },
                                                    head: {
                                                        kind: 'object',
                                                        properties: {
                                                            ref: { kind: 'string' },
                                                            sha: { kind: 'string' },
                                                            repo: {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    base: {
                                                        kind: 'object',
                                                        properties: {
                                                            ref: { kind: 'string' },
                                                            sha: { kind: 'string' },
                                                            repo: {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    url: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                head_commit: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'string' },
                                        tree_id: { kind: 'string' },
                                        message: { kind: 'string' },
                                        timestamp: { kind: 'string' },
                                        author: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        committer: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                latest_check_runs_count: { kind: 'number' },
                                check_runs_url: { kind: 'string' },
                                rerequestable: { kind: 'boolean' },
                                runs_rerequestable: { kind: 'boolean' },
                            },
                            optionalProperties: ['rerequestable', 'runs_rerequestable'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.ref, 'check-suites'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            app_id: input.inPorts.app_id,
                            check_name: input.inPorts.check_name,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the combined status for a specific reference',
        name: 'Github: Get the combined status for a specific reference',
        description:
            'Users with pull access in a repository can access a combined view of commit statuses for a given ref. The ref can be a SHA, a branch name, or a tag name.\n\n\nAdditionally, a combined `state` is returned. The `state` is one of:\n\n*   **failure** if any of the contexts report as `error` or `failure`\n*   **pending** if there are no statuses or a context is `pending`\n*   **success** if the latest status for all contexts is `success`',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    statuses: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                state: { kind: 'string' },
                                context: { kind: 'string' },
                                target_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                required: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                avatar_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                            },
                            optionalProperties: ['required'],
                            restriction: 'open',
                        },
                    },
                    sha: { kind: 'string' },
                    total_count: { kind: 'number' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    commit_url: { kind: 'string' },
                    url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.ref, 'status'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List commit statuses for a reference',
        name: 'Github: List commit statuses for a reference',
        description:
            'Users with pull access in a repository can view commit statuses for a given ref. The ref can be a SHA, a branch name, or a tag name. Statuses are returned in reverse chronological order. The first status in the list will be the latest one.\n\nThis resource is also available via a legacy route: `GET /repos/:owner/:repo/statuses/:ref`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        avatar_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        state: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        target_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        context: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'commits', input.inPorts.ref, 'statuses'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get community profile metrics',
        name: 'Github: Get community profile metrics',
        description:
            'Returns all community profile metrics for a repository. The repository cannot be a fork.\n\nThe returned metrics include an overall health score, the repository description, the presence of documentation, the\ndetected code of conduct, the detected license, and the presence of ISSUE\\_TEMPLATE, PULL\\_REQUEST\\_TEMPLATE,\nREADME, and CONTRIBUTING files.\n\nThe `health_percentage` score is defined as a percentage of how many of\nthese four documents are present: README, CONTRIBUTING, LICENSE, and\nCODE_OF_CONDUCT. For example, if all four documents are present, then\nthe `health_percentage` is `100`. If only one is present, then the\n`health_percentage` is `25`.\n\n`content_reports_enabled` is only returned for organization-owned repositories.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    health_percentage: { kind: 'number' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    documentation: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    files: {
                        kind: 'object',
                        properties: {
                            code_of_conduct: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            url: { kind: 'string' },
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            code_of_conduct_file: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' }, html_url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            contributing: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' }, html_url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            readme: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' }, html_url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            issue_template: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' }, html_url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            pull_request_template: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' }, html_url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    content_reports_enabled: { kind: 'boolean' },
                },
                optionalProperties: ['content_reports_enabled'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'community', 'profile'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Compare two commits',
        name: 'Github: Compare two commits',
        description:
            'Compares two commits against one another. You can compare branches in the same repository, or you can compare branches that exist in different repositories within the same repository network, including fork branches. For more information about how to view a repository\'s network, see "[Understanding connections between repositories](https://docs.github.com/repositories/viewing-activity-and-data-for-your-repository/understanding-connections-between-repositories)."\n\nThis endpoint is equivalent to running the `git log BASE..HEAD` command, but it returns commits in a different order. The `git log BASE..HEAD` command returns commits in reverse chronological order, whereas the API returns commits in chronological order. You can pass the appropriate [media type](https://docs.github.com/rest/overview/media-types/#commits-commit-comparison-and-pull-requests) to fetch diff and patch formats.\n\nThe API response includes details about the files that were changed between the two commits. This includes the status of the change (if a file was added, removed, modified, or renamed), and details of the change itself. For example, files with a `renamed` status have a `previous_filename` field showing the previous filename of the file, and files with a `modified` status have a `patch` field showing the changes made to the file.\n\nWhen calling this endpoint without any paging parameter (`per_page` or `page`), the returned list is limited to 250 commits, and the last commit in the list is the most recent of the entire comparison.\n\n**Working with large comparisons**\n\nTo process a response with a large number of commits, use a query parameter (`per_page` or `page`) to paginate the results. When using pagination:\n\n- The list of changed files is only shown on the first page of results, but it includes all changed files for the entire comparison.\n- The results are returned in chronological order, but the last commit in the returned list may not be the most recent one in the entire set if there are more pages of results.\n\nFor more information on working with pagination, see "[Using pagination in the REST API](https://docs.github.com/rest/guides/using-pagination-in-the-rest-api)."\n\n**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit\'s signature. The `verification` object includes the following fields:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The "signing" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user\'s account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            basehead: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    permalink_url: { kind: 'string' },
                    diff_url: { kind: 'string' },
                    patch_url: { kind: 'string' },
                    base_commit: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            sha: { kind: 'string' },
                            node_id: { kind: 'string' },
                            html_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commit: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    author: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    committer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    message: { kind: 'string' },
                                    comment_count: { kind: 'number' },
                                    tree: {
                                        kind: 'object',
                                        properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    verification: {
                                        kind: 'object',
                                        properties: {
                                            verified: { kind: 'boolean' },
                                            reason: { kind: 'string' },
                                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['verification'],
                                restriction: 'open',
                            },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            parents: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                    optionalProperties: ['html_url'],
                                    restriction: 'open',
                                },
                            },
                            stats: {
                                kind: 'object',
                                properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            files: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        sha: { kind: 'string' },
                                        filename: { kind: 'string' },
                                        status: { kind: 'string' },
                                        additions: { kind: 'number' },
                                        deletions: { kind: 'number' },
                                        changes: { kind: 'number' },
                                        blob_url: { kind: 'string' },
                                        raw_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        patch: { kind: 'string' },
                                        previous_filename: { kind: 'string' },
                                    },
                                    optionalProperties: ['patch', 'previous_filename'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['stats', 'files'],
                        restriction: 'open',
                    },
                    merge_base_commit: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            sha: { kind: 'string' },
                            node_id: { kind: 'string' },
                            html_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commit: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    author: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    committer: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'string' },
                                                    email: { kind: 'string' },
                                                    date: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    message: { kind: 'string' },
                                    comment_count: { kind: 'number' },
                                    tree: {
                                        kind: 'object',
                                        properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    verification: {
                                        kind: 'object',
                                        properties: {
                                            verified: { kind: 'boolean' },
                                            reason: { kind: 'string' },
                                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: ['verification'],
                                restriction: 'open',
                            },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            parents: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                    optionalProperties: ['html_url'],
                                    restriction: 'open',
                                },
                            },
                            stats: {
                                kind: 'object',
                                properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            files: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        sha: { kind: 'string' },
                                        filename: { kind: 'string' },
                                        status: { kind: 'string' },
                                        additions: { kind: 'number' },
                                        deletions: { kind: 'number' },
                                        changes: { kind: 'number' },
                                        blob_url: { kind: 'string' },
                                        raw_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        patch: { kind: 'string' },
                                        previous_filename: { kind: 'string' },
                                    },
                                    optionalProperties: ['patch', 'previous_filename'],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: ['stats', 'files'],
                        restriction: 'open',
                    },
                    status: { kind: 'string' },
                    ahead_by: { kind: 'number' },
                    behind_by: { kind: 'number' },
                    total_commits: { kind: 'number' },
                    commits: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                sha: { kind: 'string' },
                                node_id: { kind: 'string' },
                                html_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commit: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        author: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'string' },
                                                        email: { kind: 'string' },
                                                        date: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        committer: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'string' },
                                                        email: { kind: 'string' },
                                                        date: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        message: { kind: 'string' },
                                        comment_count: { kind: 'number' },
                                        tree: {
                                            kind: 'object',
                                            properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        verification: {
                                            kind: 'object',
                                            properties: {
                                                verified: { kind: 'boolean' },
                                                reason: { kind: 'string' },
                                                payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['verification'],
                                    restriction: 'open',
                                },
                                author: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                committer: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                parents: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                },
                                stats: {
                                    kind: 'object',
                                    properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                files: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            sha: { kind: 'string' },
                                            filename: { kind: 'string' },
                                            status: { kind: 'string' },
                                            additions: { kind: 'number' },
                                            deletions: { kind: 'number' },
                                            changes: { kind: 'number' },
                                            blob_url: { kind: 'string' },
                                            raw_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            patch: { kind: 'string' },
                                            previous_filename: { kind: 'string' },
                                        },
                                        optionalProperties: ['patch', 'previous_filename'],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: ['stats', 'files'],
                            restriction: 'open',
                        },
                    },
                    files: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                sha: { kind: 'string' },
                                filename: { kind: 'string' },
                                status: { kind: 'string' },
                                additions: { kind: 'number' },
                                deletions: { kind: 'number' },
                                changes: { kind: 'number' },
                                blob_url: { kind: 'string' },
                                raw_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                patch: { kind: 'string' },
                                previous_filename: { kind: 'string' },
                            },
                            optionalProperties: ['patch', 'previous_filename'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: ['files'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'compare', input.inPorts.basehead].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get repository content',
        name: 'Github: Get repository content',
        description:
            'Gets the contents of a file or directory in a repository. Specify the file path or directory in `:path`. If you omit\n`:path`, you will receive the contents of the repository\'s root directory. See the description below regarding what the API response includes for directories. \n\nFiles and symlinks support [a custom media type](https://docs.github.com/rest/reference/repos#custom-media-types) for\nretrieving the raw content or rendered HTML (when supported). All content types support [a custom media\ntype](https://docs.github.com/rest/reference/repos#custom-media-types) to ensure the content is returned in a consistent\nobject format.\n\n**Notes**:\n*   To get a repository\'s contents recursively, you can [recursively get the tree](https://docs.github.com/rest/reference/git#trees).\n*   This API has an upper limit of 1,000 files for a directory. If you need to retrieve more files, use the [Git Trees\nAPI](https://docs.github.com/rest/git/trees#get-a-tree).\n *  Download URLs expire and are meant to be used just once. To ensure the download URL does not expire, please use the contents API to obtain a fresh download URL for each download.\n Size limits:\nIf the requested file\'s size is:\n* 1 MB or smaller: All features of this endpoint are supported.\n* Between 1-100 MB: Only the `raw` or `object` [custom media types](https://docs.github.com/rest/repos/contents#custom-media-types-for-repository-contents) are supported. Both will work as normal, except that when using the `object` media type, the `content` field will be an empty string and the `encoding` field will be `"none"`. To get the contents of these larger files, use the `raw` media type.\n * Greater than 100 MB: This endpoint is not supported.\n\n If the content is a directory:\nThe response will be an array of objects, one object for each item in the directory.\nWhen listing the contents of a directory, submodules have their "type" specified as "file". Logically, the value\n_should_ be "submodule". This behavior exists in API v3 [for backwards compatibility purposes](https://git.io/v1YCW).\nIn the next major version of the API, the type will be returned as "submodule".\n\n If the content is a symlink: \nIf the requested `:path` points to a symlink, and the symlink\'s target is a normal file in the repository, then the\nAPI responds with the content of the file (in the format shown in the example. Otherwise, the API responds with an object \ndescribing the symlink itself.\n\n If the content is a submodule:\nThe `submodule_git_url` identifies the location of the submodule repository, and the `sha` identifies a specific\ncommit within the submodule repository. Git uses the given URL when cloning the submodule repository, and checks out\nthe submodule at that specific commit.\n\nIf the submodule repository is not hosted on github.com, the Git URLs (`git_url` and `_links["git"]`) and the\ngithub.com URLs (`html_url` and `_links["html"]`) will have null values.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            path: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: ['ref'],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'string' },
                                size: { kind: 'number' },
                                name: { kind: 'string' },
                                path: { kind: 'string' },
                                content: { kind: 'string' },
                                sha: { kind: 'string' },
                                url: { kind: 'string' },
                                git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                _links: {
                                    kind: 'object',
                                    properties: {
                                        git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        self: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['content'],
                            restriction: 'open',
                        },
                    },
                    {
                        kind: 'object',
                        properties: {
                            type: { kind: 'string' },
                            encoding: { kind: 'string' },
                            size: { kind: 'number' },
                            name: { kind: 'string' },
                            path: { kind: 'string' },
                            content: { kind: 'string' },
                            sha: { kind: 'string' },
                            url: { kind: 'string' },
                            git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            _links: {
                                kind: 'object',
                                properties: {
                                    git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    self: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            target: { kind: 'string' },
                            submodule_git_url: { kind: 'string' },
                        },
                        optionalProperties: ['target', 'submodule_git_url'],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: {
                            type: { kind: 'string' },
                            target: { kind: 'string' },
                            size: { kind: 'number' },
                            name: { kind: 'string' },
                            path: { kind: 'string' },
                            sha: { kind: 'string' },
                            url: { kind: 'string' },
                            git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            _links: {
                                kind: 'object',
                                properties: {
                                    git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    self: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: {
                            type: { kind: 'string' },
                            submodule_git_url: { kind: 'string' },
                            size: { kind: 'number' },
                            name: { kind: 'string' },
                            path: { kind: 'string' },
                            sha: { kind: 'string' },
                            url: { kind: 'string' },
                            git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            _links: {
                                kind: 'object',
                                properties: {
                                    git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    self: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'contents', input.inPorts.path].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ ref: input.inPorts.ref }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create or update file contents',
        name: 'Github: Create or update file contents',
        description:
            'Creates a new file or replaces an existing file in a repository. You must authenticate using an access token with the `workflow` scope to use this endpoint.\n\n**Note:** If you use this endpoint and the "[Delete a file](https://docs.github.com/rest/reference/repos/#delete-file)" endpoint in parallel, the concurrent requests will conflict and you will receive errors. You must use these endpoints serially instead.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            path: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    message: { kind: 'string' },
                    content: { kind: 'string' },
                    sha: { kind: 'string' },
                    branch: { kind: 'string' },
                    committer: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                        optionalProperties: ['date'],
                        restriction: 'open',
                    },
                    author: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                        optionalProperties: ['date'],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['sha', 'branch', 'committer', 'author'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    content: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    path: { kind: 'string' },
                                    sha: { kind: 'string' },
                                    size: { kind: 'number' },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    download_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    _links: {
                                        kind: 'object',
                                        properties: { self: { kind: 'string' }, git: { kind: 'string' }, html: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    commit: {
                        kind: 'object',
                        properties: {
                            sha: { kind: 'string' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            author: {
                                kind: 'object',
                                properties: { date: { kind: 'string' }, name: { kind: 'string' }, email: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            committer: {
                                kind: 'object',
                                properties: { date: { kind: 'string' }, name: { kind: 'string' }, email: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            message: { kind: 'string' },
                            tree: {
                                kind: 'object',
                                properties: { url: { kind: 'string' }, sha: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            parents: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { url: { kind: 'string' }, html_url: { kind: 'string' }, sha: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            verification: {
                                kind: 'object',
                                properties: {
                                    verified: { kind: 'boolean' },
                                    reason: { kind: 'string' },
                                    signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'contents', input.inPorts.path].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a file',
        name: 'Github: Delete a file',
        description:
            'Deletes a file in a repository.\n\nYou can provide an additional `committer` parameter, which is an object containing information about the committer. Or, you can provide an `author` parameter, which is an object containing information about the author.\n\nThe `author` section is optional and is filled in with the `committer` information if omitted. If the `committer` information is omitted, the authenticated user\'s information is used.\n\nYou must provide values for both `name` and `email`, whether you choose to use `author` or `committer`. Otherwise, you\'ll receive a `422` status code.\n\n**Note:** If you use this endpoint and the "[Create or update file contents](https://docs.github.com/rest/reference/repos/#create-or-update-file-contents)" endpoint in parallel, the concurrent requests will conflict and you will receive errors. You must use these endpoints serially instead.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            path: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    message: { kind: 'string' },
                    sha: { kind: 'string' },
                    branch: { kind: 'string' },
                    committer: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['branch', 'committer', 'author'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    content: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    path: { kind: 'string' },
                                    sha: { kind: 'string' },
                                    size: { kind: 'number' },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    download_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    _links: {
                                        kind: 'object',
                                        properties: { self: { kind: 'string' }, git: { kind: 'string' }, html: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    commit: {
                        kind: 'object',
                        properties: {
                            sha: { kind: 'string' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            author: {
                                kind: 'object',
                                properties: { date: { kind: 'string' }, name: { kind: 'string' }, email: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            committer: {
                                kind: 'object',
                                properties: { date: { kind: 'string' }, name: { kind: 'string' }, email: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            message: { kind: 'string' },
                            tree: {
                                kind: 'object',
                                properties: { url: { kind: 'string' }, sha: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            parents: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { url: { kind: 'string' }, html_url: { kind: 'string' }, sha: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            verification: {
                                kind: 'object',
                                properties: {
                                    verified: { kind: 'boolean' },
                                    reason: { kind: 'string' },
                                    signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'contents', input.inPorts.path].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository contributors',
        name: 'Github: List repository contributors',
        description:
            'Lists contributors to the specified repository and sorts them by the number of commits per contributor in descending order. This endpoint may return information that is a few hours old because the GitHub REST API caches contributor data to improve performance.\n\nGitHub identifies contributors by author email address. This endpoint groups contribution counts by GitHub user, which includes all associated email addresses. To improve performance, only the first 500 author email addresses in the repository link to GitHub users. The rest will appear as anonymous contributors without associated GitHub user information.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            anon: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['anon', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        contributions: { kind: 'number' },
                        email: { kind: 'string' },
                        name: { kind: 'string' },
                    },
                    optionalProperties: [
                        'login',
                        'id',
                        'node_id',
                        'avatar_url',
                        'gravatar_id',
                        'url',
                        'html_url',
                        'followers_url',
                        'following_url',
                        'gists_url',
                        'starred_url',
                        'subscriptions_url',
                        'organizations_url',
                        'repos_url',
                        'events_url',
                        'received_events_url',
                        'site_admin',
                        'email',
                        'name',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'contributors'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ anon: input.inPorts.anon, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List Dependabot alerts for a repository',
        name: 'Github: List Dependabot alerts for a repository',
        description:
            'You must use an access token with the `security_events` scope to use this endpoint with private repositories.\nYou can also use tokens with the `public_repo` scope for public repositories only.\nGitHub Apps must have **Dependabot alerts** read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            state: { kind: 'string' },
            severity: { kind: 'string' },
            ecosystem: { kind: 'string' },
            package: { kind: 'string' },
            manifest: { kind: 'string' },
            scope: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            before: { kind: 'string' },
            after: { kind: 'string' },
            first: { kind: 'number' },
            last: { kind: 'number' },
        },
        optionalInPorts: [
            'state',
            'severity',
            'ecosystem',
            'package',
            'manifest',
            'scope',
            'sort',
            'direction',
            'page',
            'per_page',
            'before',
            'after',
            'first',
            'last',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        dependency: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                manifest_path: { kind: 'string' },
                                scope: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        security_advisory: {
                            kind: 'object',
                            properties: {
                                ghsa_id: { kind: 'string' },
                                cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                summary: { kind: 'string' },
                                description: { kind: 'string' },
                                vulnerabilities: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            package: {
                                                kind: 'object',
                                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            severity: { kind: 'string' },
                                            vulnerable_version_range: { kind: 'string' },
                                            first_patched_version: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: { identifier: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                severity: { kind: 'string' },
                                cvss: {
                                    kind: 'object',
                                    properties: {
                                        score: { kind: 'number' },
                                        vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                cwes: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                identifiers: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                references: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                published_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        security_vulnerability: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                severity: { kind: 'string' },
                                vulnerable_version_range: { kind: 'string' },
                                first_patched_version: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { identifier: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        auto_dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['auto_dismissed_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dependabot', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            severity: input.inPorts.severity,
                            ecosystem: input.inPorts.ecosystem,
                            package: input.inPorts.package,
                            manifest: input.inPorts.manifest,
                            scope: input.inPorts.scope,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                            first: input.inPorts.first,
                            last: input.inPorts.last,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a Dependabot alert',
        name: 'Github: Get a Dependabot alert',
        description:
            'You must use an access token with the `security_events` scope to use this endpoint with private repositories.\nYou can also use tokens with the `public_repo` scope for public repositories only.\nGitHub Apps must have **Dependabot alerts** read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    dependency: {
                        kind: 'object',
                        properties: {
                            package: {
                                kind: 'object',
                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            manifest_path: { kind: 'string' },
                            scope: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_advisory: {
                        kind: 'object',
                        properties: {
                            ghsa_id: { kind: 'string' },
                            cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            summary: { kind: 'string' },
                            description: { kind: 'string' },
                            vulnerabilities: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'object',
                                            properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        severity: { kind: 'string' },
                                        vulnerable_version_range: { kind: 'string' },
                                        first_patched_version: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: { identifier: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            severity: { kind: 'string' },
                            cvss: {
                                kind: 'object',
                                properties: {
                                    score: { kind: 'number' },
                                    vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            cwes: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            identifiers: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            references: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            published_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_vulnerability: {
                        kind: 'object',
                        properties: {
                            package: {
                                kind: 'object',
                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            severity: { kind: 'string' },
                            vulnerable_version_range: { kind: 'string' },
                            first_patched_version: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { identifier: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    auto_dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['auto_dismissed_at'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dependabot', 'alerts', input.inPorts.alert_number].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a Dependabot alert',
        name: 'Github: Update a Dependabot alert',
        description:
            'You must use an access token with the `security_events` scope to use this endpoint with private repositories.\nYou can also use tokens with the `public_repo` scope for public repositories only.\nGitHub Apps must have **Dependabot alerts** write permission to use this endpoint.\n\nTo use this endpoint, you must have access to security alerts for the repository. For more information, see "[Granting access to security alerts](https://docs.github.com/repositories/managing-your-repositorys-settings-and-features/enabling-features-for-your-repository/managing-security-and-analysis-settings-for-your-repository#granting-access-to-security-alerts)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { state: { kind: 'string' }, dismissed_reason: { kind: 'string' }, dismissed_comment: { kind: 'string' } },
                optionalProperties: ['dismissed_reason', 'dismissed_comment'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    dependency: {
                        kind: 'object',
                        properties: {
                            package: {
                                kind: 'object',
                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            manifest_path: { kind: 'string' },
                            scope: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_advisory: {
                        kind: 'object',
                        properties: {
                            ghsa_id: { kind: 'string' },
                            cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            summary: { kind: 'string' },
                            description: { kind: 'string' },
                            vulnerabilities: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'object',
                                            properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        severity: { kind: 'string' },
                                        vulnerable_version_range: { kind: 'string' },
                                        first_patched_version: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: { identifier: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            severity: { kind: 'string' },
                            cvss: {
                                kind: 'object',
                                properties: {
                                    score: { kind: 'number' },
                                    vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            cwes: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            identifiers: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            references: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            published_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_vulnerability: {
                        kind: 'object',
                        properties: {
                            package: {
                                kind: 'object',
                                properties: { ecosystem: { kind: 'string' }, name: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            severity: { kind: 'string' },
                            vulnerable_version_range: { kind: 'string' },
                            first_patched_version: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { identifier: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    dismissed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    dismissed_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fixed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    auto_dismissed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['auto_dismissed_at'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dependabot', 'alerts', input.inPorts.alert_number].join(
                        '/',
                    ),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a diff of the dependencies between commits',
        name: 'Github: Get a diff of the dependencies between commits',
        description:
            'Gets the diff of the dependency changes between two commits of a repository, based on the changes to the dependency manifests made in those commits.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            basehead: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: ['name'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        change_type: { kind: 'string' },
                        manifest: { kind: 'string' },
                        ecosystem: { kind: 'string' },
                        name: { kind: 'string' },
                        version: { kind: 'string' },
                        package_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        license: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        source_repository_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        vulnerabilities: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    severity: { kind: 'string' },
                                    advisory_ghsa_id: { kind: 'string' },
                                    advisory_summary: { kind: 'string' },
                                    advisory_url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        scope: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dependency-graph', 'compare', input.inPorts.basehead].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ name: input.inPorts.name }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Export a software bill of materials (SBOM) for a repository.',
        name: 'Github: Export a software bill of materials (SBOM) for a repository.',
        description: 'Exports the software bill of materials (SBOM) for a repository in SPDX JSON format.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    sbom: {
                        kind: 'object',
                        properties: {
                            SPDXID: { kind: 'string' },
                            spdxVersion: { kind: 'string' },
                            creationInfo: {
                                kind: 'object',
                                properties: { created: { kind: 'string' }, creators: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            name: { kind: 'string' },
                            dataLicense: { kind: 'string' },
                            documentDescribes: { kind: 'array', subType: { kind: 'string' } },
                            documentNamespace: { kind: 'string' },
                            packages: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        SPDXID: { kind: 'string' },
                                        name: { kind: 'string' },
                                        versionInfo: { kind: 'string' },
                                        downloadLocation: { kind: 'string' },
                                        filesAnalyzed: { kind: 'boolean' },
                                        licenseConcluded: { kind: 'string' },
                                        licenseDeclared: { kind: 'string' },
                                        supplier: { kind: 'string' },
                                        externalRefs: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    referenceCategory: { kind: 'string' },
                                                    referenceLocator: { kind: 'string' },
                                                    referenceType: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dependency-graph', 'sbom'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a snapshot of dependencies for a repository',
        name: 'Github: Create a snapshot of dependencies for a repository',
        description:
            "Create a new snapshot of a repository's dependencies. You must authenticate using an access token with the `repo` scope to use this endpoint for a repository that the requesting user has access to.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    version: { kind: 'number' },
                    job: {
                        kind: 'object',
                        properties: { id: { kind: 'string' }, correlator: { kind: 'string' }, html_url: { kind: 'string' } },
                        optionalProperties: ['html_url'],
                        restriction: 'open',
                    },
                    sha: { kind: 'string' },
                    ref: { kind: 'string' },
                    detector: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, version: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    metadata: {
                        kind: 'object',
                        properties: {
                            nullable: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            anyOf: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    manifests: {
                        kind: 'object',
                        properties: {
                            type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            required: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            additionalProperties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    scanned: { kind: 'string' },
                },
                optionalProperties: ['metadata', 'manifests'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    created_at: { kind: 'string' },
                    result: { kind: 'string' },
                    message: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dependency-graph', 'snapshots'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deployments',
        name: 'Github: List deployments',
        description: 'Simple filtering of deployments is available via query parameters:',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sha: { kind: 'string' },
            ref: { kind: 'string' },
            task: { kind: 'string' },
            environment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sha', 'ref', 'task', 'environment', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        sha: { kind: 'string' },
                        ref: { kind: 'string' },
                        task: { kind: 'string' },
                        payload: {
                            kind: 'union',
                            subTypes: [{ kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' }, { kind: 'string' }],
                        },
                        original_environment: { kind: 'string' },
                        environment: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        transient_environment: { kind: 'boolean' },
                        production_environment: { kind: 'boolean' },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'original_environment',
                        'transient_environment',
                        'production_environment',
                        'performed_via_github_app',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'deployments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sha: input.inPorts.sha,
                            ref: input.inPorts.ref,
                            task: input.inPorts.task,
                            environment: input.inPorts.environment,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a deployment',
        name: 'Github: Create a deployment',
        description:
            "Deployments offer a few configurable parameters with certain defaults.\n\nThe `ref` parameter can be any named branch, tag, or SHA. At GitHub we often deploy branches and verify them\nbefore we merge a pull request.\n\nThe `environment` parameter allows deployments to be issued to different runtime environments. Teams often have\nmultiple environments for verifying their applications, such as `production`, `staging`, and `qa`. This parameter\nmakes it easier to track which environments have requested deployments. The default environment is `production`.\n\nThe `auto_merge` parameter is used to ensure that the requested ref is not behind the repository's default branch. If\nthe ref _is_ behind the default branch for the repository, we will attempt to merge it for you. If the merge succeeds,\nthe API will return a successful merge commit. If merge conflicts prevent the merge from succeeding, the API will\nreturn a failure response.\n\nBy default, [commit statuses](https://docs.github.com/rest/commits/statuses) for every submitted context must be in a `success`\nstate. The `required_contexts` parameter allows you to specify a subset of contexts that must be `success`, or to\nspecify contexts that have not yet been submitted. You are not required to use commit statuses to deploy. If you do\nnot require any contexts or create any commit statuses, the deployment will always succeed.\n\nThe `payload` parameter is available for any extra information that a deployment system might need. It is a JSON text\nfield that will be passed on when a deployment event is dispatched.\n\nThe `task` parameter is used by the deployment system to allow different execution paths. In the web world this might\nbe `deploy:migrations` to run schema changes on the system. In the compiled world this could be a flag to compile an\napplication with debugging enabled.\n\nUsers with `repo` or `repo_deployment` scopes can create a deployment for a given ref.\n\nMerged branch response:\n\nYou will see this response when GitHub automatically merges the base branch into the topic branch instead of creating\na deployment. This auto-merge happens when:\n*   Auto-merge option is enabled in the repository\n*   Topic branch does not include the latest changes on the base branch, which is `master` in the response example\n*   There are no merge conflicts\n\nIf there are no new commits in the base branch, a new request to create a deployment should give a successful\nresponse.\n\nMerge conflict response:\n\nThis error happens when the `auto_merge` option is enabled and when the default branch (in this case `master`), can't\nbe merged into the branch that's being deployed (in this case `topic-branch`), due to merge conflicts.\n\nFailed commit status checks:\n\nThis error happens when the `required_contexts` parameter indicates that one or more contexts need to have a `success`\nstatus for the commit to be deployed, but one or more of the required contexts do not have a state of `success`.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    ref: { kind: 'string' },
                    task: { kind: 'string' },
                    auto_merge: { kind: 'boolean' },
                    required_contexts: { kind: 'array', subType: { kind: 'string' } },
                    payload: {
                        kind: 'union',
                        subTypes: [{ kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' }, { kind: 'string' }],
                    },
                    environment: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    transient_environment: { kind: 'boolean' },
                    production_environment: { kind: 'boolean' },
                },
                optionalProperties: [
                    'task',
                    'auto_merge',
                    'required_contexts',
                    'payload',
                    'environment',
                    'description',
                    'transient_environment',
                    'production_environment',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    sha: { kind: 'string' },
                    ref: { kind: 'string' },
                    task: { kind: 'string' },
                    payload: {
                        kind: 'union',
                        subTypes: [{ kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' }, { kind: 'string' }],
                    },
                    original_environment: { kind: 'string' },
                    environment: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    transient_environment: { kind: 'boolean' },
                    production_environment: { kind: 'boolean' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['original_environment', 'transient_environment', 'production_environment', 'performed_via_github_app'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'deployments'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a deployment',
        name: 'Github: Get a deployment',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            deployment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    sha: { kind: 'string' },
                    ref: { kind: 'string' },
                    task: { kind: 'string' },
                    payload: {
                        kind: 'union',
                        subTypes: [{ kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' }, { kind: 'string' }],
                    },
                    original_environment: { kind: 'string' },
                    environment: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    transient_environment: { kind: 'boolean' },
                    production_environment: { kind: 'boolean' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['original_environment', 'transient_environment', 'production_environment', 'performed_via_github_app'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'deployments', input.inPorts.deployment_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a deployment',
        name: 'Github: Delete a deployment',
        description:
            'If the repository only has one deployment, you can delete the deployment regardless of its status. If the repository has more than one deployment, you can only delete inactive deployments. This ensures that repositories with multiple deployments will always have an active deployment. Anyone with `repo` or `repo_deployment` scopes can delete a deployment.\n\nTo set a deployment as inactive, you must:\n\n*   Create a new deployment that is active so that the system has a record of the current state, then delete the previously active deployment.\n*   Mark the active deployment as inactive by adding any non-successful deployment status.\n\nFor more information, see "[Create a deployment](https://docs.github.com/rest/deployments/deployments/#create-a-deployment)" and "[Create a deployment status](https://docs.github.com/rest/deployments/deployment-statuses#create-a-deployment-status)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            deployment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'deployments', input.inPorts.deployment_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deployment statuses',
        name: 'Github: List deployment statuses',
        description: 'Users with pull access can view deployment statuses for a deployment:',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            deployment_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        state: { kind: 'string' },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        description: { kind: 'string' },
                        environment: { kind: 'string' },
                        target_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        deployment_url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        environment_url: { kind: 'string' },
                        log_url: { kind: 'string' },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['environment', 'environment_url', 'log_url', 'performed_via_github_app'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'deployments',
                        input.inPorts.deployment_id,
                        'statuses',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a deployment status',
        name: 'Github: Create a deployment status',
        description:
            'Users with `push` access can create deployment statuses for a given deployment.\n\nGitHub Apps require `read & write` access to "Deployments" and `read-only` access to "Repo contents" (for private repos). OAuth apps require the `repo_deployment` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            deployment_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    target_url: { kind: 'string' },
                    log_url: { kind: 'string' },
                    description: { kind: 'string' },
                    environment: { kind: 'string' },
                    environment_url: { kind: 'string' },
                    auto_inactive: { kind: 'boolean' },
                },
                optionalProperties: ['target_url', 'log_url', 'description', 'environment', 'environment_url', 'auto_inactive'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    description: { kind: 'string' },
                    environment: { kind: 'string' },
                    target_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    deployment_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    environment_url: { kind: 'string' },
                    log_url: { kind: 'string' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['environment', 'environment_url', 'log_url', 'performed_via_github_app'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'deployments',
                        input.inPorts.deployment_id,
                        'statuses',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a deployment status',
        name: 'Github: Get a deployment status',
        description: 'Users with pull access can view a deployment status for a deployment:',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            deployment_id: { kind: 'number' },
            status_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    description: { kind: 'string' },
                    environment: { kind: 'string' },
                    target_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    deployment_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    environment_url: { kind: 'string' },
                    log_url: { kind: 'string' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['environment', 'environment_url', 'log_url', 'performed_via_github_app'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'deployments',
                        input.inPorts.deployment_id,
                        'statuses',
                        input.inPorts.status_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository dispatch event',
        name: 'Github: Create a repository dispatch event',
        description:
            'You can use this endpoint to trigger a webhook event called `repository_dispatch` when you want activity that happens outside of GitHub to trigger a GitHub Actions workflow or GitHub App webhook. You must configure your GitHub Actions workflow or GitHub App to run when the `repository_dispatch` event occurs. For an example `repository_dispatch` webhook payload, see "[RepositoryDispatchEvent](https://docs.github.com/webhooks/event-payloads/#repository_dispatch)."\n\nThe `client_payload` parameter is available for any extra information that your workflow might need. This parameter is a JSON payload that will be passed on when the webhook event is dispatched. For example, the `client_payload` can include a message that a user would like to send using a GitHub Actions workflow. Or the `client_payload` can be used as a test to debug your workflow.\n\nThis endpoint requires write access to the repository by providing either:\n\n  - Personal access tokens with `repo` scope. For more information, see "[Creating a personal access token for the command line](https://docs.github.com/articles/creating-a-personal-access-token-for-the-command-line)" in the GitHub Help documentation.\n  - GitHub Apps with both `metadata:read` and `contents:read&write` permissions.\n\nThis input example shows how you can use the `client_payload` as a test to debug your workflow.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    event_type: { kind: 'string' },
                    client_payload: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                },
                optionalProperties: ['client_payload'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'dispatches'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List environments',
        name: 'Github: List environments',
        description:
            'Lists the environments for a repository.\n\nAnyone with read access to the repository can use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    environments: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                protection_rules: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    wait_timer: { kind: 'number' },
                                                },
                                                optionalProperties: ['wait_timer'],
                                                restriction: 'open',
                                            },
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    reviewers: {
                                                        kind: 'array',
                                                        subType: {
                                                            kind: 'object',
                                                            properties: {
                                                                type: { kind: 'string' },
                                                                reviewer: {
                                                                    kind: 'union',
                                                                    subTypes: [
                                                                        {
                                                                            kind: 'object',
                                                                            properties: {
                                                                                name: {
                                                                                    kind: 'union',
                                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                                },
                                                                                email: {
                                                                                    kind: 'union',
                                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                                },
                                                                                login: { kind: 'string' },
                                                                                id: { kind: 'number' },
                                                                                node_id: { kind: 'string' },
                                                                                avatar_url: { kind: 'string' },
                                                                                gravatar_id: {
                                                                                    kind: 'union',
                                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                                },
                                                                                url: { kind: 'string' },
                                                                                html_url: { kind: 'string' },
                                                                                followers_url: { kind: 'string' },
                                                                                following_url: { kind: 'string' },
                                                                                gists_url: { kind: 'string' },
                                                                                starred_url: { kind: 'string' },
                                                                                subscriptions_url: { kind: 'string' },
                                                                                organizations_url: { kind: 'string' },
                                                                                repos_url: { kind: 'string' },
                                                                                events_url: { kind: 'string' },
                                                                                received_events_url: { kind: 'string' },
                                                                                type: { kind: 'string' },
                                                                                site_admin: { kind: 'boolean' },
                                                                                starred_at: { kind: 'string' },
                                                                            },
                                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                                            restriction: 'open',
                                                                        },
                                                                        {
                                                                            kind: 'object',
                                                                            properties: {
                                                                                id: { kind: 'number' },
                                                                                node_id: { kind: 'string' },
                                                                                name: { kind: 'string' },
                                                                                slug: { kind: 'string' },
                                                                                description: {
                                                                                    kind: 'union',
                                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                                },
                                                                                privacy: { kind: 'string' },
                                                                                notification_setting: { kind: 'string' },
                                                                                permission: { kind: 'string' },
                                                                                permissions: {
                                                                                    kind: 'object',
                                                                                    properties: {
                                                                                        pull: { kind: 'boolean' },
                                                                                        triage: { kind: 'boolean' },
                                                                                        push: { kind: 'boolean' },
                                                                                        maintain: { kind: 'boolean' },
                                                                                        admin: { kind: 'boolean' },
                                                                                    },
                                                                                    optionalProperties: [],
                                                                                    restriction: 'open',
                                                                                },
                                                                                url: { kind: 'string' },
                                                                                html_url: { kind: 'string' },
                                                                                members_url: { kind: 'string' },
                                                                                repositories_url: { kind: 'string' },
                                                                                parent: {
                                                                                    kind: 'union',
                                                                                    subTypes: [
                                                                                        {
                                                                                            kind: 'object',
                                                                                            properties: {
                                                                                                id: { kind: 'number' },
                                                                                                node_id: { kind: 'string' },
                                                                                                url: { kind: 'string' },
                                                                                                members_url: { kind: 'string' },
                                                                                                name: { kind: 'string' },
                                                                                                description: {
                                                                                                    kind: 'union',
                                                                                                    subTypes: [
                                                                                                        { kind: 'string' },
                                                                                                        { kind: 'null' },
                                                                                                    ],
                                                                                                },
                                                                                                permission: { kind: 'string' },
                                                                                                privacy: { kind: 'string' },
                                                                                                notification_setting: { kind: 'string' },
                                                                                                html_url: { kind: 'string' },
                                                                                                repositories_url: { kind: 'string' },
                                                                                                slug: { kind: 'string' },
                                                                                                ldap_dn: { kind: 'string' },
                                                                                            },
                                                                                            optionalProperties: [
                                                                                                'privacy',
                                                                                                'notification_setting',
                                                                                                'ldap_dn',
                                                                                            ],
                                                                                            restriction: 'open',
                                                                                        },
                                                                                        { kind: 'null' },
                                                                                    ],
                                                                                },
                                                                            },
                                                                            optionalProperties: [
                                                                                'privacy',
                                                                                'notification_setting',
                                                                                'permissions',
                                                                            ],
                                                                            restriction: 'open',
                                                                        },
                                                                    ],
                                                                },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                },
                                                optionalProperties: ['reviewers'],
                                                restriction: 'open',
                                            },
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        ],
                                    },
                                },
                                deployment_branch_policy: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                protected_branches: { kind: 'boolean' },
                                                custom_branch_policies: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: ['protection_rules', 'deployment_branch_policy'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'environments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an environment',
        name: 'Github: Get an environment',
        description:
            '**Note:** To get information about name patterns that branches must match in order to deploy to this environment, see "[Get a deployment branch policy](/rest/deployments/branch-policies#get-a-deployment-branch-policy)."\n\nAnyone with read access to the repository can use this endpoint. If the\nrepository is private, you must use an access token with the `repo` scope. GitHub\nApps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    protection_rules: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        type: { kind: 'string' },
                                        wait_timer: { kind: 'number' },
                                    },
                                    optionalProperties: ['wait_timer'],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        type: { kind: 'string' },
                                        reviewers: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    type: { kind: 'string' },
                                                    reviewer: {
                                                        kind: 'union',
                                                        subTypes: [
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    name: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    email: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    login: { kind: 'string' },
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    avatar_url: { kind: 'string' },
                                                                    gravatar_id: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    followers_url: { kind: 'string' },
                                                                    following_url: { kind: 'string' },
                                                                    gists_url: { kind: 'string' },
                                                                    starred_url: { kind: 'string' },
                                                                    subscriptions_url: { kind: 'string' },
                                                                    organizations_url: { kind: 'string' },
                                                                    repos_url: { kind: 'string' },
                                                                    events_url: { kind: 'string' },
                                                                    received_events_url: { kind: 'string' },
                                                                    type: { kind: 'string' },
                                                                    site_admin: { kind: 'boolean' },
                                                                    starred_at: { kind: 'string' },
                                                                },
                                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                                restriction: 'open',
                                                            },
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                    slug: { kind: 'string' },
                                                                    description: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    privacy: { kind: 'string' },
                                                                    notification_setting: { kind: 'string' },
                                                                    permission: { kind: 'string' },
                                                                    permissions: {
                                                                        kind: 'object',
                                                                        properties: {
                                                                            pull: { kind: 'boolean' },
                                                                            triage: { kind: 'boolean' },
                                                                            push: { kind: 'boolean' },
                                                                            maintain: { kind: 'boolean' },
                                                                            admin: { kind: 'boolean' },
                                                                        },
                                                                        optionalProperties: [],
                                                                        restriction: 'open',
                                                                    },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    members_url: { kind: 'string' },
                                                                    repositories_url: { kind: 'string' },
                                                                    parent: {
                                                                        kind: 'union',
                                                                        subTypes: [
                                                                            {
                                                                                kind: 'object',
                                                                                properties: {
                                                                                    id: { kind: 'number' },
                                                                                    node_id: { kind: 'string' },
                                                                                    url: { kind: 'string' },
                                                                                    members_url: { kind: 'string' },
                                                                                    name: { kind: 'string' },
                                                                                    description: {
                                                                                        kind: 'union',
                                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                                    },
                                                                                    permission: { kind: 'string' },
                                                                                    privacy: { kind: 'string' },
                                                                                    notification_setting: { kind: 'string' },
                                                                                    html_url: { kind: 'string' },
                                                                                    repositories_url: { kind: 'string' },
                                                                                    slug: { kind: 'string' },
                                                                                    ldap_dn: { kind: 'string' },
                                                                                },
                                                                                optionalProperties: [
                                                                                    'privacy',
                                                                                    'notification_setting',
                                                                                    'ldap_dn',
                                                                                ],
                                                                                restriction: 'open',
                                                                            },
                                                                            { kind: 'null' },
                                                                        ],
                                                                    },
                                                                },
                                                                optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                                restriction: 'open',
                                                            },
                                                        ],
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                    },
                                    optionalProperties: ['reviewers'],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: { id: { kind: 'number' }, node_id: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    deployment_branch_policy: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: { protected_branches: { kind: 'boolean' }, custom_branch_policies: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['protection_rules', 'deployment_branch_policy'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'environments', input.inPorts.environment_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create or update an environment',
        name: 'Github: Create or update an environment',
        description:
            'Create or update an environment with protection rules, such as required reviewers. For more information about environment protection rules, see "[Environments](/actions/reference/environments#environment-protection-rules)."\n\n**Note:** To create or update name patterns that branches must match in order to deploy to this environment, see "[Deployment branch policies](/rest/deployments/branch-policies)."\n\n**Note:** To create or update secrets for an environment, see "[Secrets](/rest/reference/actions#secrets)."\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration:write` permission for the repository to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            wait_timer: { kind: 'number' },
                            reviewers: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { type: { kind: 'string' }, id: { kind: 'number' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            deployment_branch_policy: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            protected_branches: { kind: 'boolean' },
                                            custom_branch_policies: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    protection_rules: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        type: { kind: 'string' },
                                        wait_timer: { kind: 'number' },
                                    },
                                    optionalProperties: ['wait_timer'],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        type: { kind: 'string' },
                                        reviewers: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    type: { kind: 'string' },
                                                    reviewer: {
                                                        kind: 'union',
                                                        subTypes: [
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    name: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    email: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    login: { kind: 'string' },
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    avatar_url: { kind: 'string' },
                                                                    gravatar_id: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    followers_url: { kind: 'string' },
                                                                    following_url: { kind: 'string' },
                                                                    gists_url: { kind: 'string' },
                                                                    starred_url: { kind: 'string' },
                                                                    subscriptions_url: { kind: 'string' },
                                                                    organizations_url: { kind: 'string' },
                                                                    repos_url: { kind: 'string' },
                                                                    events_url: { kind: 'string' },
                                                                    received_events_url: { kind: 'string' },
                                                                    type: { kind: 'string' },
                                                                    site_admin: { kind: 'boolean' },
                                                                    starred_at: { kind: 'string' },
                                                                },
                                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                                restriction: 'open',
                                                            },
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    name: { kind: 'string' },
                                                                    slug: { kind: 'string' },
                                                                    description: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    privacy: { kind: 'string' },
                                                                    notification_setting: { kind: 'string' },
                                                                    permission: { kind: 'string' },
                                                                    permissions: {
                                                                        kind: 'object',
                                                                        properties: {
                                                                            pull: { kind: 'boolean' },
                                                                            triage: { kind: 'boolean' },
                                                                            push: { kind: 'boolean' },
                                                                            maintain: { kind: 'boolean' },
                                                                            admin: { kind: 'boolean' },
                                                                        },
                                                                        optionalProperties: [],
                                                                        restriction: 'open',
                                                                    },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    members_url: { kind: 'string' },
                                                                    repositories_url: { kind: 'string' },
                                                                    parent: {
                                                                        kind: 'union',
                                                                        subTypes: [
                                                                            {
                                                                                kind: 'object',
                                                                                properties: {
                                                                                    id: { kind: 'number' },
                                                                                    node_id: { kind: 'string' },
                                                                                    url: { kind: 'string' },
                                                                                    members_url: { kind: 'string' },
                                                                                    name: { kind: 'string' },
                                                                                    description: {
                                                                                        kind: 'union',
                                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                                    },
                                                                                    permission: { kind: 'string' },
                                                                                    privacy: { kind: 'string' },
                                                                                    notification_setting: { kind: 'string' },
                                                                                    html_url: { kind: 'string' },
                                                                                    repositories_url: { kind: 'string' },
                                                                                    slug: { kind: 'string' },
                                                                                    ldap_dn: { kind: 'string' },
                                                                                },
                                                                                optionalProperties: [
                                                                                    'privacy',
                                                                                    'notification_setting',
                                                                                    'ldap_dn',
                                                                                ],
                                                                                restriction: 'open',
                                                                            },
                                                                            { kind: 'null' },
                                                                        ],
                                                                    },
                                                                },
                                                                optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                                                restriction: 'open',
                                                            },
                                                        ],
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                    },
                                    optionalProperties: ['reviewers'],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: { id: { kind: 'number' }, node_id: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    deployment_branch_policy: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: { protected_branches: { kind: 'boolean' }, custom_branch_policies: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: ['protection_rules', 'deployment_branch_policy'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'environments', input.inPorts.environment_name].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an environment',
        name: 'Github: Delete an environment',
        description: 'You must authenticate using an access token with the repo scope to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'environments', input.inPorts.environment_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deployment branch policies',
        name: 'Github: List deployment branch policies',
        description:
            'Lists the deployment branch policies for an environment.\n\nAnyone with read access to the repository can use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    branch_policies: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, node_id: { kind: 'string' }, name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment-branch-policies',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a deployment branch policy',
        name: 'Github: Create a deployment branch policy',
        description:
            'Creates a deployment branch policy for an environment.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration:write` permission for the repository to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            body: { kind: 'object', properties: { name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { id: { kind: 'number' }, node_id: { kind: 'string' }, name: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment-branch-policies',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a deployment branch policy',
        name: 'Github: Get a deployment branch policy',
        description:
            'Gets a deployment branch policy for an environment.\n\nAnyone with read access to the repository can use this endpoint. If the repository is private, you must use an access token with the `repo` scope. GitHub Apps must have the `actions:read` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            branch_policy_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { id: { kind: 'number' }, node_id: { kind: 'string' }, name: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment-branch-policies',
                        input.inPorts.branch_policy_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a deployment branch policy',
        name: 'Github: Update a deployment branch policy',
        description:
            'Updates a deployment branch policy for an environment.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration:write` permission for the repository to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            branch_policy_id: { kind: 'number' },
            body: { kind: 'object', properties: { name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { id: { kind: 'number' }, node_id: { kind: 'string' }, name: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment-branch-policies',
                        input.inPorts.branch_policy_id,
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a deployment branch policy',
        name: 'Github: Delete a deployment branch policy',
        description:
            'Deletes a deployment branch policy for an environment.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. GitHub Apps must have the `administration:write` permission for the repository to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            branch_policy_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment-branch-policies',
                        input.inPorts.branch_policy_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all deployment protection rules for an environment',
        name: 'Github: Get all deployment protection rules for an environment',
        description:
            'Gets all custom deployment protection rules that are enabled for an environment. Anyone with read access to the repository can use this endpoint. If the repository is private and you want to use a personal access token (classic), you must use an access token with the `repo` scope. GitHub Apps and fine-grained personal access tokens must have the `actions:read` permission to use this endpoint. For more information about environments, see "[Using environments for deployment](https://docs.github.com/en/actions/deployment/targeting-different-environments/using-environments-for-deployment)."\n\nFor more information about the app that is providing this custom deployment rule, see the [documentation for the `GET /apps/{app_slug}` endpoint](https://docs.github.com/rest/apps/apps#get-an-app).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            environment_name: { kind: 'string' },
            repo: { kind: 'string' },
            owner: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    custom_deployment_protection_rules: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                enabled: { kind: 'boolean' },
                                app: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        integration_url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment_protection_rules',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a custom deployment protection rule on an environment',
        name: 'Github: Create a custom deployment protection rule on an environment',
        description:
            'Enable a custom deployment protection rule for an environment.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. Enabling a custom protection rule requires admin or owner permissions to the repository. GitHub Apps must have the `actions:write` permission to use this endpoint.\n\nFor more information about the app that is providing this custom deployment rule, see the [documentation for the `GET /apps/{app_slug}` endpoint](https://docs.github.com/rest/apps/apps#get-an-app).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            environment_name: { kind: 'string' },
            repo: { kind: 'string' },
            owner: { kind: 'string' },
            body: { kind: 'object', properties: { integration_id: { kind: 'number' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    enabled: { kind: 'boolean' },
                    app: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            slug: { kind: 'string' },
                            integration_url: { kind: 'string' },
                            node_id: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment_protection_rules',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List custom deployment rule integrations available for an environment',
        name: 'Github: List custom deployment rule integrations available for an environment',
        description:
            'Gets all custom deployment protection rule integrations that are available for an environment. Anyone with read access to the repository can use this endpoint. If the repository is private and you want to use a personal access token (classic), you must use an access token with the `repo` scope. GitHub Apps and fine-grained personal access tokens must have the `actions:read` permission to use this endpoint.\n\nFor more information about environments, see "[Using environments for deployment](https://docs.github.com/en/actions/deployment/targeting-different-environments/using-environments-for-deployment)."\n\nFor more information about the app that is providing this custom deployment rule, see "[GET an app](https://docs.github.com/rest/apps/apps#get-an-app)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            environment_name: { kind: 'string' },
            repo: { kind: 'string' },
            owner: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    available_custom_deployment_protection_rule_integrations: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                slug: { kind: 'string' },
                                integration_url: { kind: 'string' },
                                node_id: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment_protection_rules',
                        'apps',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a custom deployment protection rule',
        name: 'Github: Get a custom deployment protection rule',
        description:
            'Gets an enabled custom deployment protection rule for an environment. Anyone with read access to the repository can use this endpoint. If the repository is private and you want to use a personal access token (classic), you must use an access token with the `repo` scope. GitHub Apps and fine-grained personal access tokens must have the `actions:read` permission to use this endpoint. For more information about environments, see "[Using environments for deployment](https://docs.github.com/en/actions/deployment/targeting-different-environments/using-environments-for-deployment)."\n\nFor more information about the app that is providing this custom deployment rule, see [`GET /apps/{app_slug}`](https://docs.github.com/rest/apps/apps#get-an-app).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            environment_name: { kind: 'string' },
            protection_rule_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    enabled: { kind: 'boolean' },
                    app: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            slug: { kind: 'string' },
                            integration_url: { kind: 'string' },
                            node_id: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment_protection_rules',
                        input.inPorts.protection_rule_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Disable a custom protection rule for an environment',
        name: 'Github: Disable a custom protection rule for an environment',
        description:
            'Disables a custom deployment protection rule for an environment.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint. Removing a custom protection rule requires admin or owner permissions to the repository. GitHub Apps must have the `actions:write` permission to use this endpoint. For more information, see "[Get an app](https://docs.github.com/rest/apps/apps#get-an-app)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            environment_name: { kind: 'string' },
            repo: { kind: 'string' },
            owner: { kind: 'string' },
            protection_rule_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'environments',
                        input.inPorts.environment_name,
                        'deployment_protection_rules',
                        input.inPorts.protection_rule_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository events',
        name: 'Github: List repository events',
        description:
            '**Note**: This API is not built to serve real-time use cases. Depending on the time of day, event latency can be anywhere from 30s to 6h.\n',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List forks',
        name: 'Github: List forks',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sort: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'forks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ sort: input.inPorts.sort, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a fork',
        name: 'Github: Create a fork',
        description:
            'Create a fork for the authenticated user.\n\n**Note**: Forking a Repository happens asynchronously. You may have to wait a short period of time before you can access the git objects. If this takes longer than 5 minutes, be sure to contact [GitHub Support](https://support.github.com/contact?tags=dotcom-rest-api).\n\n**Note**: Although this endpoint works with GitHub Apps, the GitHub App must be installed on the destination account with access to all repositories and on the source account with access to the source repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            organization: { kind: 'string' },
                            name: { kind: 'string' },
                            default_branch_only: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                        },
                        optionalProperties: ['maintain', 'triage'],
                        restriction: 'open',
                    },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    organization: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    parent: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    source: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    forks: { kind: 'number' },
                    master_branch: { kind: 'string' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    anonymous_access_enabled: { kind: 'boolean' },
                    code_of_conduct: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            key: { kind: 'string' },
                            name: { kind: 'string' },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    security_and_analysis: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    advanced_security: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dependabot_security_updates: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning_push_protection: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [
                    'is_template',
                    'topics',
                    'visibility',
                    'permissions',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_merge_commit',
                    'allow_update_branch',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'organization',
                    'parent',
                    'source',
                    'master_branch',
                    'anonymous_access_enabled',
                    'code_of_conduct',
                    'security_and_analysis',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'forks'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a blob',
        name: 'Github: Create a blob',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { content: { kind: 'string' }, encoding: { kind: 'string' } },
                optionalProperties: ['encoding'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, sha: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'blobs'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a blob',
        name: 'Github: Get a blob',
        description:
            'The `content` in the response will always be Base64 encoded.\n\n_Note_: This API supports blobs up to 100 megabytes in size.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            file_sha: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    content: { kind: 'string' },
                    encoding: { kind: 'string' },
                    url: { kind: 'string' },
                    sha: { kind: 'string' },
                    size: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    node_id: { kind: 'string' },
                    highlighted_content: { kind: 'string' },
                },
                optionalProperties: ['highlighted_content'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'blobs', input.inPorts.file_sha].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a commit',
        name: 'Github: Create a commit',
        description:
            'Creates a new Git [commit object](https://git-scm.com/book/en/v2/Git-Internals-Git-Objects).\n\n**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit\'s signature. The following fields are included in the `verification` object:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in the table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The "signing" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user\'s account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    message: { kind: 'string' },
                    tree: { kind: 'string' },
                    parents: { kind: 'array', subType: { kind: 'string' } },
                    author: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                        optionalProperties: ['date'],
                        restriction: 'open',
                    },
                    committer: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    signature: { kind: 'string' },
                },
                optionalProperties: ['parents', 'author', 'committer', 'signature'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    author: {
                        kind: 'object',
                        properties: { date: { kind: 'string' }, email: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    committer: {
                        kind: 'object',
                        properties: { date: { kind: 'string' }, email: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    message: { kind: 'string' },
                    tree: {
                        kind: 'object',
                        properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    parents: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    verification: {
                        kind: 'object',
                        properties: {
                            verified: { kind: 'boolean' },
                            reason: { kind: 'string' },
                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    html_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'commits'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a commit object',
        name: 'Github: Get a commit object',
        description:
            'Gets a Git [commit object](https://git-scm.com/book/en/v2/Git-Internals-Git-Objects).\n\nTo get the contents of a commit, see "[Get a commit](/rest/commits/commits#get-a-commit)."\n\n**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit\'s signature. The following fields are included in the `verification` object:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in the table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The "signing" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user\'s account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            commit_sha: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    author: {
                        kind: 'object',
                        properties: { date: { kind: 'string' }, email: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    committer: {
                        kind: 'object',
                        properties: { date: { kind: 'string' }, email: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    message: { kind: 'string' },
                    tree: {
                        kind: 'object',
                        properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    parents: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    verification: {
                        kind: 'object',
                        properties: {
                            verified: { kind: 'boolean' },
                            reason: { kind: 'string' },
                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    html_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'commits', input.inPorts.commit_sha].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List matching references',
        name: 'Github: List matching references',
        description:
            'Returns an array of references from your Git database that match the supplied name. The `:ref` in the URL must be formatted as `heads/<branch name>` for branches and `tags/<tag name>` for tags. If the `:ref` doesn\'t exist in the repository, but existing refs start with `:ref`, they will be returned as an array.\n\nWhen you use this endpoint without providing a `:ref`, it will return an array of all the references from your Git database, including notes and stashes if they exist on the server. Anything in the namespace is returned, not just `heads` and `tags`.\n\n**Note:** You need to explicitly [request a pull request](https://docs.github.com/rest/pulls/pulls#get-a-pull-request) to trigger a test merge commit, which checks the mergeability of pull requests. For more information, see "[Checking mergeability of pull requests](https://docs.github.com/rest/guides/getting-started-with-the-git-database-api#checking-mergeability-of-pull-requests)".\n\nIf you request matching references for a branch named `feature` but the branch `feature` doesn\'t exist, the response can still include other matching head refs that start with the word `feature`, such as `featureA` and `featureB`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        ref: { kind: 'string' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        object: {
                            kind: 'object',
                            properties: { type: { kind: 'string' }, sha: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'matching-refs', input.inPorts.ref].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a reference',
        name: 'Github: Get a reference',
        description:
            'Returns a single reference from your Git database. The `:ref` in the URL must be formatted as `heads/<branch name>` for branches and `tags/<tag name>` for tags. If the `:ref` doesn\'t match an existing ref, a `404` is returned.\n\n**Note:** You need to explicitly [request a pull request](https://docs.github.com/rest/pulls/pulls#get-a-pull-request) to trigger a test merge commit, which checks the mergeability of pull requests. For more information, see "[Checking mergeability of pull requests](https://docs.github.com/rest/guides/getting-started-with-the-git-database-api#checking-mergeability-of-pull-requests)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ref: { kind: 'string' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    object: {
                        kind: 'object',
                        properties: { type: { kind: 'string' }, sha: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'ref', input.inPorts.ref].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a reference',
        name: 'Github: Create a reference',
        description:
            'Creates a reference for your repository. You are unable to create new references for empty repositories, even if the commit SHA-1 hash used exists. Empty repositories are repositories without branches.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { ref: { kind: 'string' }, sha: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ref: { kind: 'string' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    object: {
                        kind: 'object',
                        properties: { type: { kind: 'string' }, sha: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'refs'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a reference',
        name: 'Github: Delete a reference',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'refs', input.inPorts.ref].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a reference',
        name: 'Github: Update a reference',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { sha: { kind: 'string' }, force: { kind: 'boolean' } },
                optionalProperties: ['force'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ref: { kind: 'string' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    object: {
                        kind: 'object',
                        properties: { type: { kind: 'string' }, sha: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'refs', input.inPorts.ref].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a tag object',
        name: 'Github: Create a tag object',
        description:
            'Note that creating a tag object does not create the reference that makes a tag in Git. If you want to create an annotated tag in Git, you have to do this call to create the tag object, and then [create](https://docs.github.com/rest/git/refs#create-a-reference) the `refs/tags/[tag]` reference. If you want to create a lightweight tag, you only have to [create](https://docs.github.com/rest/git/refs#create-a-reference) the tag reference - this call would be unnecessary.\n\n**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit\'s signature. The following fields are included in the `verification` object:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The "signing" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user\'s account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    tag: { kind: 'string' },
                    message: { kind: 'string' },
                    object: { kind: 'string' },
                    type: { kind: 'string' },
                    tagger: {
                        kind: 'object',
                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                        optionalProperties: ['date'],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['tagger'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    node_id: { kind: 'string' },
                    tag: { kind: 'string' },
                    sha: { kind: 'string' },
                    url: { kind: 'string' },
                    message: { kind: 'string' },
                    tagger: {
                        kind: 'object',
                        properties: { date: { kind: 'string' }, email: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    object: {
                        kind: 'object',
                        properties: { sha: { kind: 'string' }, type: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    verification: {
                        kind: 'object',
                        properties: {
                            verified: { kind: 'boolean' },
                            reason: { kind: 'string' },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['verification'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'tags'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a tag',
        name: 'Github: Get a tag',
        description:
            '**Signature verification object**\n\nThe response will include a `verification` object that describes the result of verifying the commit\'s signature. The following fields are included in the `verification` object:\n\n| Name | Type | Description |\n| ---- | ---- | ----------- |\n| `verified` | `boolean` | Indicates whether GitHub considers the signature in this commit to be verified. |\n| `reason` | `string` | The reason for verified value. Possible values and their meanings are enumerated in table below. |\n| `signature` | `string` | The signature that was extracted from the commit. |\n| `payload` | `string` | The value that was signed. |\n\nThese are the possible values for `reason` in the `verification` object:\n\n| Value | Description |\n| ----- | ----------- |\n| `expired_key` | The key that made the signature is expired. |\n| `not_signing_key` | The "signing" flag is not among the usage flags in the GPG key that made the signature. |\n| `gpgverify_error` | There was an error communicating with the signature verification service. |\n| `gpgverify_unavailable` | The signature verification service is currently unavailable. |\n| `unsigned` | The object does not include a signature. |\n| `unknown_signature_type` | A non-PGP signature was found in the commit. |\n| `no_user` | No user was associated with the `committer` email address in the commit. |\n| `unverified_email` | The `committer` email address in the commit was associated with a user, but the email address is not verified on their account. |\n| `bad_email` | The `committer` email address in the commit is not included in the identities of the PGP key that made the signature. |\n| `unknown_key` | The key that made the signature has not been registered with any user\'s account. |\n| `malformed_signature` | There was an error parsing the signature. |\n| `invalid` | The signature could not be cryptographically verified using the key whose key-id was found in the signature. |\n| `valid` | None of the above errors applied, so the signature is considered to be verified. |',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            tag_sha: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    node_id: { kind: 'string' },
                    tag: { kind: 'string' },
                    sha: { kind: 'string' },
                    url: { kind: 'string' },
                    message: { kind: 'string' },
                    tagger: {
                        kind: 'object',
                        properties: { date: { kind: 'string' }, email: { kind: 'string' }, name: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    object: {
                        kind: 'object',
                        properties: { sha: { kind: 'string' }, type: { kind: 'string' }, url: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    verification: {
                        kind: 'object',
                        properties: {
                            verified: { kind: 'boolean' },
                            reason: { kind: 'string' },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['verification'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'tags', input.inPorts.tag_sha].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a tree',
        name: 'Github: Create a tree',
        description:
            'The tree creation API accepts nested entries. If you specify both a tree and a nested path modifying that tree, this endpoint will overwrite the contents of the tree with the new path contents, and create a new tree structure.\n\nIf you use this endpoint to add, delete, or modify the file contents in a tree, you will need to commit the tree and then update a branch to point to the commit. For more information see "[Create a commit](https://docs.github.com/rest/git/commits#create-a-commit)" and "[Update a reference](https://docs.github.com/rest/git/refs#update-a-reference)."\n\nReturns an error if you try to delete a file that does not exist.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    tree: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                path: { kind: 'string' },
                                mode: { kind: 'string' },
                                type: { kind: 'string' },
                                sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                content: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    base_tree: { kind: 'string' },
                },
                optionalProperties: ['base_tree'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    sha: { kind: 'string' },
                    url: { kind: 'string' },
                    truncated: { kind: 'boolean' },
                    tree: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                path: { kind: 'string' },
                                mode: { kind: 'string' },
                                type: { kind: 'string' },
                                sha: { kind: 'string' },
                                size: { kind: 'number' },
                                url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'trees'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a tree',
        name: 'Github: Get a tree',
        description:
            'Returns a single tree using the SHA1 value for that tree.\n\nIf `truncated` is `true` in the response then the number of items in the `tree` array exceeded our maximum limit. If you need to fetch more items, use the non-recursive method of fetching trees, and fetch one sub-tree at a time.\n\n\n**Note**: The limit for the `tree` array is 100,000 entries with a maximum size of 7 MB when using the `recursive` parameter.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            tree_sha: { kind: 'string' },
            recursive: { kind: 'string' },
        },
        optionalInPorts: ['recursive'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    sha: { kind: 'string' },
                    url: { kind: 'string' },
                    truncated: { kind: 'boolean' },
                    tree: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                path: { kind: 'string' },
                                mode: { kind: 'string' },
                                type: { kind: 'string' },
                                sha: { kind: 'string' },
                                size: { kind: 'number' },
                                url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'git', 'trees', input.inPorts.tree_sha].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ recursive: input.inPorts.recursive }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository webhooks',
        name: 'Github: List repository webhooks',
        description:
            'Lists webhooks for a repository. `last response` may return null if there have not been any deliveries within 30 days.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        type: { kind: 'string' },
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        active: { kind: 'boolean' },
                        events: { kind: 'array', subType: { kind: 'string' } },
                        config: {
                            kind: 'object',
                            properties: {
                                email: { kind: 'string' },
                                password: { kind: 'string' },
                                room: { kind: 'string' },
                                subdomain: { kind: 'string' },
                                url: { kind: 'string' },
                                insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                                content_type: { kind: 'string' },
                                digest: { kind: 'string' },
                                secret: { kind: 'string' },
                                token: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        updated_at: { kind: 'string' },
                        created_at: { kind: 'string' },
                        url: { kind: 'string' },
                        test_url: { kind: 'string' },
                        ping_url: { kind: 'string' },
                        deliveries_url: { kind: 'string' },
                        last_response: {
                            kind: 'object',
                            properties: {
                                code: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['deliveries_url'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository webhook',
        name: 'Github: Create a repository webhook',
        description:
            'Repositories can have multiple webhooks installed. Each webhook should have a unique `config`. Multiple webhooks can\nshare the same `config` as long as those webhooks do not have any `events` that overlap.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            name: { kind: 'string' },
                            config: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    content_type: { kind: 'string' },
                                    secret: { kind: 'string' },
                                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                                    token: { kind: 'string' },
                                    digest: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            events: { kind: 'array', subType: { kind: 'string' } },
                            active: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    type: { kind: 'string' },
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    active: { kind: 'boolean' },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    config: {
                        kind: 'object',
                        properties: {
                            email: { kind: 'string' },
                            password: { kind: 'string' },
                            room: { kind: 'string' },
                            subdomain: { kind: 'string' },
                            url: { kind: 'string' },
                            insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                            content_type: { kind: 'string' },
                            digest: { kind: 'string' },
                            secret: { kind: 'string' },
                            token: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    url: { kind: 'string' },
                    test_url: { kind: 'string' },
                    ping_url: { kind: 'string' },
                    deliveries_url: { kind: 'string' },
                    last_response: {
                        kind: 'object',
                        properties: {
                            code: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['deliveries_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository webhook',
        name: 'Github: Get a repository webhook',
        description:
            'Returns a webhook configured in a repository. To get only the webhook `config` properties, see "[Get a webhook configuration for a repository](/rest/reference/repos#get-a-webhook-configuration-for-a-repository)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    type: { kind: 'string' },
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    active: { kind: 'boolean' },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    config: {
                        kind: 'object',
                        properties: {
                            email: { kind: 'string' },
                            password: { kind: 'string' },
                            room: { kind: 'string' },
                            subdomain: { kind: 'string' },
                            url: { kind: 'string' },
                            insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                            content_type: { kind: 'string' },
                            digest: { kind: 'string' },
                            secret: { kind: 'string' },
                            token: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    url: { kind: 'string' },
                    test_url: { kind: 'string' },
                    ping_url: { kind: 'string' },
                    deliveries_url: { kind: 'string' },
                    last_response: {
                        kind: 'object',
                        properties: {
                            code: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['deliveries_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository webhook',
        name: 'Github: Delete a repository webhook',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a repository webhook',
        name: 'Github: Update a repository webhook',
        description:
            'Updates a webhook configured in a repository. If you previously had a `secret` set, you must provide the same `secret` or set a new `secret` or the secret will be removed. If you are only updating individual webhook `config` properties, use "[Update a webhook configuration for a repository](/rest/reference/repos#update-a-webhook-configuration-for-a-repository)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    config: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            content_type: { kind: 'string' },
                            secret: { kind: 'string' },
                            insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                            address: { kind: 'string' },
                            room: { kind: 'string' },
                        },
                        optionalProperties: ['content_type', 'secret', 'insecure_ssl', 'address', 'room'],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    add_events: { kind: 'array', subType: { kind: 'string' } },
                    remove_events: { kind: 'array', subType: { kind: 'string' } },
                    active: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    type: { kind: 'string' },
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    active: { kind: 'boolean' },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    config: {
                        kind: 'object',
                        properties: {
                            email: { kind: 'string' },
                            password: { kind: 'string' },
                            room: { kind: 'string' },
                            subdomain: { kind: 'string' },
                            url: { kind: 'string' },
                            insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                            content_type: { kind: 'string' },
                            digest: { kind: 'string' },
                            secret: { kind: 'string' },
                            token: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    updated_at: { kind: 'string' },
                    created_at: { kind: 'string' },
                    url: { kind: 'string' },
                    test_url: { kind: 'string' },
                    ping_url: { kind: 'string' },
                    deliveries_url: { kind: 'string' },
                    last_response: {
                        kind: 'object',
                        properties: {
                            code: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['deliveries_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a webhook configuration for a repository',
        name: 'Github: Get a webhook configuration for a repository',
        description:
            'Returns the webhook configuration for a repository. To get more information about the webhook, including the `active` state and `events`, use "[Get a repository webhook](/rest/reference/orgs#get-a-repository-webhook)."\n\nAccess tokens must have the `read:repo_hook` or `repo` scope, and GitHub Apps must have the `repository_hooks:read` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id, 'config'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a webhook configuration for a repository',
        name: 'Github: Update a webhook configuration for a repository',
        description:
            'Updates the webhook configuration for a repository. To update more information about the webhook, including the `active` state and `events`, use "[Update a repository webhook](/rest/reference/orgs#update-a-repository-webhook)."\n\nAccess tokens must have the `write:repo_hook` or `repo` scope, and GitHub Apps must have the `repository_hooks:write` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    content_type: { kind: 'string' },
                    secret: { kind: 'string' },
                    insecure_ssl: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id, 'config'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deliveries for a repository webhook',
        name: 'Github: List deliveries for a repository webhook',
        description: 'Returns a list of webhook deliveries for a webhook configured in a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
            per_page: { kind: 'number' },
            cursor: { kind: 'string' },
            redelivery: { kind: 'boolean' },
        },
        optionalInPorts: ['per_page', 'cursor', 'redelivery'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        guid: { kind: 'string' },
                        delivered_at: { kind: 'string' },
                        redelivery: { kind: 'boolean' },
                        duration: { kind: 'number' },
                        status: { kind: 'string' },
                        status_code: { kind: 'number' },
                        event: { kind: 'string' },
                        action: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        installation_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        repository_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id, 'deliveries'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            cursor: input.inPorts.cursor,
                            redelivery: input.inPorts.redelivery,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a delivery for a repository webhook',
        name: 'Github: Get a delivery for a repository webhook',
        description: 'Returns a delivery for a webhook configured in a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
            delivery_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    guid: { kind: 'string' },
                    delivered_at: { kind: 'string' },
                    redelivery: { kind: 'boolean' },
                    duration: { kind: 'number' },
                    status: { kind: 'string' },
                    status_code: { kind: 'number' },
                    event: { kind: 'string' },
                    action: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    installation_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    repository_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    request: {
                        kind: 'object',
                        properties: {
                            headers: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                            payload: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    response: {
                        kind: 'object',
                        properties: {
                            headers: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    { kind: 'null' },
                                ],
                            },
                            payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'hooks',
                        input.inPorts.hook_id,
                        'deliveries',
                        input.inPorts.delivery_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Redeliver a delivery for a repository webhook',
        name: 'Github: Redeliver a delivery for a repository webhook',
        description: 'Redeliver a webhook delivery for a webhook configured in a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
            delivery_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'hooks',
                        input.inPorts.hook_id,
                        'deliveries',
                        input.inPorts.delivery_id,
                        'attempts',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Ping a repository webhook',
        name: 'Github: Ping a repository webhook',
        description: 'This will trigger a [ping event](https://docs.github.com/webhooks/#ping-event) to be sent to the hook.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id, 'pings'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Test the push repository webhook',
        name: 'Github: Test the push repository webhook',
        description:
            'This will trigger the hook with the latest push to the current repository if the hook is subscribed to `push` events. If the hook is not subscribed to `push` events, the server will respond with 204 but no test POST will be generated.\n\n**Note**: Previously `/repos/:owner/:repo/hooks/:hook_id/test`',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            hook_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'hooks', input.inPorts.hook_id, 'tests'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an import status',
        name: 'Github: Get an import status',
        description:
            'View the progress of an import.\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.\n\n**Import status**\n\nThis section includes details about the possible values of the `status` field of the Import Progress response.\n\nAn import that does not have errors will progress through these steps:\n\n*   `detecting` - the "detection" step of the import is in progress because the request did not include a `vcs` parameter. The import is identifying the type of source control present at the URL.\n*   `importing` - the "raw" step of the import is in progress. This is where commit data is fetched from the original repository. The import progress response will include `commit_count` (the total number of raw commits that will be imported) and `percent` (0 - 100, the current progress through the import).\n*   `mapping` - the "rewrite" step of the import is in progress. This is where SVN branches are converted to Git branches, and where author updates are applied. The import progress response does not include progress information.\n*   `pushing` - the "push" step of the import is in progress. This is where the importer updates the repository on GitHub. The import progress response will include `push_percent`, which is the percent value reported by `git push` when it is "Writing objects".\n*   `complete` - the import is complete, and the repository is ready on GitHub.\n\nIf there are problems, you will see one of these in the `status` field:\n\n*   `auth_failed` - the import requires authentication in order to connect to the original repository. To update authentication for the import, please see the [Update an import](https://docs.github.com/rest/migrations/source-imports#update-an-import) section.\n*   `error` - the import encountered an error. The import progress response will include the `failed_step` and an error message. Contact [GitHub Support](https://support.github.com/contact?tags=dotcom-rest-api) for more information.\n*   `detection_needs_auth` - the importer requires authentication for the originating repository to continue detection. To update authentication for the import, please see the [Update an import](https://docs.github.com/rest/migrations/source-imports#update-an-import) section.\n*   `detection_found_nothing` - the importer didn\'t recognize any source control at the URL. To resolve, [Cancel the import](https://docs.github.com/rest/migrations/source-imports#cancel-an-import) and [retry](https://docs.github.com/rest/migrations/source-imports#start-an-import) with the correct URL.\n*   `detection_found_multiple` - the importer found several projects or repositories at the provided URL. When this is the case, the Import Progress response will also include a `project_choices` field with the possible project choices as values. To update project choice, please see the [Update an import](https://docs.github.com/rest/migrations/source-imports#update-an-import) section.\n\n**The project_choices field**\n\nWhen multiple projects are found at the provided URL, the response hash will include a `project_choices` field, the value of which is an array of hashes each representing a project choice. The exact key/value pairs of the project hashes will differ depending on the version control type.\n\n**Git LFS related fields**\n\nThis section includes details about Git LFS related fields that may be present in the Import Progress response.\n\n*   `use_lfs` - describes whether the import has been opted in or out of using Git LFS. The value can be `opt_in`, `opt_out`, or `undecided` if no action has been taken.\n*   `has_large_files` - the boolean value describing whether files larger than 100MB were found during the `importing` step.\n*   `large_files_size` - the total size in gigabytes of files larger than 100MB found in the originating repository.\n*   `large_files_count` - the total number of files larger than 100MB found in the originating repository. To see a list of these files, make a "Get Large Files" request.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    vcs: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    use_lfs: { kind: 'boolean' },
                    vcs_url: { kind: 'string' },
                    svc_root: { kind: 'string' },
                    tfvc_project: { kind: 'string' },
                    status: { kind: 'string' },
                    status_text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    failed_step: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    error_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    import_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    push_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    has_large_files: { kind: 'boolean' },
                    large_files_size: { kind: 'number' },
                    large_files_count: { kind: 'number' },
                    project_choices: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { vcs: { kind: 'string' }, tfvc_project: { kind: 'string' }, human_name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    message: { kind: 'string' },
                    authors_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    authors_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    svn_root: { kind: 'string' },
                },
                optionalProperties: [
                    'use_lfs',
                    'svc_root',
                    'tfvc_project',
                    'status_text',
                    'failed_step',
                    'error_message',
                    'import_percent',
                    'commit_count',
                    'push_percent',
                    'has_large_files',
                    'large_files_size',
                    'large_files_count',
                    'project_choices',
                    'message',
                    'authors_count',
                    'svn_root',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Start an import',
        name: 'Github: Start an import',
        description:
            'Start a source import to a GitHub repository using GitHub Importer. Importing into a GitHub repository with GitHub Actions enabled is not supported and will return a status `422 Unprocessable Entity` response.\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end on October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update these docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    vcs_url: { kind: 'string' },
                    vcs: { kind: 'string' },
                    vcs_username: { kind: 'string' },
                    vcs_password: { kind: 'string' },
                    tfvc_project: { kind: 'string' },
                },
                optionalProperties: ['vcs', 'vcs_username', 'vcs_password', 'tfvc_project'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    vcs: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    use_lfs: { kind: 'boolean' },
                    vcs_url: { kind: 'string' },
                    svc_root: { kind: 'string' },
                    tfvc_project: { kind: 'string' },
                    status: { kind: 'string' },
                    status_text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    failed_step: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    error_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    import_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    push_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    has_large_files: { kind: 'boolean' },
                    large_files_size: { kind: 'number' },
                    large_files_count: { kind: 'number' },
                    project_choices: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { vcs: { kind: 'string' }, tfvc_project: { kind: 'string' }, human_name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    message: { kind: 'string' },
                    authors_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    authors_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    svn_root: { kind: 'string' },
                },
                optionalProperties: [
                    'use_lfs',
                    'svc_root',
                    'tfvc_project',
                    'status_text',
                    'failed_step',
                    'error_message',
                    'import_percent',
                    'commit_count',
                    'push_percent',
                    'has_large_files',
                    'large_files_size',
                    'large_files_count',
                    'project_choices',
                    'message',
                    'authors_count',
                    'svn_root',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Cancel an import',
        name: 'Github: Cancel an import',
        description:
            'Stop an import for a repository.\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.\n',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an import',
        name: 'Github: Update an import',
        description:
            'An import can be updated with credentials or a project choice by passing in the appropriate parameters in this API\nrequest. If no parameters are provided, the import will be restarted.\n\nSome servers (e.g. TFS servers) can have several projects at a single URL. In those cases the import progress will\nhave the status `detection_found_multiple` and the Import Progress response will include a `project_choices` array.\nYou can select the project to import by providing one of the objects in the `project_choices` array in the update request.\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            vcs_username: { kind: 'string' },
                            vcs_password: { kind: 'string' },
                            vcs: { kind: 'string' },
                            tfvc_project: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    vcs: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    use_lfs: { kind: 'boolean' },
                    vcs_url: { kind: 'string' },
                    svc_root: { kind: 'string' },
                    tfvc_project: { kind: 'string' },
                    status: { kind: 'string' },
                    status_text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    failed_step: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    error_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    import_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    push_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    has_large_files: { kind: 'boolean' },
                    large_files_size: { kind: 'number' },
                    large_files_count: { kind: 'number' },
                    project_choices: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { vcs: { kind: 'string' }, tfvc_project: { kind: 'string' }, human_name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    message: { kind: 'string' },
                    authors_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    authors_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    svn_root: { kind: 'string' },
                },
                optionalProperties: [
                    'use_lfs',
                    'svc_root',
                    'tfvc_project',
                    'status_text',
                    'failed_step',
                    'error_message',
                    'import_percent',
                    'commit_count',
                    'push_percent',
                    'has_large_files',
                    'large_files_size',
                    'large_files_count',
                    'project_choices',
                    'message',
                    'authors_count',
                    'svn_root',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get commit authors',
        name: 'Github: Get commit authors',
        description:
            'Each type of source control system represents authors in a different way. For example, a Git commit author has a display name and an email address, but a Subversion commit author just has a username. The GitHub Importer will make the author information valid, but the author might not be correct. For example, it will change the bare Subversion username `hubot` into something like `hubot <hubot@12341234-abab-fefe-8787-fedcba987654>`.\n\nThis endpoint and the [Map a commit author](https://docs.github.com/rest/migrations/source-imports#map-a-commit-author) endpoint allow you to provide correct Git author information.\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            since: { kind: 'number' },
        },
        optionalInPorts: ['since'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        remote_id: { kind: 'string' },
                        remote_name: { kind: 'string' },
                        email: { kind: 'string' },
                        name: { kind: 'string' },
                        url: { kind: 'string' },
                        import_url: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import', 'authors'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ since: input.inPorts.since }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Map a commit author',
        name: 'Github: Map a commit author',
        description:
            'Update an author\'s identity for the import. Your application can continue updating authors any time before you push\nnew commits to the repository.\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.\n',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            author_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { email: { kind: 'string' }, name: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    remote_id: { kind: 'string' },
                    remote_name: { kind: 'string' },
                    email: { kind: 'string' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    import_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import', 'authors', input.inPorts.author_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get large files',
        name: 'Github: Get large files',
        description:
            'List files larger than 100MB found during the import\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.\n',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        ref_name: { kind: 'string' },
                        path: { kind: 'string' },
                        oid: { kind: 'string' },
                        size: { kind: 'number' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import', 'large_files'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update Git LFS preference',
        name: 'Github: Update Git LFS preference',
        description:
            'You can import repositories from Subversion, Mercurial, and TFS that include files larger than 100MB. This ability\nis powered by [Git LFS](https://git-lfs.com).\n\nYou can learn more about our LFS feature and working with large files [on our help\nsite](https://docs.github.com/repositories/working-with-files/managing-large-files).\n\n**Warning:** Support for importing Mercurial, Subversion and Team Foundation Version Control repositories will end\non October 17, 2023. For more details, see [changelog](https://gh.io/github-importer-non-git-eol). In the coming weeks, we will update\nthese docs to reflect relevant changes to the API and will contact all integrators using the "Source imports" API.\n',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { use_lfs: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    vcs: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    use_lfs: { kind: 'boolean' },
                    vcs_url: { kind: 'string' },
                    svc_root: { kind: 'string' },
                    tfvc_project: { kind: 'string' },
                    status: { kind: 'string' },
                    status_text: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    failed_step: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    error_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    import_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    commit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    push_percent: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    has_large_files: { kind: 'boolean' },
                    large_files_size: { kind: 'number' },
                    large_files_count: { kind: 'number' },
                    project_choices: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { vcs: { kind: 'string' }, tfvc_project: { kind: 'string' }, human_name: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    message: { kind: 'string' },
                    authors_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    authors_url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    svn_root: { kind: 'string' },
                },
                optionalProperties: [
                    'use_lfs',
                    'svc_root',
                    'tfvc_project',
                    'status_text',
                    'failed_step',
                    'error_message',
                    'import_percent',
                    'commit_count',
                    'push_percent',
                    'has_large_files',
                    'large_files_size',
                    'large_files_count',
                    'project_choices',
                    'message',
                    'authors_count',
                    'svn_root',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'import', 'lfs'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository installation for the authenticated app',
        name: 'Github: Get a repository installation for the authenticated app',
        description:
            "Enables an authenticated GitHub App to find the repository's installation information. The installation's account type will be either an organization or a user account, depending which account the repository belongs to.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    account: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    {
                                        kind: 'object',
                                        properties: {
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            avatar_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['description', 'website_url'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository_selection: { kind: 'string' },
                    access_tokens_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    app_id: { kind: 'number' },
                    target_id: { kind: 'number' },
                    target_type: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    has_multiple_single_files: { kind: 'boolean' },
                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                    app_slug: { kind: 'string' },
                    suspended_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'installation'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get interaction restrictions for a repository',
        name: 'Github: Get interaction restrictions for a repository',
        description:
            'Shows which type of GitHub user can interact with this repository and when the restriction expires. If there are no restrictions, you will see an empty response.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { limit: { kind: 'string' }, origin: { kind: 'string' }, expires_at: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'interaction-limits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set interaction restrictions for a repository',
        name: 'Github: Set interaction restrictions for a repository',
        description:
            'Temporarily restricts interactions to a certain type of GitHub user within the given repository. You must have owner or admin access to set these restrictions. If an interaction limit is set for the user or organization that owns this repository, you will receive a `409 Conflict` response and will not be able to use this endpoint to change the interaction limit for a single repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { limit: { kind: 'string' }, expiry: { kind: 'string' } },
                optionalProperties: ['expiry'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { limit: { kind: 'string' }, origin: { kind: 'string' }, expires_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'interaction-limits'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove interaction restrictions for a repository',
        name: 'Github: Remove interaction restrictions for a repository',
        description:
            'Removes all interaction restrictions from the given repository. You must have owner or admin access to remove restrictions. If the interaction limit is set for the user or organization that owns this repository, you will receive a `409 Conflict` response and will not be able to use this endpoint to change the interaction limit for a single repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'interaction-limits'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository invitations',
        name: 'Github: List repository invitations',
        description:
            'When authenticating as a user with admin rights to a repository, this endpoint will list all currently open repository invitations.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                        invitee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        inviter: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        permissions: { kind: 'string' },
                        created_at: { kind: 'string' },
                        expired: { kind: 'boolean' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                    },
                    optionalProperties: ['expired'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'invitations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository invitation',
        name: 'Github: Delete a repository invitation',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            invitation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'invitations', input.inPorts.invitation_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a repository invitation',
        name: 'Github: Update a repository invitation',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            invitation_id: { kind: 'number' },
            body: { kind: 'object', properties: { permissions: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    invitee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    inviter: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    permissions: { kind: 'string' },
                    created_at: { kind: 'string' },
                    expired: { kind: 'boolean' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                },
                optionalProperties: ['expired'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'invitations', input.inPorts.invitation_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository issues',
        name: 'Github: List repository issues',
        description:
            'List issues in a repository. Only open issues will be listed.\n\n**Note**: GitHub\'s REST API considers every pull request an issue, but not every issue is a pull request. For this\nreason, "Issues" endpoints may return both issues and pull requests in the response. You can identify pull requests by\nthe `pull_request` key. Be aware that the `id` of a pull request returned from "Issues" endpoints will be an _issue id_. To find out the pull\nrequest id, use the "[List pull requests](https://docs.github.com/rest/pulls/pulls#list-pull-requests)" endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            milestone: { kind: 'string' },
            state: { kind: 'string' },
            assignee: { kind: 'string' },
            creator: { kind: 'string' },
            mentioned: { kind: 'string' },
            labels: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: [
            'milestone',
            'state',
            'assignee',
            'creator',
            'mentioned',
            'labels',
            'sort',
            'direction',
            'since',
            'per_page',
            'page',
        ],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        title: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        labels: {
                            kind: 'array',
                            subType: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'string' },
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            default: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                        },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assignees: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        milestone: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        title: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        creator: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        open_issues: { kind: 'number' },
                                        closed_issues: { kind: 'number' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        locked: { kind: 'boolean' },
                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        pull_request: {
                            kind: 'object',
                            properties: {
                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['merged_at'],
                            restriction: 'open',
                        },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        draft: { kind: 'boolean' },
                        closed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                        timeline_url: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        author_association: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [
                        'state_reason',
                        'body',
                        'assignees',
                        'active_lock_reason',
                        'pull_request',
                        'draft',
                        'closed_by',
                        'body_html',
                        'body_text',
                        'timeline_url',
                        'repository',
                        'performed_via_github_app',
                        'reactions',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            milestone: input.inPorts.milestone,
                            state: input.inPorts.state,
                            assignee: input.inPorts.assignee,
                            creator: input.inPorts.creator,
                            mentioned: input.inPorts.mentioned,
                            labels: input.inPorts.labels,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an issue',
        name: 'Github: Create an issue',
        description:
            'Any user with pull access to a repository can create an issue. If [issues are disabled in the repository](https://docs.github.com/articles/disabling-issues/), the API returns a `410 Gone` status.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    title: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] },
                    body: { kind: 'string' },
                    assignee: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    milestone: {
                        kind: 'union',
                        subTypes: [{ kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] }, { kind: 'null' }],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignees: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['body', 'assignee', 'milestone', 'labels', 'assignees'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    title: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        default: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    locked: { kind: 'boolean' },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    pull_request: {
                        kind: 'object',
                        properties: {
                            merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['merged_at'],
                        restriction: 'open',
                    },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    closed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    timeline_url: { kind: 'string' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'state_reason',
                    'body',
                    'assignees',
                    'active_lock_reason',
                    'pull_request',
                    'draft',
                    'closed_by',
                    'body_html',
                    'body_text',
                    'timeline_url',
                    'repository',
                    'performed_via_github_app',
                    'reactions',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List issue comments for a repository',
        name: 'Github: List issue comments for a repository',
        description:
            'You can use the REST API to list comments on issues and pull requests for a repository. Every pull request is an issue, but not every issue is a pull request.\n\nBy default, issue comments are ordered by ascending ID.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        body: { kind: 'string' },
                        body_text: { kind: 'string' },
                        body_html: { kind: 'string' },
                        html_url: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        issue_url: { kind: 'string' },
                        author_association: { kind: 'string' },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an issue comment',
        name: 'Github: Get an issue comment',
        description:
            'You can use the REST API to get comments on issues and pull requests. Every pull request is an issue, but not every issue is a pull request.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    body_text: { kind: 'string' },
                    body_html: { kind: 'string' },
                    html_url: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', 'comments', input.inPorts.comment_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an issue comment',
        name: 'Github: Delete an issue comment',
        description:
            'You can use the REST API to delete comments on issues and pull requests. Every pull request is an issue, but not every issue is a pull request.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', 'comments', input.inPorts.comment_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an issue comment',
        name: 'Github: Update an issue comment',
        description:
            'You can use the REST API to update comments on issues and pull requests. Every pull request is an issue, but not every issue is a pull request.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    body_text: { kind: 'string' },
                    body_html: { kind: 'string' },
                    html_url: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', 'comments', input.inPorts.comment_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for an issue comment',
        name: 'Github: List reactions for an issue comment',
        description: 'List the reactions to an [issue comment](https://docs.github.com/rest/reference/issues#comments).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'issues',
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for an issue comment',
        name: 'Github: Create reaction for an issue comment',
        description:
            'Create a reaction to an [issue comment](https://docs.github.com/rest/reference/issues#comments). A response with an HTTP `200` status means that you already added the reaction type to this issue comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'issues',
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an issue comment reaction',
        name: 'Github: Delete an issue comment reaction',
        description:
            '**Note:** You can also specify a repository by `repository_id` using the route `DELETE delete /repositories/:repository_id/issues/comments/:comment_id/reactions/:reaction_id`.\n\nDelete a reaction to an [issue comment](https://docs.github.com/rest/reference/issues#comments).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'issues',
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List issue events for a repository',
        name: 'Github: List issue events for a repository',
        description: 'Lists events for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        actor: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        event: { kind: 'string' },
                        commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        issue: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        label: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assigner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        review_requester: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        requested_reviewer: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        requested_team: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                slug: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                privacy: { kind: 'string' },
                                notification_setting: { kind: 'string' },
                                permission: { kind: 'string' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        admin: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                members_url: { kind: 'string' },
                                repositories_url: { kind: 'string' },
                                parent: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                permission: { kind: 'string' },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                ldap_dn: { kind: 'string' },
                                            },
                                            optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                            restriction: 'open',
                        },
                        dismissed_review: {
                            kind: 'object',
                            properties: {
                                state: { kind: 'string' },
                                review_id: { kind: 'number' },
                                dismissal_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                dismissal_commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['dismissal_commit_id'],
                            restriction: 'open',
                        },
                        milestone: {
                            kind: 'object',
                            properties: { title: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        project_card: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                id: { kind: 'number' },
                                project_url: { kind: 'string' },
                                project_id: { kind: 'number' },
                                column_name: { kind: 'string' },
                                previous_column_name: { kind: 'string' },
                            },
                            optionalProperties: ['previous_column_name'],
                            restriction: 'open',
                        },
                        rename: {
                            kind: 'object',
                            properties: { from: { kind: 'string' }, to: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        author_association: { kind: 'string' },
                        lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'issue',
                        'label',
                        'assignee',
                        'assigner',
                        'review_requester',
                        'requested_reviewer',
                        'requested_team',
                        'dismissed_review',
                        'milestone',
                        'project_card',
                        'rename',
                        'author_association',
                        'lock_reason',
                        'performed_via_github_app',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an issue event',
        name: 'Github: Get an issue event',
        description: 'Gets a single event by the event id.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            event_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    actor: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    event: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    issue: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    repository_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    title: { kind: 'string' },
                                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    user: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    labels: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'union',
                                            subTypes: [
                                                { kind: 'string' },
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        default: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            ],
                                        },
                                    },
                                    assignee: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    assignees: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    milestone: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    number: { kind: 'number' },
                                                    state: { kind: 'string' },
                                                    title: { kind: 'string' },
                                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    creator: {
                                                        kind: 'union',
                                                        subTypes: [
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    name: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    email: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    login: { kind: 'string' },
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    avatar_url: { kind: 'string' },
                                                                    gravatar_id: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    followers_url: { kind: 'string' },
                                                                    following_url: { kind: 'string' },
                                                                    gists_url: { kind: 'string' },
                                                                    starred_url: { kind: 'string' },
                                                                    subscriptions_url: { kind: 'string' },
                                                                    organizations_url: { kind: 'string' },
                                                                    repos_url: { kind: 'string' },
                                                                    events_url: { kind: 'string' },
                                                                    received_events_url: { kind: 'string' },
                                                                    type: { kind: 'string' },
                                                                    site_admin: { kind: 'boolean' },
                                                                    starred_at: { kind: 'string' },
                                                                },
                                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                                restriction: 'open',
                                                            },
                                                            { kind: 'null' },
                                                        ],
                                                    },
                                                    open_issues: { kind: 'number' },
                                                    closed_issues: { kind: 'number' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    locked: { kind: 'boolean' },
                                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    comments: { kind: 'number' },
                                    pull_request: {
                                        kind: 'object',
                                        properties: {
                                            merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: ['merged_at'],
                                        restriction: 'open',
                                    },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    draft: { kind: 'boolean' },
                                    closed_by: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    body_html: { kind: 'string' },
                                    body_text: { kind: 'string' },
                                    timeline_url: { kind: 'string' },
                                    repository: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            license: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            key: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            node_id: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['html_url'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            organization: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            forks: { kind: 'number' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                },
                                                optionalProperties: ['triage', 'maintain'],
                                                restriction: 'open',
                                            },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            has_discussions: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            template_repository: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            full_name: { kind: 'string' },
                                                            owner: {
                                                                kind: 'object',
                                                                properties: {
                                                                    login: { kind: 'string' },
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    avatar_url: { kind: 'string' },
                                                                    gravatar_id: { kind: 'string' },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    followers_url: { kind: 'string' },
                                                                    following_url: { kind: 'string' },
                                                                    gists_url: { kind: 'string' },
                                                                    starred_url: { kind: 'string' },
                                                                    subscriptions_url: { kind: 'string' },
                                                                    organizations_url: { kind: 'string' },
                                                                    repos_url: { kind: 'string' },
                                                                    events_url: { kind: 'string' },
                                                                    received_events_url: { kind: 'string' },
                                                                    type: { kind: 'string' },
                                                                    site_admin: { kind: 'boolean' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            private: { kind: 'boolean' },
                                                            html_url: { kind: 'string' },
                                                            description: { kind: 'string' },
                                                            fork: { kind: 'boolean' },
                                                            url: { kind: 'string' },
                                                            archive_url: { kind: 'string' },
                                                            assignees_url: { kind: 'string' },
                                                            blobs_url: { kind: 'string' },
                                                            branches_url: { kind: 'string' },
                                                            collaborators_url: { kind: 'string' },
                                                            comments_url: { kind: 'string' },
                                                            commits_url: { kind: 'string' },
                                                            compare_url: { kind: 'string' },
                                                            contents_url: { kind: 'string' },
                                                            contributors_url: { kind: 'string' },
                                                            deployments_url: { kind: 'string' },
                                                            downloads_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            forks_url: { kind: 'string' },
                                                            git_commits_url: { kind: 'string' },
                                                            git_refs_url: { kind: 'string' },
                                                            git_tags_url: { kind: 'string' },
                                                            git_url: { kind: 'string' },
                                                            issue_comment_url: { kind: 'string' },
                                                            issue_events_url: { kind: 'string' },
                                                            issues_url: { kind: 'string' },
                                                            keys_url: { kind: 'string' },
                                                            labels_url: { kind: 'string' },
                                                            languages_url: { kind: 'string' },
                                                            merges_url: { kind: 'string' },
                                                            milestones_url: { kind: 'string' },
                                                            notifications_url: { kind: 'string' },
                                                            pulls_url: { kind: 'string' },
                                                            releases_url: { kind: 'string' },
                                                            ssh_url: { kind: 'string' },
                                                            stargazers_url: { kind: 'string' },
                                                            statuses_url: { kind: 'string' },
                                                            subscribers_url: { kind: 'string' },
                                                            subscription_url: { kind: 'string' },
                                                            tags_url: { kind: 'string' },
                                                            teams_url: { kind: 'string' },
                                                            trees_url: { kind: 'string' },
                                                            clone_url: { kind: 'string' },
                                                            mirror_url: { kind: 'string' },
                                                            hooks_url: { kind: 'string' },
                                                            svn_url: { kind: 'string' },
                                                            homepage: { kind: 'string' },
                                                            language: { kind: 'string' },
                                                            forks_count: { kind: 'number' },
                                                            stargazers_count: { kind: 'number' },
                                                            watchers_count: { kind: 'number' },
                                                            size: { kind: 'number' },
                                                            default_branch: { kind: 'string' },
                                                            open_issues_count: { kind: 'number' },
                                                            is_template: { kind: 'boolean' },
                                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                                            has_issues: { kind: 'boolean' },
                                                            has_projects: { kind: 'boolean' },
                                                            has_wiki: { kind: 'boolean' },
                                                            has_pages: { kind: 'boolean' },
                                                            has_downloads: { kind: 'boolean' },
                                                            archived: { kind: 'boolean' },
                                                            disabled: { kind: 'boolean' },
                                                            visibility: { kind: 'string' },
                                                            pushed_at: { kind: 'string' },
                                                            created_at: { kind: 'string' },
                                                            updated_at: { kind: 'string' },
                                                            permissions: {
                                                                kind: 'object',
                                                                properties: {
                                                                    admin: { kind: 'boolean' },
                                                                    maintain: { kind: 'boolean' },
                                                                    push: { kind: 'boolean' },
                                                                    triage: { kind: 'boolean' },
                                                                    pull: { kind: 'boolean' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            allow_rebase_merge: { kind: 'boolean' },
                                                            temp_clone_token: { kind: 'string' },
                                                            allow_squash_merge: { kind: 'boolean' },
                                                            allow_auto_merge: { kind: 'boolean' },
                                                            delete_branch_on_merge: { kind: 'boolean' },
                                                            allow_update_branch: { kind: 'boolean' },
                                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                                            squash_merge_commit_title: { kind: 'string' },
                                                            squash_merge_commit_message: { kind: 'string' },
                                                            merge_commit_title: { kind: 'string' },
                                                            merge_commit_message: { kind: 'string' },
                                                            allow_merge_commit: { kind: 'boolean' },
                                                            subscribers_count: { kind: 'number' },
                                                            network_count: { kind: 'number' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            allow_forking: { kind: 'boolean' },
                                            web_commit_signoff_required: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                            open_issues: { kind: 'number' },
                                            watchers: { kind: 'number' },
                                            master_branch: { kind: 'string' },
                                            starred_at: { kind: 'string' },
                                            anonymous_access_enabled: { kind: 'boolean' },
                                        },
                                        optionalProperties: [
                                            'organization',
                                            'permissions',
                                            'is_template',
                                            'topics',
                                            'has_discussions',
                                            'visibility',
                                            'allow_rebase_merge',
                                            'template_repository',
                                            'temp_clone_token',
                                            'allow_squash_merge',
                                            'allow_auto_merge',
                                            'delete_branch_on_merge',
                                            'allow_update_branch',
                                            'use_squash_pr_title_as_default',
                                            'squash_merge_commit_title',
                                            'squash_merge_commit_message',
                                            'merge_commit_title',
                                            'merge_commit_message',
                                            'allow_merge_commit',
                                            'allow_forking',
                                            'web_commit_signoff_required',
                                            'subscribers_count',
                                            'network_count',
                                            'master_branch',
                                            'starred_at',
                                            'anonymous_access_enabled',
                                        ],
                                        restriction: 'open',
                                    },
                                    performed_via_github_app: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    slug: { kind: 'string' },
                                                    node_id: { kind: 'string' },
                                                    owner: {
                                                        kind: 'union',
                                                        subTypes: [
                                                            {
                                                                kind: 'object',
                                                                properties: {
                                                                    name: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    email: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    login: { kind: 'string' },
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    avatar_url: { kind: 'string' },
                                                                    gravatar_id: {
                                                                        kind: 'union',
                                                                        subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                    },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    followers_url: { kind: 'string' },
                                                                    following_url: { kind: 'string' },
                                                                    gists_url: { kind: 'string' },
                                                                    starred_url: { kind: 'string' },
                                                                    subscriptions_url: { kind: 'string' },
                                                                    organizations_url: { kind: 'string' },
                                                                    repos_url: { kind: 'string' },
                                                                    events_url: { kind: 'string' },
                                                                    received_events_url: { kind: 'string' },
                                                                    type: { kind: 'string' },
                                                                    site_admin: { kind: 'boolean' },
                                                                    starred_at: { kind: 'string' },
                                                                },
                                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                                restriction: 'open',
                                                            },
                                                            { kind: 'null' },
                                                        ],
                                                    },
                                                    name: { kind: 'string' },
                                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    external_url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            issues: { kind: 'string' },
                                                            checks: { kind: 'string' },
                                                            metadata: { kind: 'string' },
                                                            contents: { kind: 'string' },
                                                            deployments: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    events: { kind: 'array', subType: { kind: 'string' } },
                                                    installations_count: { kind: 'number' },
                                                    client_id: { kind: 'string' },
                                                    client_secret: { kind: 'string' },
                                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    pem: { kind: 'string' },
                                                },
                                                optionalProperties: [
                                                    'slug',
                                                    'installations_count',
                                                    'client_id',
                                                    'client_secret',
                                                    'webhook_secret',
                                                    'pem',
                                                ],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    author_association: { kind: 'string' },
                                    reactions: {
                                        kind: 'object',
                                        properties: {
                                            url: { kind: 'string' },
                                            total_count: { kind: 'number' },
                                            '+1': { kind: 'number' },
                                            '-1': { kind: 'number' },
                                            laugh: { kind: 'number' },
                                            confused: { kind: 'number' },
                                            heart: { kind: 'number' },
                                            hooray: { kind: 'number' },
                                            eyes: { kind: 'number' },
                                            rocket: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [
                                    'state_reason',
                                    'body',
                                    'assignees',
                                    'active_lock_reason',
                                    'pull_request',
                                    'draft',
                                    'closed_by',
                                    'body_html',
                                    'body_text',
                                    'timeline_url',
                                    'repository',
                                    'performed_via_github_app',
                                    'reactions',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    label: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assigner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    review_requester: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_reviewer: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_team: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            slug: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            privacy: { kind: 'string' },
                            notification_setting: { kind: 'string' },
                            permission: { kind: 'string' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    admin: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            repositories_url: { kind: 'string' },
                            parent: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            members_url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            permission: { kind: 'string' },
                                            privacy: { kind: 'string' },
                                            notification_setting: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            repositories_url: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            ldap_dn: { kind: 'string' },
                                        },
                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                        restriction: 'open',
                    },
                    dismissed_review: {
                        kind: 'object',
                        properties: {
                            state: { kind: 'string' },
                            review_id: { kind: 'number' },
                            dismissal_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            dismissal_commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['dismissal_commit_id'],
                        restriction: 'open',
                    },
                    milestone: { kind: 'object', properties: { title: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    project_card: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            id: { kind: 'number' },
                            project_url: { kind: 'string' },
                            project_id: { kind: 'number' },
                            column_name: { kind: 'string' },
                            previous_column_name: { kind: 'string' },
                        },
                        optionalProperties: ['previous_column_name'],
                        restriction: 'open',
                    },
                    rename: {
                        kind: 'object',
                        properties: { from: { kind: 'string' }, to: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author_association: { kind: 'string' },
                    lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [
                    'issue',
                    'label',
                    'assignee',
                    'assigner',
                    'review_requester',
                    'requested_reviewer',
                    'requested_team',
                    'dismissed_review',
                    'milestone',
                    'project_card',
                    'rename',
                    'author_association',
                    'lock_reason',
                    'performed_via_github_app',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', 'events', input.inPorts.event_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an issue',
        name: 'Github: Get an issue',
        description:
            'The API returns a [`301 Moved Permanently` status](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-redirects-redirects) if the issue was\n[transferred](https://docs.github.com/articles/transferring-an-issue-to-another-repository/) to another repository. If\nthe issue was transferred to or deleted from a repository where the authenticated user lacks read access, the API\nreturns a `404 Not Found` status. If the issue was deleted from a repository where the authenticated user has read\naccess, the API returns a `410 Gone` status. To receive webhook events for transferred and deleted issues, subscribe\nto the [`issues`](https://docs.github.com/webhooks/event-payloads/#issues) webhook.\n\n**Note**: GitHub\'s REST API considers every pull request an issue, but not every issue is a pull request. For this\nreason, "Issues" endpoints may return both issues and pull requests in the response. You can identify pull requests by\nthe `pull_request` key. Be aware that the `id` of a pull request returned from "Issues" endpoints will be an _issue id_. To find out the pull\nrequest id, use the "[List pull requests](https://docs.github.com/rest/pulls/pulls#list-pull-requests)" endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    title: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        default: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    locked: { kind: 'boolean' },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    pull_request: {
                        kind: 'object',
                        properties: {
                            merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['merged_at'],
                        restriction: 'open',
                    },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    closed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    timeline_url: { kind: 'string' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'state_reason',
                    'body',
                    'assignees',
                    'active_lock_reason',
                    'pull_request',
                    'draft',
                    'closed_by',
                    'body_html',
                    'body_text',
                    'timeline_url',
                    'repository',
                    'performed_via_github_app',
                    'reactions',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an issue',
        name: 'Github: Update an issue',
        description: 'Issue owners and users with push access can edit an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    title: {
                        kind: 'union',
                        subTypes: [{ kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] }, { kind: 'null' }],
                    },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    assignee: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    state: { kind: 'string' },
                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    milestone: {
                        kind: 'union',
                        subTypes: [{ kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'number' }] }, { kind: 'null' }],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignees: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    title: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        default: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    locked: { kind: 'boolean' },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    pull_request: {
                        kind: 'object',
                        properties: {
                            merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['merged_at'],
                        restriction: 'open',
                    },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    closed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    timeline_url: { kind: 'string' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'state_reason',
                    'body',
                    'assignees',
                    'active_lock_reason',
                    'pull_request',
                    'draft',
                    'closed_by',
                    'body_html',
                    'body_text',
                    'timeline_url',
                    'repository',
                    'performed_via_github_app',
                    'reactions',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add assignees to an issue',
        name: 'Github: Add assignees to an issue',
        description: 'Adds up to 10 assignees to an issue. Users already assigned to an issue are not replaced.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { assignees: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    title: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        default: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    locked: { kind: 'boolean' },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    pull_request: {
                        kind: 'object',
                        properties: {
                            merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['merged_at'],
                        restriction: 'open',
                    },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    closed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    timeline_url: { kind: 'string' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'state_reason',
                    'body',
                    'assignees',
                    'active_lock_reason',
                    'pull_request',
                    'draft',
                    'closed_by',
                    'body_html',
                    'body_text',
                    'timeline_url',
                    'repository',
                    'performed_via_github_app',
                    'reactions',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'assignees'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove assignees from an issue',
        name: 'Github: Remove assignees from an issue',
        description: 'Removes one or more assignees from an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { assignees: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    title: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'union',
                            subTypes: [
                                { kind: 'string' },
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        default: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    locked: { kind: 'boolean' },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    comments: { kind: 'number' },
                    pull_request: {
                        kind: 'object',
                        properties: {
                            merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['merged_at'],
                        restriction: 'open',
                    },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    closed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    timeline_url: { kind: 'string' },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                },
                                optionalProperties: ['triage', 'maintain'],
                                restriction: 'open',
                            },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'string' },
                                            language: { kind: 'string' },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'string' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'string' },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            starred_at: { kind: 'string' },
                            anonymous_access_enabled: { kind: 'boolean' },
                        },
                        optionalProperties: [
                            'organization',
                            'permissions',
                            'is_template',
                            'topics',
                            'has_discussions',
                            'visibility',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_merge_commit',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'subscribers_count',
                            'network_count',
                            'master_branch',
                            'starred_at',
                            'anonymous_access_enabled',
                        ],
                        restriction: 'open',
                    },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    author_association: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [
                    'state_reason',
                    'body',
                    'assignees',
                    'active_lock_reason',
                    'pull_request',
                    'draft',
                    'closed_by',
                    'body_html',
                    'body_text',
                    'timeline_url',
                    'repository',
                    'performed_via_github_app',
                    'reactions',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'assignees'].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a user can be assigned to a issue',
        name: 'Github: Check if a user can be assigned to a issue',
        description:
            'Checks if a user has permission to be assigned to a specific issue.\n\nIf the `assignee` can be assigned to this issue, a `204` status code with no content is returned.\n\nOtherwise a `404` status code is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            assignee: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'issues',
                        input.inPorts.issue_number,
                        'assignees',
                        input.inPorts.assignee,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List issue comments',
        name: 'Github: List issue comments',
        description:
            'You can use the REST API to list comments on issues and pull requests. Every pull request is an issue, but not every issue is a pull request.\n\nIssue comments are ordered by ascending ID.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        body: { kind: 'string' },
                        body_text: { kind: 'string' },
                        body_html: { kind: 'string' },
                        html_url: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        issue_url: { kind: 'string' },
                        author_association: { kind: 'string' },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an issue comment',
        name: 'Github: Create an issue comment',
        description:
            '\nYou can use the REST API to create comments on issues and pull requests. Every pull request is an issue, but not every issue is a pull request.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications).\nCreating content too quickly using this endpoint may result in secondary rate limiting.\nSee "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)"\nand "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)"\nfor details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    body: { kind: 'string' },
                    body_text: { kind: 'string' },
                    body_html: { kind: 'string' },
                    html_url: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    performed_via_github_app: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    slug: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                    owner: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    external_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            issues: { kind: 'string' },
                                            checks: { kind: 'string' },
                                            metadata: { kind: 'string' },
                                            contents: { kind: 'string' },
                                            deployments: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    events: { kind: 'array', subType: { kind: 'string' } },
                                    installations_count: { kind: 'number' },
                                    client_id: { kind: 'string' },
                                    client_secret: { kind: 'string' },
                                    webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    pem: { kind: 'string' },
                                },
                                optionalProperties: ['slug', 'installations_count', 'client_id', 'client_secret', 'webhook_secret', 'pem'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'comments'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List issue events',
        name: 'Github: List issue events',
        description: 'Lists all events for an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'union',
                    subTypes: [
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                label: {
                                    kind: 'object',
                                    properties: { name: { kind: 'string' }, color: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                label: {
                                    kind: 'object',
                                    properties: { name: { kind: 'string' }, color: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                assignee: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                assigner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                assignee: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                assigner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                milestone: {
                                    kind: 'object',
                                    properties: { title: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                milestone: {
                                    kind: 'object',
                                    properties: { title: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                rename: {
                                    kind: 'object',
                                    properties: { from: { kind: 'string' }, to: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                review_requester: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                requested_team: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                                requested_reviewer: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['requested_team', 'requested_reviewer'],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                review_requester: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                requested_team: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                                requested_reviewer: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['requested_team', 'requested_reviewer'],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                dismissed_review: {
                                    kind: 'object',
                                    properties: {
                                        state: { kind: 'string' },
                                        review_id: { kind: 'number' },
                                        dismissal_message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        dismissal_commit_id: { kind: 'string' },
                                    },
                                    optionalProperties: ['dismissal_commit_id'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                project_card: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        url: { kind: 'string' },
                                        project_id: { kind: 'number' },
                                        project_url: { kind: 'string' },
                                        column_name: { kind: 'string' },
                                        previous_column_name: { kind: 'string' },
                                    },
                                    optionalProperties: ['previous_column_name'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['project_card'],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                project_card: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        url: { kind: 'string' },
                                        project_id: { kind: 'number' },
                                        project_url: { kind: 'string' },
                                        column_name: { kind: 'string' },
                                        previous_column_name: { kind: 'string' },
                                    },
                                    optionalProperties: ['previous_column_name'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['project_card'],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                project_card: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        url: { kind: 'string' },
                                        project_id: { kind: 'number' },
                                        project_url: { kind: 'string' },
                                        column_name: { kind: 'string' },
                                        previous_column_name: { kind: 'string' },
                                    },
                                    optionalProperties: ['previous_column_name'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['project_card'],
                            restriction: 'open',
                        },
                        {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                actor: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                event: { kind: 'string' },
                                commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                commit_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                project_card: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        url: { kind: 'string' },
                                        project_id: { kind: 'number' },
                                        project_url: { kind: 'string' },
                                        column_name: { kind: 'string' },
                                        previous_column_name: { kind: 'string' },
                                    },
                                    optionalProperties: ['previous_column_name'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['project_card'],
                            restriction: 'open',
                        },
                    ],
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List labels for an issue',
        name: 'Github: List labels for an issue',
        description: 'Lists all labels for an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        color: { kind: 'string' },
                        default: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'labels'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add labels to an issue',
        name: 'Github: Add labels to an issue',
        description: 'Adds labels to an issue. If you provide an empty array of labels, all labels are removed from the issue. ',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { labels: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                    {
                        kind: 'object',
                        properties: {
                            labels: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    {
                        kind: 'array',
                        subType: { kind: 'object', properties: { name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    },
                    { kind: 'string' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        color: { kind: 'string' },
                        default: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'labels'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set labels for an issue',
        name: 'Github: Set labels for an issue',
        description: 'Removes any previous labels and sets the new labels for an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { labels: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                    {
                        kind: 'object',
                        properties: {
                            labels: {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    {
                        kind: 'array',
                        subType: { kind: 'object', properties: { name: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    },
                    { kind: 'string' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        color: { kind: 'string' },
                        default: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'labels'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove all labels from an issue',
        name: 'Github: Remove all labels from an issue',
        description: 'Removes all labels from an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'labels'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a label from an issue',
        name: 'Github: Remove a label from an issue',
        description:
            'Removes the specified label from the issue, and returns the remaining labels on the issue. This endpoint returns a `404 Not Found` status if the label does not exist.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        color: { kind: 'string' },
                        default: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'issues',
                        input.inPorts.issue_number,
                        'labels',
                        input.inPorts.name,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Lock an issue',
        name: 'Github: Lock an issue',
        description:
            'Users with push access can lock an issue or pull request\'s conversation.\n\nNote that, if you choose not to pass any parameters, you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    { kind: 'object', properties: { lock_reason: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'lock'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unlock an issue',
        name: 'Github: Unlock an issue',
        description: "Users with push access can unlock an issue's conversation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'lock'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for an issue',
        name: 'Github: List reactions for an issue',
        description: 'List the reactions to an [issue](https://docs.github.com/rest/reference/issues).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'reactions'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for an issue',
        name: 'Github: Create reaction for an issue',
        description:
            'Create a reaction to an [issue](https://docs.github.com/rest/reference/issues/). A response with an HTTP `200` status means that you already added the reaction type to this issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'reactions'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an issue reaction',
        name: 'Github: Delete an issue reaction',
        description:
            '**Note:** You can also specify a repository by `repository_id` using the route `DELETE /repositories/:repository_id/issues/:issue_number/reactions/:reaction_id`.\n\nDelete a reaction to an [issue](https://docs.github.com/rest/reference/issues/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'issues',
                        input.inPorts.issue_number,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List timeline events for an issue',
        name: 'Github: List timeline events for an issue',
        description: 'List all timeline events for an issue.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            issue_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: { Response: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'issues', input.inPorts.issue_number, 'timeline'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List deploy keys',
        name: 'Github: List deploy keys',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        key: { kind: 'string' },
                        url: { kind: 'string' },
                        title: { kind: 'string' },
                        verified: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        read_only: { kind: 'boolean' },
                        added_by: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        last_used: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['added_by', 'last_used'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a deploy key',
        name: 'Github: Create a deploy key',
        description: 'You can create a read-only deploy key.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, key: { kind: 'string' }, read_only: { kind: 'boolean' } },
                optionalProperties: ['title', 'read_only'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    key: { kind: 'string' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    verified: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    read_only: { kind: 'boolean' },
                    added_by: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_used: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['added_by', 'last_used'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'keys'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a deploy key',
        name: 'Github: Get a deploy key',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    key: { kind: 'string' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    verified: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    read_only: { kind: 'boolean' },
                    added_by: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_used: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['added_by', 'last_used'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'keys', input.inPorts.key_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a deploy key',
        name: 'Github: Delete a deploy key',
        description: 'Deploy keys are immutable. If you need to update a key, remove the key and create a new one instead.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'keys', input.inPorts.key_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List labels for a repository',
        name: 'Github: List labels for a repository',
        description: 'Lists all labels for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        color: { kind: 'string' },
                        default: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'labels'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a label',
        name: 'Github: Create a label',
        description:
            'Creates a label for the specified repository with the given name and color. The name and color parameters are required. The color must be a valid [hexadecimal color code](http://www.color-hex.com/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, color: { kind: 'string' }, description: { kind: 'string' } },
                optionalProperties: ['color', 'description'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    name: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    color: { kind: 'string' },
                    default: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'labels'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a label',
        name: 'Github: Get a label',
        description: 'Gets a label using the given name.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    name: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    color: { kind: 'string' },
                    default: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'labels', input.inPorts.name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a label',
        name: 'Github: Delete a label',
        description: 'Deletes a label using the given label name.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'labels', input.inPorts.name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a label',
        name: 'Github: Update a label',
        description: 'Updates a label using the given label name.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { new_name: { kind: 'string' }, color: { kind: 'string' }, description: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    name: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    color: { kind: 'string' },
                    default: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'labels', input.inPorts.name].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository languages',
        name: 'Github: List repository languages',
        description:
            'Lists languages for the specified repository. The value shown for each language is the number of bytes of code written in that language.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'languages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Enable Git LFS for a repository',
        name: 'Github: Enable Git LFS for a repository',
        description: 'Enables Git LFS for a repository. Access tokens must have the `admin:enterprise` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'lfs'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Disable Git LFS for a repository',
        name: 'Github: Disable Git LFS for a repository',
        description: 'Disables Git LFS for a repository. Access tokens must have the `admin:enterprise` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'lfs'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the license for a repository',
        name: 'Github: Get the license for a repository',
        description:
            "This method returns the contents of the repository's license file, if one is detected.\n\nSimilar to [Get repository content](https://docs.github.com/rest/repos/contents#get-repository-content), this method also supports [custom media types](https://docs.github.com/rest/overview/media-types) for retrieving the raw license content or rendered license HTML.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    path: { kind: 'string' },
                    sha: { kind: 'string' },
                    size: { kind: 'number' },
                    url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    type: { kind: 'string' },
                    content: { kind: 'string' },
                    encoding: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            self: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'license'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Sync a fork branch with the upstream repository',
        name: 'Github: Sync a fork branch with the upstream repository',
        description: 'Sync a branch of a forked repository to keep it up-to-date with the upstream repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { branch: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { message: { kind: 'string' }, merge_type: { kind: 'string' }, base_branch: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'merge-upstream'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Merge a branch',
        name: 'Github: Merge a branch',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { base: { kind: 'string' }, head: { kind: 'string' }, commit_message: { kind: 'string' } },
                optionalProperties: ['commit_message'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    sha: { kind: 'string' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commit: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            author: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            committer: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            message: { kind: 'string' },
                            comment_count: { kind: 'number' },
                            tree: {
                                kind: 'object',
                                properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            verification: {
                                kind: 'object',
                                properties: {
                                    verified: { kind: 'boolean' },
                                    reason: { kind: 'string' },
                                    payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: ['verification'],
                        restriction: 'open',
                    },
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    committer: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    parents: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                            optionalProperties: ['html_url'],
                            restriction: 'open',
                        },
                    },
                    stats: {
                        kind: 'object',
                        properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    files: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                sha: { kind: 'string' },
                                filename: { kind: 'string' },
                                status: { kind: 'string' },
                                additions: { kind: 'number' },
                                deletions: { kind: 'number' },
                                changes: { kind: 'number' },
                                blob_url: { kind: 'string' },
                                raw_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                patch: { kind: 'string' },
                                previous_filename: { kind: 'string' },
                            },
                            optionalProperties: ['patch', 'previous_filename'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: ['stats', 'files'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'merges'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List milestones',
        name: 'Github: List milestones',
        description: 'Lists milestones for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            state: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['state', 'sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        title: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        open_issues: { kind: 'number' },
                        closed_issues: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'milestones'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a milestone',
        name: 'Github: Create a milestone',
        description: 'Creates a milestone.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    title: { kind: 'string' },
                    state: { kind: 'string' },
                    description: { kind: 'string' },
                    due_on: { kind: 'string' },
                },
                optionalProperties: ['state', 'description', 'due_on'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    title: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    open_issues: { kind: 'number' },
                    closed_issues: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'milestones'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a milestone',
        name: 'Github: Get a milestone',
        description: 'Gets a milestone using the given milestone number.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            milestone_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    title: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    open_issues: { kind: 'number' },
                    closed_issues: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'milestones', input.inPorts.milestone_number].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a milestone',
        name: 'Github: Delete a milestone',
        description: 'Deletes a milestone using the given milestone number.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            milestone_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'milestones', input.inPorts.milestone_number].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a milestone',
        name: 'Github: Update a milestone',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            milestone_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    title: { kind: 'string' },
                    state: { kind: 'string' },
                    description: { kind: 'string' },
                    due_on: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    title: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    open_issues: { kind: 'number' },
                    closed_issues: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'milestones', input.inPorts.milestone_number].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List labels for issues in a milestone',
        name: 'Github: List labels for issues in a milestone',
        description: 'Lists labels for issues in a milestone.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            milestone_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        name: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        color: { kind: 'string' },
                        default: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'milestones',
                        input.inPorts.milestone_number,
                        'labels',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository notifications for the authenticated user',
        name: 'Github: List repository notifications for the authenticated user',
        description: 'Lists all notifications for the current user in the specified repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            all: { kind: 'boolean' },
            participating: { kind: 'boolean' },
            since: { kind: 'string' },
            before: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['all', 'participating', 'since', 'before', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                        subject: {
                            kind: 'object',
                            properties: {
                                title: { kind: 'string' },
                                url: { kind: 'string' },
                                latest_comment_url: { kind: 'string' },
                                type: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        reason: { kind: 'string' },
                        unread: { kind: 'boolean' },
                        updated_at: { kind: 'string' },
                        last_read_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'notifications'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            all: input.inPorts.all,
                            participating: input.inPorts.participating,
                            since: input.inPorts.since,
                            before: input.inPorts.before,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Mark repository notifications as read',
        name: 'Github: Mark repository notifications as read',
        description:
            'Marks all notifications in a repository as "read" for the current user. If the number of notifications is too large to complete in one request, you will receive a `202 Accepted` status and GitHub will run an asynchronous process to mark notifications as "read." To check whether any "unread" notifications remain, you can use the [List repository notifications for the authenticated user](https://docs.github.com/rest/activity/notifications#list-repository-notifications-for-the-authenticated-user) endpoint and pass the query parameter `all=false`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { last_read_at: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { message: { kind: 'string' }, url: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'notifications'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a GitHub Pages site',
        name: 'Github: Get a GitHub Pages site',
        description:
            'Gets information about a GitHub Pages site.\n\nA token with the `repo` scope is required. GitHub Apps must have the `pages:read` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    cname: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    protected_domain_state: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pending_domain_unverified_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    custom_404: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    build_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    source: {
                        kind: 'object',
                        properties: { branch: { kind: 'string' }, path: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    https_certificate: {
                        kind: 'object',
                        properties: {
                            state: { kind: 'string' },
                            description: { kind: 'string' },
                            domains: { kind: 'array', subType: { kind: 'string' } },
                            expires_at: { kind: 'string' },
                        },
                        optionalProperties: ['expires_at'],
                        restriction: 'open',
                    },
                    https_enforced: { kind: 'boolean' },
                },
                optionalProperties: [
                    'protected_domain_state',
                    'pending_domain_unverified_at',
                    'html_url',
                    'build_type',
                    'source',
                    'https_certificate',
                    'https_enforced',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a GitHub Pages site',
        name: 'Github: Create a GitHub Pages site',
        description:
            'Configures a GitHub Pages site. For more information, see "[About GitHub Pages](/github/working-with-github-pages/about-github-pages)."\n\nTo use this endpoint, you must be a repository administrator, maintainer, or have the \'manage GitHub Pages settings\' permission. A token with the `repo` scope or Pages write permission is required. GitHub Apps must have the `administration:write` and `pages:write` permissions.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            build_type: { kind: 'string' },
                            source: {
                                kind: 'object',
                                properties: { branch: { kind: 'string' }, path: { kind: 'string' } },
                                optionalProperties: ['path'],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    status: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    cname: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    protected_domain_state: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pending_domain_unverified_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    custom_404: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    build_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    source: {
                        kind: 'object',
                        properties: { branch: { kind: 'string' }, path: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    public: { kind: 'boolean' },
                    https_certificate: {
                        kind: 'object',
                        properties: {
                            state: { kind: 'string' },
                            description: { kind: 'string' },
                            domains: { kind: 'array', subType: { kind: 'string' } },
                            expires_at: { kind: 'string' },
                        },
                        optionalProperties: ['expires_at'],
                        restriction: 'open',
                    },
                    https_enforced: { kind: 'boolean' },
                },
                optionalProperties: [
                    'protected_domain_state',
                    'pending_domain_unverified_at',
                    'html_url',
                    'build_type',
                    'source',
                    'https_certificate',
                    'https_enforced',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update information about a GitHub Pages site',
        name: 'Github: Update information about a GitHub Pages site',
        description:
            "Updates information for a GitHub Pages site. For more information, see \"[About GitHub Pages](/github/working-with-github-pages/about-github-pages).\n\nTo use this endpoint, you must be a repository administrator, maintainer, or have the 'manage GitHub Pages settings' permission. A token with the `repo` scope or Pages write permission is required. GitHub Apps must have the `administration:write` and `pages:write` permissions.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    cname: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    https_enforced: { kind: 'boolean' },
                    build_type: { kind: 'string' },
                    source: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'string' },
                            {
                                kind: 'object',
                                properties: { branch: { kind: 'string' }, path: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a GitHub Pages site',
        name: 'Github: Delete a GitHub Pages site',
        description:
            "Deletes a GitHub Pages site. For more information, see \"[About GitHub Pages](/github/working-with-github-pages/about-github-pages).\n\nTo use this endpoint, you must be a repository administrator, maintainer, or have the 'manage GitHub Pages settings' permission. A token with the `repo` scope or Pages write permission is required. GitHub Apps must have the `administration:write` and `pages:write` permissions.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List GitHub Pages builds',
        name: 'Github: List GitHub Pages builds',
        description:
            'Lists builts of a GitHub Pages site.\n\nA token with the `repo` scope is required. GitHub Apps must have the `pages:read` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        status: { kind: 'string' },
                        error: {
                            kind: 'object',
                            properties: { message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        pusher: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        commit: { kind: 'string' },
                        duration: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages', 'builds'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Request a GitHub Pages build',
        name: 'Github: Request a GitHub Pages build',
        description:
            'You can request that your site be built from the latest revision on the default branch. This has the same effect as pushing a commit to your default branch, but does not require an additional commit. Manually triggering page builds can be helpful when diagnosing build warnings and failures.\n\nBuild requests are limited to one concurrent build per repository and one concurrent build per requester. If you request a build while another is still in progress, the second request will be queued until the first completes.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, status: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages', 'builds'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get latest Pages build',
        name: 'Github: Get latest Pages build',
        description:
            'Gets information about the single most recent build of a GitHub Pages site.\n\nA token with the `repo` scope is required. GitHub Apps must have the `pages:read` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    status: { kind: 'string' },
                    error: {
                        kind: 'object',
                        properties: { message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pusher: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    commit: { kind: 'string' },
                    duration: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages', 'builds', 'latest'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Pages build',
        name: 'Github: Get GitHub Pages build',
        description:
            'Gets information about a GitHub Pages build.\n\nA token with the `repo` scope is required. GitHub Apps must have the `pages:read` permission.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            build_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    status: { kind: 'string' },
                    error: {
                        kind: 'object',
                        properties: { message: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pusher: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    commit: { kind: 'string' },
                    duration: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages', 'builds', input.inPorts.build_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a GitHub Pages deployment',
        name: 'Github: Create a GitHub Pages deployment',
        description:
            'Create a GitHub Pages deployment for a repository.\n\nUsers must have write permissions. GitHub Apps must have the `pages:write` permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    artifact_url: { kind: 'string' },
                    environment: { kind: 'string' },
                    pages_build_version: { kind: 'string' },
                    oidc_token: { kind: 'string' },
                },
                optionalProperties: ['environment'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { status_url: { kind: 'string' }, page_url: { kind: 'string' }, preview_url: { kind: 'string' } },
                optionalProperties: ['preview_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages', 'deployment'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a DNS health check for GitHub Pages',
        name: 'Github: Get a DNS health check for GitHub Pages',
        description:
            "Gets a health check of the DNS settings for the `CNAME` record configured for a repository's GitHub Pages.\n\nThe first request to this endpoint returns a `202 Accepted` status and starts an asynchronous background task to get the results for the domain. After the background task completes, subsequent requests to this endpoint return a `200 OK` status with the health check results in the response.\n\nTo use this endpoint, you must be a repository administrator, maintainer, or have the 'manage GitHub Pages settings' permission. A token with the `repo` scope or Pages write permission is required. GitHub Apps must have the `administrative:write` and `pages:write` permissions.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    domain: {
                        kind: 'object',
                        properties: {
                            host: { kind: 'string' },
                            uri: { kind: 'string' },
                            nameservers: { kind: 'string' },
                            dns_resolves: { kind: 'boolean' },
                            is_proxied: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_cloudflare_ip: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_fastly_ip: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_old_ip_address: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_a_record: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            has_cname_record: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            has_mx_records_present: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_valid_domain: { kind: 'boolean' },
                            is_apex_domain: { kind: 'boolean' },
                            should_be_a_record: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_cname_to_github_user_domain: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_cname_to_pages_dot_github_dot_com: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_cname_to_fastly: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_pointed_to_github_pages_ip: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_non_github_pages_ip_present: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_pages_domain: { kind: 'boolean' },
                            is_served_by_pages: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            is_valid: { kind: 'boolean' },
                            reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            responds_to_https: { kind: 'boolean' },
                            enforces_https: { kind: 'boolean' },
                            https_error: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_https_eligible: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            caa_error: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    alt_domain: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    host: { kind: 'string' },
                                    uri: { kind: 'string' },
                                    nameservers: { kind: 'string' },
                                    dns_resolves: { kind: 'boolean' },
                                    is_proxied: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_cloudflare_ip: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_fastly_ip: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_old_ip_address: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_a_record: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    has_cname_record: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    has_mx_records_present: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_valid_domain: { kind: 'boolean' },
                                    is_apex_domain: { kind: 'boolean' },
                                    should_be_a_record: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_cname_to_github_user_domain: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_cname_to_pages_dot_github_dot_com: {
                                        kind: 'union',
                                        subTypes: [{ kind: 'boolean' }, { kind: 'null' }],
                                    },
                                    is_cname_to_fastly: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_pointed_to_github_pages_ip: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_non_github_pages_ip_present: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_pages_domain: { kind: 'boolean' },
                                    is_served_by_pages: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    is_valid: { kind: 'boolean' },
                                    reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    responds_to_https: { kind: 'boolean' },
                                    enforces_https: { kind: 'boolean' },
                                    https_error: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    is_https_eligible: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                    caa_error: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pages', 'health'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository projects',
        name: 'Github: List repository projects',
        description:
            'Lists the projects in a repository. Returns a `404 Not Found` status if projects are disabled in the repository. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            state: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['state', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        owner_url: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        columns_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        organization_permission: { kind: 'string' },
                        private: { kind: 'boolean' },
                    },
                    optionalProperties: ['organization_permission', 'private'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'projects'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ state: input.inPorts.state, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository project',
        name: 'Github: Create a repository project',
        description:
            'Creates a repository project board. Returns a `410 Gone` status if projects are disabled in the repository or if the repository does not have existing classic projects. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, body: { kind: 'string' } },
                optionalProperties: ['body'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'projects'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List pull requests',
        name: 'Github: List pull requests',
        description:
            "Draft pull requests are available in public repositories with GitHub Free and GitHub Free for organizations, GitHub Pro, and legacy per-repository billing plans, and in public and private repositories with GitHub Team and GitHub Enterprise Cloud. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            state: { kind: 'string' },
            head: { kind: 'string' },
            base: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['state', 'head', 'base', 'sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        html_url: { kind: 'string' },
                        diff_url: { kind: 'string' },
                        patch_url: { kind: 'string' },
                        issue_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        review_comments_url: { kind: 'string' },
                        review_comment_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        locked: { kind: 'boolean' },
                        title: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        labels: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'string' },
                                    color: { kind: 'string' },
                                    default: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        milestone: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        title: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        creator: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        open_issues: { kind: 'number' },
                                        closed_issues: { kind: 'number' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assignees: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        requested_reviewers: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        requested_teams: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            privacy: { kind: 'string' },
                                            notification_setting: { kind: 'string' },
                                            permission: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    pull: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            members_url: { kind: 'string' },
                                            repositories_url: { kind: 'string' },
                                            parent: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            permission: { kind: 'string' },
                                                            privacy: { kind: 'string' },
                                                            notification_setting: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            repositories_url: { kind: 'string' },
                                                            slug: { kind: 'string' },
                                                            ldap_dn: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        head: {
                            kind: 'object',
                            properties: {
                                label: { kind: 'string' },
                                ref: { kind: 'string' },
                                repo: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        node_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['html_url'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        organization: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                            },
                                            optionalProperties: ['triage', 'maintain'],
                                            restriction: 'open',
                                        },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        template_repository: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        full_name: { kind: 'string' },
                                                        owner: {
                                                            kind: 'object',
                                                            properties: {
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        private: { kind: 'boolean' },
                                                        html_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        fork: { kind: 'boolean' },
                                                        url: { kind: 'string' },
                                                        archive_url: { kind: 'string' },
                                                        assignees_url: { kind: 'string' },
                                                        blobs_url: { kind: 'string' },
                                                        branches_url: { kind: 'string' },
                                                        collaborators_url: { kind: 'string' },
                                                        comments_url: { kind: 'string' },
                                                        commits_url: { kind: 'string' },
                                                        compare_url: { kind: 'string' },
                                                        contents_url: { kind: 'string' },
                                                        contributors_url: { kind: 'string' },
                                                        deployments_url: { kind: 'string' },
                                                        downloads_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        forks_url: { kind: 'string' },
                                                        git_commits_url: { kind: 'string' },
                                                        git_refs_url: { kind: 'string' },
                                                        git_tags_url: { kind: 'string' },
                                                        git_url: { kind: 'string' },
                                                        issue_comment_url: { kind: 'string' },
                                                        issue_events_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        keys_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        languages_url: { kind: 'string' },
                                                        merges_url: { kind: 'string' },
                                                        milestones_url: { kind: 'string' },
                                                        notifications_url: { kind: 'string' },
                                                        pulls_url: { kind: 'string' },
                                                        releases_url: { kind: 'string' },
                                                        ssh_url: { kind: 'string' },
                                                        stargazers_url: { kind: 'string' },
                                                        statuses_url: { kind: 'string' },
                                                        subscribers_url: { kind: 'string' },
                                                        subscription_url: { kind: 'string' },
                                                        tags_url: { kind: 'string' },
                                                        teams_url: { kind: 'string' },
                                                        trees_url: { kind: 'string' },
                                                        clone_url: { kind: 'string' },
                                                        mirror_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        svn_url: { kind: 'string' },
                                                        homepage: { kind: 'string' },
                                                        language: { kind: 'string' },
                                                        forks_count: { kind: 'number' },
                                                        stargazers_count: { kind: 'number' },
                                                        watchers_count: { kind: 'number' },
                                                        size: { kind: 'number' },
                                                        default_branch: { kind: 'string' },
                                                        open_issues_count: { kind: 'number' },
                                                        is_template: { kind: 'boolean' },
                                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                                        has_issues: { kind: 'boolean' },
                                                        has_projects: { kind: 'boolean' },
                                                        has_wiki: { kind: 'boolean' },
                                                        has_pages: { kind: 'boolean' },
                                                        has_downloads: { kind: 'boolean' },
                                                        archived: { kind: 'boolean' },
                                                        disabled: { kind: 'boolean' },
                                                        visibility: { kind: 'string' },
                                                        pushed_at: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                admin: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                pull: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        allow_rebase_merge: { kind: 'boolean' },
                                                        temp_clone_token: { kind: 'string' },
                                                        allow_squash_merge: { kind: 'boolean' },
                                                        allow_auto_merge: { kind: 'boolean' },
                                                        delete_branch_on_merge: { kind: 'boolean' },
                                                        allow_update_branch: { kind: 'boolean' },
                                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                                        squash_merge_commit_title: { kind: 'string' },
                                                        squash_merge_commit_message: { kind: 'string' },
                                                        merge_commit_title: { kind: 'string' },
                                                        merge_commit_message: { kind: 'string' },
                                                        allow_merge_commit: { kind: 'boolean' },
                                                        subscribers_count: { kind: 'number' },
                                                        network_count: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        master_branch: { kind: 'string' },
                                        starred_at: { kind: 'string' },
                                        anonymous_access_enabled: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'organization',
                                        'permissions',
                                        'is_template',
                                        'topics',
                                        'has_discussions',
                                        'visibility',
                                        'allow_rebase_merge',
                                        'template_repository',
                                        'temp_clone_token',
                                        'allow_squash_merge',
                                        'allow_auto_merge',
                                        'delete_branch_on_merge',
                                        'allow_update_branch',
                                        'use_squash_pr_title_as_default',
                                        'squash_merge_commit_title',
                                        'squash_merge_commit_message',
                                        'merge_commit_title',
                                        'merge_commit_message',
                                        'allow_merge_commit',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'subscribers_count',
                                        'network_count',
                                        'master_branch',
                                        'starred_at',
                                        'anonymous_access_enabled',
                                    ],
                                    restriction: 'open',
                                },
                                sha: { kind: 'string' },
                                user: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        base: {
                            kind: 'object',
                            properties: {
                                label: { kind: 'string' },
                                ref: { kind: 'string' },
                                repo: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        node_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['html_url'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        organization: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                            },
                                            optionalProperties: ['triage', 'maintain'],
                                            restriction: 'open',
                                        },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        template_repository: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        full_name: { kind: 'string' },
                                                        owner: {
                                                            kind: 'object',
                                                            properties: {
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        private: { kind: 'boolean' },
                                                        html_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        fork: { kind: 'boolean' },
                                                        url: { kind: 'string' },
                                                        archive_url: { kind: 'string' },
                                                        assignees_url: { kind: 'string' },
                                                        blobs_url: { kind: 'string' },
                                                        branches_url: { kind: 'string' },
                                                        collaborators_url: { kind: 'string' },
                                                        comments_url: { kind: 'string' },
                                                        commits_url: { kind: 'string' },
                                                        compare_url: { kind: 'string' },
                                                        contents_url: { kind: 'string' },
                                                        contributors_url: { kind: 'string' },
                                                        deployments_url: { kind: 'string' },
                                                        downloads_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        forks_url: { kind: 'string' },
                                                        git_commits_url: { kind: 'string' },
                                                        git_refs_url: { kind: 'string' },
                                                        git_tags_url: { kind: 'string' },
                                                        git_url: { kind: 'string' },
                                                        issue_comment_url: { kind: 'string' },
                                                        issue_events_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        keys_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        languages_url: { kind: 'string' },
                                                        merges_url: { kind: 'string' },
                                                        milestones_url: { kind: 'string' },
                                                        notifications_url: { kind: 'string' },
                                                        pulls_url: { kind: 'string' },
                                                        releases_url: { kind: 'string' },
                                                        ssh_url: { kind: 'string' },
                                                        stargazers_url: { kind: 'string' },
                                                        statuses_url: { kind: 'string' },
                                                        subscribers_url: { kind: 'string' },
                                                        subscription_url: { kind: 'string' },
                                                        tags_url: { kind: 'string' },
                                                        teams_url: { kind: 'string' },
                                                        trees_url: { kind: 'string' },
                                                        clone_url: { kind: 'string' },
                                                        mirror_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        svn_url: { kind: 'string' },
                                                        homepage: { kind: 'string' },
                                                        language: { kind: 'string' },
                                                        forks_count: { kind: 'number' },
                                                        stargazers_count: { kind: 'number' },
                                                        watchers_count: { kind: 'number' },
                                                        size: { kind: 'number' },
                                                        default_branch: { kind: 'string' },
                                                        open_issues_count: { kind: 'number' },
                                                        is_template: { kind: 'boolean' },
                                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                                        has_issues: { kind: 'boolean' },
                                                        has_projects: { kind: 'boolean' },
                                                        has_wiki: { kind: 'boolean' },
                                                        has_pages: { kind: 'boolean' },
                                                        has_downloads: { kind: 'boolean' },
                                                        archived: { kind: 'boolean' },
                                                        disabled: { kind: 'boolean' },
                                                        visibility: { kind: 'string' },
                                                        pushed_at: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                admin: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                pull: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        allow_rebase_merge: { kind: 'boolean' },
                                                        temp_clone_token: { kind: 'string' },
                                                        allow_squash_merge: { kind: 'boolean' },
                                                        allow_auto_merge: { kind: 'boolean' },
                                                        delete_branch_on_merge: { kind: 'boolean' },
                                                        allow_update_branch: { kind: 'boolean' },
                                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                                        squash_merge_commit_title: { kind: 'string' },
                                                        squash_merge_commit_message: { kind: 'string' },
                                                        merge_commit_title: { kind: 'string' },
                                                        merge_commit_message: { kind: 'string' },
                                                        allow_merge_commit: { kind: 'boolean' },
                                                        subscribers_count: { kind: 'number' },
                                                        network_count: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        master_branch: { kind: 'string' },
                                        starred_at: { kind: 'string' },
                                        anonymous_access_enabled: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'organization',
                                        'permissions',
                                        'is_template',
                                        'topics',
                                        'has_discussions',
                                        'visibility',
                                        'allow_rebase_merge',
                                        'template_repository',
                                        'temp_clone_token',
                                        'allow_squash_merge',
                                        'allow_auto_merge',
                                        'delete_branch_on_merge',
                                        'allow_update_branch',
                                        'use_squash_pr_title_as_default',
                                        'squash_merge_commit_title',
                                        'squash_merge_commit_message',
                                        'merge_commit_title',
                                        'merge_commit_message',
                                        'allow_merge_commit',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'subscribers_count',
                                        'network_count',
                                        'master_branch',
                                        'starred_at',
                                        'anonymous_access_enabled',
                                    ],
                                    restriction: 'open',
                                },
                                sha: { kind: 'string' },
                                user: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        _links: {
                            kind: 'object',
                            properties: {
                                comments: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                commits: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                statuses: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                issue: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                review_comments: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                review_comment: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        author_association: { kind: 'string' },
                        auto_merge: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        enabled_by: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        merge_method: { kind: 'string' },
                                        commit_title: { kind: 'string' },
                                        commit_message: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        draft: { kind: 'boolean' },
                    },
                    optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            head: input.inPorts.head,
                            base: input.inPorts.base,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a pull request',
        name: 'Github: Create a pull request',
        description:
            'Draft pull requests are available in public repositories with GitHub Free and GitHub Free for organizations, GitHub Pro, and legacy per-repository billing plans, and in public and private repositories with GitHub Team and GitHub Enterprise Cloud. For more information, see [GitHub\'s products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nTo open or update a pull request in a public repository, you must have write access to the head or the source branch. For organization-owned repositories, you must be a member of the organization that owns the repository to open or update a pull request.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    title: { kind: 'string' },
                    head: { kind: 'string' },
                    head_repo: { kind: 'string' },
                    base: { kind: 'string' },
                    body: { kind: 'string' },
                    maintainer_can_modify: { kind: 'boolean' },
                    draft: { kind: 'boolean' },
                    issue: { kind: 'number' },
                },
                optionalProperties: ['title', 'head_repo', 'body', 'maintainer_can_modify', 'draft', 'issue'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    diff_url: { kind: 'string' },
                    patch_url: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    review_comments_url: { kind: 'string' },
                    review_comment_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    locked: { kind: 'boolean' },
                    title: { kind: 'string' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                name: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                color: { kind: 'string' },
                                default: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_reviewers: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    head: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            forks_url: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    avatar_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    html_url: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    login: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            default_branch: { kind: 'string' },
                                            forks: { kind: 'number' },
                                            forks_count: { kind: 'number' },
                                            git_url: { kind: 'string' },
                                            has_downloads: { kind: 'boolean' },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_discussions: { kind: 'boolean' },
                                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            master_branch: { kind: 'string' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            open_issues: { kind: 'number' },
                                            open_issues_count: { kind: 'number' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: ['maintain', 'triage'],
                                                restriction: 'open',
                                            },
                                            temp_clone_token: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            license: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            key: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            node_id: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            pushed_at: { kind: 'string' },
                                            size: { kind: 'number' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_count: { kind: 'number' },
                                            svn_url: { kind: 'string' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            watchers: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            allow_forking: { kind: 'boolean' },
                                            is_template: { kind: 'boolean' },
                                            web_commit_signoff_required: { kind: 'boolean' },
                                        },
                                        optionalProperties: [
                                            'master_branch',
                                            'visibility',
                                            'permissions',
                                            'temp_clone_token',
                                            'allow_merge_commit',
                                            'allow_squash_merge',
                                            'allow_rebase_merge',
                                            'topics',
                                            'allow_forking',
                                            'is_template',
                                            'web_commit_signoff_required',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'object',
                                properties: {
                                    avatar_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    login: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    base: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    fork: { kind: 'boolean' },
                                    forks_url: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    hooks_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    node_id: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            avatar_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            login: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    default_branch: { kind: 'string' },
                                    forks: { kind: 'number' },
                                    forks_count: { kind: 'number' },
                                    git_url: { kind: 'string' },
                                    has_downloads: { kind: 'boolean' },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    master_branch: { kind: 'string' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    open_issues: { kind: 'number' },
                                    open_issues_count: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['maintain', 'triage'],
                                        restriction: 'open',
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    pushed_at: { kind: 'string' },
                                    size: { kind: 'number' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_count: { kind: 'number' },
                                    svn_url: { kind: 'string' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    watchers: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'is_template',
                                    'master_branch',
                                    'visibility',
                                    'permissions',
                                    'temp_clone_token',
                                    'allow_merge_commit',
                                    'allow_squash_merge',
                                    'allow_rebase_merge',
                                    'topics',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'object',
                                properties: {
                                    avatar_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    login: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            commits: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            statuses: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            issue: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comment: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author_association: { kind: 'string' },
                    auto_merge: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    enabled_by: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    merge_method: { kind: 'string' },
                                    commit_title: { kind: 'string' },
                                    commit_message: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    draft: { kind: 'boolean' },
                    merged: { kind: 'boolean' },
                    mergeable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    rebaseable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    mergeable_state: { kind: 'string' },
                    merged_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    comments: { kind: 'number' },
                    review_comments: { kind: 'number' },
                    maintainer_can_modify: { kind: 'boolean' },
                    commits: { kind: 'number' },
                    additions: { kind: 'number' },
                    deletions: { kind: 'number' },
                    changed_files: { kind: 'number' },
                },
                optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft', 'rebaseable'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List review comments in a repository',
        name: 'Github: List review comments in a repository',
        description:
            'Lists review comments for all pull requests in a repository. By default, review comments are in ascending order by ID.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        diff_hunk: { kind: 'string' },
                        path: { kind: 'string' },
                        position: { kind: 'number' },
                        original_position: { kind: 'number' },
                        commit_id: { kind: 'string' },
                        original_commit_id: { kind: 'string' },
                        in_reply_to_id: { kind: 'number' },
                        user: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        body: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        html_url: { kind: 'string' },
                        pull_request_url: { kind: 'string' },
                        author_association: { kind: 'string' },
                        _links: {
                            kind: 'object',
                            properties: {
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pull_request: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        line: { kind: 'number' },
                        original_line: { kind: 'number' },
                        side: { kind: 'string' },
                        subject_type: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                    },
                    optionalProperties: [
                        'position',
                        'original_position',
                        'in_reply_to_id',
                        'start_line',
                        'original_start_line',
                        'start_side',
                        'line',
                        'original_line',
                        'side',
                        'subject_type',
                        'reactions',
                        'body_html',
                        'body_text',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a review comment for a pull request',
        name: 'Github: Get a review comment for a pull request',
        description: 'Provides details for a review comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    diff_hunk: { kind: 'string' },
                    path: { kind: 'string' },
                    position: { kind: 'number' },
                    original_position: { kind: 'number' },
                    commit_id: { kind: 'string' },
                    original_commit_id: { kind: 'string' },
                    in_reply_to_id: { kind: 'number' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    line: { kind: 'number' },
                    original_line: { kind: 'number' },
                    side: { kind: 'string' },
                    subject_type: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                },
                optionalProperties: [
                    'position',
                    'original_position',
                    'in_reply_to_id',
                    'start_line',
                    'original_start_line',
                    'start_side',
                    'line',
                    'original_line',
                    'side',
                    'subject_type',
                    'reactions',
                    'body_html',
                    'body_text',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', 'comments', input.inPorts.comment_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a review comment for a pull request',
        name: 'Github: Delete a review comment for a pull request',
        description: 'Deletes a review comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', 'comments', input.inPorts.comment_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a review comment for a pull request',
        name: 'Github: Update a review comment for a pull request',
        description: 'Enables you to edit a review comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    diff_hunk: { kind: 'string' },
                    path: { kind: 'string' },
                    position: { kind: 'number' },
                    original_position: { kind: 'number' },
                    commit_id: { kind: 'string' },
                    original_commit_id: { kind: 'string' },
                    in_reply_to_id: { kind: 'number' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    line: { kind: 'number' },
                    original_line: { kind: 'number' },
                    side: { kind: 'string' },
                    subject_type: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                },
                optionalProperties: [
                    'position',
                    'original_position',
                    'in_reply_to_id',
                    'start_line',
                    'original_start_line',
                    'start_side',
                    'line',
                    'original_line',
                    'side',
                    'subject_type',
                    'reactions',
                    'body_html',
                    'body_text',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', 'comments', input.inPorts.comment_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a pull request review comment',
        name: 'Github: List reactions for a pull request review comment',
        description: 'List the reactions to a [pull request review comment](https://docs.github.com/rest/reference/pulls#review-comments).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a pull request review comment',
        name: 'Github: Create reaction for a pull request review comment',
        description:
            'Create a reaction to a [pull request review comment](https://docs.github.com/rest/reference/pulls#comments). A response with an HTTP `200` status means that you already added the reaction type to this pull request review comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a pull request comment reaction',
        name: 'Github: Delete a pull request comment reaction',
        description:
            '**Note:** You can also specify a repository by `repository_id` using the route `DELETE /repositories/:repository_id/pulls/comments/:comment_id/reactions/:reaction_id.`\n\nDelete a reaction to a [pull request review comment](https://docs.github.com/rest/reference/pulls#review-comments).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            comment_id: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        'comments',
                        input.inPorts.comment_id,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a pull request',
        name: 'Github: Get a pull request',
        description:
            'Draft pull requests are available in public repositories with GitHub Free and GitHub Free for organizations, GitHub Pro, and legacy per-repository billing plans, and in public and private repositories with GitHub Team and GitHub Enterprise Cloud. For more information, see [GitHub\'s products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nLists details of a pull request by providing its number.\n\nWhen you get, [create](https://docs.github.com/rest/reference/pulls/#create-a-pull-request), or [edit](https://docs.github.com/rest/reference/pulls#update-a-pull-request) a pull request, GitHub creates a merge commit to test whether the pull request can be automatically merged into the base branch. This test commit is not added to the base branch or the head branch. You can review the status of the test commit using the `mergeable` key. For more information, see "[Checking mergeability of pull requests](https://docs.github.com/rest/guides/getting-started-with-the-git-database-api#checking-mergeability-of-pull-requests)".\n\nThe value of the `mergeable` attribute can be `true`, `false`, or `null`. If the value is `null`, then GitHub has started a background job to compute the mergeability. After giving the job time to complete, resubmit the request. When the job finishes, you will see a non-`null` value for the `mergeable` attribute in the response. If `mergeable` is `true`, then `merge_commit_sha` will be the SHA of the _test_ merge commit.\n\nThe value of the `merge_commit_sha` attribute changes depending on the state of the pull request. Before merging a pull request, the `merge_commit_sha` attribute holds the SHA of the _test_ merge commit. After merging a pull request, the `merge_commit_sha` attribute changes depending on how you merged the pull request:\n\n*   If merged as a [merge commit](https://docs.github.com/articles/about-merge-methods-on-github/), `merge_commit_sha` represents the SHA of the merge commit.\n*   If merged via a [squash](https://docs.github.com/articles/about-merge-methods-on-github/#squashing-your-merge-commits), `merge_commit_sha` represents the SHA of the squashed commit on the base branch.\n*   If [rebased](https://docs.github.com/articles/about-merge-methods-on-github/#rebasing-and-merging-your-commits), `merge_commit_sha` represents the commit that the base branch was updated to.\n\nPass the appropriate [media type](https://docs.github.com/rest/overview/media-types/#commits-commit-comparison-and-pull-requests) to fetch diff and patch formats.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    diff_url: { kind: 'string' },
                    patch_url: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    review_comments_url: { kind: 'string' },
                    review_comment_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    locked: { kind: 'boolean' },
                    title: { kind: 'string' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                name: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                color: { kind: 'string' },
                                default: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_reviewers: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    head: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            forks_url: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    avatar_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    html_url: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    login: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            default_branch: { kind: 'string' },
                                            forks: { kind: 'number' },
                                            forks_count: { kind: 'number' },
                                            git_url: { kind: 'string' },
                                            has_downloads: { kind: 'boolean' },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_discussions: { kind: 'boolean' },
                                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            master_branch: { kind: 'string' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            open_issues: { kind: 'number' },
                                            open_issues_count: { kind: 'number' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: ['maintain', 'triage'],
                                                restriction: 'open',
                                            },
                                            temp_clone_token: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            license: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            key: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            node_id: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            pushed_at: { kind: 'string' },
                                            size: { kind: 'number' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_count: { kind: 'number' },
                                            svn_url: { kind: 'string' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            watchers: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            allow_forking: { kind: 'boolean' },
                                            is_template: { kind: 'boolean' },
                                            web_commit_signoff_required: { kind: 'boolean' },
                                        },
                                        optionalProperties: [
                                            'master_branch',
                                            'visibility',
                                            'permissions',
                                            'temp_clone_token',
                                            'allow_merge_commit',
                                            'allow_squash_merge',
                                            'allow_rebase_merge',
                                            'topics',
                                            'allow_forking',
                                            'is_template',
                                            'web_commit_signoff_required',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'object',
                                properties: {
                                    avatar_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    login: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    base: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    fork: { kind: 'boolean' },
                                    forks_url: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    hooks_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    node_id: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            avatar_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            login: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    default_branch: { kind: 'string' },
                                    forks: { kind: 'number' },
                                    forks_count: { kind: 'number' },
                                    git_url: { kind: 'string' },
                                    has_downloads: { kind: 'boolean' },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    master_branch: { kind: 'string' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    open_issues: { kind: 'number' },
                                    open_issues_count: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['maintain', 'triage'],
                                        restriction: 'open',
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    pushed_at: { kind: 'string' },
                                    size: { kind: 'number' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_count: { kind: 'number' },
                                    svn_url: { kind: 'string' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    watchers: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'is_template',
                                    'master_branch',
                                    'visibility',
                                    'permissions',
                                    'temp_clone_token',
                                    'allow_merge_commit',
                                    'allow_squash_merge',
                                    'allow_rebase_merge',
                                    'topics',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'object',
                                properties: {
                                    avatar_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    login: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            commits: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            statuses: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            issue: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comment: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author_association: { kind: 'string' },
                    auto_merge: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    enabled_by: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    merge_method: { kind: 'string' },
                                    commit_title: { kind: 'string' },
                                    commit_message: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    draft: { kind: 'boolean' },
                    merged: { kind: 'boolean' },
                    mergeable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    rebaseable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    mergeable_state: { kind: 'string' },
                    merged_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    comments: { kind: 'number' },
                    review_comments: { kind: 'number' },
                    maintainer_can_modify: { kind: 'boolean' },
                    commits: { kind: 'number' },
                    additions: { kind: 'number' },
                    deletions: { kind: 'number' },
                    changed_files: { kind: 'number' },
                },
                optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft', 'rebaseable'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a pull request',
        name: 'Github: Update a pull request',
        description:
            "Draft pull requests are available in public repositories with GitHub Free and GitHub Free for organizations, GitHub Pro, and legacy per-repository billing plans, and in public and private repositories with GitHub Team and GitHub Enterprise Cloud. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nTo open or update a pull request in a public repository, you must have write access to the head or the source branch. For organization-owned repositories, you must be a member of the organization that owns the repository to open or update a pull request.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    title: { kind: 'string' },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    base: { kind: 'string' },
                    maintainer_can_modify: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    diff_url: { kind: 'string' },
                    patch_url: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    review_comments_url: { kind: 'string' },
                    review_comment_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    locked: { kind: 'boolean' },
                    title: { kind: 'string' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                name: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                color: { kind: 'string' },
                                default: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_reviewers: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    head: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            fork: { kind: 'boolean' },
                                            forks_url: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    avatar_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    html_url: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    login: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            default_branch: { kind: 'string' },
                                            forks: { kind: 'number' },
                                            forks_count: { kind: 'number' },
                                            git_url: { kind: 'string' },
                                            has_downloads: { kind: 'boolean' },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_discussions: { kind: 'boolean' },
                                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            master_branch: { kind: 'string' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            open_issues: { kind: 'number' },
                                            open_issues_count: { kind: 'number' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                },
                                                optionalProperties: ['maintain', 'triage'],
                                                restriction: 'open',
                                            },
                                            temp_clone_token: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            license: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            key: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            node_id: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            pushed_at: { kind: 'string' },
                                            size: { kind: 'number' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_count: { kind: 'number' },
                                            svn_url: { kind: 'string' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            watchers: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            created_at: { kind: 'string' },
                                            updated_at: { kind: 'string' },
                                            allow_forking: { kind: 'boolean' },
                                            is_template: { kind: 'boolean' },
                                            web_commit_signoff_required: { kind: 'boolean' },
                                        },
                                        optionalProperties: [
                                            'master_branch',
                                            'visibility',
                                            'permissions',
                                            'temp_clone_token',
                                            'allow_merge_commit',
                                            'allow_squash_merge',
                                            'allow_rebase_merge',
                                            'topics',
                                            'allow_forking',
                                            'is_template',
                                            'web_commit_signoff_required',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'object',
                                properties: {
                                    avatar_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    login: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    base: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    fork: { kind: 'boolean' },
                                    forks_url: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    hooks_url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    node_id: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            avatar_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            login: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    default_branch: { kind: 'string' },
                                    forks: { kind: 'number' },
                                    forks_count: { kind: 'number' },
                                    git_url: { kind: 'string' },
                                    has_downloads: { kind: 'boolean' },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    master_branch: { kind: 'string' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    open_issues: { kind: 'number' },
                                    open_issues_count: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['maintain', 'triage'],
                                        restriction: 'open',
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    pushed_at: { kind: 'string' },
                                    size: { kind: 'number' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_count: { kind: 'number' },
                                    svn_url: { kind: 'string' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    watchers: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'is_template',
                                    'master_branch',
                                    'visibility',
                                    'permissions',
                                    'temp_clone_token',
                                    'allow_merge_commit',
                                    'allow_squash_merge',
                                    'allow_rebase_merge',
                                    'topics',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'object',
                                properties: {
                                    avatar_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    html_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    login: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    url: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            commits: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            statuses: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            issue: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comment: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author_association: { kind: 'string' },
                    auto_merge: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    enabled_by: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    merge_method: { kind: 'string' },
                                    commit_title: { kind: 'string' },
                                    commit_message: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    draft: { kind: 'boolean' },
                    merged: { kind: 'boolean' },
                    mergeable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    rebaseable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    mergeable_state: { kind: 'string' },
                    merged_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    comments: { kind: 'number' },
                    review_comments: { kind: 'number' },
                    maintainer_can_modify: { kind: 'boolean' },
                    commits: { kind: 'number' },
                    additions: { kind: 'number' },
                    deletions: { kind: 'number' },
                    changed_files: { kind: 'number' },
                },
                optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft', 'rebaseable'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a codespace from a pull request',
        name: 'Github: Create a codespace from a pull request',
        description:
            'Creates a codespace owned by the authenticated user for the specified pull request.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            location: { kind: 'string' },
                            geo: { kind: 'string' },
                            client_ip: { kind: 'string' },
                            machine: { kind: 'string' },
                            devcontainer_path: { kind: 'string' },
                            multi_repo_permissions_opt_out: { kind: 'boolean' },
                            working_directory: { kind: 'string' },
                            idle_timeout_minutes: { kind: 'number' },
                            display_name: { kind: 'string' },
                            retention_period_minutes: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'codespaces'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List review comments on a pull request',
        name: 'Github: List review comments on a pull request',
        description: 'Lists all review comments for a pull request. By default, review comments are in ascending order by ID.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        diff_hunk: { kind: 'string' },
                        path: { kind: 'string' },
                        position: { kind: 'number' },
                        original_position: { kind: 'number' },
                        commit_id: { kind: 'string' },
                        original_commit_id: { kind: 'string' },
                        in_reply_to_id: { kind: 'number' },
                        user: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        body: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        html_url: { kind: 'string' },
                        pull_request_url: { kind: 'string' },
                        author_association: { kind: 'string' },
                        _links: {
                            kind: 'object',
                            properties: {
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pull_request: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        line: { kind: 'number' },
                        original_line: { kind: 'number' },
                        side: { kind: 'string' },
                        subject_type: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                    },
                    optionalProperties: [
                        'position',
                        'original_position',
                        'in_reply_to_id',
                        'start_line',
                        'original_start_line',
                        'start_side',
                        'line',
                        'original_line',
                        'side',
                        'subject_type',
                        'reactions',
                        'body_html',
                        'body_text',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a review comment for a pull request',
        name: 'Github: Create a review comment for a pull request',
        description:
            '\nCreates a review comment in the pull request diff. To add a regular comment to a pull request timeline, see "[Create an issue comment](https://docs.github.com/rest/issues/comments#create-an-issue-comment)." We recommend creating a review comment using `line`, `side`, and optionally `start_line` and `start_side` if your comment applies to more than one line in the pull request diff.\n\nThe `position` parameter is deprecated. If you use `position`, the `line`, `side`, `start_line`, and `start_side` parameters are not required.\n\n**Note:** The position value equals the number of lines down from the first "@@" hunk header in the file you want to add a comment. The line just below the "@@" line is position 1, the next line is position 2, and so on. The position in the diff continues to increase through lines of whitespace and additional hunks until the beginning of a new file.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    body: { kind: 'string' },
                    commit_id: { kind: 'string' },
                    path: { kind: 'string' },
                    position: { kind: 'number' },
                    side: { kind: 'string' },
                    line: { kind: 'number' },
                    start_line: { kind: 'number' },
                    start_side: { kind: 'string' },
                    in_reply_to: { kind: 'number' },
                    subject_type: { kind: 'string' },
                },
                optionalProperties: ['position', 'side', 'line', 'start_line', 'start_side', 'in_reply_to', 'subject_type'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    diff_hunk: { kind: 'string' },
                    path: { kind: 'string' },
                    position: { kind: 'number' },
                    original_position: { kind: 'number' },
                    commit_id: { kind: 'string' },
                    original_commit_id: { kind: 'string' },
                    in_reply_to_id: { kind: 'number' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    line: { kind: 'number' },
                    original_line: { kind: 'number' },
                    side: { kind: 'string' },
                    subject_type: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                },
                optionalProperties: [
                    'position',
                    'original_position',
                    'in_reply_to_id',
                    'start_line',
                    'original_start_line',
                    'start_side',
                    'line',
                    'original_line',
                    'side',
                    'subject_type',
                    'reactions',
                    'body_html',
                    'body_text',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'comments'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a reply for a review comment',
        name: 'Github: Create a reply for a review comment',
        description:
            'Creates a reply to a review comment for a pull request. For the `comment_id`, provide the ID of the review comment you are replying to. This must be the ID of a _top-level review comment_, not a reply to that comment. Replies to replies are not supported.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            comment_id: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    diff_hunk: { kind: 'string' },
                    path: { kind: 'string' },
                    position: { kind: 'number' },
                    original_position: { kind: 'number' },
                    commit_id: { kind: 'string' },
                    original_commit_id: { kind: 'string' },
                    in_reply_to_id: { kind: 'number' },
                    user: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    body: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    author_association: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    line: { kind: 'number' },
                    original_line: { kind: 'number' },
                    side: { kind: 'string' },
                    subject_type: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                },
                optionalProperties: [
                    'position',
                    'original_position',
                    'in_reply_to_id',
                    'start_line',
                    'original_start_line',
                    'start_side',
                    'line',
                    'original_line',
                    'side',
                    'subject_type',
                    'reactions',
                    'body_html',
                    'body_text',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'comments',
                        input.inPorts.comment_id,
                        'replies',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List commits on a pull request',
        name: 'Github: List commits on a pull request',
        description:
            'Lists a maximum of 250 commits for a pull request. To receive a complete commit list for pull requests with more than 250 commits, use the [List commits](https://docs.github.com/rest/reference/repos#list-commits) endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        sha: { kind: 'string' },
                        node_id: { kind: 'string' },
                        html_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commit: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                author: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                committer: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                message: { kind: 'string' },
                                comment_count: { kind: 'number' },
                                tree: {
                                    kind: 'object',
                                    properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                verification: {
                                    kind: 'object',
                                    properties: {
                                        verified: { kind: 'boolean' },
                                        reason: { kind: 'string' },
                                        payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['verification'],
                            restriction: 'open',
                        },
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        committer: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        parents: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: { sha: { kind: 'string' }, url: { kind: 'string' }, html_url: { kind: 'string' } },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                        },
                        stats: {
                            kind: 'object',
                            properties: { additions: { kind: 'number' }, deletions: { kind: 'number' }, total: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        files: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    sha: { kind: 'string' },
                                    filename: { kind: 'string' },
                                    status: { kind: 'string' },
                                    additions: { kind: 'number' },
                                    deletions: { kind: 'number' },
                                    changes: { kind: 'number' },
                                    blob_url: { kind: 'string' },
                                    raw_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    patch: { kind: 'string' },
                                    previous_filename: { kind: 'string' },
                                },
                                optionalProperties: ['patch', 'previous_filename'],
                                restriction: 'open',
                            },
                        },
                    },
                    optionalProperties: ['stats', 'files'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'commits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List pull requests files',
        name: 'Github: List pull requests files',
        description: '**Note:** Responses include a maximum of 3000 files. The paginated response returns 30 files per page by default.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        sha: { kind: 'string' },
                        filename: { kind: 'string' },
                        status: { kind: 'string' },
                        additions: { kind: 'number' },
                        deletions: { kind: 'number' },
                        changes: { kind: 'number' },
                        blob_url: { kind: 'string' },
                        raw_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        patch: { kind: 'string' },
                        previous_filename: { kind: 'string' },
                    },
                    optionalProperties: ['patch', 'previous_filename'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'files'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a pull request has been merged',
        name: 'Github: Check if a pull request has been merged',
        description:
            'Checks if a pull request has been merged into the base branch. The HTTP status of the response indicates whether or not the pull request has been merged; the response body is empty.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'merge'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Merge a pull request',
        name: 'Github: Merge a pull request',
        description:
            'Merges a pull request into the base branch.\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            commit_title: { kind: 'string' },
                            commit_message: { kind: 'string' },
                            sha: { kind: 'string' },
                            merge_method: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { sha: { kind: 'string' }, merged: { kind: 'boolean' }, message: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'merge'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all requested reviewers for a pull request',
        name: 'Github: Get all requested reviewers for a pull request',
        description:
            'Gets the users or teams whose review is requested for a pull request. Once a requested reviewer submits a review, they are no longer considered a requested reviewer. Their review will instead be returned by the [List reviews for a pull request](https://docs.github.com/rest/pulls/reviews#list-reviews-for-a-pull-request) operation.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    users: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                    },
                    teams: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                slug: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                privacy: { kind: 'string' },
                                notification_setting: { kind: 'string' },
                                permission: { kind: 'string' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        admin: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                members_url: { kind: 'string' },
                                repositories_url: { kind: 'string' },
                                parent: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                members_url: { kind: 'string' },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                permission: { kind: 'string' },
                                                privacy: { kind: 'string' },
                                                notification_setting: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                repositories_url: { kind: 'string' },
                                                slug: { kind: 'string' },
                                                ldap_dn: { kind: 'string' },
                                            },
                                            optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'requested_reviewers',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Request reviewers for a pull request',
        name: 'Github: Request reviewers for a pull request',
        description:
            'Requests reviews for a pull request from a given set of users and/or teams.\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    reviewers: { kind: 'array', subType: { kind: 'string' } },
                    team_reviewers: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    diff_url: { kind: 'string' },
                    patch_url: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    review_comments_url: { kind: 'string' },
                    review_comment_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    locked: { kind: 'boolean' },
                    title: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                name: { kind: 'string' },
                                description: { kind: 'string' },
                                color: { kind: 'string' },
                                default: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_reviewers: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    head: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    base: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            commits: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            statuses: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            issue: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comment: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author_association: { kind: 'string' },
                    auto_merge: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    enabled_by: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    merge_method: { kind: 'string' },
                                    commit_title: { kind: 'string' },
                                    commit_message: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    draft: { kind: 'boolean' },
                },
                optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'requested_reviewers',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove requested reviewers from a pull request',
        name: 'Github: Remove requested reviewers from a pull request',
        description: 'Removes review requests from a pull request for a given set of users and/or teams.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    reviewers: { kind: 'array', subType: { kind: 'string' } },
                    team_reviewers: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['team_reviewers'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    html_url: { kind: 'string' },
                    diff_url: { kind: 'string' },
                    patch_url: { kind: 'string' },
                    issue_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    review_comments_url: { kind: 'string' },
                    review_comment_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    locked: { kind: 'boolean' },
                    title: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    labels: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                name: { kind: 'string' },
                                description: { kind: 'string' },
                                color: { kind: 'string' },
                                default: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    milestone: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    number: { kind: 'number' },
                                    state: { kind: 'string' },
                                    title: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    creator: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    open_issues: { kind: 'number' },
                                    closed_issues: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    merge_commit_sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    assignee: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    assignees: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_reviewers: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    requested_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    head: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    base: {
                        kind: 'object',
                        properties: {
                            label: { kind: 'string' },
                            ref: { kind: 'string' },
                            repo: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            sha: { kind: 'string' },
                            user: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    _links: {
                        kind: 'object',
                        properties: {
                            comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            commits: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            statuses: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            issue: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comments: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            review_comment: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    author_association: { kind: 'string' },
                    auto_merge: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    enabled_by: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    merge_method: { kind: 'string' },
                                    commit_title: { kind: 'string' },
                                    commit_message: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    draft: { kind: 'boolean' },
                },
                optionalProperties: ['active_lock_reason', 'assignees', 'requested_reviewers', 'requested_teams', 'draft'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'requested_reviewers',
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reviews for a pull request',
        name: 'Github: List reviews for a pull request',
        description: 'The list of reviews returns in chronological order.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'string' },
                        state: { kind: 'string' },
                        html_url: { kind: 'string' },
                        pull_request_url: { kind: 'string' },
                        _links: {
                            kind: 'object',
                            properties: {
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pull_request: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        submitted_at: { kind: 'string' },
                        commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                        author_association: { kind: 'string' },
                    },
                    optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'reviews'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a review for a pull request',
        name: 'Github: Create a review for a pull request',
        description:
            'This endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.\n\nPull request reviews created in the `PENDING` state are not submitted and therefore do not include the `submitted_at` property in the response. To create a pending review for a pull request, leave the `event` parameter blank. For more information about submitting a `PENDING` review, see "[Submit a review for a pull request](https://docs.github.com/rest/pulls#submit-a-review-for-a-pull-request)."\n\n**Note:** To comment on a specific line in a file, you need to first determine the _position_ of that line in the diff. The GitHub REST API offers the `application/vnd.github.v3.diff` [media type](https://docs.github.com/rest/overview/media-types#commits-commit-comparison-and-pull-requests). To see a pull request diff, add this media type to the `Accept` header of a call to the [single pull request](https://docs.github.com/rest/pulls/pulls#get-a-pull-request) endpoint.\n\nThe `position` value equals the number of lines down from the first "@@" hunk header in the file you want to add a comment. The line just below the "@@" line is position 1, the next line is position 2, and so on. The position in the diff continues to increase through lines of whitespace and additional hunks until the beginning of a new file.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    commit_id: { kind: 'string' },
                    body: { kind: 'string' },
                    event: { kind: 'string' },
                    comments: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                path: { kind: 'string' },
                                position: { kind: 'number' },
                                body: { kind: 'string' },
                                line: { kind: 'number' },
                                side: { kind: 'string' },
                                start_line: { kind: 'number' },
                                start_side: { kind: 'string' },
                            },
                            optionalProperties: ['position', 'line', 'side', 'start_line', 'start_side'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    submitted_at: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'reviews'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a review for a pull request',
        name: 'Github: Get a review for a pull request',
        description: 'Retrieves a pull request review by its ID.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            review_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    submitted_at: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'reviews',
                        input.inPorts.review_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a review for a pull request',
        name: 'Github: Update a review for a pull request',
        description: 'Update the review summary comment with new text.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            review_id: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    submitted_at: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'reviews',
                        input.inPorts.review_id,
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a pending review for a pull request',
        name: 'Github: Delete a pending review for a pull request',
        description: 'Deletes a pull request review that has not been submitted. Submitted reviews cannot be deleted.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            review_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    submitted_at: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'reviews',
                        input.inPorts.review_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List comments for a pull request review',
        name: 'Github: List comments for a pull request review',
        description: 'List comments for a specific pull request review.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            review_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        pull_request_review_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        diff_hunk: { kind: 'string' },
                        path: { kind: 'string' },
                        position: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        original_position: { kind: 'number' },
                        commit_id: { kind: 'string' },
                        original_commit_id: { kind: 'string' },
                        in_reply_to_id: { kind: 'number' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        html_url: { kind: 'string' },
                        pull_request_url: { kind: 'string' },
                        author_association: { kind: 'string' },
                        _links: {
                            kind: 'object',
                            properties: {
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pull_request: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        body_text: { kind: 'string' },
                        body_html: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        side: { kind: 'string' },
                        start_side: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        line: { kind: 'number' },
                        original_line: { kind: 'number' },
                        start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        original_start_line: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    },
                    optionalProperties: [
                        'in_reply_to_id',
                        'body_text',
                        'body_html',
                        'reactions',
                        'side',
                        'start_side',
                        'line',
                        'original_line',
                        'start_line',
                        'original_start_line',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'reviews',
                        input.inPorts.review_id,
                        'comments',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Dismiss a review for a pull request',
        name: 'Github: Dismiss a review for a pull request',
        description:
            '**Note:** To dismiss a pull request review on a [protected branch](https://docs.github.com/rest/reference/repos#branches), you must be a repository administrator or be included in the list of people or teams who can dismiss pull request reviews.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            review_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { message: { kind: 'string' }, event: { kind: 'string' } },
                optionalProperties: ['event'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    submitted_at: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'reviews',
                        input.inPorts.review_id,
                        'dismissals',
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Submit a review for a pull request',
        name: 'Github: Submit a review for a pull request',
        description:
            'Submits a pending review for a pull request. For more information about creating a pending review for a pull request, see "[Create a review for a pull request](https://docs.github.com/rest/pulls#create-a-review-for-a-pull-request)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            review_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { body: { kind: 'string' }, event: { kind: 'string' } },
                optionalProperties: ['body'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    state: { kind: 'string' },
                    html_url: { kind: 'string' },
                    pull_request_url: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            pull_request: {
                                kind: 'object',
                                properties: { href: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    submitted_at: { kind: 'string' },
                    commit_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    author_association: { kind: 'string' },
                },
                optionalProperties: ['submitted_at', 'body_html', 'body_text'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'pulls',
                        input.inPorts.pull_number,
                        'reviews',
                        input.inPorts.review_id,
                        'events',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a pull request branch',
        name: 'Github: Update a pull request branch',
        description:
            'Updates the pull request branch with the latest upstream changes by merging HEAD from the base branch into the pull request branch.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            pull_number: { kind: 'number' },
            body: {
                kind: 'union',
                subTypes: [
                    { kind: 'object', properties: { expected_head_sha: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                    { kind: 'null' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { message: { kind: 'string' }, url: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'pulls', input.inPorts.pull_number, 'update-branch'].join(
                        '/',
                    ),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository README',
        name: 'Github: Get a repository README',
        description:
            'Gets the preferred README for a repository.\n\nREADMEs support [custom media types](https://docs.github.com/rest/reference/repos#custom-media-types) for retrieving the raw content or rendered HTML.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: ['ref'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    type: { kind: 'string' },
                    encoding: { kind: 'string' },
                    size: { kind: 'number' },
                    name: { kind: 'string' },
                    path: { kind: 'string' },
                    content: { kind: 'string' },
                    sha: { kind: 'string' },
                    url: { kind: 'string' },
                    git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    _links: {
                        kind: 'object',
                        properties: {
                            git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            self: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    target: { kind: 'string' },
                    submodule_git_url: { kind: 'string' },
                },
                optionalProperties: ['target', 'submodule_git_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'readme'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ ref: input.inPorts.ref }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository README for a directory',
        name: 'Github: Get a repository README for a directory',
        description:
            'Gets the README from a repository directory.\n\nREADMEs support [custom media types](https://docs.github.com/rest/reference/repos#custom-media-types) for retrieving the raw content or rendered HTML.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            dir: { kind: 'string' },
            ref: { kind: 'string' },
        },
        optionalInPorts: ['ref'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    type: { kind: 'string' },
                    encoding: { kind: 'string' },
                    size: { kind: 'number' },
                    name: { kind: 'string' },
                    path: { kind: 'string' },
                    content: { kind: 'string' },
                    sha: { kind: 'string' },
                    url: { kind: 'string' },
                    git_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    download_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    _links: {
                        kind: 'object',
                        properties: {
                            git: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            html: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            self: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    target: { kind: 'string' },
                    submodule_git_url: { kind: 'string' },
                },
                optionalProperties: ['target', 'submodule_git_url'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'readme', input.inPorts.dir].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ ref: input.inPorts.ref }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List releases',
        name: 'Github: List releases',
        description:
            'This returns a list of releases, which does not include regular Git tags that have not been associated with a release. To get a list of Git tags, use the [Repository Tags API](https://docs.github.com/rest/repos/repos#list-repository-tags).\n\nInformation about published releases are available to everyone. Only users with push access will receive listings for draft releases.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        assets_url: { kind: 'string' },
                        upload_url: { kind: 'string' },
                        tarball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        zipball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        tag_name: { kind: 'string' },
                        target_commitish: { kind: 'string' },
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        draft: { kind: 'boolean' },
                        prerelease: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        author: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        assets: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    browser_download_url: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    state: { kind: 'string' },
                                    content_type: { kind: 'string' },
                                    size: { kind: 'number' },
                                    download_count: { kind: 'number' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    uploader: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                        mentions_count: { kind: 'number' },
                        discussion_url: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['body', 'body_html', 'body_text', 'mentions_count', 'discussion_url', 'reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a release',
        name: 'Github: Create a release',
        description:
            'Users with push access to the repository can create a release.\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    discussion_category_name: { kind: 'string' },
                    generate_release_notes: { kind: 'boolean' },
                    make_latest: { kind: 'string' },
                },
                optionalProperties: [
                    'target_commitish',
                    'name',
                    'body',
                    'draft',
                    'prerelease',
                    'discussion_category_name',
                    'generate_release_notes',
                    'make_latest',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    assets_url: { kind: 'string' },
                    upload_url: { kind: 'string' },
                    tarball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    zipball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    assets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                browser_download_url: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                content_type: { kind: 'string' },
                                size: { kind: 'number' },
                                download_count: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                uploader: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    mentions_count: { kind: 'number' },
                    discussion_url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_html', 'body_text', 'mentions_count', 'discussion_url', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a release asset',
        name: 'Github: Get a release asset',
        description:
            "To download the asset's binary content, set the `Accept` header of the request to [`application/octet-stream`](https://docs.github.com/rest/overview/media-types). The API will either redirect the client to the location, or stream it directly if possible. API clients should handle both a `200` or `302` response.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            asset_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    browser_download_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    state: { kind: 'string' },
                    content_type: { kind: 'string' },
                    size: { kind: 'number' },
                    download_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    uploader: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', 'assets', input.inPorts.asset_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a release asset',
        name: 'Github: Delete a release asset',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            asset_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', 'assets', input.inPorts.asset_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a release asset',
        name: 'Github: Update a release asset',
        description: 'Users with push access to the repository can edit a release asset.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            asset_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, label: { kind: 'string' }, state: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    browser_download_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    state: { kind: 'string' },
                    content_type: { kind: 'string' },
                    size: { kind: 'number' },
                    download_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    uploader: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', 'assets', input.inPorts.asset_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Generate release notes content for a release',
        name: 'Github: Generate release notes content for a release',
        description:
            'Generate a name and body describing a [release](https://docs.github.com/rest/reference/repos#releases). The body content will be markdown formatted and contain information like the changes since last release and users who contributed. The generated release notes are not saved anywhere. They are intended to be generated and used when creating a new release.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    previous_tag_name: { kind: 'string' },
                    configuration_file_path: { kind: 'string' },
                },
                optionalProperties: ['target_commitish', 'previous_tag_name', 'configuration_file_path'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { name: { kind: 'string' }, body: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', 'generate-notes'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the latest release',
        name: 'Github: Get the latest release',
        description:
            'View the latest published full release for the repository.\n\nThe latest release is the most recent non-prerelease, non-draft release, sorted by the `created_at` attribute. The `created_at` attribute is the date of the commit used for the release, and not the date when the release was drafted or published.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    assets_url: { kind: 'string' },
                    upload_url: { kind: 'string' },
                    tarball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    zipball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    assets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                browser_download_url: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                content_type: { kind: 'string' },
                                size: { kind: 'number' },
                                download_count: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                uploader: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    mentions_count: { kind: 'number' },
                    discussion_url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_html', 'body_text', 'mentions_count', 'discussion_url', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', 'latest'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a release by tag name',
        name: 'Github: Get a release by tag name',
        description: 'Get a published release with the specified tag.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            tag: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    assets_url: { kind: 'string' },
                    upload_url: { kind: 'string' },
                    tarball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    zipball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    assets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                browser_download_url: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                content_type: { kind: 'string' },
                                size: { kind: 'number' },
                                download_count: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                uploader: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    mentions_count: { kind: 'number' },
                    discussion_url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_html', 'body_text', 'mentions_count', 'discussion_url', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', 'tags', input.inPorts.tag].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a release',
        name: 'Github: Get a release',
        description:
            '**Note:** This returns an `upload_url` key corresponding to the endpoint for uploading release assets. This key is a [hypermedia resource](https://docs.github.com/rest/overview/resources-in-the-rest-api#hypermedia).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    assets_url: { kind: 'string' },
                    upload_url: { kind: 'string' },
                    tarball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    zipball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    assets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                browser_download_url: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                content_type: { kind: 'string' },
                                size: { kind: 'number' },
                                download_count: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                uploader: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    mentions_count: { kind: 'number' },
                    discussion_url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_html', 'body_text', 'mentions_count', 'discussion_url', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a release',
        name: 'Github: Delete a release',
        description: 'Users with push access to the repository can delete a release.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a release',
        name: 'Github: Update a release',
        description: 'Users with push access to the repository can edit a release.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'string' },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    make_latest: { kind: 'string' },
                    discussion_category_name: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    assets_url: { kind: 'string' },
                    upload_url: { kind: 'string' },
                    tarball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    zipball_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    tag_name: { kind: 'string' },
                    target_commitish: { kind: 'string' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    draft: { kind: 'boolean' },
                    prerelease: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    assets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                browser_download_url: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                content_type: { kind: 'string' },
                                size: { kind: 'number' },
                                download_count: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                uploader: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    body_html: { kind: 'string' },
                    body_text: { kind: 'string' },
                    mentions_count: { kind: 'number' },
                    discussion_url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['body', 'body_html', 'body_text', 'mentions_count', 'discussion_url', 'reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List release assets',
        name: 'Github: List release assets',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        browser_download_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        state: { kind: 'string' },
                        content_type: { kind: 'string' },
                        size: { kind: 'number' },
                        download_count: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        uploader: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id, 'assets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Upload a release asset',
        name: 'Github: Upload a release asset',
        description:
            'This endpoint makes use of [a Hypermedia relation](https://docs.github.com/rest/overview/resources-in-the-rest-api#hypermedia) to determine which URL to access. The endpoint you call to upload release assets is specific to your release. Use the `upload_url` returned in\nthe response of the [Create a release endpoint](https://docs.github.com/rest/releases/releases#create-a-release) to upload a release asset.\n\nYou need to use an HTTP client which supports [SNI](http://en.wikipedia.org/wiki/Server_Name_Indication) to make calls to this endpoint.\n\nMost libraries will set the required `Content-Length` header automatically. Use the required `Content-Type` header to provide the media type of the asset. For a list of media types, see [Media Types](https://www.iana.org/assignments/media-types/media-types.xhtml). For example: \n\n`application/zip`\n\nGitHub expects the asset data in its raw binary form, rather than JSON. You will send the raw binary content of the asset as the request body. Everything else about the endpoint is the same as the rest of the API. For example,\nyou\'ll still need to pass your authentication to be able to upload an asset.\n\nWhen an upstream failure occurs, you will receive a `502 Bad Gateway` status. This may leave an empty asset with a state of `starter`. It can be safely deleted.\n\n**Notes:**\n*   GitHub renames asset filenames that have special characters, non-alphanumeric characters, and leading or trailing periods. The "[List assets for a release](https://docs.github.com/rest/reference/repos#list-assets-for-a-release)"\nendpoint lists the renamed filenames. For more information and help, contact [GitHub Support](https://support.github.com/contact?tags=dotcom-rest-api).\n*   To find the `release_id` query the [`GET /repos/{owner}/{repo}/releases/latest` endpoint](https://docs.github.com/rest/releases/releases#get-the-latest-release). \n*   If you upload an asset with the same filename as another uploaded asset, you\'ll receive an error and must delete the old file before you can re-upload the new asset.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
            name: { kind: 'string' },
            label: { kind: 'string' },
        },
        optionalInPorts: ['label'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    browser_download_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    label: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    state: { kind: 'string' },
                    content_type: { kind: 'string' },
                    size: { kind: 'number' },
                    download_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    uploader: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id, 'assets'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(
                        Object.entries({ name: input.inPorts.name, label: input.inPorts.label }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a release',
        name: 'Github: List reactions for a release',
        description: 'List the reactions to a [release](https://docs.github.com/rest/reference/repos#releases).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id, 'reactions'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a release',
        name: 'Github: Create reaction for a release',
        description:
            'Create a reaction to a [release](https://docs.github.com/rest/reference/repos#releases). A response with a `Status: 200 OK` means that you already added the reaction type to this release.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'releases', input.inPorts.release_id, 'reactions'].join(
                        '/',
                    ),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a release reaction',
        name: 'Github: Delete a release reaction',
        description:
            '**Note:** You can also specify a repository by `repository_id` using the route `DELETE delete /repositories/:repository_id/releases/:release_id/reactions/:reaction_id`.\n\nDelete a reaction to a [release](https://docs.github.com/rest/reference/repos#releases).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            release_id: { kind: 'number' },
            reaction_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'releases',
                        input.inPorts.release_id,
                        'reactions',
                        input.inPorts.reaction_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get rules for a branch',
        name: 'Github: Get rules for a branch',
        description:
            'Returns all rules that apply to the specified branch. The branch does not need to exist; rules that would apply to a\nbranch with that name will be returned. All rules that apply will be returned, regardless of the level at which they\nare configured.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            branch: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: { Response: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'rules', 'branches', input.inPorts.branch].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all repository rulesets',
        name: 'Github: Get all repository rulesets',
        description: 'Get all the rulesets for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            includes_parents: { kind: 'boolean' },
        },
        optionalInPorts: ['per_page', 'page', 'includes_parents'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        target: { kind: 'string' },
                        source_type: { kind: 'string' },
                        source: { kind: 'string' },
                        enforcement: { kind: 'string' },
                        bypass_actors: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    actor_id: { kind: 'number' },
                                    actor_type: { kind: 'string' },
                                    bypass_mode: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        current_user_can_bypass: { kind: 'string' },
                        node_id: { kind: 'string' },
                        _links: {
                            kind: 'object',
                            properties: {
                                self: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                html: {
                                    kind: 'object',
                                    properties: { href: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        conditions: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        ref_name: {
                                            kind: 'object',
                                            properties: {
                                                include: { kind: 'array', subType: { kind: 'string' } },
                                                exclude: { kind: 'array', subType: { kind: 'string' } },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            ],
                        },
                        rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: [
                        'target',
                        'source_type',
                        'bypass_actors',
                        'current_user_can_bypass',
                        'node_id',
                        '_links',
                        'conditions',
                        'rules',
                        'created_at',
                        'updated_at',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'rulesets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            includes_parents: input.inPorts.includes_parents,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository ruleset',
        name: 'Github: Create a repository ruleset',
        description: 'Create a ruleset for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    conditions: {
                        kind: 'object',
                        properties: {
                            ref_name: {
                                kind: 'object',
                                properties: {
                                    include: { kind: 'array', subType: { kind: 'string' } },
                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                },
                optionalProperties: ['target', 'bypass_actors', 'conditions', 'rules'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    source_type: { kind: 'string' },
                    source: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    current_user_can_bypass: { kind: 'string' },
                    node_id: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    ref_name: {
                                        kind: 'object',
                                        properties: {
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        ],
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'target',
                    'source_type',
                    'bypass_actors',
                    'current_user_can_bypass',
                    'node_id',
                    '_links',
                    'conditions',
                    'rules',
                    'created_at',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'rulesets'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository ruleset',
        name: 'Github: Get a repository ruleset',
        description: 'Get a ruleset for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ruleset_id: { kind: 'number' },
            includes_parents: { kind: 'boolean' },
        },
        optionalInPorts: ['includes_parents'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    source_type: { kind: 'string' },
                    source: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    current_user_can_bypass: { kind: 'string' },
                    node_id: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    ref_name: {
                                        kind: 'object',
                                        properties: {
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        ],
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'target',
                    'source_type',
                    'bypass_actors',
                    'current_user_can_bypass',
                    'node_id',
                    '_links',
                    'conditions',
                    'rules',
                    'created_at',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'rulesets', input.inPorts.ruleset_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ includes_parents: input.inPorts.includes_parents }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a repository ruleset',
        name: 'Github: Update a repository ruleset',
        description: 'Update a ruleset for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ruleset_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    conditions: {
                        kind: 'object',
                        properties: {
                            ref_name: {
                                kind: 'object',
                                properties: {
                                    include: { kind: 'array', subType: { kind: 'string' } },
                                    exclude: { kind: 'array', subType: { kind: 'string' } },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    target: { kind: 'string' },
                    source_type: { kind: 'string' },
                    source: { kind: 'string' },
                    enforcement: { kind: 'string' },
                    bypass_actors: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { actor_id: { kind: 'number' }, actor_type: { kind: 'string' }, bypass_mode: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    current_user_can_bypass: { kind: 'string' },
                    node_id: { kind: 'string' },
                    _links: {
                        kind: 'object',
                        properties: {
                            self: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                            html: { kind: 'object', properties: { href: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    conditions: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    ref_name: {
                                        kind: 'object',
                                        properties: {
                                            include: { kind: 'array', subType: { kind: 'string' } },
                                            exclude: { kind: 'array', subType: { kind: 'string' } },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        ],
                    },
                    rules: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [
                    'target',
                    'source_type',
                    'bypass_actors',
                    'current_user_can_bypass',
                    'node_id',
                    '_links',
                    'conditions',
                    'rules',
                    'created_at',
                    'updated_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'rulesets', input.inPorts.ruleset_id].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository ruleset',
        name: 'Github: Delete a repository ruleset',
        description: 'Delete a ruleset for a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ruleset_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'rulesets', input.inPorts.ruleset_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List secret scanning alerts for a repository',
        name: 'Github: List secret scanning alerts for a repository',
        description:
            'Lists secret scanning alerts for an eligible repository, from newest to oldest.\nTo use this endpoint, you must be an administrator for the repository or for the organization that owns the repository, and you must use a personal access token with the `repo` scope or `security_events` scope.\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have the `secret_scanning_alerts` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            state: { kind: 'string' },
            secret_type: { kind: 'string' },
            resolution: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            before: { kind: 'string' },
            after: { kind: 'string' },
        },
        optionalInPorts: ['state', 'secret_type', 'resolution', 'sort', 'direction', 'page', 'per_page', 'before', 'after'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        number: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        locations_url: { kind: 'string' },
                        state: { kind: 'string' },
                        resolution: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolved_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        resolved_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        resolution_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        secret_type: { kind: 'string' },
                        secret_type_display_name: { kind: 'string' },
                        secret: { kind: 'string' },
                        push_protection_bypassed: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                        push_protection_bypassed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        push_protection_bypassed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'secret-scanning', 'alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            state: input.inPorts.state,
                            secret_type: input.inPorts.secret_type,
                            resolution: input.inPorts.resolution,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a secret scanning alert',
        name: 'Github: Get a secret scanning alert',
        description:
            'Gets a single secret scanning alert detected in an eligible repository.\nTo use this endpoint, you must be an administrator for the repository or for the organization that owns the repository, and you must use a personal access token with the `repo` scope or `security_events` scope.\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have the `secret_scanning_alerts` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    number: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    locations_url: { kind: 'string' },
                    state: { kind: 'string' },
                    resolution: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    resolved_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    resolved_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    resolution_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    secret_type: { kind: 'string' },
                    secret_type_display_name: { kind: 'string' },
                    secret: { kind: 'string' },
                    push_protection_bypassed: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    push_protection_bypassed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    push_protection_bypassed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'secret-scanning',
                        'alerts',
                        input.inPorts.alert_number,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a secret scanning alert',
        name: 'Github: Update a secret scanning alert',
        description:
            'Updates the status of a secret scanning alert in an eligible repository.\nTo use this endpoint, you must be an administrator for the repository or for the organization that owns the repository, and you must use a personal access token with the `repo` scope or `security_events` scope.\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have the `secret_scanning_alerts` write permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    resolution: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    resolution_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['resolution', 'resolution_comment'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    number: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    locations_url: { kind: 'string' },
                    state: { kind: 'string' },
                    resolution: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    resolved_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    resolved_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    resolution_comment: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    secret_type: { kind: 'string' },
                    secret_type_display_name: { kind: 'string' },
                    secret: { kind: 'string' },
                    push_protection_bypassed: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    push_protection_bypassed_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    push_protection_bypassed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'secret-scanning',
                        'alerts',
                        input.inPorts.alert_number,
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List locations for a secret scanning alert',
        name: 'Github: List locations for a secret scanning alert',
        description:
            'Lists all locations for a given secret scanning alert for an eligible repository.\nTo use this endpoint, you must be an administrator for the repository or for the organization that owns the repository, and you must use a personal access token with the `repo` scope or `security_events` scope.\nFor public repositories, you may instead use the `public_repo` scope.\n\nGitHub Apps must have the `secret_scanning_alerts` read permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            alert_number: { kind: 'number' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        type: { kind: 'string' },
                        details: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        path: { kind: 'string' },
                                        start_line: { kind: 'number' },
                                        end_line: { kind: 'number' },
                                        start_column: { kind: 'number' },
                                        end_column: { kind: 'number' },
                                        blob_sha: { kind: 'string' },
                                        blob_url: { kind: 'string' },
                                        commit_sha: { kind: 'string' },
                                        commit_url: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: { issue_title_url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: { issue_body_url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                {
                                    kind: 'object',
                                    properties: { issue_comment_url: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            ],
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repos',
                        input.inPorts.owner,
                        input.inPorts.repo,
                        'secret-scanning',
                        'alerts',
                        input.inPorts.alert_number,
                        'locations',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository security advisories',
        name: 'Github: List repository security advisories',
        description:
            'Lists security advisories in a repository.\nYou must authenticate using an access token with the `repo` scope or `repository_advisories:read` permission\nin order to get published security advisories in a private repository, or any unpublished security advisories that you have access to.\n\nYou can access unpublished security advisories from a repository if you are a security manager or administrator of that repository, or if you are a collaborator on any security advisory.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            direction: { kind: 'string' },
            sort: { kind: 'string' },
            before: { kind: 'string' },
            after: { kind: 'string' },
            per_page: { kind: 'number' },
            state: { kind: 'string' },
        },
        optionalInPorts: ['direction', 'sort', 'before', 'after', 'per_page', 'state'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        ghsa_id: { kind: 'string' },
                        cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        summary: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                    ],
                                },
                                { kind: 'null' },
                            ],
                        },
                        publisher: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                    ],
                                },
                                { kind: 'null' },
                            ],
                        },
                        identifiers: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        state: { kind: 'string' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        submission: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: { accepted: { kind: 'boolean' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        vulnerabilities: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            package: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            ecosystem: { kind: 'string' },
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            vulnerable_functions: {
                                                kind: 'union',
                                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        cvss: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        score: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        cwes: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                        credits: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        credits_detailed: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            user: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            type: { kind: 'string' },
                                            state: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        collaborating_users: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        collaborating_teams: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            privacy: { kind: 'string' },
                                            notification_setting: { kind: 'string' },
                                            permission: { kind: 'string' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    pull: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                    admin: { kind: 'boolean' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            members_url: { kind: 'string' },
                                            repositories_url: { kind: 'string' },
                                            parent: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            members_url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            permission: { kind: 'string' },
                                                            privacy: { kind: 'string' },
                                                            notification_setting: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            repositories_url: { kind: 'string' },
                                                            slug: { kind: 'string' },
                                                            ldap_dn: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                        },
                                        optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        private_fork: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    ],
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'security-advisories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            direction: input.inPorts.direction,
                            sort: input.inPorts.sort,
                            before: input.inPorts.before,
                            after: input.inPorts.after,
                            per_page: input.inPorts.per_page,
                            state: input.inPorts.state,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository security advisory',
        name: 'Github: Create a repository security advisory',
        description:
            'Creates a new repository security advisory.\nYou must authenticate using an access token with the `repo` scope or `repository_advisories:write` permission to use this endpoint.\n\nIn order to create a draft repository security advisory, you must be a security manager or administrator of that repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    summary: { kind: 'string' },
                    description: { kind: 'string' },
                    cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    vulnerabilities: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: {
                                        ecosystem: { kind: 'string' },
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['name'],
                                    restriction: 'open',
                                },
                                vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                vulnerable_functions: {
                                    kind: 'union',
                                    subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                },
                            },
                            optionalProperties: ['vulnerable_version_range', 'patched_versions', 'vulnerable_functions'],
                            restriction: 'open',
                        },
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    credits: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    cvss_vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['cve_id', 'cwe_ids', 'credits', 'severity', 'cvss_vector_string'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ghsa_id: { kind: 'string' },
                    cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    summary: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    publisher: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    identifiers: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    state: { kind: 'string' },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    submission: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { accepted: { kind: 'boolean' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    vulnerabilities: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        ecosystem: { kind: 'string' },
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        vulnerable_functions: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cvss: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    score: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwes: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    credits: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    credits_detailed: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        type: { kind: 'string' },
                                        state: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_users: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    private_fork: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'security-advisories'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Privately report a security vulnerability',
        name: 'Github: Privately report a security vulnerability',
        description:
            'Report a security vulnerability to the maintainers of the repository.\nSee "[Privately reporting a security vulnerability](https://docs.github.com/code-security/security-advisories/guidance-on-reporting-and-writing/privately-reporting-a-security-vulnerability)" for more information about private vulnerability reporting.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    summary: { kind: 'string' },
                    description: { kind: 'string' },
                    vulnerabilities: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'object',
                                            properties: {
                                                ecosystem: { kind: 'string' },
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['name'],
                                            restriction: 'open',
                                        },
                                        vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        vulnerable_functions: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: ['vulnerable_version_range', 'patched_versions', 'vulnerable_functions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    cvss_vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['vulnerabilities', 'cwe_ids', 'severity', 'cvss_vector_string'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ghsa_id: { kind: 'string' },
                    cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    summary: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    publisher: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    identifiers: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    state: { kind: 'string' },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    submission: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { accepted: { kind: 'boolean' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    vulnerabilities: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        ecosystem: { kind: 'string' },
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        vulnerable_functions: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cvss: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    score: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwes: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    credits: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    credits_detailed: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        type: { kind: 'string' },
                                        state: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_users: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    private_fork: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'security-advisories', 'reports'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository security advisory',
        name: 'Github: Get a repository security advisory',
        description:
            'Get a repository security advisory using its GitHub Security Advisory (GHSA) identifier.\nYou can access any published security advisory on a public repository.\nYou must authenticate using an access token with the `repo` scope or `repository_advisories:read` permission\nin order to get a published security advisory in a private repository, or any unpublished security advisory that you have access to.\n\nYou can access an unpublished security advisory from a repository if you are a security manager or administrator of that repository, or if you are a\ncollaborator on the security advisory.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ghsa_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ghsa_id: { kind: 'string' },
                    cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    summary: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    publisher: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    identifiers: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    state: { kind: 'string' },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    submission: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { accepted: { kind: 'boolean' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    vulnerabilities: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        ecosystem: { kind: 'string' },
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        vulnerable_functions: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cvss: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    score: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwes: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    credits: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    credits_detailed: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        type: { kind: 'string' },
                                        state: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_users: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    private_fork: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'security-advisories', input.inPorts.ghsa_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a repository security advisory',
        name: 'Github: Update a repository security advisory',
        description:
            'Update a repository security advisory using its GitHub Security Advisory (GHSA) identifier.\nYou must authenticate using an access token with the `repo` scope or `repository_advisories:write` permission to use this endpoint.\n\nIn order to update any security advisory, you must be a security manager or administrator of that repository,\nor a collaborator on the repository security advisory.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            ghsa_id: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    summary: { kind: 'string' },
                    description: { kind: 'string' },
                    cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    vulnerabilities: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                package: {
                                    kind: 'object',
                                    properties: {
                                        ecosystem: { kind: 'string' },
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['name'],
                                    restriction: 'open',
                                },
                                vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                vulnerable_functions: {
                                    kind: 'union',
                                    subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                },
                            },
                            optionalProperties: ['vulnerable_version_range', 'patched_versions', 'vulnerable_functions'],
                            restriction: 'open',
                        },
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    credits: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    cvss_vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    state: { kind: 'string' },
                    collaborating_users: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    collaborating_teams: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    ghsa_id: { kind: 'string' },
                    cve_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    summary: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    severity: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    publisher: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    identifiers: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { type: { kind: 'string' }, value: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    state: { kind: 'string' },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    published_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    withdrawn_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    submission: {
                        kind: 'union',
                        subTypes: [
                            { kind: 'object', properties: { accepted: { kind: 'boolean' } }, optionalProperties: [], restriction: 'open' },
                            { kind: 'null' },
                        ],
                    },
                    vulnerabilities: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        package: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        ecosystem: { kind: 'string' },
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        vulnerable_version_range: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        patched_versions: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        vulnerable_functions: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cvss: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    vector_string: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    score: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwes: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { cwe_id: { kind: 'string' }, name: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    cwe_ids: { kind: 'union', subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }] },
                    credits: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: { login: { kind: 'string' }, type: { kind: 'string' } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    credits_detailed: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        user: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        type: { kind: 'string' },
                                        state: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_users: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    collaborating_teams: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'array',
                                subType: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        permission: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        parent: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        members_url: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        permission: { kind: 'string' },
                                                        privacy: { kind: 'string' },
                                                        notification_setting: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        repositories_url: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        ldap_dn: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                                    restriction: 'open',
                                },
                            },
                            { kind: 'null' },
                        ],
                    },
                    private_fork: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            hooks_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'security-advisories', input.inPorts.ghsa_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List stargazers',
        name: 'Github: List stargazers',
        description:
            'Lists the people that have starred the repository.\n\nYou can also find out _when_ stars were created by passing the following custom [media type](https://docs.github.com/rest/overview/media-types/) via the `Accept` header: `application/vnd.github.star+json`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                    },
                    {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                starred_at: { kind: 'string' },
                                user: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'stargazers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the weekly commit activity',
        name: 'Github: Get the weekly commit activity',
        description: 'Returns a weekly aggregate of the number of additions and deletions pushed to a repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'array', subType: { kind: 'array', subType: { kind: 'number' } } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'stats', 'code_frequency'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the last year of commit activity',
        name: 'Github: Get the last year of commit activity',
        description:
            'Returns the last year of commit activity grouped by week. The `days` array is a group of commits per day, starting on `Sunday`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        days: { kind: 'array', subType: { kind: 'number' } },
                        total: { kind: 'number' },
                        week: { kind: 'number' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'stats', 'commit_activity'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all contributor commit activity',
        name: 'Github: Get all contributor commit activity',
        description:
            '\nReturns the `total` number of commits authored by the contributor. In addition, the response includes a Weekly Hash (`weeks` array) with the following information:\n\n*   `w` - Start of the week, given as a [Unix timestamp](http://en.wikipedia.org/wiki/Unix_time).\n*   `a` - Number of additions\n*   `d` - Number of deletions\n*   `c` - Number of commits',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        total: { kind: 'number' },
                        weeks: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: { w: { kind: 'number' }, a: { kind: 'number' }, d: { kind: 'number' }, c: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'stats', 'contributors'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the weekly commit count',
        name: 'Github: Get the weekly commit count',
        description:
            "Returns the total commit counts for the `owner` and total commit counts in `all`. `all` is everyone combined, including the `owner` in the last 52 weeks. If you'd like to get the commit counts for non-owners, you can subtract `owner` from `all`.\n\nThe array order is oldest week (index 0) to most recent week.\n\nThe most recent week is seven days ago at UTC midnight to today at UTC midnight.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { all: { kind: 'array', subType: { kind: 'number' } }, owner: { kind: 'array', subType: { kind: 'number' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'stats', 'participation'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the hourly commit count for each day',
        name: 'Github: Get the hourly commit count for each day',
        description:
            'Each array contains the day number, hour number, and number of commits:\n\n*   `0-6`: Sunday - Saturday\n*   `0-23`: Hour of day\n*   Number of commits\n\nFor example, `[2, 14, 25]` indicates that there were 25 total commits, during the 2:00pm hour on Tuesdays. All times are based on the time zone of individual commits.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'array', subType: { kind: 'array', subType: { kind: 'number' } } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'stats', 'punch_card'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a commit status',
        name: 'Github: Create a commit status',
        description:
            'Users with push access in a repository can create commit statuses for a given SHA.\n\nNote: there is a limit of 1000 statuses per `sha` and `context` within a repository. Attempts to create more than 1000 statuses will result in a validation error.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            sha: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    state: { kind: 'string' },
                    target_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    context: { kind: 'string' },
                },
                optionalProperties: ['target_url', 'description', 'context'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    avatar_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    state: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    target_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    context: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'statuses', input.inPorts.sha].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List watchers',
        name: 'Github: List watchers',
        description: 'Lists the people watching the specified repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'subscribers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a repository subscription',
        name: 'Github: Get a repository subscription',
        description: 'Gets information about whether the authenticated user is subscribed to the repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    subscribed: { kind: 'boolean' },
                    ignored: { kind: 'boolean' },
                    reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'subscription'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set a repository subscription',
        name: 'Github: Set a repository subscription',
        description:
            "If you would like to watch a repository, set `subscribed` to `true`. If you would like to ignore notifications made within a repository, set `ignored` to `true`. If you would like to stop watching a repository, [delete the repository's subscription](https://docs.github.com/rest/activity/watching#delete-a-repository-subscription) completely.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { subscribed: { kind: 'boolean' }, ignored: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    subscribed: { kind: 'boolean' },
                    ignored: { kind: 'boolean' },
                    reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    url: { kind: 'string' },
                    repository_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'subscription'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a repository subscription',
        name: 'Github: Delete a repository subscription',
        description:
            "This endpoint should only be used to stop watching a repository. To control whether or not you wish to receive notifications from a repository, [set the repository's subscription manually](https://docs.github.com/rest/activity/watching#set-a-repository-subscription).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'subscription'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository tags',
        name: 'Github: List repository tags',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'string' },
                        commit: {
                            kind: 'object',
                            properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        zipball_url: { kind: 'string' },
                        tarball_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'tags'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List tag protection states for a repository',
        name: 'Github: List tag protection states for a repository',
        description:
            'This returns the tag protection states of a repository.\n\nThis information is only available to repository administrators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        enabled: { kind: 'boolean' },
                        pattern: { kind: 'string' },
                    },
                    optionalProperties: ['id', 'created_at', 'updated_at', 'enabled'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'tags', 'protection'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a tag protection state for a repository',
        name: 'Github: Create a tag protection state for a repository',
        description: 'This creates a tag protection state for a repository.\nThis endpoint is only available to repository administrators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { pattern: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    enabled: { kind: 'boolean' },
                    pattern: { kind: 'string' },
                },
                optionalProperties: ['id', 'created_at', 'updated_at', 'enabled'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'tags', 'protection'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a tag protection state for a repository',
        name: 'Github: Delete a tag protection state for a repository',
        description: 'This deletes a tag protection state for a repository.\nThis endpoint is only available to repository administrators.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            tag_protection_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'tags', 'protection', input.inPorts.tag_protection_id].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository teams',
        name: 'Github: List repository teams',
        description:
            'Lists the teams that have access to the specified repository and that are also visible to the authenticated user.\n\nFor a public repository, a team is listed only if that team added the public repository explicitly.\n\nPersonal access tokens require the following scopes:\n* `public_repo` to call this endpoint on a public repository\n* `repo` to call this endpoint on a private repository (this scope also includes public repositories)\n\nThis endpoint is not compatible with fine-grained personal access tokens.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'teams'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all repository topics',
        name: 'Github: Get all repository topics',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { names: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'topics'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Replace all repository topics',
        name: 'Github: Replace all repository topics',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { names: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { names: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'topics'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get repository clones',
        name: 'Github: Get repository clones',
        description:
            'Get the total number of clones and breakdown per day or week for the last 14 days. Timestamps are aligned to UTC midnight of the beginning of the day or week. Week begins on Monday.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per: { kind: 'string' },
        },
        optionalInPorts: ['per'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    count: { kind: 'number' },
                    uniques: { kind: 'number' },
                    clones: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { timestamp: { kind: 'string' }, uniques: { kind: 'number' }, count: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'traffic', 'clones'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ per: input.inPorts.per }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get top referral paths',
        name: 'Github: Get top referral paths',
        description: 'Get the top 10 popular contents over the last 14 days.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        path: { kind: 'string' },
                        title: { kind: 'string' },
                        count: { kind: 'number' },
                        uniques: { kind: 'number' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'traffic', 'popular', 'paths'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get top referral sources',
        name: 'Github: Get top referral sources',
        description: 'Get the top 10 referrers over the last 14 days.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: { referrer: { kind: 'string' }, count: { kind: 'number' }, uniques: { kind: 'number' } },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'traffic', 'popular', 'referrers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get page views',
        name: 'Github: Get page views',
        description:
            'Get the total number of views and breakdown per day or week for the last 14 days. Timestamps are aligned to UTC midnight of the beginning of the day or week. Week begins on Monday.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            per: { kind: 'string' },
        },
        optionalInPorts: ['per'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    count: { kind: 'number' },
                    uniques: { kind: 'number' },
                    views: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { timestamp: { kind: 'string' }, uniques: { kind: 'number' }, count: { kind: 'number' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'traffic', 'views'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ per: input.inPorts.per }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Transfer a repository',
        name: 'Github: Transfer a repository',
        description:
            'A transfer request will need to be accepted by the new owner when transferring a personal repository to another user. The response will contain the original `owner`, and the transfer will continue asynchronously. For more details on the requirements to transfer personal and organization-owned repositories, see [about repository transfers](https://docs.github.com/articles/about-repository-transfers/).\nYou must use a personal access token (classic) or an OAuth token for this endpoint. An installation access token or a fine-grained personal access token cannot be used because they are only granted access to a single account.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    new_owner: { kind: 'string' },
                    new_name: { kind: 'string' },
                    team_ids: { kind: 'array', subType: { kind: 'number' } },
                },
                optionalProperties: ['new_name', 'team_ids'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    role_name: { kind: 'string' },
                    temp_clone_token: { kind: 'string' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    code_of_conduct: {
                        kind: 'object',
                        properties: {
                            key: { kind: 'string' },
                            name: { kind: 'string' },
                            url: { kind: 'string' },
                            body: { kind: 'string' },
                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: ['body'],
                        restriction: 'open',
                    },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    spdx_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    node_id: { kind: 'string' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    forks: { kind: 'number' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    security_and_analysis: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    advanced_security: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    dependabot_security_updates: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    secret_scanning_push_protection: {
                                        kind: 'object',
                                        properties: { status: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                },
                optionalProperties: [
                    'git_url',
                    'ssh_url',
                    'clone_url',
                    'mirror_url',
                    'svn_url',
                    'homepage',
                    'language',
                    'forks_count',
                    'stargazers_count',
                    'watchers_count',
                    'size',
                    'default_branch',
                    'open_issues_count',
                    'is_template',
                    'topics',
                    'has_issues',
                    'has_projects',
                    'has_wiki',
                    'has_pages',
                    'has_downloads',
                    'has_discussions',
                    'archived',
                    'disabled',
                    'visibility',
                    'pushed_at',
                    'created_at',
                    'updated_at',
                    'permissions',
                    'role_name',
                    'temp_clone_token',
                    'delete_branch_on_merge',
                    'subscribers_count',
                    'network_count',
                    'code_of_conduct',
                    'license',
                    'forks',
                    'open_issues',
                    'watchers',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'security_and_analysis',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'transfer'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if vulnerability alerts are enabled for a repository',
        name: 'Github: Check if vulnerability alerts are enabled for a repository',
        description:
            'Shows whether dependency alerts are enabled or disabled for a repository. The authenticated user must have admin read access to the repository. For more information, see "[About security alerts for vulnerable dependencies](https://docs.github.com/articles/about-security-alerts-for-vulnerable-dependencies)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'vulnerability-alerts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Enable vulnerability alerts',
        name: 'Github: Enable vulnerability alerts',
        description:
            'Enables dependency alerts and the dependency graph for a repository. The authenticated user must have admin access to the repository. For more information, see "[About security alerts for vulnerable dependencies](https://docs.github.com/articles/about-security-alerts-for-vulnerable-dependencies)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'vulnerability-alerts'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Disable vulnerability alerts',
        name: 'Github: Disable vulnerability alerts',
        description:
            'Disables dependency alerts and the dependency graph for a repository.\nThe authenticated user must have admin access to the repository. For more information,\nsee "[About security alerts for vulnerable dependencies](https://docs.github.com/articles/about-security-alerts-for-vulnerable-dependencies)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.owner, input.inPorts.repo, 'vulnerability-alerts'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository using a template',
        name: 'Github: Create a repository using a template',
        description:
            "Creates a new repository using a repository template. Use the `template_owner` and `template_repo` route parameters to specify the repository to use as the template. If the repository is not public, the authenticated user must own or be a member of an organization that owns the repository. To check if a repository is available to use as a template, get the repository's information using the [Get a repository](https://docs.github.com/rest/repos/repos#get-a-repository) endpoint and check that the `is_template` key is `true`.\n\n**OAuth scope requirements**\n\nWhen using [OAuth](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/), authorizations must include:\n\n*   `public_repo` scope or `repo` scope to create a public repository. Note: For GitHub AE, use `repo` scope to create an internal repository.\n*   `repo` scope to create a private repository",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            template_owner: { kind: 'string' },
            template_repo: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    owner: { kind: 'string' },
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    include_all_branches: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: ['owner', 'description', 'include_all_branches', 'private'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    organization: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    forks: { kind: 'number' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                        },
                        optionalProperties: ['triage', 'maintain'],
                        restriction: 'open',
                    },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'string' },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'string' },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'string' },
                                    language: { kind: 'string' },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    master_branch: { kind: 'string' },
                    starred_at: { kind: 'string' },
                    anonymous_access_enabled: { kind: 'boolean' },
                },
                optionalProperties: [
                    'organization',
                    'permissions',
                    'is_template',
                    'topics',
                    'has_discussions',
                    'visibility',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_update_branch',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'allow_merge_commit',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'subscribers_count',
                    'network_count',
                    'master_branch',
                    'starred_at',
                    'anonymous_access_enabled',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repos', input.inPorts.template_owner, input.inPorts.template_repo, 'generate'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public repositories',
        name: 'Github: List public repositories',
        description:
            'Lists all public repositories in the order that they were created.\n\nNote:\n- For GitHub Enterprise Server, this endpoint will only list repositories available to all users on the enterprise.\n- Pagination is powered exclusively by the `since` parameter. Use the [Link header](https://docs.github.com/rest/guides/using-pagination-in-the-rest-api#using-link-headers) to get the URL for the next page of repositories.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            since: { kind: 'number' },
        },
        optionalInPorts: ['since'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ since: input.inPorts.since }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List environment secrets',
        name: 'Github: List environment secrets',
        description:
            'Lists all secrets available in an environment without revealing their\nencrypted values.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    secrets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { name: { kind: 'string' }, created_at: { kind: 'string' }, updated_at: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'repositories', input.inPorts.repository_id, 'environments', input.inPorts.environment_name, 'secrets'].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an environment public key',
        name: 'Github: Get an environment public key',
        description:
            'Get the public key for an environment, which you need to encrypt environment\nsecrets. You need to encrypt a secret before you can create or update secrets.\n\nAnyone with read access to the repository can use this endpoint.\nIf the repository is private you must use an access token with the `repo` scope.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key_id: { kind: 'string' },
                    key: { kind: 'string' },
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: ['id', 'url', 'title', 'created_at'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'secrets',
                        'public-key',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an environment secret',
        name: 'Github: Get an environment secret',
        description:
            'Gets a single environment secret without revealing its encrypted value.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { name: { kind: 'string' }, created_at: { kind: 'string' }, updated_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'secrets',
                        input.inPorts.secret_name,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create or update an environment secret',
        name: 'Github: Create or update an environment secret',
        description:
            'Creates or updates an environment secret with an encrypted value. Encrypt your secret using\n[LibSodium](https://libsodium.gitbook.io/doc/bindings_for_other_languages).\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.\n\n**Example encrypting a secret using Node.js**\n\nEncrypt your secret using the [libsodium-wrappers](https://www.npmjs.com/package/libsodium-wrappers) library.\n\n```\nconst sodium = require(\'libsodium-wrappers\')\nconst secret = \'plain-text-secret\' // replace with the secret you want to encrypt\nconst key = \'base64-encoded-public-key\' // replace with the Base64 encoded public key\n\n//Check if libsodium is ready and then proceed.\nsodium.ready.then(() => {\n  // Convert Secret & Base64 key to Uint8Array.\n  let binkey = sodium.from_base64(key, sodium.base64_variants.ORIGINAL)\n  let binsec = sodium.from_string(secret)\n\n  //Encrypt the secret using LibSodium\n  let encBytes = sodium.crypto_box_seal(binsec, binkey)\n\n  // Convert encrypted Uint8Array to Base64\n  let output = sodium.to_base64(encBytes, sodium.base64_variants.ORIGINAL)\n\n  console.log(output)\n});\n```\n\n**Example encrypting a secret using Python**\n\nEncrypt your secret using [pynacl](https://pynacl.readthedocs.io/en/latest/public/#nacl-public-sealedbox) with Python 3.\n\n```\nfrom base64 import b64encode\nfrom nacl import encoding, public\n\ndef encrypt(public_key: str, secret_value: str) -> str:\n  """Encrypt a Unicode string using the public key."""\n  public_key = public.PublicKey(public_key.encode("utf-8"), encoding.Base64Encoder())\n  sealed_box = public.SealedBox(public_key)\n  encrypted = sealed_box.encrypt(secret_value.encode("utf-8"))\n  return b64encode(encrypted).decode("utf-8")\n```\n\n**Example encrypting a secret using C#**\n\nEncrypt your secret using the [Sodium.Core](https://www.nuget.org/packages/Sodium.Core/) package.\n\n```\nvar secretValue = System.Text.Encoding.UTF8.GetBytes("mySecret");\nvar publicKey = Convert.FromBase64String("2Sg8iYjAxxmI2LvUXpJjkYrMxURPc8r+dB7TJyvvcCU=");\n\nvar sealedPublicKeyBox = Sodium.SealedPublicKeyBox.Create(secretValue, publicKey);\n\nConsole.WriteLine(Convert.ToBase64String(sealedPublicKeyBox));\n```\n\n**Example encrypting a secret using Ruby**\n\nEncrypt your secret using the [rbnacl](https://github.com/RubyCrypto/rbnacl) gem.\n\n```ruby\nrequire "rbnacl"\nrequire "base64"\n\nkey = Base64.decode64("+ZYvJDZMHUfBkJdyq5Zm9SKqeuBQ4sj+6sfjlH4CgG0=")\npublic_key = RbNaCl::PublicKey.new(key)\n\nbox = RbNaCl::Boxes::Sealed.from_public_key(public_key)\nencrypted_secret = box.encrypt("my_secret")\n\n# Print the base64 encoded secret\nputs Base64.strict_encode64(encrypted_secret)\n```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            secret_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { encrypted_value: { kind: 'string' }, key_id: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'secrets',
                        input.inPorts.secret_name,
                    ].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an environment secret',
        name: 'Github: Delete an environment secret',
        description:
            'Deletes a secret in an environment using the secret name.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nGitHub Apps must have the `secrets` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read secrets.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'secrets',
                        input.inPorts.secret_name,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List environment variables',
        name: 'Github: List environment variables',
        description:
            'Lists all environment variables.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `environments:read` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    variables: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                value: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'variables',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create an environment variable',
        name: 'Github: Create an environment variable',
        description:
            'Create an environment variable that you can reference in a GitHub Actions workflow.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `environment:write` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, value: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'variables',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an environment variable',
        name: 'Github: Get an environment variable',
        description:
            'Gets a specific variable in an environment.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `environments:read` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            environment_name: { kind: 'string' },
            name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    value: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'variables',
                        input.inPorts.name,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an environment variable',
        name: 'Github: Delete an environment variable',
        description:
            'Deletes an environment variable using the variable name.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `environment:write` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            name: { kind: 'string' },
            environment_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'variables',
                        input.inPorts.name,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an environment variable',
        name: 'Github: Update an environment variable',
        description:
            'Updates an environment variable that you can reference in a GitHub Actions workflow.\n\nYou must authenticate using an access token with the `repo` scope to use this endpoint.\nIf the repository is private, you must use an access token with the `repo` scope.\nGitHub Apps must have the `environment:write` repository permission to use this endpoint.\nAuthenticated users must have collaborator access to a repository to create, update, or read variables.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            name: { kind: 'string' },
            environment_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, value: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'repositories',
                        input.inPorts.repository_id,
                        'environments',
                        input.inPorts.environment_name,
                        'variables',
                        input.inPorts.name,
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search code',
        name: 'Github: Search code',
        description:
            "Searches for query terms inside of a file. This method returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination).\n\nWhen searching for code, you can get text match metadata for the file **content** and file **path** fields when you pass the `text-match` media type. For more details about how to receive highlighted search results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you want to find the definition of the `addClass` function inside [jQuery](https://github.com/jquery/jquery) repository, your query would look something like this:\n\n`q=addClass+in:file+language:js+repo:jquery/jquery`\n\nThis query searches for the keyword `addClass` within a file's contents. The query limits the search to files where the language is JavaScript in the `jquery/jquery` repository.\n\nConsiderations for code search:\n\nDue to the complexity of searching code, there are a few restrictions on how searches are performed:\n\n*   Only the _default branch_ is considered. In most cases, this will be the `master` branch.\n*   Only files smaller than 384 KB are searchable.\n*   You must always include at least one search term when searching source code. For example, searching for [`language:go`](https://github.com/search?utf8=%E2%9C%93&q=language%3Ago&type=Code) is not valid, while [`amazing\nlanguage:go`](https://github.com/search?utf8=%E2%9C%93&q=amazing+language%3Ago&type=Code) is.\n\nThis endpoint requires you to authenticate and limits you to 10 requests per minute.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            q: { kind: 'string' },
            sort: { kind: 'string' },
            order: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'order', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                path: { kind: 'string' },
                                sha: { kind: 'string' },
                                url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                score: { kind: 'number' },
                                file_size: { kind: 'number' },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_modified_at: { kind: 'string' },
                                line_numbers: { kind: 'array', subType: { kind: 'string' } },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: ['file_size', 'language', 'last_modified_at', 'line_numbers', 'text_matches'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'code'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            sort: input.inPorts.sort,
                            order: input.inPorts.order,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search commits',
        name: 'Github: Search commits',
        description:
            'Find commits via various criteria on the default branch (usually `main`). This method returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination).\n\nWhen searching for commits, you can get text match metadata for the **message** field when you provide the `text-match` media type. For more details about how to receive highlighted search results, see [Text match\nmetadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you want to find commits related to CSS in the [octocat/Spoon-Knife](https://github.com/octocat/Spoon-Knife) repository. Your query would look something like this:\n\n`q=repo:octocat/Spoon-Knife+css`',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            q: { kind: 'string' },
            sort: { kind: 'string' },
            order: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'order', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                sha: { kind: 'string' },
                                html_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commit: {
                                    kind: 'object',
                                    properties: {
                                        author: {
                                            kind: 'object',
                                            properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        committer: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'string' },
                                                        email: { kind: 'string' },
                                                        date: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        comment_count: { kind: 'number' },
                                        message: { kind: 'string' },
                                        tree: {
                                            kind: 'object',
                                            properties: { sha: { kind: 'string' }, url: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        url: { kind: 'string' },
                                        verification: {
                                            kind: 'object',
                                            properties: {
                                                verified: { kind: 'boolean' },
                                                reason: { kind: 'string' },
                                                payload: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                signature: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['verification'],
                                    restriction: 'open',
                                },
                                author: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                committer: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: { name: { kind: 'string' }, email: { kind: 'string' }, date: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                parents: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { url: { kind: 'string' }, html_url: { kind: 'string' }, sha: { kind: 'string' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                score: { kind: 'number' },
                                node_id: { kind: 'string' },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: ['text_matches'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'commits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            sort: input.inPorts.sort,
                            order: input.inPorts.order,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search issues and pull requests',
        name: 'Github: Search issues and pull requests',
        description:
            'Find issues by state and keyword. This method returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination).\n\nWhen searching for issues, you can get text match metadata for the issue **title**, issue **body**, and issue **comment body** fields when you pass the `text-match` media type. For more details about how to receive highlighted\nsearch results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you want to find the oldest unresolved Python bugs on Windows. Your query might look something like this.\n\n`q=windows+label:bug+language:python+state:open&sort=created&order=asc`\n\nThis query searches for the keyword `windows`, within any open issue that is labeled as `bug`. The search runs across repositories whose primary language is Python. The results are sorted by creation date in ascending order, which means the oldest issues appear first in the search results.\n\n**Note:** For requests made by GitHub Apps with a user access token, you can\'t retrieve a combination of issues and pull requests in a single query. Requests that don\'t include the `is:issue` or `is:pull-request` qualifier will receive an HTTP `422 Unprocessable Entity` response. To get results for both issues and pull requests, you must send separate queries for issues and pull requests. For more information about the `is` qualifier, see "[Searching only issues or pull requests](https://docs.github.com/github/searching-for-information-on-github/searching-issues-and-pull-requests#search-only-issues-or-pull-requests)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            q: { kind: 'string' },
            sort: { kind: 'string' },
            order: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'order', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                repository_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                number: { kind: 'number' },
                                title: { kind: 'string' },
                                locked: { kind: 'boolean' },
                                active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                assignees: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                user: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                labels: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            color: { kind: 'string' },
                                            default: { kind: 'boolean' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                state: { kind: 'string' },
                                state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                assignee: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                milestone: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                number: { kind: 'number' },
                                                state: { kind: 'string' },
                                                title: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                creator: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                open_issues: { kind: 'number' },
                                                closed_issues: { kind: 'number' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                comments: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                pull_request: {
                                    kind: 'object',
                                    properties: {
                                        merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['merged_at'],
                                    restriction: 'open',
                                },
                                body: { kind: 'string' },
                                score: { kind: 'number' },
                                author_association: { kind: 'string' },
                                draft: { kind: 'boolean' },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        node_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['html_url'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        organization: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                            },
                                            optionalProperties: ['triage', 'maintain'],
                                            restriction: 'open',
                                        },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        template_repository: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        full_name: { kind: 'string' },
                                                        owner: {
                                                            kind: 'object',
                                                            properties: {
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        private: { kind: 'boolean' },
                                                        html_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        fork: { kind: 'boolean' },
                                                        url: { kind: 'string' },
                                                        archive_url: { kind: 'string' },
                                                        assignees_url: { kind: 'string' },
                                                        blobs_url: { kind: 'string' },
                                                        branches_url: { kind: 'string' },
                                                        collaborators_url: { kind: 'string' },
                                                        comments_url: { kind: 'string' },
                                                        commits_url: { kind: 'string' },
                                                        compare_url: { kind: 'string' },
                                                        contents_url: { kind: 'string' },
                                                        contributors_url: { kind: 'string' },
                                                        deployments_url: { kind: 'string' },
                                                        downloads_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        forks_url: { kind: 'string' },
                                                        git_commits_url: { kind: 'string' },
                                                        git_refs_url: { kind: 'string' },
                                                        git_tags_url: { kind: 'string' },
                                                        git_url: { kind: 'string' },
                                                        issue_comment_url: { kind: 'string' },
                                                        issue_events_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        keys_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        languages_url: { kind: 'string' },
                                                        merges_url: { kind: 'string' },
                                                        milestones_url: { kind: 'string' },
                                                        notifications_url: { kind: 'string' },
                                                        pulls_url: { kind: 'string' },
                                                        releases_url: { kind: 'string' },
                                                        ssh_url: { kind: 'string' },
                                                        stargazers_url: { kind: 'string' },
                                                        statuses_url: { kind: 'string' },
                                                        subscribers_url: { kind: 'string' },
                                                        subscription_url: { kind: 'string' },
                                                        tags_url: { kind: 'string' },
                                                        teams_url: { kind: 'string' },
                                                        trees_url: { kind: 'string' },
                                                        clone_url: { kind: 'string' },
                                                        mirror_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        svn_url: { kind: 'string' },
                                                        homepage: { kind: 'string' },
                                                        language: { kind: 'string' },
                                                        forks_count: { kind: 'number' },
                                                        stargazers_count: { kind: 'number' },
                                                        watchers_count: { kind: 'number' },
                                                        size: { kind: 'number' },
                                                        default_branch: { kind: 'string' },
                                                        open_issues_count: { kind: 'number' },
                                                        is_template: { kind: 'boolean' },
                                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                                        has_issues: { kind: 'boolean' },
                                                        has_projects: { kind: 'boolean' },
                                                        has_wiki: { kind: 'boolean' },
                                                        has_pages: { kind: 'boolean' },
                                                        has_downloads: { kind: 'boolean' },
                                                        archived: { kind: 'boolean' },
                                                        disabled: { kind: 'boolean' },
                                                        visibility: { kind: 'string' },
                                                        pushed_at: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                admin: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                pull: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        allow_rebase_merge: { kind: 'boolean' },
                                                        temp_clone_token: { kind: 'string' },
                                                        allow_squash_merge: { kind: 'boolean' },
                                                        allow_auto_merge: { kind: 'boolean' },
                                                        delete_branch_on_merge: { kind: 'boolean' },
                                                        allow_update_branch: { kind: 'boolean' },
                                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                                        squash_merge_commit_title: { kind: 'string' },
                                                        squash_merge_commit_message: { kind: 'string' },
                                                        merge_commit_title: { kind: 'string' },
                                                        merge_commit_message: { kind: 'string' },
                                                        allow_merge_commit: { kind: 'boolean' },
                                                        subscribers_count: { kind: 'number' },
                                                        network_count: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        master_branch: { kind: 'string' },
                                        starred_at: { kind: 'string' },
                                        anonymous_access_enabled: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'organization',
                                        'permissions',
                                        'is_template',
                                        'topics',
                                        'has_discussions',
                                        'visibility',
                                        'allow_rebase_merge',
                                        'template_repository',
                                        'temp_clone_token',
                                        'allow_squash_merge',
                                        'allow_auto_merge',
                                        'delete_branch_on_merge',
                                        'allow_update_branch',
                                        'use_squash_pr_title_as_default',
                                        'squash_merge_commit_title',
                                        'squash_merge_commit_message',
                                        'merge_commit_title',
                                        'merge_commit_message',
                                        'allow_merge_commit',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'subscribers_count',
                                        'network_count',
                                        'master_branch',
                                        'starred_at',
                                        'anonymous_access_enabled',
                                    ],
                                    restriction: 'open',
                                },
                                body_html: { kind: 'string' },
                                body_text: { kind: 'string' },
                                timeline_url: { kind: 'string' },
                                performed_via_github_app: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                slug: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                                owner: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                name: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                external_url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        issues: { kind: 'string' },
                                                        checks: { kind: 'string' },
                                                        metadata: { kind: 'string' },
                                                        contents: { kind: 'string' },
                                                        deployments: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                events: { kind: 'array', subType: { kind: 'string' } },
                                                installations_count: { kind: 'number' },
                                                client_id: { kind: 'string' },
                                                client_secret: { kind: 'string' },
                                                webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                pem: { kind: 'string' },
                                            },
                                            optionalProperties: [
                                                'slug',
                                                'installations_count',
                                                'client_id',
                                                'client_secret',
                                                'webhook_secret',
                                                'pem',
                                            ],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                reactions: {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        total_count: { kind: 'number' },
                                        '+1': { kind: 'number' },
                                        '-1': { kind: 'number' },
                                        laugh: { kind: 'number' },
                                        confused: { kind: 'number' },
                                        heart: { kind: 'number' },
                                        hooray: { kind: 'number' },
                                        eyes: { kind: 'number' },
                                        rocket: { kind: 'number' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [
                                'active_lock_reason',
                                'assignees',
                                'state_reason',
                                'text_matches',
                                'pull_request',
                                'body',
                                'draft',
                                'repository',
                                'body_html',
                                'body_text',
                                'timeline_url',
                                'performed_via_github_app',
                                'reactions',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'issues'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            sort: input.inPorts.sort,
                            order: input.inPorts.order,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search labels',
        name: 'Github: Search labels',
        description:
            'Find labels in a repository with names or descriptions that match search keywords. Returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination).\n\nWhen searching for labels, you can get text match metadata for the label **name** and **description** fields when you pass the `text-match` media type. For more details about how to receive highlighted search results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you want to find labels in the `linguist` repository that match `bug`, `defect`, or `enhancement`. Your query might look like this:\n\n`q=bug+defect+enhancement&repository_id=64778136`\n\nThe labels that best match the query appear first in the search results.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            repository_id: { kind: 'number' },
            q: { kind: 'string' },
            sort: { kind: 'string' },
            order: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'order', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                name: { kind: 'string' },
                                color: { kind: 'string' },
                                default: { kind: 'boolean' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                score: { kind: 'number' },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: ['text_matches'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'labels'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            repository_id: input.inPorts.repository_id,
                            q: input.inPorts.q,
                            sort: input.inPorts.sort,
                            order: input.inPorts.order,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search repositories',
        name: 'Github: Search repositories',
        description:
            'Find repositories via various criteria. This method returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination).\n\nWhen searching for repositories, you can get text match metadata for the **name** and **description** fields when you pass the `text-match` media type. For more details about how to receive highlighted search results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you want to search for popular Tetris repositories written in assembly code, your query might look like this:\n\n`q=tetris+language:assembly&sort=stars&order=desc`\n\nThis query searches for repositories with the word `tetris` in the name, the description, or the README. The results are limited to repositories where the primary language is assembly. The results are sorted by stars in descending order, so that the most popular repositories appear first in the search results.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            q: { kind: 'string' },
            sort: { kind: 'string' },
            order: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'order', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                pushed_at: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                size: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                open_issues_count: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                default_branch: { kind: 'string' },
                                score: { kind: 'number' },
                                forks_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['maintain', 'triage'],
                                    restriction: 'open',
                                },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_rebase_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                is_template: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'master_branch',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'permissions',
                                'text_matches',
                                'temp_clone_token',
                                'allow_merge_commit',
                                'allow_squash_merge',
                                'allow_rebase_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_forking',
                                'is_template',
                                'web_commit_signoff_required',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            sort: input.inPorts.sort,
                            order: input.inPorts.order,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search topics',
        name: 'Github: Search topics',
        description:
            'Find topics via various criteria. Results are sorted by best match. This method returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination). See "[Searching topics](https://docs.github.com/articles/searching-topics/)" for a detailed list of qualifiers.\n\nWhen searching for topics, you can get text match metadata for the topic\'s **short\\_description**, **description**, **name**, or **display\\_name** field when you pass the `text-match` media type. For more details about how to receive highlighted search results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you want to search for topics related to Ruby that are featured on https://github.com/topics. Your query might look like this:\n\n`q=ruby+is:featured`\n\nThis query searches for topics with the keyword `ruby` and limits the results to find only topics that are featured. The topics that are the best match for the query appear first in the search results.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            q: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                short_description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_by: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                released: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                featured: { kind: 'boolean' },
                                curated: { kind: 'boolean' },
                                score: { kind: 'number' },
                                repository_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                logo_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                related: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    topic_relation: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            name: { kind: 'string' },
                                                            topic_id: { kind: 'number' },
                                                            relation_type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                aliases: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'array',
                                            subType: {
                                                kind: 'object',
                                                properties: {
                                                    topic_relation: {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            name: { kind: 'string' },
                                                            topic_id: { kind: 'number' },
                                                            relation_type: { kind: 'string' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: ['repository_count', 'logo_url', 'text_matches', 'related', 'aliases'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'topics'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ q: input.inPorts.q, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Search users',
        name: 'Github: Search users',
        description:
            'Find users via various criteria. This method returns up to 100 results [per page](https://docs.github.com/rest/overview/resources-in-the-rest-api#pagination).\n\nWhen searching for users, you can get text match metadata for the issue **login**, public **email**, and **name** fields when you pass the `text-match` media type. For more details about highlighting search results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata). For more details about how to receive highlighted search results, see [Text match metadata](https://docs.github.com/rest/reference/search#text-match-metadata).\n\nFor example, if you\'re looking for a list of popular users, you might try this query:\n\n`q=tom+repos:%3E42+followers:%3E1000`\n\nThis query searches for users with the name `tom`. The results are restricted to users with more than 42 repositories and over 1,000 followers.\n\nThis endpoint does not accept authentication and will only include publicly visible users. As an alternative, you can use the GraphQL API. The GraphQL API requires authentication and will return private users, including Enterprise Managed Users (EMUs), that you are authorized to view. For more information, see "[GraphQL Queries](https://docs.github.com/graphql/reference/queries#search)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            q: { kind: 'string' },
            sort: { kind: 'string' },
            order: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'order', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    incomplete_results: { kind: 'boolean' },
                    items: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                score: { kind: 'number' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                public_repos: { kind: 'number' },
                                public_gists: { kind: 'number' },
                                followers: { kind: 'number' },
                                following: { kind: 'number' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                site_admin: { kind: 'boolean' },
                                hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                text_matches: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            object_url: { kind: 'string' },
                                            object_type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            property: { kind: 'string' },
                                            fragment: { kind: 'string' },
                                            matches: {
                                                kind: 'array',
                                                subType: {
                                                    kind: 'object',
                                                    properties: {
                                                        text: { kind: 'string' },
                                                        indices: { kind: 'array', subType: { kind: 'number' } },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [
                                'public_repos',
                                'public_gists',
                                'followers',
                                'following',
                                'created_at',
                                'updated_at',
                                'name',
                                'bio',
                                'email',
                                'location',
                                'hireable',
                                'text_matches',
                                'blog',
                                'company',
                                'suspended_at',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'search', 'users'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            q: input.inPorts.q,
                            sort: input.inPorts.sort,
                            order: input.inPorts.order,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a team (Legacy)',
        name: 'Github: Get a team (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the [Get a team by name](https://docs.github.com/rest/teams/teams#get-a-team-by-name) endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    name: { kind: 'string' },
                    slug: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    members_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    parent: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    members_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permission: { kind: 'string' },
                                    privacy: { kind: 'string' },
                                    notification_setting: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    repositories_url: { kind: 'string' },
                                    slug: { kind: 'string' },
                                    ldap_dn: { kind: 'string' },
                                },
                                optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    members_count: { kind: 'number' },
                    repos_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            company: { kind: 'string' },
                            blog: { kind: 'string' },
                            location: { kind: 'string' },
                            email: { kind: 'string' },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_verified: { kind: 'boolean' },
                            has_organization_projects: { kind: 'boolean' },
                            has_repository_projects: { kind: 'boolean' },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            html_url: { kind: 'string' },
                            created_at: { kind: 'string' },
                            type: { kind: 'string' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            plan: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                    filled_seats: { kind: 'number' },
                                    seats: { kind: 'number' },
                                },
                                optionalProperties: ['filled_seats', 'seats'],
                                restriction: 'open',
                            },
                            default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            members_allowed_repository_creation_type: { kind: 'string' },
                            members_can_create_public_repositories: { kind: 'boolean' },
                            members_can_create_private_repositories: { kind: 'boolean' },
                            members_can_create_internal_repositories: { kind: 'boolean' },
                            members_can_create_pages: { kind: 'boolean' },
                            members_can_create_public_pages: { kind: 'boolean' },
                            members_can_create_private_pages: { kind: 'boolean' },
                            members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            web_commit_signoff_required: { kind: 'boolean' },
                            updated_at: { kind: 'string' },
                            archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [
                            'name',
                            'company',
                            'blog',
                            'location',
                            'email',
                            'twitter_username',
                            'is_verified',
                            'total_private_repos',
                            'owned_private_repos',
                            'private_gists',
                            'disk_usage',
                            'collaborators',
                            'billing_email',
                            'plan',
                            'default_repository_permission',
                            'members_can_create_repositories',
                            'two_factor_requirement_enabled',
                            'members_allowed_repository_creation_type',
                            'members_can_create_public_repositories',
                            'members_can_create_private_repositories',
                            'members_can_create_internal_repositories',
                            'members_can_create_pages',
                            'members_can_create_public_pages',
                            'members_can_create_private_pages',
                            'members_can_fork_private_repositories',
                            'web_commit_signoff_required',
                        ],
                        restriction: 'open',
                    },
                    ldap_dn: { kind: 'string' },
                },
                optionalProperties: ['privacy', 'notification_setting', 'parent', 'ldap_dn'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a team (Legacy)',
        name: 'Github: Delete a team (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Delete a team](https://docs.github.com/rest/teams/teams#delete-a-team) endpoint.\n\nTo delete a team, the authenticated user must be an organization owner or team maintainer.\n\nIf you are an organization owner, deleting a parent team will delete all of its child teams as well.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a team (Legacy)',
        name: 'Github: Update a team (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Update a team](https://docs.github.com/rest/teams/teams#update-a-team) endpoint.\n\nTo edit a team, the authenticated user must either be an organization owner or a team maintainer.\n\n**Note:** With nested teams, the `privacy` for parent teams cannot be `secret`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    parent_team_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                },
                optionalProperties: ['description', 'privacy', 'notification_setting', 'permission', 'parent_team_id'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    name: { kind: 'string' },
                    slug: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    privacy: { kind: 'string' },
                    notification_setting: { kind: 'string' },
                    permission: { kind: 'string' },
                    members_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    parent: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    url: { kind: 'string' },
                                    members_url: { kind: 'string' },
                                    name: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permission: { kind: 'string' },
                                    privacy: { kind: 'string' },
                                    notification_setting: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    repositories_url: { kind: 'string' },
                                    slug: { kind: 'string' },
                                    ldap_dn: { kind: 'string' },
                                },
                                optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    members_count: { kind: 'number' },
                    repos_count: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            name: { kind: 'string' },
                            company: { kind: 'string' },
                            blog: { kind: 'string' },
                            location: { kind: 'string' },
                            email: { kind: 'string' },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            is_verified: { kind: 'boolean' },
                            has_organization_projects: { kind: 'boolean' },
                            has_repository_projects: { kind: 'boolean' },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            html_url: { kind: 'string' },
                            created_at: { kind: 'string' },
                            type: { kind: 'string' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                            billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            plan: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                    filled_seats: { kind: 'number' },
                                    seats: { kind: 'number' },
                                },
                                optionalProperties: ['filled_seats', 'seats'],
                                restriction: 'open',
                            },
                            default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            members_allowed_repository_creation_type: { kind: 'string' },
                            members_can_create_public_repositories: { kind: 'boolean' },
                            members_can_create_private_repositories: { kind: 'boolean' },
                            members_can_create_internal_repositories: { kind: 'boolean' },
                            members_can_create_pages: { kind: 'boolean' },
                            members_can_create_public_pages: { kind: 'boolean' },
                            members_can_create_private_pages: { kind: 'boolean' },
                            members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            web_commit_signoff_required: { kind: 'boolean' },
                            updated_at: { kind: 'string' },
                            archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [
                            'name',
                            'company',
                            'blog',
                            'location',
                            'email',
                            'twitter_username',
                            'is_verified',
                            'total_private_repos',
                            'owned_private_repos',
                            'private_gists',
                            'disk_usage',
                            'collaborators',
                            'billing_email',
                            'plan',
                            'default_repository_permission',
                            'members_can_create_repositories',
                            'two_factor_requirement_enabled',
                            'members_allowed_repository_creation_type',
                            'members_can_create_public_repositories',
                            'members_can_create_private_repositories',
                            'members_can_create_internal_repositories',
                            'members_can_create_pages',
                            'members_can_create_public_pages',
                            'members_can_create_private_pages',
                            'members_can_fork_private_repositories',
                            'web_commit_signoff_required',
                        ],
                        restriction: 'open',
                    },
                    ldap_dn: { kind: 'string' },
                },
                optionalProperties: ['privacy', 'notification_setting', 'parent', 'ldap_dn'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List discussions (Legacy)',
        name: 'Github: List discussions (Legacy)',
        description:
            "**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List discussions`](https://docs.github.com/rest/teams/discussions#list-discussions) endpoint.\n\nList all discussions on a team's page. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'string' },
                        body_html: { kind: 'string' },
                        body_version: { kind: 'string' },
                        comments_count: { kind: 'number' },
                        comments_url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        html_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                        number: { kind: 'number' },
                        pinned: { kind: 'boolean' },
                        private: { kind: 'boolean' },
                        team_url: { kind: 'string' },
                        title: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a discussion (Legacy)',
        name: 'Github: Create a discussion (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`Create a discussion`](https://docs.github.com/rest/teams/discussions#create-a-discussion) endpoint.\n\nCreates a new discussion post on a team\'s page. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, body: { kind: 'string' }, private: { kind: 'boolean' } },
                optionalProperties: ['private'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    comments_count: { kind: 'number' },
                    comments_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    pinned: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                    team_url: { kind: 'string' },
                    title: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a discussion (Legacy)',
        name: 'Github: Get a discussion (Legacy)',
        description:
            "**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Get a discussion](https://docs.github.com/rest/teams/discussions#get-a-discussion) endpoint.\n\nGet a specific discussion on a team's page. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    comments_count: { kind: 'number' },
                    comments_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    pinned: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                    team_url: { kind: 'string' },
                    title: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a discussion (Legacy)',
        name: 'Github: Delete a discussion (Legacy)',
        description:
            "**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`Delete a discussion`](https://docs.github.com/rest/teams/discussions#delete-a-discussion) endpoint.\n\nDelete a discussion from a team's page. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a discussion (Legacy)',
        name: 'Github: Update a discussion (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Update a discussion](https://docs.github.com/rest/teams/discussions#update-a-discussion) endpoint.\n\nEdits the title and body text of a discussion post. Only the parameters you provide are updated. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, body: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    comments_count: { kind: 'number' },
                    comments_url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    pinned: { kind: 'boolean' },
                    private: { kind: 'boolean' },
                    team_url: { kind: 'string' },
                    title: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List discussion comments (Legacy)',
        name: 'Github: List discussion comments (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [List discussion comments](https://docs.github.com/rest/teams/discussion-comments#list-discussion-comments) endpoint.\n\nList all comments on a team discussion. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        author: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body: { kind: 'string' },
                        body_html: { kind: 'string' },
                        body_version: { kind: 'string' },
                        created_at: { kind: 'string' },
                        last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        discussion_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                        number: { kind: 'number' },
                        updated_at: { kind: 'string' },
                        url: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['reactions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number, 'comments'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a discussion comment (Legacy)',
        name: 'Github: Create a discussion comment (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Create a discussion comment](https://docs.github.com/rest/teams/discussion-comments#create-a-discussion-comment) endpoint.\n\nCreates a new comment on a team discussion. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).\n\nThis endpoint triggers [notifications](https://docs.github.com/github/managing-subscriptions-and-notifications-on-github/about-notifications). Creating content too quickly using this endpoint may result in secondary rate limiting. See "[Secondary rate limits](https://docs.github.com/rest/overview/resources-in-the-rest-api#secondary-rate-limits)" and "[Dealing with secondary rate limits](https://docs.github.com/rest/guides/best-practices-for-integrators#dealing-with-secondary-rate-limits)" for details.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    discussion_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number, 'comments'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a discussion comment (Legacy)',
        name: 'Github: Get a discussion comment (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Get a discussion comment](https://docs.github.com/rest/teams/discussion-comments#get-a-discussion-comment) endpoint.\n\nGet a specific comment on a team discussion. OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    discussion_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'teams',
                        input.inPorts.team_id,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a discussion comment (Legacy)',
        name: 'Github: Delete a discussion comment (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Delete a discussion comment](https://docs.github.com/rest/teams/discussion-comments#delete-a-discussion-comment) endpoint.\n\nDeletes a comment on a team discussion. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'teams',
                        input.inPorts.team_id,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a discussion comment (Legacy)',
        name: 'Github: Update a discussion comment (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Update a discussion comment](https://docs.github.com/rest/teams/discussion-comments#update-a-discussion-comment) endpoint.\n\nEdits the body text of a discussion comment. OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            body: { kind: 'object', properties: { body: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    author: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    body: { kind: 'string' },
                    body_html: { kind: 'string' },
                    body_version: { kind: 'string' },
                    created_at: { kind: 'string' },
                    last_edited_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    discussion_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    node_id: { kind: 'string' },
                    number: { kind: 'number' },
                    updated_at: { kind: 'string' },
                    url: { kind: 'string' },
                    reactions: {
                        kind: 'object',
                        properties: {
                            url: { kind: 'string' },
                            total_count: { kind: 'number' },
                            '+1': { kind: 'number' },
                            '-1': { kind: 'number' },
                            laugh: { kind: 'number' },
                            confused: { kind: 'number' },
                            heart: { kind: 'number' },
                            hooray: { kind: 'number' },
                            eyes: { kind: 'number' },
                            rocket: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['reactions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'teams',
                        input.inPorts.team_id,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                    ].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a team discussion comment (Legacy)',
        name: 'Github: List reactions for a team discussion comment (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List reactions for a team discussion comment`](https://docs.github.com/rest/reactions/reactions#list-reactions-for-a-team-discussion-comment) endpoint.\n\nList the reactions to a [team discussion comment](https://docs.github.com/rest/reference/teams#discussion-comments). OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'teams',
                        input.inPorts.team_id,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                        'reactions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a team discussion comment (Legacy)',
        name: 'Github: Create reaction for a team discussion comment (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new "[Create reaction for a team discussion comment](https://docs.github.com/rest/reactions/reactions#create-reaction-for-a-team-discussion-comment)" endpoint.\n\nCreate a reaction to a [team discussion comment](https://docs.github.com/rest/reference/teams#discussion-comments). OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/). A response with an HTTP `200` status means that you already added the reaction type to this team discussion comment.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            comment_number: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'teams',
                        input.inPorts.team_id,
                        'discussions',
                        input.inPorts.discussion_number,
                        'comments',
                        input.inPorts.comment_number,
                        'reactions',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List reactions for a team discussion (Legacy)',
        name: 'Github: List reactions for a team discussion (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List reactions for a team discussion`](https://docs.github.com/rest/reactions/reactions#list-reactions-for-a-team-discussion) endpoint.\n\nList the reactions to a [team discussion](https://docs.github.com/rest/reference/teams#discussions). OAuth access tokens require the `read:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            content: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['content', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        content: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number, 'reactions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            content: input.inPorts.content,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create reaction for a team discussion (Legacy)',
        name: 'Github: Create reaction for a team discussion (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`Create reaction for a team discussion`](https://docs.github.com/rest/reactions/reactions#create-reaction-for-a-team-discussion) endpoint.\n\nCreate a reaction to a [team discussion](https://docs.github.com/rest/reference/teams#discussions). OAuth access tokens require the `write:discussion` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/). A response with an HTTP `200` status means that you already added the reaction type to this team discussion.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            discussion_number: { kind: 'number' },
            body: { kind: 'object', properties: { content: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    content: { kind: 'string' },
                    created_at: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'discussions', input.inPorts.discussion_number, 'reactions'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List pending team invitations (Legacy)',
        name: 'Github: List pending team invitations (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List pending team invitations`](https://docs.github.com/rest/teams/members#list-pending-team-invitations) endpoint.\n\nThe return hash contains a `role` field which refers to the Organization Invitation role and will be one of the following values: `direct_member`, `admin`, `billing_manager`, `hiring_manager`, or `reinstate`. If the invitee is not a GitHub member, the `login` field in the return hash will be `null`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        login: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        role: { kind: 'string' },
                        created_at: { kind: 'string' },
                        failed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        failed_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        inviter: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        team_count: { kind: 'number' },
                        node_id: { kind: 'string' },
                        invitation_teams_url: { kind: 'string' },
                        invitation_source: { kind: 'string' },
                    },
                    optionalProperties: ['failed_at', 'failed_reason', 'invitation_source'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'invitations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List team members (Legacy)',
        name: 'Github: List team members (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List team members`](https://docs.github.com/rest/teams/members#list-team-members) endpoint.\n\nTeam members will include the members of child teams.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            role: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['role', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'members'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ role: input.inPorts.role, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get team member (Legacy)',
        name: 'Github: Get team member (Legacy)',
        description:
            'The "Get team member" endpoint (described below) is deprecated.\n\nWe recommend using the [Get team membership for a user](https://docs.github.com/rest/teams/members#get-team-membership-for-a-user) endpoint instead. It allows you to get both active and pending memberships.\n\nTo list members in a team, the team must be visible to the authenticated user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'members', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add team member (Legacy)',
        name: 'Github: Add team member (Legacy)',
        description:
            'The "Add team member" endpoint (described below) is deprecated.\n\nWe recommend using the [Add or update team membership for a user](https://docs.github.com/rest/teams/members#add-or-update-team-membership-for-a-user) endpoint instead. It allows you to invite new organization members to your teams.\n\nTeam synchronization is available for organizations using GitHub Enterprise Cloud. For more information, see [GitHub\'s products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nTo add someone to a team, the authenticated user must be an organization owner or a team maintainer in the team they\'re changing. The person being added to the team must be a member of the team\'s organization.\n\n**Note:** When you have team synchronization set up for a team with your organization\'s identity provider (IdP), you will see an error if you attempt to use the API for making changes to the team\'s membership. If you have access to manage group membership in your IdP, you can manage GitHub team membership through your identity provider, which automatically adds and removes team members in an organization. For more information, see "[Synchronizing teams between your identity provider and GitHub](https://docs.github.com/articles/synchronizing-teams-between-your-identity-provider-and-github/)."\n\nNote that you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'members', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove team member (Legacy)',
        name: 'Github: Remove team member (Legacy)',
        description:
            "The \"Remove team member\" endpoint (described below) is deprecated.\n\nWe recommend using the [Remove team membership for a user](https://docs.github.com/rest/teams/members#remove-team-membership-for-a-user) endpoint instead. It allows you to remove both active and pending memberships.\n\nTeam synchronization is available for organizations using GitHub Enterprise Cloud. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nTo remove a team member, the authenticated user must have 'admin' permissions to the team or be an owner of the org that the team is associated with. Removing a team member does not delete the user, it just removes them from the team.\n\n**Note:** When you have team synchronization set up for a team with your organization's identity provider (IdP), you will see an error if you attempt to use the API for making changes to the team's membership. If you have access to manage group membership in your IdP, you can manage GitHub team membership through your identity provider, which automatically adds and removes team members in an organization. For more information, see \"[Synchronizing teams between your identity provider and GitHub](https://docs.github.com/articles/synchronizing-teams-between-your-identity-provider-and-github/).\"",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'members', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get team membership for a user (Legacy)',
        name: 'Github: Get team membership for a user (Legacy)',
        description:
            "**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Get team membership for a user](https://docs.github.com/rest/teams/members#get-team-membership-for-a-user) endpoint.\n\nTeam members will include the members of child teams.\n\nTo get a user's membership with a team, the team must be visible to the authenticated user.\n\n**Note:**\nThe response contains the `state` of the membership and the member's `role`.\n\nThe `role` for organization owners is set to `maintainer`. For more information about `maintainer` roles, see [Create a team](https://docs.github.com/rest/teams/teams#create-a-team).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, role: { kind: 'string' }, state: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'memberships', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add or update team membership for a user (Legacy)',
        name: 'Github: Add or update team membership for a user (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Add or update team membership for a user](https://docs.github.com/rest/teams/members#add-or-update-team-membership-for-a-user) endpoint.\n\nTeam synchronization is available for organizations using GitHub Enterprise Cloud. For more information, see [GitHub\'s products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nIf the user is already a member of the team\'s organization, this endpoint will add the user to the team. To add a membership between an organization member and a team, the authenticated user must be an organization owner or a team maintainer.\n\n**Note:** When you have team synchronization set up for a team with your organization\'s identity provider (IdP), you will see an error if you attempt to use the API for making changes to the team\'s membership. If you have access to manage group membership in your IdP, you can manage GitHub team membership through your identity provider, which automatically adds and removes team members in an organization. For more information, see "[Synchronizing teams between your identity provider and GitHub](https://docs.github.com/articles/synchronizing-teams-between-your-identity-provider-and-github/)."\n\nIf the user is unaffiliated with the team\'s organization, this endpoint will send an invitation to the user via email. This newly-created membership will be in the "pending" state until the user accepts the invitation, at which point the membership will transition to the "active" state and the user will be added as a member of the team. To add a membership between an unaffiliated user and a team, the authenticated user must be an organization owner.\n\nIf the user is already a member of the team, this endpoint will update the role of the team member\'s role. To update the membership of a team member, the authenticated user must be an organization owner or a team maintainer.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            username: { kind: 'string' },
            body: { kind: 'object', properties: { role: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { url: { kind: 'string' }, role: { kind: 'string' }, state: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'memberships', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove team membership for a user (Legacy)',
        name: 'Github: Remove team membership for a user (Legacy)',
        description:
            "**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Remove team membership for a user](https://docs.github.com/rest/teams/members#remove-team-membership-for-a-user) endpoint.\n\nTeam synchronization is available for organizations using GitHub Enterprise Cloud. For more information, see [GitHub's products](https://docs.github.com/github/getting-started-with-github/githubs-products) in the GitHub Help documentation.\n\nTo remove a membership between a user and a team, the authenticated user must have 'admin' permissions to the team or be an owner of the organization that the team is associated with. Removing team membership does not delete the user, it just removes their membership from the team.\n\n**Note:** When you have team synchronization set up for a team with your organization's identity provider (IdP), you will see an error if you attempt to use the API for making changes to the team's membership. If you have access to manage group membership in your IdP, you can manage GitHub team membership through your identity provider, which automatically adds and removes team members in an organization. For more information, see \"[Synchronizing teams between your identity provider and GitHub](https://docs.github.com/articles/synchronizing-teams-between-your-identity-provider-and-github/).\"",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'memberships', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List team projects (Legacy)',
        name: 'Github: List team projects (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List team projects`](https://docs.github.com/rest/teams/teams#list-team-projects) endpoint.\n\nLists the organization projects for a team.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        owner_url: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        columns_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        creator: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        organization_permission: { kind: 'string' },
                        private: { kind: 'boolean' },
                        permissions: {
                            kind: 'object',
                            properties: { read: { kind: 'boolean' }, write: { kind: 'boolean' }, admin: { kind: 'boolean' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['organization_permission', 'private'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'projects'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check team permissions for a project (Legacy)',
        name: 'Github: Check team permissions for a project (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Check team permissions for a project](https://docs.github.com/rest/teams/teams#check-team-permissions-for-a-project) endpoint.\n\nChecks whether a team has `read`, `write`, or `admin` permissions for an organization project. The response includes projects inherited from a parent team.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            project_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                    permissions: {
                        kind: 'object',
                        properties: { read: { kind: 'boolean' }, write: { kind: 'boolean' }, admin: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'projects', input.inPorts.project_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add or update team project permissions (Legacy)',
        name: 'Github: Add or update team project permissions (Legacy)',
        description:
            "**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Add or update team project permissions](https://docs.github.com/rest/teams/teams#add-or-update-team-project-permissions) endpoint.\n\nAdds an organization project to a team. To add a project to a team or update the team's permission on a project, the authenticated user must have `admin` permissions for the project. The project and team must be part of the same organization.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            project_id: { kind: 'number' },
            body: { kind: 'object', properties: { permission: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'projects', input.inPorts.project_id].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a project from a team (Legacy)',
        name: 'Github: Remove a project from a team (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Remove a project from a team](https://docs.github.com/rest/teams/teams#remove-a-project-from-a-team) endpoint.\n\nRemoves an organization project from a team. An organization owner or a team maintainer can remove any project from the team. To remove a project from a team as an organization member, the authenticated user must have `read` access to both the team and project, or `admin` access to the team or project. **Note:** This endpoint removes the project from the team, but does not delete it.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            project_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'projects', input.inPorts.project_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List team repositories (Legacy)',
        name: 'Github: List team repositories (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [List team repositories](https://docs.github.com/rest/teams/teams#list-team-repositories) endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'repos'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check team permissions for a repository (Legacy)',
        name: 'Github: Check team permissions for a repository (Legacy)',
        description:
            '**Note**: Repositories inherited through a parent team will also be checked.\n\n**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Check team permissions for a repository](https://docs.github.com/rest/reference/teams#check-team-permissions-for-a-repository) endpoint.\n\nYou can also get information about the specified repository, including what permissions the team grants on it, by passing the following custom [media type](https://docs.github.com/rest/overview/media-types/) via the `Accept` header:',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    forks: { kind: 'number' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                        },
                        optionalProperties: ['triage', 'maintain'],
                        restriction: 'open',
                    },
                    role_name: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    master_branch: { kind: 'string' },
                },
                optionalProperties: [
                    'permissions',
                    'role_name',
                    'is_template',
                    'topics',
                    'visibility',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_merge_commit',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'subscribers_count',
                    'network_count',
                    'master_branch',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'repos', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add or update team repository permissions (Legacy)',
        name: 'Github: Add or update team repository permissions (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new "[Add or update team repository permissions](https://docs.github.com/rest/reference/teams#add-or-update-team-repository-permissions)" endpoint.\n\nTo add a repository to a team or update the team\'s permission on a repository, the authenticated user must have admin access to the repository, and must be able to see the team. The repository must be owned by the organization, or a direct fork of a repository owned by the organization. You will get a `422 Unprocessable Entity` status if you attempt to add a repository to a team that is not owned by the organization.\n\nNote that, if you choose not to pass any parameters, you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            owner: { kind: 'string' },
            repo: { kind: 'string' },
            body: { kind: 'object', properties: { permission: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'repos', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a repository from a team (Legacy)',
        name: 'Github: Remove a repository from a team (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [Remove a repository from a team](https://docs.github.com/rest/reference/teams#remove-a-repository-from-a-team) endpoint.\n\nIf the authenticated user is an organization owner or a team maintainer, they can remove any repositories from the team. To remove a repository from a team as an organization member, the authenticated user must have admin access to the repository and must be able to see the team. NOTE: This does not delete the repository, it just removes it from the team.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'repos', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List child teams (Legacy)',
        name: 'Github: List child teams (Legacy)',
        description:
            '**Deprecation Notice:** This endpoint route is deprecated and will be removed from the Teams API. We recommend migrating your existing code to use the new [`List child teams`](https://docs.github.com/rest/teams/teams#list-child-teams) endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            team_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                admin: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'teams', input.inPorts.team_id, 'teams'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the authenticated user',
        name: 'Github: Get the authenticated user',
        description:
            'If the authenticated user is authenticated with an OAuth token with the `user` scope, then the response lists public and private profile information.\n\nIf the authenticated user is authenticated through OAuth without the `user` scope, then the response lists only public profile information.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            private_gists: { kind: 'number' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            disk_usage: { kind: 'number' },
                            collaborators: { kind: 'number' },
                            two_factor_authentication: { kind: 'boolean' },
                            plan: {
                                kind: 'object',
                                properties: {
                                    collaborators: { kind: 'number' },
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            business_plus: { kind: 'boolean' },
                            ldap_dn: { kind: 'string' },
                        },
                        optionalProperties: ['twitter_username', 'plan', 'suspended_at', 'business_plus', 'ldap_dn'],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            plan: {
                                kind: 'object',
                                properties: {
                                    collaborators: { kind: 'number' },
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            private_gists: { kind: 'number' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            disk_usage: { kind: 'number' },
                            collaborators: { kind: 'number' },
                        },
                        optionalProperties: [
                            'twitter_username',
                            'plan',
                            'suspended_at',
                            'private_gists',
                            'total_private_repos',
                            'owned_private_repos',
                            'disk_usage',
                            'collaborators',
                        ],
                        restriction: 'open',
                    },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update the authenticated user',
        name: 'Github: Update the authenticated user',
        description:
            '**Note:** If your email is set to private and you send an `email` parameter as part of this request to update your profile, your privacy settings are still enforced: the email address will not be displayed on your public profile or via the API.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    email: { kind: 'string' },
                    blog: { kind: 'string' },
                    twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    company: { kind: 'string' },
                    location: { kind: 'string' },
                    hireable: { kind: 'boolean' },
                    bio: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    login: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    avatar_url: { kind: 'string' },
                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    followers_url: { kind: 'string' },
                    following_url: { kind: 'string' },
                    gists_url: { kind: 'string' },
                    starred_url: { kind: 'string' },
                    subscriptions_url: { kind: 'string' },
                    organizations_url: { kind: 'string' },
                    repos_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    received_events_url: { kind: 'string' },
                    type: { kind: 'string' },
                    site_admin: { kind: 'boolean' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    public_repos: { kind: 'number' },
                    public_gists: { kind: 'number' },
                    followers: { kind: 'number' },
                    following: { kind: 'number' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    private_gists: { kind: 'number' },
                    total_private_repos: { kind: 'number' },
                    owned_private_repos: { kind: 'number' },
                    disk_usage: { kind: 'number' },
                    collaborators: { kind: 'number' },
                    two_factor_authentication: { kind: 'boolean' },
                    plan: {
                        kind: 'object',
                        properties: {
                            collaborators: { kind: 'number' },
                            name: { kind: 'string' },
                            space: { kind: 'number' },
                            private_repos: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    business_plus: { kind: 'boolean' },
                    ldap_dn: { kind: 'string' },
                },
                optionalProperties: ['twitter_username', 'plan', 'suspended_at', 'business_plus', 'ldap_dn'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List users blocked by the authenticated user',
        name: 'Github: List users blocked by the authenticated user',
        description: "List the users you've blocked on your personal account.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'blocks'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a user is blocked by the authenticated user',
        name: 'Github: Check if a user is blocked by the authenticated user',
        description:
            'Returns a 204 if the given user is blocked by the authenticated user. Returns a 404 if the given user is not blocked by the authenticated user, or if the given user account has been identified as spam by GitHub.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'blocks', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Block a user',
        name: 'Github: Block a user',
        description: 'Blocks the given user and returns a 204. If the authenticated user cannot block the given user a 422 is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'blocks', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unblock a user',
        name: 'Github: Unblock a user',
        description: 'Unblocks the given user and returns a 204.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'blocks', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List codespaces for the authenticated user',
        name: 'Github: List codespaces for the authenticated user',
        description:
            "Lists the authenticated user's codespaces.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page', 'repository_id'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    codespaces: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                name: { kind: 'string' },
                                display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                billable_owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                repository: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                machine: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'string' },
                                                display_name: { kind: 'string' },
                                                operating_system: { kind: 'string' },
                                                storage_in_bytes: { kind: 'number' },
                                                memory_in_bytes: { kind: 'number' },
                                                cpus: { kind: 'number' },
                                                prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                last_used_at: { kind: 'string' },
                                state: { kind: 'string' },
                                url: { kind: 'string' },
                                git_status: {
                                    kind: 'object',
                                    properties: {
                                        ahead: { kind: 'number' },
                                        behind: { kind: 'number' },
                                        has_unpushed_changes: { kind: 'boolean' },
                                        has_uncommitted_changes: { kind: 'boolean' },
                                        ref: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                location: { kind: 'string' },
                                idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                web_url: { kind: 'string' },
                                machines_url: { kind: 'string' },
                                start_url: { kind: 'string' },
                                stop_url: { kind: 'string' },
                                publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                recent_folders: { kind: 'array', subType: { kind: 'string' } },
                                runtime_constraints: {
                                    kind: 'object',
                                    properties: {
                                        allowed_port_privacy_settings: {
                                            kind: 'union',
                                            subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [
                                'display_name',
                                'devcontainer_path',
                                'publish_url',
                                'runtime_constraints',
                                'pending_operation',
                                'pending_operation_disabled_reason',
                                'idle_timeout_notice',
                                'retention_period_minutes',
                                'retention_expires_at',
                                'last_known_stop_notice',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            repository_id: input.inPorts.repository_id,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a codespace for the authenticated user',
        name: 'Github: Create a codespace for the authenticated user',
        description:
            'Creates a new codespace, owned by the authenticated user.\n\nThis endpoint requires either a `repository_id` OR a `pull_request` but not both.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            repository_id: { kind: 'number' },
                            ref: { kind: 'string' },
                            location: { kind: 'string' },
                            geo: { kind: 'string' },
                            client_ip: { kind: 'string' },
                            machine: { kind: 'string' },
                            devcontainer_path: { kind: 'string' },
                            multi_repo_permissions_opt_out: { kind: 'boolean' },
                            working_directory: { kind: 'string' },
                            idle_timeout_minutes: { kind: 'number' },
                            display_name: { kind: 'string' },
                            retention_period_minutes: { kind: 'number' },
                        },
                        optionalProperties: [
                            'ref',
                            'location',
                            'geo',
                            'client_ip',
                            'machine',
                            'devcontainer_path',
                            'multi_repo_permissions_opt_out',
                            'working_directory',
                            'idle_timeout_minutes',
                            'display_name',
                            'retention_period_minutes',
                        ],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: {
                            pull_request: {
                                kind: 'object',
                                properties: { pull_request_number: { kind: 'number' }, repository_id: { kind: 'number' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            location: { kind: 'string' },
                            geo: { kind: 'string' },
                            machine: { kind: 'string' },
                            devcontainer_path: { kind: 'string' },
                            working_directory: { kind: 'string' },
                            idle_timeout_minutes: { kind: 'number' },
                        },
                        optionalProperties: [
                            'location',
                            'geo',
                            'machine',
                            'devcontainer_path',
                            'working_directory',
                            'idle_timeout_minutes',
                        ],
                        restriction: 'open',
                    },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List secrets for the authenticated user',
        name: 'Github: List secrets for the authenticated user',
        description:
            "Lists all secrets available for a user's Codespaces without revealing their\nencrypted values.\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_user_secrets` user permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    secrets: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                visibility: { kind: 'string' },
                                selected_repositories_url: { kind: 'string' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get public key for the authenticated user',
        name: 'Github: Get public key for the authenticated user',
        description:
            'Gets your public key, which you need to encrypt secrets. You need to encrypt a secret before you can create or update secrets.\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_user_secrets` user permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { key_id: { kind: 'string' }, key: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', 'public-key'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a secret for the authenticated user',
        name: 'Github: Get a secret for the authenticated user',
        description:
            "Gets a secret available to a user's codespaces without revealing its encrypted value.\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_user_secrets` user permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    visibility: { kind: 'string' },
                    selected_repositories_url: { kind: 'string' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create or update a secret for the authenticated user',
        name: 'Github: Create or update a secret for the authenticated user',
        description:
            'Creates or updates a secret for a user\'s codespace with an encrypted value. Encrypt your secret using\n[LibSodium](https://libsodium.gitbook.io/doc/bindings_for_other_languages).\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must also have Codespaces access to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_user_secrets` user permission and `codespaces_secrets` repository permission on all referenced repositories to use this endpoint.\n\n**Example encrypting a secret using Node.js**\n\nEncrypt your secret using the [libsodium-wrappers](https://www.npmjs.com/package/libsodium-wrappers) library.\n\n```\nconst sodium = require(\'libsodium-wrappers\')\nconst secret = \'plain-text-secret\' // replace with the secret you want to encrypt\nconst key = \'base64-encoded-public-key\' // replace with the Base64 encoded public key\n\n//Check if libsodium is ready and then proceed.\nsodium.ready.then(() => {\n  // Convert Secret & Base64 key to Uint8Array.\n  let binkey = sodium.from_base64(key, sodium.base64_variants.ORIGINAL)\n  let binsec = sodium.from_string(secret)\n\n  //Encrypt the secret using LibSodium\n  let encBytes = sodium.crypto_box_seal(binsec, binkey)\n\n  // Convert encrypted Uint8Array to Base64\n  let output = sodium.to_base64(encBytes, sodium.base64_variants.ORIGINAL)\n\n  console.log(output)\n});\n```\n\n**Example encrypting a secret using Python**\n\nEncrypt your secret using [pynacl](https://pynacl.readthedocs.io/en/latest/public/#nacl-public-sealedbox) with Python 3.\n\n```\nfrom base64 import b64encode\nfrom nacl import encoding, public\n\ndef encrypt(public_key: str, secret_value: str) -> str:\n  """Encrypt a Unicode string using the public key."""\n  public_key = public.PublicKey(public_key.encode("utf-8"), encoding.Base64Encoder())\n  sealed_box = public.SealedBox(public_key)\n  encrypted = sealed_box.encrypt(secret_value.encode("utf-8"))\n  return b64encode(encrypted).decode("utf-8")\n```\n\n**Example encrypting a secret using C#**\n\nEncrypt your secret using the [Sodium.Core](https://www.nuget.org/packages/Sodium.Core/) package.\n\n```\nvar secretValue = System.Text.Encoding.UTF8.GetBytes("mySecret");\nvar publicKey = Convert.FromBase64String("2Sg8iYjAxxmI2LvUXpJjkYrMxURPc8r+dB7TJyvvcCU=");\n\nvar sealedPublicKeyBox = Sodium.SealedPublicKeyBox.Create(secretValue, publicKey);\n\nConsole.WriteLine(Convert.ToBase64String(sealedPublicKeyBox));\n```\n\n**Example encrypting a secret using Ruby**\n\nEncrypt your secret using the [rbnacl](https://github.com/RubyCrypto/rbnacl) gem.\n\n```ruby\nrequire "rbnacl"\nrequire "base64"\n\nkey = Base64.decode64("+ZYvJDZMHUfBkJdyq5Zm9SKqeuBQ4sj+6sfjlH4CgG0=")\npublic_key = RbNaCl::PublicKey.new(key)\n\nbox = RbNaCl::Boxes::Sealed.from_public_key(public_key)\nencrypted_secret = box.encrypt("my_secret")\n\n# Print the base64 encoded secret\nputs Base64.strict_encode64(encrypted_secret)\n```',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    encrypted_value: { kind: 'string' },
                    key_id: { kind: 'string' },
                    selected_repository_ids: {
                        kind: 'array',
                        subType: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'string' }] },
                    },
                },
                optionalProperties: ['encrypted_value', 'selected_repository_ids'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a secret for the authenticated user',
        name: 'Github: Delete a secret for the authenticated user',
        description:
            "Deletes a secret from a user's codespaces using the secret name. Deleting the secret will remove access from all codespaces that were allowed to access the secret.\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_user_secrets` user permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List selected repositories for a user secret',
        name: 'Github: List selected repositories for a user secret',
        description:
            "List the repositories that have been granted the ability to use a user's codespace secret.\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_user_secrets` user permission and write access to the `codespaces_secrets` repository permission on all referenced repositories to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set selected repositories for a user secret',
        name: 'Github: Set selected repositories for a user secret',
        description:
            "Select the repositories that will use a user's codespace secret.\n\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_user_secrets` user permission and write access to the `codespaces_secrets` repository permission on all referenced repositories to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { selected_repository_ids: { kind: 'array', subType: { kind: 'number' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name, 'repositories'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add a selected repository to a user secret',
        name: 'Github: Add a selected repository to a user secret',
        description:
            "Adds a repository to the selected repositories for a user's codespace secret.\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\nGitHub Apps must have write access to the `codespaces_user_secrets` user permission and write access to the `codespaces_secrets` repository permission on the referenced repository to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name, 'repositories', input.inPorts.repository_id].join(
                        '/',
                    ),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a selected repository from a user secret',
        name: 'Github: Remove a selected repository from a user secret',
        description:
            "Removes a repository from the selected repositories for a user's codespace secret.\nYou must authenticate using an access token with the `codespace` or `codespace:secrets` scope to use this endpoint. User must have Codespaces access to use this endpoint.\nGitHub Apps must have write access to the `codespaces_user_secrets` user permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            secret_name: { kind: 'string' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', 'secrets', input.inPorts.secret_name, 'repositories', input.inPorts.repository_id].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a codespace for the authenticated user',
        name: 'Github: Get a codespace for the authenticated user',
        description:
            "Gets information about a user's codespace.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a codespace for the authenticated user',
        name: 'Github: Delete a codespace for the authenticated user',
        description:
            "Deletes a user's codespace.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update a codespace for the authenticated user',
        name: 'Github: Update a codespace for the authenticated user',
        description:
            "Updates a codespace owned by the authenticated user. Currently only the codespace's machine type and recent folders can be modified using this endpoint.\n\nIf you specify a new machine type it will be applied the next time your codespace is started.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: {
                    machine: { kind: 'string' },
                    display_name: { kind: 'string' },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Export a codespace for the authenticated user',
        name: 'Github: Export a codespace for the authenticated user',
        description:
            "Triggers an export of the specified codespace and returns a URL and ID where the status of the export can be monitored.\n\nIf changes cannot be pushed to the codespace's repository, they will be pushed to a new or previously-existing fork instead.\n\nYou must authenticate using a personal access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_lifecycle_admin` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    state: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'string' },
                    export_url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name, 'exports'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get details about a codespace export',
        name: 'Github: Get details about a codespace export',
        description:
            'Gets information about an export of a codespace.\n\nYou must authenticate using a personal access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_lifecycle_admin` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
            export_id: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    state: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    completed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    branch: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    sha: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    id: { kind: 'string' },
                    export_url: { kind: 'string' },
                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name, 'exports', input.inPorts.export_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List machine types for a codespace',
        name: 'Github: List machine types for a codespace',
        description:
            'List the machine types a codespace can transition to use.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have read access to the `codespaces_metadata` repository permission to use this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    machines: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'string' },
                                display_name: { kind: 'string' },
                                operating_system: { kind: 'string' },
                                storage_in_bytes: { kind: 'number' },
                                memory_in_bytes: { kind: 'number' },
                                cpus: { kind: 'number' },
                                prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name, 'machines'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository from an unpublished codespace',
        name: 'Github: Create a repository from an unpublished codespace',
        description:
            "Publishes an unpublished codespace, creating a new repository and assigning it to the codespace.\n\nThe codespace's token is granted write permissions to the repository, allowing the user to push their changes.\n\nThis will fail for a codespace that is already published, meaning it has an associated repository.\n\nYou must authenticate using a personal access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, private: { kind: 'boolean' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'string' },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: ['maintain', 'triage'],
                                restriction: 'open',
                            },
                            allow_rebase_merge: { kind: 'boolean' },
                            template_repository: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            full_name: { kind: 'string' },
                                            license: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            key: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            node_id: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['html_url'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            organization: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            forks: { kind: 'number' },
                                            permissions: {
                                                kind: 'object',
                                                properties: {
                                                    admin: { kind: 'boolean' },
                                                    pull: { kind: 'boolean' },
                                                    triage: { kind: 'boolean' },
                                                    push: { kind: 'boolean' },
                                                    maintain: { kind: 'boolean' },
                                                },
                                                optionalProperties: ['triage', 'maintain'],
                                                restriction: 'open',
                                            },
                                            owner: {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            private: { kind: 'boolean' },
                                            html_url: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            fork: { kind: 'boolean' },
                                            url: { kind: 'string' },
                                            archive_url: { kind: 'string' },
                                            assignees_url: { kind: 'string' },
                                            blobs_url: { kind: 'string' },
                                            branches_url: { kind: 'string' },
                                            collaborators_url: { kind: 'string' },
                                            comments_url: { kind: 'string' },
                                            commits_url: { kind: 'string' },
                                            compare_url: { kind: 'string' },
                                            contents_url: { kind: 'string' },
                                            contributors_url: { kind: 'string' },
                                            deployments_url: { kind: 'string' },
                                            downloads_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            forks_url: { kind: 'string' },
                                            git_commits_url: { kind: 'string' },
                                            git_refs_url: { kind: 'string' },
                                            git_tags_url: { kind: 'string' },
                                            git_url: { kind: 'string' },
                                            issue_comment_url: { kind: 'string' },
                                            issue_events_url: { kind: 'string' },
                                            issues_url: { kind: 'string' },
                                            keys_url: { kind: 'string' },
                                            labels_url: { kind: 'string' },
                                            languages_url: { kind: 'string' },
                                            merges_url: { kind: 'string' },
                                            milestones_url: { kind: 'string' },
                                            notifications_url: { kind: 'string' },
                                            pulls_url: { kind: 'string' },
                                            releases_url: { kind: 'string' },
                                            ssh_url: { kind: 'string' },
                                            stargazers_url: { kind: 'string' },
                                            statuses_url: { kind: 'string' },
                                            subscribers_url: { kind: 'string' },
                                            subscription_url: { kind: 'string' },
                                            tags_url: { kind: 'string' },
                                            teams_url: { kind: 'string' },
                                            trees_url: { kind: 'string' },
                                            clone_url: { kind: 'string' },
                                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            hooks_url: { kind: 'string' },
                                            svn_url: { kind: 'string' },
                                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            forks_count: { kind: 'number' },
                                            stargazers_count: { kind: 'number' },
                                            watchers_count: { kind: 'number' },
                                            size: { kind: 'number' },
                                            default_branch: { kind: 'string' },
                                            open_issues_count: { kind: 'number' },
                                            is_template: { kind: 'boolean' },
                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                            has_issues: { kind: 'boolean' },
                                            has_projects: { kind: 'boolean' },
                                            has_wiki: { kind: 'boolean' },
                                            has_pages: { kind: 'boolean' },
                                            has_downloads: { kind: 'boolean' },
                                            has_discussions: { kind: 'boolean' },
                                            archived: { kind: 'boolean' },
                                            disabled: { kind: 'boolean' },
                                            visibility: { kind: 'string' },
                                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            allow_rebase_merge: { kind: 'boolean' },
                                            template_repository: {
                                                kind: 'union',
                                                subTypes: [
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            full_name: { kind: 'string' },
                                                            owner: {
                                                                kind: 'object',
                                                                properties: {
                                                                    login: { kind: 'string' },
                                                                    id: { kind: 'number' },
                                                                    node_id: { kind: 'string' },
                                                                    avatar_url: { kind: 'string' },
                                                                    gravatar_id: { kind: 'string' },
                                                                    url: { kind: 'string' },
                                                                    html_url: { kind: 'string' },
                                                                    followers_url: { kind: 'string' },
                                                                    following_url: { kind: 'string' },
                                                                    gists_url: { kind: 'string' },
                                                                    starred_url: { kind: 'string' },
                                                                    subscriptions_url: { kind: 'string' },
                                                                    organizations_url: { kind: 'string' },
                                                                    repos_url: { kind: 'string' },
                                                                    events_url: { kind: 'string' },
                                                                    received_events_url: { kind: 'string' },
                                                                    type: { kind: 'string' },
                                                                    site_admin: { kind: 'boolean' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            private: { kind: 'boolean' },
                                                            html_url: { kind: 'string' },
                                                            description: { kind: 'string' },
                                                            fork: { kind: 'boolean' },
                                                            url: { kind: 'string' },
                                                            archive_url: { kind: 'string' },
                                                            assignees_url: { kind: 'string' },
                                                            blobs_url: { kind: 'string' },
                                                            branches_url: { kind: 'string' },
                                                            collaborators_url: { kind: 'string' },
                                                            comments_url: { kind: 'string' },
                                                            commits_url: { kind: 'string' },
                                                            compare_url: { kind: 'string' },
                                                            contents_url: { kind: 'string' },
                                                            contributors_url: { kind: 'string' },
                                                            deployments_url: { kind: 'string' },
                                                            downloads_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            forks_url: { kind: 'string' },
                                                            git_commits_url: { kind: 'string' },
                                                            git_refs_url: { kind: 'string' },
                                                            git_tags_url: { kind: 'string' },
                                                            git_url: { kind: 'string' },
                                                            issue_comment_url: { kind: 'string' },
                                                            issue_events_url: { kind: 'string' },
                                                            issues_url: { kind: 'string' },
                                                            keys_url: { kind: 'string' },
                                                            labels_url: { kind: 'string' },
                                                            languages_url: { kind: 'string' },
                                                            merges_url: { kind: 'string' },
                                                            milestones_url: { kind: 'string' },
                                                            notifications_url: { kind: 'string' },
                                                            pulls_url: { kind: 'string' },
                                                            releases_url: { kind: 'string' },
                                                            ssh_url: { kind: 'string' },
                                                            stargazers_url: { kind: 'string' },
                                                            statuses_url: { kind: 'string' },
                                                            subscribers_url: { kind: 'string' },
                                                            subscription_url: { kind: 'string' },
                                                            tags_url: { kind: 'string' },
                                                            teams_url: { kind: 'string' },
                                                            trees_url: { kind: 'string' },
                                                            clone_url: { kind: 'string' },
                                                            mirror_url: { kind: 'string' },
                                                            hooks_url: { kind: 'string' },
                                                            svn_url: { kind: 'string' },
                                                            homepage: { kind: 'string' },
                                                            language: { kind: 'string' },
                                                            forks_count: { kind: 'number' },
                                                            stargazers_count: { kind: 'number' },
                                                            watchers_count: { kind: 'number' },
                                                            size: { kind: 'number' },
                                                            default_branch: { kind: 'string' },
                                                            open_issues_count: { kind: 'number' },
                                                            is_template: { kind: 'boolean' },
                                                            topics: { kind: 'array', subType: { kind: 'string' } },
                                                            has_issues: { kind: 'boolean' },
                                                            has_projects: { kind: 'boolean' },
                                                            has_wiki: { kind: 'boolean' },
                                                            has_pages: { kind: 'boolean' },
                                                            has_downloads: { kind: 'boolean' },
                                                            archived: { kind: 'boolean' },
                                                            disabled: { kind: 'boolean' },
                                                            visibility: { kind: 'string' },
                                                            pushed_at: { kind: 'string' },
                                                            created_at: { kind: 'string' },
                                                            updated_at: { kind: 'string' },
                                                            permissions: {
                                                                kind: 'object',
                                                                properties: {
                                                                    admin: { kind: 'boolean' },
                                                                    maintain: { kind: 'boolean' },
                                                                    push: { kind: 'boolean' },
                                                                    triage: { kind: 'boolean' },
                                                                    pull: { kind: 'boolean' },
                                                                },
                                                                optionalProperties: [],
                                                                restriction: 'open',
                                                            },
                                                            allow_rebase_merge: { kind: 'boolean' },
                                                            temp_clone_token: { kind: 'string' },
                                                            allow_squash_merge: { kind: 'boolean' },
                                                            allow_auto_merge: { kind: 'boolean' },
                                                            delete_branch_on_merge: { kind: 'boolean' },
                                                            allow_update_branch: { kind: 'boolean' },
                                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                                            squash_merge_commit_title: { kind: 'string' },
                                                            squash_merge_commit_message: { kind: 'string' },
                                                            merge_commit_title: { kind: 'string' },
                                                            merge_commit_message: { kind: 'string' },
                                                            allow_merge_commit: { kind: 'boolean' },
                                                            subscribers_count: { kind: 'number' },
                                                            network_count: { kind: 'number' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    { kind: 'null' },
                                                ],
                                            },
                                            temp_clone_token: { kind: 'string' },
                                            allow_squash_merge: { kind: 'boolean' },
                                            allow_auto_merge: { kind: 'boolean' },
                                            delete_branch_on_merge: { kind: 'boolean' },
                                            allow_update_branch: { kind: 'boolean' },
                                            use_squash_pr_title_as_default: { kind: 'boolean' },
                                            squash_merge_commit_title: { kind: 'string' },
                                            squash_merge_commit_message: { kind: 'string' },
                                            merge_commit_title: { kind: 'string' },
                                            merge_commit_message: { kind: 'string' },
                                            allow_merge_commit: { kind: 'boolean' },
                                            allow_forking: { kind: 'boolean' },
                                            web_commit_signoff_required: { kind: 'boolean' },
                                            subscribers_count: { kind: 'number' },
                                            network_count: { kind: 'number' },
                                            open_issues: { kind: 'number' },
                                            watchers: { kind: 'number' },
                                            master_branch: { kind: 'string' },
                                            starred_at: { kind: 'string' },
                                            anonymous_access_enabled: { kind: 'boolean' },
                                        },
                                        optionalProperties: [
                                            'organization',
                                            'permissions',
                                            'is_template',
                                            'topics',
                                            'has_discussions',
                                            'visibility',
                                            'allow_rebase_merge',
                                            'template_repository',
                                            'temp_clone_token',
                                            'allow_squash_merge',
                                            'allow_auto_merge',
                                            'delete_branch_on_merge',
                                            'allow_update_branch',
                                            'use_squash_pr_title_as_default',
                                            'squash_merge_commit_title',
                                            'squash_merge_commit_message',
                                            'merge_commit_title',
                                            'merge_commit_message',
                                            'allow_merge_commit',
                                            'allow_forking',
                                            'web_commit_signoff_required',
                                            'subscribers_count',
                                            'network_count',
                                            'master_branch',
                                            'starred_at',
                                            'anonymous_access_enabled',
                                        ],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            temp_clone_token: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            allow_squash_merge: { kind: 'boolean' },
                            allow_auto_merge: { kind: 'boolean' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            allow_merge_commit: { kind: 'boolean' },
                            allow_update_branch: { kind: 'boolean' },
                            use_squash_pr_title_as_default: { kind: 'boolean' },
                            squash_merge_commit_title: { kind: 'string' },
                            squash_merge_commit_message: { kind: 'string' },
                            merge_commit_title: { kind: 'string' },
                            merge_commit_message: { kind: 'string' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            node_id: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['html_url'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            organization: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            parent: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            source: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                            forks: { kind: 'number' },
                            master_branch: { kind: 'string' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            anonymous_access_enabled: { kind: 'boolean' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    url: { kind: 'string' },
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'is_template',
                            'topics',
                            'visibility',
                            'permissions',
                            'allow_rebase_merge',
                            'template_repository',
                            'temp_clone_token',
                            'allow_squash_merge',
                            'allow_auto_merge',
                            'delete_branch_on_merge',
                            'allow_merge_commit',
                            'allow_update_branch',
                            'use_squash_pr_title_as_default',
                            'squash_merge_commit_title',
                            'squash_merge_commit_message',
                            'merge_commit_title',
                            'merge_commit_message',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'organization',
                            'parent',
                            'source',
                            'master_branch',
                            'anonymous_access_enabled',
                            'code_of_conduct',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name, 'publish'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Start a codespace for the authenticated user',
        name: 'Github: Start a codespace for the authenticated user',
        description:
            "Starts a user's codespace.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_lifecycle_admin` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name, 'start'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Stop a codespace for the authenticated user',
        name: 'Github: Stop a codespace for the authenticated user',
        description:
            "Stops a user's codespace.\n\nYou must authenticate using an access token with the `codespace` scope to use this endpoint.\n\nGitHub Apps must have write access to the `codespaces_lifecycle_admin` repository permission to use this endpoint.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            codespace_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    display_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    environment_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    billable_owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    repository: {
                        kind: 'object',
                        properties: {
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            name: { kind: 'string' },
                            full_name: { kind: 'string' },
                            owner: {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            private: { kind: 'boolean' },
                            html_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            fork: { kind: 'boolean' },
                            url: { kind: 'string' },
                            archive_url: { kind: 'string' },
                            assignees_url: { kind: 'string' },
                            blobs_url: { kind: 'string' },
                            branches_url: { kind: 'string' },
                            collaborators_url: { kind: 'string' },
                            comments_url: { kind: 'string' },
                            commits_url: { kind: 'string' },
                            compare_url: { kind: 'string' },
                            contents_url: { kind: 'string' },
                            contributors_url: { kind: 'string' },
                            deployments_url: { kind: 'string' },
                            downloads_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            forks_url: { kind: 'string' },
                            git_commits_url: { kind: 'string' },
                            git_refs_url: { kind: 'string' },
                            git_tags_url: { kind: 'string' },
                            git_url: { kind: 'string' },
                            issue_comment_url: { kind: 'string' },
                            issue_events_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            keys_url: { kind: 'string' },
                            labels_url: { kind: 'string' },
                            languages_url: { kind: 'string' },
                            merges_url: { kind: 'string' },
                            milestones_url: { kind: 'string' },
                            notifications_url: { kind: 'string' },
                            pulls_url: { kind: 'string' },
                            releases_url: { kind: 'string' },
                            ssh_url: { kind: 'string' },
                            stargazers_url: { kind: 'string' },
                            statuses_url: { kind: 'string' },
                            subscribers_url: { kind: 'string' },
                            subscription_url: { kind: 'string' },
                            tags_url: { kind: 'string' },
                            teams_url: { kind: 'string' },
                            trees_url: { kind: 'string' },
                            clone_url: { kind: 'string' },
                            mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hooks_url: { kind: 'string' },
                            svn_url: { kind: 'string' },
                            homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            forks_count: { kind: 'number' },
                            stargazers_count: { kind: 'number' },
                            watchers_count: { kind: 'number' },
                            size: { kind: 'number' },
                            default_branch: { kind: 'string' },
                            open_issues_count: { kind: 'number' },
                            is_template: { kind: 'boolean' },
                            topics: { kind: 'array', subType: { kind: 'string' } },
                            has_issues: { kind: 'boolean' },
                            has_projects: { kind: 'boolean' },
                            has_wiki: { kind: 'boolean' },
                            has_pages: { kind: 'boolean' },
                            has_downloads: { kind: 'boolean' },
                            has_discussions: { kind: 'boolean' },
                            archived: { kind: 'boolean' },
                            disabled: { kind: 'boolean' },
                            visibility: { kind: 'string' },
                            pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            permissions: {
                                kind: 'object',
                                properties: {
                                    admin: { kind: 'boolean' },
                                    maintain: { kind: 'boolean' },
                                    push: { kind: 'boolean' },
                                    triage: { kind: 'boolean' },
                                    pull: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            role_name: { kind: 'string' },
                            temp_clone_token: { kind: 'string' },
                            delete_branch_on_merge: { kind: 'boolean' },
                            subscribers_count: { kind: 'number' },
                            network_count: { kind: 'number' },
                            code_of_conduct: {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'string' },
                                    body: { kind: 'string' },
                                    html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: ['body'],
                                restriction: 'open',
                            },
                            license: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            spdx_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            node_id: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                            forks: { kind: 'number' },
                            open_issues: { kind: 'number' },
                            watchers: { kind: 'number' },
                            allow_forking: { kind: 'boolean' },
                            web_commit_signoff_required: { kind: 'boolean' },
                            security_and_analysis: {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            advanced_security: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            dependabot_security_updates: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            secret_scanning_push_protection: {
                                                kind: 'object',
                                                properties: { status: { kind: 'string' } },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    { kind: 'null' },
                                ],
                            },
                        },
                        optionalProperties: [
                            'git_url',
                            'ssh_url',
                            'clone_url',
                            'mirror_url',
                            'svn_url',
                            'homepage',
                            'language',
                            'forks_count',
                            'stargazers_count',
                            'watchers_count',
                            'size',
                            'default_branch',
                            'open_issues_count',
                            'is_template',
                            'topics',
                            'has_issues',
                            'has_projects',
                            'has_wiki',
                            'has_pages',
                            'has_downloads',
                            'has_discussions',
                            'archived',
                            'disabled',
                            'visibility',
                            'pushed_at',
                            'created_at',
                            'updated_at',
                            'permissions',
                            'role_name',
                            'temp_clone_token',
                            'delete_branch_on_merge',
                            'subscribers_count',
                            'network_count',
                            'code_of_conduct',
                            'license',
                            'forks',
                            'open_issues',
                            'watchers',
                            'allow_forking',
                            'web_commit_signoff_required',
                            'security_and_analysis',
                        ],
                        restriction: 'open',
                    },
                    machine: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'string' },
                                    display_name: { kind: 'string' },
                                    operating_system: { kind: 'string' },
                                    storage_in_bytes: { kind: 'number' },
                                    memory_in_bytes: { kind: 'number' },
                                    cpus: { kind: 'number' },
                                    prebuild_availability: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    devcontainer_path: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    prebuild: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    last_used_at: { kind: 'string' },
                    state: { kind: 'string' },
                    url: { kind: 'string' },
                    git_status: {
                        kind: 'object',
                        properties: {
                            ahead: { kind: 'number' },
                            behind: { kind: 'number' },
                            has_unpushed_changes: { kind: 'boolean' },
                            has_uncommitted_changes: { kind: 'boolean' },
                            ref: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    location: { kind: 'string' },
                    idle_timeout_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    web_url: { kind: 'string' },
                    machines_url: { kind: 'string' },
                    start_url: { kind: 'string' },
                    stop_url: { kind: 'string' },
                    publish_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    pulls_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    recent_folders: { kind: 'array', subType: { kind: 'string' } },
                    runtime_constraints: {
                        kind: 'object',
                        properties: {
                            allowed_port_privacy_settings: {
                                kind: 'union',
                                subTypes: [{ kind: 'array', subType: { kind: 'string' } }, { kind: 'null' }],
                            },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    pending_operation: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                    pending_operation_disabled_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    idle_timeout_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    retention_period_minutes: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    retention_expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    last_known_stop_notice: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: [
                    'display_name',
                    'devcontainer_path',
                    'publish_url',
                    'runtime_constraints',
                    'pending_operation',
                    'pending_operation_disabled_reason',
                    'idle_timeout_notice',
                    'retention_period_minutes',
                    'retention_expires_at',
                    'last_known_stop_notice',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'codespaces', input.inPorts.codespace_name, 'stop'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get list of conflicting packages during Docker migration for authenticated-user',
        name: 'Github: Get list of conflicting packages during Docker migration for authenticated-user',
        description:
            "Lists all packages that are owned by the authenticated user within the user's namespace, and that encountered a conflict during a Docker migration.\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        package_type: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        version_count: { kind: 'number' },
                        visibility: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: ['owner', 'repository'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'docker', 'conflicts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set primary email visibility for the authenticated user',
        name: 'Github: Set primary email visibility for the authenticated user',
        description: 'Sets the visibility for your primary email addresses.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: { kind: 'object', properties: { visibility: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        email: { kind: 'string' },
                        primary: { kind: 'boolean' },
                        verified: { kind: 'boolean' },
                        visibility: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'email', 'visibility'].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List email addresses for the authenticated user',
        name: 'Github: List email addresses for the authenticated user',
        description:
            'Lists all of your email addresses, and specifies which one is visible to the public. This endpoint is accessible with the `user:email` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        email: { kind: 'string' },
                        primary: { kind: 'boolean' },
                        verified: { kind: 'boolean' },
                        visibility: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'emails'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add an email address for the authenticated user',
        name: 'Github: Add an email address for the authenticated user',
        description: 'This endpoint is accessible with the `user` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { emails: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                    { kind: 'string' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        email: { kind: 'string' },
                        primary: { kind: 'boolean' },
                        verified: { kind: 'boolean' },
                        visibility: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'emails'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an email address for the authenticated user',
        name: 'Github: Delete an email address for the authenticated user',
        description: 'This endpoint is accessible with the `user` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { emails: { kind: 'array', subType: { kind: 'string' } } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'array', subType: { kind: 'string' } },
                    { kind: 'string' },
                ],
            },
        },
        optionalInPorts: ['body'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'emails'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List followers of the authenticated user',
        name: 'Github: List followers of the authenticated user',
        description: 'Lists the people following the authenticated user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'followers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List the people the authenticated user follows',
        name: 'Github: List the people the authenticated user follows',
        description: 'Lists the people who the authenticated user follows.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'following'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a person is followed by the authenticated user',
        name: 'Github: Check if a person is followed by the authenticated user',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'following', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Follow a user',
        name: 'Github: Follow a user',
        description:
            'Note that you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."\n\nFollowing a user requires the user to be logged in and authenticated with basic auth or OAuth with the `user:follow` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'following', input.inPorts.username].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unfollow a user',
        name: 'Github: Unfollow a user',
        description:
            'Unfollowing a user requires the user to be logged in and authenticated with basic auth or OAuth with the `user:follow` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'following', input.inPorts.username].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List GPG keys for the authenticated user',
        name: 'Github: List GPG keys for the authenticated user',
        description:
            "Lists the current user's GPG keys. Requires that you are authenticated via Basic Auth or via OAuth with at least `read:gpg_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        primary_key_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        key_id: { kind: 'string' },
                        public_key: { kind: 'string' },
                        emails: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        subkeys: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    primary_key_id: { kind: 'number' },
                                    key_id: { kind: 'string' },
                                    public_key: { kind: 'string' },
                                    emails: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    subkeys: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    can_sign: { kind: 'boolean' },
                                    can_encrypt_comms: { kind: 'boolean' },
                                    can_encrypt_storage: { kind: 'boolean' },
                                    can_certify: { kind: 'boolean' },
                                    created_at: { kind: 'string' },
                                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    revoked: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        can_sign: { kind: 'boolean' },
                        can_encrypt_comms: { kind: 'boolean' },
                        can_encrypt_storage: { kind: 'boolean' },
                        can_certify: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        revoked: { kind: 'boolean' },
                        raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['name'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'gpg_keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a GPG key for the authenticated user',
        name: 'Github: Create a GPG key for the authenticated user',
        description:
            "Adds a GPG key to the authenticated user's GitHub account. Requires that you are authenticated via Basic Auth, or OAuth with at least `write:gpg_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, armored_public_key: { kind: 'string' } },
                optionalProperties: ['name'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    primary_key_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    key_id: { kind: 'string' },
                    public_key: { kind: 'string' },
                    emails: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    subkeys: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                primary_key_id: { kind: 'number' },
                                key_id: { kind: 'string' },
                                public_key: { kind: 'string' },
                                emails: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                subkeys: {
                                    kind: 'array',
                                    subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                can_sign: { kind: 'boolean' },
                                can_encrypt_comms: { kind: 'boolean' },
                                can_encrypt_storage: { kind: 'boolean' },
                                can_certify: { kind: 'boolean' },
                                created_at: { kind: 'string' },
                                expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                revoked: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    can_sign: { kind: 'boolean' },
                    can_encrypt_comms: { kind: 'boolean' },
                    can_encrypt_storage: { kind: 'boolean' },
                    can_certify: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    revoked: { kind: 'boolean' },
                    raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['name'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'gpg_keys'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a GPG key for the authenticated user',
        name: 'Github: Get a GPG key for the authenticated user',
        description:
            'View extended details for a single GPG key. Requires that you are authenticated via Basic Auth or via OAuth with at least `read:gpg_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gpg_key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    primary_key_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                    key_id: { kind: 'string' },
                    public_key: { kind: 'string' },
                    emails: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    subkeys: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                primary_key_id: { kind: 'number' },
                                key_id: { kind: 'string' },
                                public_key: { kind: 'string' },
                                emails: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                                subkeys: {
                                    kind: 'array',
                                    subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                },
                                can_sign: { kind: 'boolean' },
                                can_encrypt_comms: { kind: 'boolean' },
                                can_encrypt_storage: { kind: 'boolean' },
                                can_certify: { kind: 'boolean' },
                                created_at: { kind: 'string' },
                                expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                revoked: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    can_sign: { kind: 'boolean' },
                    can_encrypt_comms: { kind: 'boolean' },
                    can_encrypt_storage: { kind: 'boolean' },
                    can_certify: { kind: 'boolean' },
                    created_at: { kind: 'string' },
                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    revoked: { kind: 'boolean' },
                    raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['name'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'gpg_keys', input.inPorts.gpg_key_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a GPG key for the authenticated user',
        name: 'Github: Delete a GPG key for the authenticated user',
        description:
            "Removes a GPG key from the authenticated user's GitHub account. Requires that you are authenticated via Basic Auth or via OAuth with at least `admin:gpg_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            gpg_key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'gpg_keys', input.inPorts.gpg_key_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List app installations accessible to the user access token',
        name: 'Github: List app installations accessible to the user access token',
        description:
            'Lists installations of your GitHub App that the authenticated user has explicit permission (`:read`, `:write`, or `:admin`) to access.\n\nYou must use a [user access token](https://docs.github.com/apps/creating-github-apps/authenticating-with-a-github-app/generating-a-user-access-token-for-a-github-app), created for a user who has authorized your GitHub App, to access this endpoint.\n\nThe authenticated user has explicit permission to access repositories they own, repositories where they are a collaborator, and repositories that they can access through an organization membership.\n\nYou can find the permissions for the installation under the `permissions` key.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    installations: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                account: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        html_url: { kind: 'string' },
                                                        website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        slug: { kind: 'string' },
                                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        avatar_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['description', 'website_url'],
                                                    restriction: 'open',
                                                },
                                            ],
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                repository_selection: { kind: 'string' },
                                access_tokens_url: { kind: 'string' },
                                repositories_url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                app_id: { kind: 'number' },
                                target_id: { kind: 'number' },
                                target_type: { kind: 'string' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        actions: { kind: 'string' },
                                        administration: { kind: 'string' },
                                        checks: { kind: 'string' },
                                        contents: { kind: 'string' },
                                        deployments: { kind: 'string' },
                                        environments: { kind: 'string' },
                                        issues: { kind: 'string' },
                                        metadata: { kind: 'string' },
                                        packages: { kind: 'string' },
                                        pages: { kind: 'string' },
                                        pull_requests: { kind: 'string' },
                                        repository_hooks: { kind: 'string' },
                                        repository_projects: { kind: 'string' },
                                        secret_scanning_alerts: { kind: 'string' },
                                        secrets: { kind: 'string' },
                                        security_events: { kind: 'string' },
                                        single_file: { kind: 'string' },
                                        statuses: { kind: 'string' },
                                        vulnerability_alerts: { kind: 'string' },
                                        workflows: { kind: 'string' },
                                        members: { kind: 'string' },
                                        organization_administration: { kind: 'string' },
                                        organization_custom_roles: { kind: 'string' },
                                        organization_announcement_banners: { kind: 'string' },
                                        organization_hooks: { kind: 'string' },
                                        organization_personal_access_tokens: { kind: 'string' },
                                        organization_personal_access_token_requests: { kind: 'string' },
                                        organization_plan: { kind: 'string' },
                                        organization_projects: { kind: 'string' },
                                        organization_packages: { kind: 'string' },
                                        organization_secrets: { kind: 'string' },
                                        organization_self_hosted_runners: { kind: 'string' },
                                        organization_user_blocking: { kind: 'string' },
                                        team_discussions: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                events: { kind: 'array', subType: { kind: 'string' } },
                                created_at: { kind: 'string' },
                                updated_at: { kind: 'string' },
                                single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                has_multiple_single_files: { kind: 'boolean' },
                                single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                                app_slug: { kind: 'string' },
                                suspended_by: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'installations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories accessible to the user access token',
        name: 'Github: List repositories accessible to the user access token',
        description:
            'List repositories that the authenticated user has explicit permission (`:read`, `:write`, or `:admin`) to access for an installation.\n\nThe authenticated user has explicit permission to access repositories they own, repositories where they are a collaborator, and repositories that they can access through an organization membership.\n\nYou must use a [user access token](https://docs.github.com/apps/creating-github-apps/authenticating-with-a-github-app/generating-a-user-access-token-for-a-github-app), created for a user who has authorized your GitHub App, to access this endpoint.\n\nThe access the user has to each repository is included in the hash under the `permissions` key.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_count: { kind: 'number' },
                    repository_selection: { kind: 'string' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: ['repository_selection'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'installations', input.inPorts.installation_id, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add a repository to an app installation',
        name: 'Github: Add a repository to an app installation',
        description:
            'Add a single repository to an installation. The authenticated user must have admin access to the repository.\n\nYou must use a personal access token (which you can create via the [command line](https://docs.github.com/github/authenticating-to-github/creating-a-personal-access-token) or [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication)) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'installations', input.inPorts.installation_id, 'repositories', input.inPorts.repository_id].join(
                        '/',
                    ),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove a repository from an app installation',
        name: 'Github: Remove a repository from an app installation',
        description:
            'Remove a single repository from an installation. The authenticated user must have admin access to the repository. The installation must have the `repository_selection` of `selected`.\n\nYou must use a personal access token (which you can create via the [command line](https://docs.github.com/github/authenticating-to-github/creating-a-personal-access-token) or [Basic Authentication](https://docs.github.com/rest/overview/other-authentication-methods#basic-authentication)) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            installation_id: { kind: 'number' },
            repository_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'installations', input.inPorts.installation_id, 'repositories', input.inPorts.repository_id].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get interaction restrictions for your public repositories',
        name: 'Github: Get interaction restrictions for your public repositories',
        description: 'Shows which type of GitHub user can interact with your public repositories and when the restriction expires.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: { limit: { kind: 'string' }, origin: { kind: 'string' }, expires_at: { kind: 'string' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'interaction-limits'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Set interaction restrictions for your public repositories',
        name: 'Github: Set interaction restrictions for your public repositories',
        description:
            'Temporarily restricts which type of GitHub user can interact with your public repositories. Setting the interaction limit at the user level will overwrite any interaction limits that are set for individual repositories owned by the user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { limit: { kind: 'string' }, expiry: { kind: 'string' } },
                optionalProperties: ['expiry'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { limit: { kind: 'string' }, origin: { kind: 'string' }, expires_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'interaction-limits'].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Remove interaction restrictions from your public repositories',
        name: 'Github: Remove interaction restrictions from your public repositories',
        description: 'Removes any interaction restrictions from your public repositories.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'interaction-limits'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List user account issues assigned to the authenticated user',
        name: 'Github: List user account issues assigned to the authenticated user',
        description:
            'List issues across owned and member repositories assigned to the authenticated user.\n\n**Note**: GitHub\'s REST API considers every pull request an issue, but not every issue is a pull request. For this\nreason, "Issues" endpoints may return both issues and pull requests in the response. You can identify pull requests by\nthe `pull_request` key. Be aware that the `id` of a pull request returned from "Issues" endpoints will be an _issue id_. To find out the pull\nrequest id, use the "[List pull requests](https://docs.github.com/rest/pulls/pulls#list-pull-requests)" endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            filter: { kind: 'string' },
            state: { kind: 'string' },
            labels: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['filter', 'state', 'labels', 'sort', 'direction', 'since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repository_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        title: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        labels: {
                            kind: 'array',
                            subType: {
                                kind: 'union',
                                subTypes: [
                                    { kind: 'string' },
                                    {
                                        kind: 'object',
                                        properties: {
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            name: { kind: 'string' },
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            default: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                ],
                            },
                        },
                        assignee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        assignees: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                },
                                { kind: 'null' },
                            ],
                        },
                        milestone: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        title: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        creator: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        open_issues: { kind: 'number' },
                                        closed_issues: { kind: 'number' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        locked: { kind: 'boolean' },
                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        pull_request: {
                            kind: 'object',
                            properties: {
                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['merged_at'],
                            restriction: 'open',
                        },
                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        draft: { kind: 'boolean' },
                        closed_by: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        body_html: { kind: 'string' },
                        body_text: { kind: 'string' },
                        timeline_url: { kind: 'string' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                        performed_via_github_app: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        slug: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                        owner: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        external_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                issues: { kind: 'string' },
                                                checks: { kind: 'string' },
                                                metadata: { kind: 'string' },
                                                contents: { kind: 'string' },
                                                deployments: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        events: { kind: 'array', subType: { kind: 'string' } },
                                        installations_count: { kind: 'number' },
                                        client_id: { kind: 'string' },
                                        client_secret: { kind: 'string' },
                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        pem: { kind: 'string' },
                                    },
                                    optionalProperties: [
                                        'slug',
                                        'installations_count',
                                        'client_id',
                                        'client_secret',
                                        'webhook_secret',
                                        'pem',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        author_association: { kind: 'string' },
                        reactions: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                total_count: { kind: 'number' },
                                '+1': { kind: 'number' },
                                '-1': { kind: 'number' },
                                laugh: { kind: 'number' },
                                confused: { kind: 'number' },
                                heart: { kind: 'number' },
                                hooray: { kind: 'number' },
                                eyes: { kind: 'number' },
                                rocket: { kind: 'number' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [
                        'state_reason',
                        'body',
                        'assignees',
                        'active_lock_reason',
                        'pull_request',
                        'draft',
                        'closed_by',
                        'body_html',
                        'body_text',
                        'timeline_url',
                        'repository',
                        'performed_via_github_app',
                        'reactions',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'issues'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            filter: input.inPorts.filter,
                            state: input.inPorts.state,
                            labels: input.inPorts.labels,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            since: input.inPorts.since,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public SSH keys for the authenticated user',
        name: 'Github: List public SSH keys for the authenticated user',
        description:
            "Lists the public SSH keys for the authenticated user's GitHub account. Requires that you are authenticated via Basic Auth or via OAuth with at least `read:public_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        key: { kind: 'string' },
                        id: { kind: 'number' },
                        url: { kind: 'string' },
                        title: { kind: 'string' },
                        created_at: { kind: 'string' },
                        verified: { kind: 'boolean' },
                        read_only: { kind: 'boolean' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a public SSH key for the authenticated user',
        name: 'Github: Create a public SSH key for the authenticated user',
        description:
            "Adds a public SSH key to the authenticated user's GitHub account. Requires that you are authenticated via Basic Auth, or OAuth with at least `write:public_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, key: { kind: 'string' } },
                optionalProperties: ['title'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key: { kind: 'string' },
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    created_at: { kind: 'string' },
                    verified: { kind: 'boolean' },
                    read_only: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'keys'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a public SSH key for the authenticated user',
        name: 'Github: Get a public SSH key for the authenticated user',
        description:
            'View extended details for a single public SSH key. Requires that you are authenticated via Basic Auth or via OAuth with at least `read:public_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    key: { kind: 'string' },
                    id: { kind: 'number' },
                    url: { kind: 'string' },
                    title: { kind: 'string' },
                    created_at: { kind: 'string' },
                    verified: { kind: 'boolean' },
                    read_only: { kind: 'boolean' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'keys', input.inPorts.key_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a public SSH key for the authenticated user',
        name: 'Github: Delete a public SSH key for the authenticated user',
        description:
            "Removes a public SSH key from the authenticated user's GitHub account. Requires that you are authenticated via Basic Auth or via OAuth with at least `admin:public_key` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'keys', input.inPorts.key_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List subscriptions for the authenticated user',
        name: 'Github: List subscriptions for the authenticated user',
        description:
            'Lists the active subscriptions for the authenticated user. GitHub Apps must use a [user access token](https://docs.github.com/apps/creating-github-apps/authenticating-with-a-github-app/generating-a-user-access-token-for-a-github-app), created for a user who has authorized your GitHub App, to access this endpoint. OAuth apps must authenticate using an [OAuth token](https://docs.github.com/apps/oauth-apps/building-oauth-apps/authorizing-oauth-apps).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        billing_cycle: { kind: 'string' },
                        next_billing_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        on_free_trial: { kind: 'boolean' },
                        free_trial_ends_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        account: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                id: { kind: 'number' },
                                type: { kind: 'string' },
                                node_id: { kind: 'string' },
                                login: { kind: 'string' },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                organization_billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['node_id', 'email', 'organization_billing_email'],
                            restriction: 'open',
                        },
                        plan: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                accounts_url: { kind: 'string' },
                                id: { kind: 'number' },
                                number: { kind: 'number' },
                                name: { kind: 'string' },
                                description: { kind: 'string' },
                                monthly_price_in_cents: { kind: 'number' },
                                yearly_price_in_cents: { kind: 'number' },
                                price_model: { kind: 'string' },
                                has_free_trial: { kind: 'boolean' },
                                unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                bullets: { kind: 'array', subType: { kind: 'string' } },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'marketplace_purchases'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List subscriptions for the authenticated user (stubbed)',
        name: 'Github: List subscriptions for the authenticated user (stubbed)',
        description:
            'Lists the active subscriptions for the authenticated user. GitHub Apps must use a [user access token](https://docs.github.com/apps/creating-github-apps/authenticating-with-a-github-app/generating-a-user-access-token-for-a-github-app), created for a user who has authorized your GitHub App, to access this endpoint. OAuth apps must authenticate using an [OAuth token](https://docs.github.com/apps/oauth-apps/building-oauth-apps/authorizing-oauth-apps).',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        billing_cycle: { kind: 'string' },
                        next_billing_date: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        unit_count: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        on_free_trial: { kind: 'boolean' },
                        free_trial_ends_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        account: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                id: { kind: 'number' },
                                type: { kind: 'string' },
                                node_id: { kind: 'string' },
                                login: { kind: 'string' },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                organization_billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['node_id', 'email', 'organization_billing_email'],
                            restriction: 'open',
                        },
                        plan: {
                            kind: 'object',
                            properties: {
                                url: { kind: 'string' },
                                accounts_url: { kind: 'string' },
                                id: { kind: 'number' },
                                number: { kind: 'number' },
                                name: { kind: 'string' },
                                description: { kind: 'string' },
                                monthly_price_in_cents: { kind: 'number' },
                                yearly_price_in_cents: { kind: 'number' },
                                price_model: { kind: 'string' },
                                has_free_trial: { kind: 'boolean' },
                                unit_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                state: { kind: 'string' },
                                bullets: { kind: 'array', subType: { kind: 'string' } },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'marketplace_purchases', 'stubbed'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization memberships for the authenticated user',
        name: 'Github: List organization memberships for the authenticated user',
        description: "Lists all of the authenticated user's organization memberships.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            state: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['state', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        state: { kind: 'string' },
                        role: { kind: 'string' },
                        organization_url: { kind: 'string' },
                        organization: {
                            kind: 'object',
                            properties: {
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                members_url: { kind: 'string' },
                                public_members_url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        permissions: {
                            kind: 'object',
                            properties: { can_create_repository: { kind: 'boolean' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['permissions'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'memberships', 'orgs'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ state: input.inPorts.state, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an organization membership for the authenticated user',
        name: 'Github: Get an organization membership for the authenticated user',
        description:
            "If the authenticated user is an active or pending member of the organization, this endpoint will return the user's membership. If the authenticated user is not affiliated with the organization, a `404` is returned. This endpoint will return a `403` if the request is made by a GitHub App that is blocked by the organization.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    state: { kind: 'string' },
                    role: { kind: 'string' },
                    organization_url: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    permissions: {
                        kind: 'object',
                        properties: { can_create_repository: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['permissions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'memberships', 'orgs', input.inPorts.org].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Update an organization membership for the authenticated user',
        name: 'Github: Update an organization membership for the authenticated user',
        description:
            'Converts the authenticated user to an active member of the organization, if that user has a pending invitation from the organization.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            org: { kind: 'string' },
            body: { kind: 'object', properties: { state: { kind: 'string' } }, optionalProperties: [], restriction: 'open' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    url: { kind: 'string' },
                    state: { kind: 'string' },
                    role: { kind: 'string' },
                    organization_url: { kind: 'string' },
                    organization: {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            hooks_url: { kind: 'string' },
                            issues_url: { kind: 'string' },
                            members_url: { kind: 'string' },
                            public_members_url: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    user: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    permissions: {
                        kind: 'object',
                        properties: { can_create_repository: { kind: 'boolean' } },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['permissions'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'memberships', 'orgs', input.inPorts.org].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List user migrations',
        name: 'Github: List user migrations',
        description: 'Lists all migrations a user has started.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        guid: { kind: 'string' },
                        state: { kind: 'string' },
                        lock_repositories: { kind: 'boolean' },
                        exclude_metadata: { kind: 'boolean' },
                        exclude_git_data: { kind: 'boolean' },
                        exclude_attachments: { kind: 'boolean' },
                        exclude_releases: { kind: 'boolean' },
                        exclude_owner_projects: { kind: 'boolean' },
                        org_metadata_only: { kind: 'boolean' },
                        repositories: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    node_id: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                },
                                                optionalProperties: ['html_url'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    organization: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    login: { kind: 'string' },
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    avatar_url: { kind: 'string' },
                                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    url: { kind: 'string' },
                                                    html_url: { kind: 'string' },
                                                    followers_url: { kind: 'string' },
                                                    following_url: { kind: 'string' },
                                                    gists_url: { kind: 'string' },
                                                    starred_url: { kind: 'string' },
                                                    subscriptions_url: { kind: 'string' },
                                                    organizations_url: { kind: 'string' },
                                                    repos_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    received_events_url: { kind: 'string' },
                                                    type: { kind: 'string' },
                                                    site_admin: { kind: 'boolean' },
                                                    starred_at: { kind: 'string' },
                                                },
                                                optionalProperties: ['name', 'email', 'starred_at'],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                        },
                                        optionalProperties: ['triage', 'maintain'],
                                        restriction: 'open',
                                    },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    template_repository: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    id: { kind: 'number' },
                                                    node_id: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    full_name: { kind: 'string' },
                                                    owner: {
                                                        kind: 'object',
                                                        properties: {
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    private: { kind: 'boolean' },
                                                    html_url: { kind: 'string' },
                                                    description: { kind: 'string' },
                                                    fork: { kind: 'boolean' },
                                                    url: { kind: 'string' },
                                                    archive_url: { kind: 'string' },
                                                    assignees_url: { kind: 'string' },
                                                    blobs_url: { kind: 'string' },
                                                    branches_url: { kind: 'string' },
                                                    collaborators_url: { kind: 'string' },
                                                    comments_url: { kind: 'string' },
                                                    commits_url: { kind: 'string' },
                                                    compare_url: { kind: 'string' },
                                                    contents_url: { kind: 'string' },
                                                    contributors_url: { kind: 'string' },
                                                    deployments_url: { kind: 'string' },
                                                    downloads_url: { kind: 'string' },
                                                    events_url: { kind: 'string' },
                                                    forks_url: { kind: 'string' },
                                                    git_commits_url: { kind: 'string' },
                                                    git_refs_url: { kind: 'string' },
                                                    git_tags_url: { kind: 'string' },
                                                    git_url: { kind: 'string' },
                                                    issue_comment_url: { kind: 'string' },
                                                    issue_events_url: { kind: 'string' },
                                                    issues_url: { kind: 'string' },
                                                    keys_url: { kind: 'string' },
                                                    labels_url: { kind: 'string' },
                                                    languages_url: { kind: 'string' },
                                                    merges_url: { kind: 'string' },
                                                    milestones_url: { kind: 'string' },
                                                    notifications_url: { kind: 'string' },
                                                    pulls_url: { kind: 'string' },
                                                    releases_url: { kind: 'string' },
                                                    ssh_url: { kind: 'string' },
                                                    stargazers_url: { kind: 'string' },
                                                    statuses_url: { kind: 'string' },
                                                    subscribers_url: { kind: 'string' },
                                                    subscription_url: { kind: 'string' },
                                                    tags_url: { kind: 'string' },
                                                    teams_url: { kind: 'string' },
                                                    trees_url: { kind: 'string' },
                                                    clone_url: { kind: 'string' },
                                                    mirror_url: { kind: 'string' },
                                                    hooks_url: { kind: 'string' },
                                                    svn_url: { kind: 'string' },
                                                    homepage: { kind: 'string' },
                                                    language: { kind: 'string' },
                                                    forks_count: { kind: 'number' },
                                                    stargazers_count: { kind: 'number' },
                                                    watchers_count: { kind: 'number' },
                                                    size: { kind: 'number' },
                                                    default_branch: { kind: 'string' },
                                                    open_issues_count: { kind: 'number' },
                                                    is_template: { kind: 'boolean' },
                                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                                    has_issues: { kind: 'boolean' },
                                                    has_projects: { kind: 'boolean' },
                                                    has_wiki: { kind: 'boolean' },
                                                    has_pages: { kind: 'boolean' },
                                                    has_downloads: { kind: 'boolean' },
                                                    archived: { kind: 'boolean' },
                                                    disabled: { kind: 'boolean' },
                                                    visibility: { kind: 'string' },
                                                    pushed_at: { kind: 'string' },
                                                    created_at: { kind: 'string' },
                                                    updated_at: { kind: 'string' },
                                                    permissions: {
                                                        kind: 'object',
                                                        properties: {
                                                            admin: { kind: 'boolean' },
                                                            maintain: { kind: 'boolean' },
                                                            push: { kind: 'boolean' },
                                                            triage: { kind: 'boolean' },
                                                            pull: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    allow_rebase_merge: { kind: 'boolean' },
                                                    temp_clone_token: { kind: 'string' },
                                                    allow_squash_merge: { kind: 'boolean' },
                                                    allow_auto_merge: { kind: 'boolean' },
                                                    delete_branch_on_merge: { kind: 'boolean' },
                                                    allow_update_branch: { kind: 'boolean' },
                                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                                    squash_merge_commit_title: { kind: 'string' },
                                                    squash_merge_commit_message: { kind: 'string' },
                                                    merge_commit_title: { kind: 'string' },
                                                    merge_commit_message: { kind: 'string' },
                                                    allow_merge_commit: { kind: 'boolean' },
                                                    subscribers_count: { kind: 'number' },
                                                    network_count: { kind: 'number' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    master_branch: { kind: 'string' },
                                    starred_at: { kind: 'string' },
                                    anonymous_access_enabled: { kind: 'boolean' },
                                },
                                optionalProperties: [
                                    'organization',
                                    'permissions',
                                    'is_template',
                                    'topics',
                                    'has_discussions',
                                    'visibility',
                                    'allow_rebase_merge',
                                    'template_repository',
                                    'temp_clone_token',
                                    'allow_squash_merge',
                                    'allow_auto_merge',
                                    'delete_branch_on_merge',
                                    'allow_update_branch',
                                    'use_squash_pr_title_as_default',
                                    'squash_merge_commit_title',
                                    'squash_merge_commit_message',
                                    'merge_commit_title',
                                    'merge_commit_message',
                                    'allow_merge_commit',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'subscribers_count',
                                    'network_count',
                                    'master_branch',
                                    'starred_at',
                                    'anonymous_access_enabled',
                                ],
                                restriction: 'open',
                            },
                        },
                        url: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        node_id: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        exclude: { kind: 'array', subType: { kind: 'string' } },
                    },
                    optionalProperties: ['archive_url', 'exclude'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'migrations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Start a user migration',
        name: 'Github: Start a user migration',
        description: 'Initiates the generation of a user migration archive.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    lock_repositories: { kind: 'boolean' },
                    exclude_metadata: { kind: 'boolean' },
                    exclude_git_data: { kind: 'boolean' },
                    exclude_attachments: { kind: 'boolean' },
                    exclude_releases: { kind: 'boolean' },
                    exclude_owner_projects: { kind: 'boolean' },
                    org_metadata_only: { kind: 'boolean' },
                    exclude: { kind: 'array', subType: { kind: 'string' } },
                    repositories: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: [
                    'lock_repositories',
                    'exclude_metadata',
                    'exclude_git_data',
                    'exclude_attachments',
                    'exclude_releases',
                    'exclude_owner_projects',
                    'org_metadata_only',
                    'exclude',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    guid: { kind: 'string' },
                    state: { kind: 'string' },
                    lock_repositories: { kind: 'boolean' },
                    exclude_metadata: { kind: 'boolean' },
                    exclude_git_data: { kind: 'boolean' },
                    exclude_attachments: { kind: 'boolean' },
                    exclude_releases: { kind: 'boolean' },
                    exclude_owner_projects: { kind: 'boolean' },
                    org_metadata_only: { kind: 'boolean' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    node_id: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    exclude: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['archive_url', 'exclude'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'migrations'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a user migration status',
        name: 'Github: Get a user migration status',
        description:
            "Fetches a single user migration. The response includes the `state` of the migration, which can be one of the following values:\n\n*   `pending` - the migration hasn't started yet.\n*   `exporting` - the migration is in progress.\n*   `exported` - the migration finished successfully.\n*   `failed` - the migration failed.\n\nOnce the migration has been `exported` you can [download the migration archive](https://docs.github.com/rest/migrations/users#download-a-user-migration-archive).",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            migration_id: { kind: 'number' },
            exclude: { kind: 'array', subType: { kind: 'string' } },
        },
        optionalInPorts: ['exclude'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    guid: { kind: 'string' },
                    state: { kind: 'string' },
                    lock_repositories: { kind: 'boolean' },
                    exclude_metadata: { kind: 'boolean' },
                    exclude_git_data: { kind: 'boolean' },
                    exclude_attachments: { kind: 'boolean' },
                    exclude_releases: { kind: 'boolean' },
                    exclude_owner_projects: { kind: 'boolean' },
                    org_metadata_only: { kind: 'boolean' },
                    repositories: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                    url: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    node_id: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    exclude: { kind: 'array', subType: { kind: 'string' } },
                },
                optionalProperties: ['archive_url', 'exclude'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'migrations', input.inPorts.migration_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({ exclude: input.inPorts.exclude }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a user migration archive',
        name: 'Github: Delete a user migration archive',
        description:
            'Deletes a previous migration archive. Downloadable migration archives are automatically deleted after seven days. Migration metadata, which is returned in the [List user migrations](https://docs.github.com/rest/migrations/users#list-user-migrations) and [Get a user migration status](https://docs.github.com/rest/migrations/users#get-a-user-migration-status) endpoints, will continue to be available even after an archive is deleted.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            migration_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'migrations', input.inPorts.migration_id, 'archive'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unlock a user repository',
        name: 'Github: Unlock a user repository',
        description:
            'Unlocks a repository. You can lock repositories when you [start a user migration](https://docs.github.com/rest/migrations/users#start-a-user-migration). Once the migration is complete you can unlock each repository to begin using it again or [delete the repository](https://docs.github.com/rest/repos/repos#delete-a-repository) if you no longer need the source data. Returns a status of `404 Not Found` if the repository is not locked.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            migration_id: { kind: 'number' },
            repo_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'migrations', input.inPorts.migration_id, 'repos', input.inPorts.repo_name, 'lock'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories for a user migration',
        name: 'Github: List repositories for a user migration',
        description: 'Lists all the repositories for this user migration.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            migration_id: { kind: 'number' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'migrations', input.inPorts.migration_id, 'repositories'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organizations for the authenticated user',
        name: 'Github: List organizations for the authenticated user',
        description:
            'List organizations for the authenticated user.\n\n**OAuth scope requirements**\n\nThis only lists organizations that your authorization allows you to operate on in some way (e.g., you can list teams with `read:org` scope, you can publicize your organization membership with `user` scope, etc.). Therefore, this API requires at least `user` or `read:org` scope. OAuth requests with insufficient scope receive a `403 Forbidden` response.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        hooks_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        public_members_url: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'orgs'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: "Github: List packages for the authenticated user's namespace",
        name: "Github: List packages for the authenticated user's namespace",
        description:
            'Lists packages owned by the authenticated user within the user\'s namespace.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            visibility: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['visibility', 'page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        package_type: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        version_count: { kind: 'number' },
                        visibility: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: ['owner', 'repository'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'packages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            package_type: input.inPorts.package_type,
                            visibility: input.inPorts.visibility,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a package for the authenticated user',
        name: 'Github: Get a package for the authenticated user',
        description:
            'Gets a specific package for a package owned by the authenticated user.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    package_type: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    version_count: { kind: 'number' },
                    visibility: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    role_name: { kind: 'string' },
                                    temp_clone_token: { kind: 'string' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    code_of_conduct: {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'string' },
                                            body: { kind: 'string' },
                                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: ['body'],
                                        restriction: 'open',
                                    },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    spdx_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    node_id: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    security_and_analysis: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    advanced_security: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    dependabot_security_updates: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    secret_scanning: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    secret_scanning_push_protection: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                },
                                optionalProperties: [
                                    'git_url',
                                    'ssh_url',
                                    'clone_url',
                                    'mirror_url',
                                    'svn_url',
                                    'homepage',
                                    'language',
                                    'forks_count',
                                    'stargazers_count',
                                    'watchers_count',
                                    'size',
                                    'default_branch',
                                    'open_issues_count',
                                    'is_template',
                                    'topics',
                                    'has_issues',
                                    'has_projects',
                                    'has_wiki',
                                    'has_pages',
                                    'has_downloads',
                                    'has_discussions',
                                    'archived',
                                    'disabled',
                                    'visibility',
                                    'pushed_at',
                                    'created_at',
                                    'updated_at',
                                    'permissions',
                                    'role_name',
                                    'temp_clone_token',
                                    'delete_branch_on_merge',
                                    'subscribers_count',
                                    'network_count',
                                    'code_of_conduct',
                                    'license',
                                    'forks',
                                    'open_issues',
                                    'watchers',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'security_and_analysis',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: ['owner', 'repository'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'packages', input.inPorts.package_type, input.inPorts.package_name].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a package for the authenticated user',
        name: 'Github: Delete a package for the authenticated user',
        description:
            'Deletes a package owned by the authenticated user. You cannot delete a public package if any version of the package has more than 5,000 downloads. In this scenario, contact GitHub support for further assistance.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `delete:packages` scopes.\nIf the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'packages', input.inPorts.package_type, input.inPorts.package_name].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Restore a package for the authenticated user',
        name: 'Github: Restore a package for the authenticated user',
        description:
            'Restores a package owned by the authenticated user.\n\nYou can restore a deleted package under the following conditions:\n  - The package was deleted within the last 30 days.\n  - The same package namespace and version is still available and not reused for a new package. If the same package namespace is not available, you will not be able to restore your package. In this scenario, to restore the deleted package, you must delete the new package that uses the deleted package\'s namespace first.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `write:packages` scopes. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            token: { kind: 'string' },
        },
        optionalInPorts: ['token'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'packages', input.inPorts.package_type, input.inPorts.package_name, 'restore'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ token: input.inPorts.token }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List package versions for a package owned by the authenticated user',
        name: 'Github: List package versions for a package owned by the authenticated user',
        description:
            'Lists package versions for a package owned by the authenticated user.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
            state: { kind: 'string' },
        },
        optionalInPorts: ['page', 'per_page', 'state'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        url: { kind: 'string' },
                        package_html_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        license: { kind: 'string' },
                        description: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        deleted_at: { kind: 'string' },
                        metadata: {
                            kind: 'object',
                            properties: {
                                package_type: { kind: 'string' },
                                container: {
                                    kind: 'object',
                                    properties: { tags: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                docker: {
                                    kind: 'object',
                                    properties: { tag: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: ['tag'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['container', 'docker'],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['html_url', 'license', 'description', 'deleted_at', 'metadata'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'packages', input.inPorts.package_type, input.inPorts.package_name, 'versions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ page: input.inPorts.page, per_page: input.inPorts.per_page, state: input.inPorts.state }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a package version for the authenticated user',
        name: 'Github: Get a package version for the authenticated user',
        description:
            'Gets a specific package version for a package owned by the authenticated user.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    package_html_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    license: { kind: 'string' },
                    description: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    deleted_at: { kind: 'string' },
                    metadata: {
                        kind: 'object',
                        properties: {
                            package_type: { kind: 'string' },
                            container: {
                                kind: 'object',
                                properties: { tags: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            docker: {
                                kind: 'object',
                                properties: { tag: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: ['tag'],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: ['container', 'docker'],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['html_url', 'license', 'description', 'deleted_at', 'metadata'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'user',
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a package version for the authenticated user',
        name: 'Github: Delete a package version for the authenticated user',
        description:
            'Deletes a specific package version for a package owned by the authenticated user.  If the package is public and the package version has more than 5,000 downloads, you cannot delete the package version. In this scenario, contact GitHub support for further assistance.\n\nTo use this endpoint, you must have admin permissions in the organization and authenticate using an access token with the `read:packages` and `delete:packages` scopes.\nIf the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'user',
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Restore a package version for the authenticated user',
        name: 'Github: Restore a package version for the authenticated user',
        description:
            'Restores a package version owned by the authenticated user.\n\nYou can restore a deleted package version under the following conditions:\n  - The package was deleted within the last 30 days.\n  - The same package namespace and version is still available and not reused for a new package. If the same package namespace is not available, you will not be able to restore your package. In this scenario, to restore the deleted package, you must delete the new package that uses the deleted package\'s namespace first.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `write:packages` scopes. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'user',
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                        'restore',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a user project',
        name: 'Github: Create a user project',
        description:
            'Creates a user project board. Returns a `410 Gone` status if the user does not have existing classic projects. If you do not have sufficient privileges to perform this action, a `401 Unauthorized` or `410 Gone` status is returned.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { name: { kind: 'string' }, body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] } },
                optionalProperties: ['body'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    owner_url: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    columns_url: { kind: 'string' },
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    number: { kind: 'number' },
                    state: { kind: 'string' },
                    creator: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    organization_permission: { kind: 'string' },
                    private: { kind: 'boolean' },
                },
                optionalProperties: ['organization_permission', 'private'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'projects'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public email addresses for the authenticated user',
        name: 'Github: List public email addresses for the authenticated user',
        description:
            'Lists your publicly visible email address, which you can set with the [Set primary email visibility for the authenticated user](https://docs.github.com/rest/users/emails#set-primary-email-visibility-for-the-authenticated-user) endpoint. This endpoint is accessible with the `user:email` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        email: { kind: 'string' },
                        primary: { kind: 'boolean' },
                        verified: { kind: 'boolean' },
                        visibility: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'public_emails'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories for the authenticated user',
        name: 'Github: List repositories for the authenticated user',
        description:
            'Lists repositories that the authenticated user has explicit permission (`:read`, `:write`, or `:admin`) to access.\n\nThe authenticated user has explicit permission to access repositories they own, repositories where they are a collaborator, and repositories that they can access through an organization membership.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            visibility: { kind: 'string' },
            affiliation: { kind: 'string' },
            type: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
            since: { kind: 'string' },
            before: { kind: 'string' },
        },
        optionalInPorts: ['visibility', 'affiliation', 'type', 'sort', 'direction', 'per_page', 'page', 'since', 'before'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        node_id: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                    },
                                    optionalProperties: ['html_url'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        organization: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                            },
                            optionalProperties: ['triage', 'maintain'],
                            restriction: 'open',
                        },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        allow_rebase_merge: { kind: 'boolean' },
                        template_repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'string' },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'string' },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'string' },
                                        language: { kind: 'string' },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        temp_clone_token: { kind: 'string' },
                        allow_squash_merge: { kind: 'boolean' },
                        allow_auto_merge: { kind: 'boolean' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        allow_update_branch: { kind: 'boolean' },
                        use_squash_pr_title_as_default: { kind: 'boolean' },
                        squash_merge_commit_title: { kind: 'string' },
                        squash_merge_commit_message: { kind: 'string' },
                        merge_commit_title: { kind: 'string' },
                        merge_commit_message: { kind: 'string' },
                        allow_merge_commit: { kind: 'boolean' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        master_branch: { kind: 'string' },
                        starred_at: { kind: 'string' },
                        anonymous_access_enabled: { kind: 'boolean' },
                    },
                    optionalProperties: [
                        'organization',
                        'permissions',
                        'is_template',
                        'topics',
                        'has_discussions',
                        'visibility',
                        'allow_rebase_merge',
                        'template_repository',
                        'temp_clone_token',
                        'allow_squash_merge',
                        'allow_auto_merge',
                        'delete_branch_on_merge',
                        'allow_update_branch',
                        'use_squash_pr_title_as_default',
                        'squash_merge_commit_title',
                        'squash_merge_commit_message',
                        'merge_commit_title',
                        'merge_commit_message',
                        'allow_merge_commit',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'subscribers_count',
                        'network_count',
                        'master_branch',
                        'starred_at',
                        'anonymous_access_enabled',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'repos'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            visibility: input.inPorts.visibility,
                            affiliation: input.inPorts.affiliation,
                            type: input.inPorts.type,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                            since: input.inPorts.since,
                            before: input.inPorts.before,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a repository for the authenticated user',
        name: 'Github: Create a repository for the authenticated user',
        description:
            'Creates a new repository for the authenticated user.\n\n**OAuth scope requirements**\n\nWhen using [OAuth](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/), authorizations must include:\n\n*   `public_repo` scope or `repo` scope to create a public repository. Note: For GitHub AE, use `repo` scope to create an internal repository.\n*   `repo` scope to create a private repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    description: { kind: 'string' },
                    homepage: { kind: 'string' },
                    private: { kind: 'boolean' },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    team_id: { kind: 'number' },
                    auto_init: { kind: 'boolean' },
                    gitignore_template: { kind: 'string' },
                    license_template: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_rebase_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    has_downloads: { kind: 'boolean' },
                    is_template: { kind: 'boolean' },
                },
                optionalProperties: [
                    'description',
                    'homepage',
                    'private',
                    'has_issues',
                    'has_projects',
                    'has_wiki',
                    'has_discussions',
                    'team_id',
                    'auto_init',
                    'gitignore_template',
                    'license_template',
                    'allow_squash_merge',
                    'allow_merge_commit',
                    'allow_rebase_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'has_downloads',
                    'is_template',
                ],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    node_id: { kind: 'string' },
                    name: { kind: 'string' },
                    full_name: { kind: 'string' },
                    license: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    key: { kind: 'string' },
                                    name: { kind: 'string' },
                                    url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    node_id: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                },
                                optionalProperties: ['html_url'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    organization: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    forks: { kind: 'number' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            admin: { kind: 'boolean' },
                            pull: { kind: 'boolean' },
                            triage: { kind: 'boolean' },
                            push: { kind: 'boolean' },
                            maintain: { kind: 'boolean' },
                        },
                        optionalProperties: ['triage', 'maintain'],
                        restriction: 'open',
                    },
                    owner: {
                        kind: 'object',
                        properties: {
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            starred_at: { kind: 'string' },
                        },
                        optionalProperties: ['name', 'email', 'starred_at'],
                        restriction: 'open',
                    },
                    private: { kind: 'boolean' },
                    html_url: { kind: 'string' },
                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    fork: { kind: 'boolean' },
                    url: { kind: 'string' },
                    archive_url: { kind: 'string' },
                    assignees_url: { kind: 'string' },
                    blobs_url: { kind: 'string' },
                    branches_url: { kind: 'string' },
                    collaborators_url: { kind: 'string' },
                    comments_url: { kind: 'string' },
                    commits_url: { kind: 'string' },
                    compare_url: { kind: 'string' },
                    contents_url: { kind: 'string' },
                    contributors_url: { kind: 'string' },
                    deployments_url: { kind: 'string' },
                    downloads_url: { kind: 'string' },
                    events_url: { kind: 'string' },
                    forks_url: { kind: 'string' },
                    git_commits_url: { kind: 'string' },
                    git_refs_url: { kind: 'string' },
                    git_tags_url: { kind: 'string' },
                    git_url: { kind: 'string' },
                    issue_comment_url: { kind: 'string' },
                    issue_events_url: { kind: 'string' },
                    issues_url: { kind: 'string' },
                    keys_url: { kind: 'string' },
                    labels_url: { kind: 'string' },
                    languages_url: { kind: 'string' },
                    merges_url: { kind: 'string' },
                    milestones_url: { kind: 'string' },
                    notifications_url: { kind: 'string' },
                    pulls_url: { kind: 'string' },
                    releases_url: { kind: 'string' },
                    ssh_url: { kind: 'string' },
                    stargazers_url: { kind: 'string' },
                    statuses_url: { kind: 'string' },
                    subscribers_url: { kind: 'string' },
                    subscription_url: { kind: 'string' },
                    tags_url: { kind: 'string' },
                    teams_url: { kind: 'string' },
                    trees_url: { kind: 'string' },
                    clone_url: { kind: 'string' },
                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    hooks_url: { kind: 'string' },
                    svn_url: { kind: 'string' },
                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    forks_count: { kind: 'number' },
                    stargazers_count: { kind: 'number' },
                    watchers_count: { kind: 'number' },
                    size: { kind: 'number' },
                    default_branch: { kind: 'string' },
                    open_issues_count: { kind: 'number' },
                    is_template: { kind: 'boolean' },
                    topics: { kind: 'array', subType: { kind: 'string' } },
                    has_issues: { kind: 'boolean' },
                    has_projects: { kind: 'boolean' },
                    has_wiki: { kind: 'boolean' },
                    has_pages: { kind: 'boolean' },
                    has_downloads: { kind: 'boolean' },
                    has_discussions: { kind: 'boolean' },
                    archived: { kind: 'boolean' },
                    disabled: { kind: 'boolean' },
                    visibility: { kind: 'string' },
                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    allow_rebase_merge: { kind: 'boolean' },
                    template_repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'string' },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'string' },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'string' },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'string' },
                                    language: { kind: 'string' },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'string' },
                                    created_at: { kind: 'string' },
                                    updated_at: { kind: 'string' },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    allow_rebase_merge: { kind: 'boolean' },
                                    temp_clone_token: { kind: 'string' },
                                    allow_squash_merge: { kind: 'boolean' },
                                    allow_auto_merge: { kind: 'boolean' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    allow_update_branch: { kind: 'boolean' },
                                    use_squash_pr_title_as_default: { kind: 'boolean' },
                                    squash_merge_commit_title: { kind: 'string' },
                                    squash_merge_commit_message: { kind: 'string' },
                                    merge_commit_title: { kind: 'string' },
                                    merge_commit_message: { kind: 'string' },
                                    allow_merge_commit: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    temp_clone_token: { kind: 'string' },
                    allow_squash_merge: { kind: 'boolean' },
                    allow_auto_merge: { kind: 'boolean' },
                    delete_branch_on_merge: { kind: 'boolean' },
                    allow_update_branch: { kind: 'boolean' },
                    use_squash_pr_title_as_default: { kind: 'boolean' },
                    squash_merge_commit_title: { kind: 'string' },
                    squash_merge_commit_message: { kind: 'string' },
                    merge_commit_title: { kind: 'string' },
                    merge_commit_message: { kind: 'string' },
                    allow_merge_commit: { kind: 'boolean' },
                    allow_forking: { kind: 'boolean' },
                    web_commit_signoff_required: { kind: 'boolean' },
                    subscribers_count: { kind: 'number' },
                    network_count: { kind: 'number' },
                    open_issues: { kind: 'number' },
                    watchers: { kind: 'number' },
                    master_branch: { kind: 'string' },
                    starred_at: { kind: 'string' },
                    anonymous_access_enabled: { kind: 'boolean' },
                },
                optionalProperties: [
                    'organization',
                    'permissions',
                    'is_template',
                    'topics',
                    'has_discussions',
                    'visibility',
                    'allow_rebase_merge',
                    'template_repository',
                    'temp_clone_token',
                    'allow_squash_merge',
                    'allow_auto_merge',
                    'delete_branch_on_merge',
                    'allow_update_branch',
                    'use_squash_pr_title_as_default',
                    'squash_merge_commit_title',
                    'squash_merge_commit_message',
                    'merge_commit_title',
                    'merge_commit_message',
                    'allow_merge_commit',
                    'allow_forking',
                    'web_commit_signoff_required',
                    'subscribers_count',
                    'network_count',
                    'master_branch',
                    'starred_at',
                    'anonymous_access_enabled',
                ],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'repos'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repository invitations for the authenticated user',
        name: 'Github: List repository invitations for the authenticated user',
        description: 'When authenticating as a user, this endpoint will list all currently open repository invitations for that user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        repository: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                role_name: { kind: 'string' },
                                temp_clone_token: { kind: 'string' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                code_of_conduct: {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    },
                                    optionalProperties: ['body'],
                                    restriction: 'open',
                                },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                spdx_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                node_id: { kind: 'string' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                security_and_analysis: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                advanced_security: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                dependabot_security_updates: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                secret_scanning_push_protection: {
                                                    kind: 'object',
                                                    properties: { status: { kind: 'string' } },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                            },
                            optionalProperties: [
                                'git_url',
                                'ssh_url',
                                'clone_url',
                                'mirror_url',
                                'svn_url',
                                'homepage',
                                'language',
                                'forks_count',
                                'stargazers_count',
                                'watchers_count',
                                'size',
                                'default_branch',
                                'open_issues_count',
                                'is_template',
                                'topics',
                                'has_issues',
                                'has_projects',
                                'has_wiki',
                                'has_pages',
                                'has_downloads',
                                'has_discussions',
                                'archived',
                                'disabled',
                                'visibility',
                                'pushed_at',
                                'created_at',
                                'updated_at',
                                'permissions',
                                'role_name',
                                'temp_clone_token',
                                'delete_branch_on_merge',
                                'subscribers_count',
                                'network_count',
                                'code_of_conduct',
                                'license',
                                'forks',
                                'open_issues',
                                'watchers',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'security_and_analysis',
                            ],
                            restriction: 'open',
                        },
                        invitee: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        inviter: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        permissions: { kind: 'string' },
                        created_at: { kind: 'string' },
                        expired: { kind: 'boolean' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        node_id: { kind: 'string' },
                    },
                    optionalProperties: ['expired'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'repository_invitations'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Decline a repository invitation',
        name: 'Github: Decline a repository invitation',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            invitation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'repository_invitations', input.inPorts.invitation_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Accept a repository invitation',
        name: 'Github: Accept a repository invitation',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            invitation_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'repository_invitations', input.inPorts.invitation_id].join('/'),
                    method: 'patch',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List social accounts for the authenticated user',
        name: 'Github: List social accounts for the authenticated user',
        description: 'Lists all of your social accounts.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: { provider: { kind: 'string' }, url: { kind: 'string' } },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'social_accounts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Add social accounts for the authenticated user',
        name: 'Github: Add social accounts for the authenticated user',
        description:
            "Add one or more social accounts to the authenticated user's profile. This endpoint is accessible with the `user` scope.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { account_urls: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: { provider: { kind: 'string' }, url: { kind: 'string' } },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'social_accounts'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete social accounts for the authenticated user',
        name: 'Github: Delete social accounts for the authenticated user',
        description:
            "Deletes one or more social accounts from the authenticated user's profile. This endpoint is accessible with the `user` scope.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { account_urls: { kind: 'array', subType: { kind: 'string' } } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'social_accounts'].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List SSH signing keys for the authenticated user',
        name: 'Github: List SSH signing keys for the authenticated user',
        description:
            'Lists the SSH signing keys for the authenticated user\'s GitHub account. You must authenticate with Basic Authentication, or you must authenticate with OAuth with at least `read:ssh_signing_key` scope. For more information, see "[Understanding scopes for OAuth apps](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        key: { kind: 'string' },
                        id: { kind: 'number' },
                        title: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'ssh_signing_keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Create a SSH signing key for the authenticated user',
        name: 'Github: Create a SSH signing key for the authenticated user',
        description:
            'Creates an SSH signing key for the authenticated user\'s GitHub account. You must authenticate with Basic Authentication, or you must authenticate with OAuth with at least `write:ssh_signing_key` scope. For more information, see "[Understanding scopes for OAuth apps](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            body: {
                kind: 'object',
                properties: { title: { kind: 'string' }, key: { kind: 'string' } },
                optionalProperties: ['title'],
                restriction: 'open',
            },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { key: { kind: 'string' }, id: { kind: 'number' }, title: { kind: 'string' }, created_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'ssh_signing_keys'].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                    data: input.inPorts.body,
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get an SSH signing key for the authenticated user',
        name: 'Github: Get an SSH signing key for the authenticated user',
        description:
            'Gets extended details for an SSH signing key. You must authenticate with Basic Authentication, or you must authenticate with OAuth with at least `read:ssh_signing_key` scope. For more information, see "[Understanding scopes for OAuth apps](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            ssh_signing_key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: { key: { kind: 'string' }, id: { kind: 'number' }, title: { kind: 'string' }, created_at: { kind: 'string' } },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'ssh_signing_keys', input.inPorts.ssh_signing_key_id].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete an SSH signing key for the authenticated user',
        name: 'Github: Delete an SSH signing key for the authenticated user',
        description:
            'Deletes an SSH signing key from the authenticated user\'s GitHub account. You must authenticate with Basic Authentication, or you must authenticate with OAuth with at least `admin:ssh_signing_key` scope. For more information, see "[Understanding scopes for OAuth apps](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            ssh_signing_key_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'ssh_signing_keys', input.inPorts.ssh_signing_key_id].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories starred by the authenticated user',
        name: 'Github: List repositories starred by the authenticated user',
        description:
            'Lists repositories the authenticated user has starred.\n\nYou can also find out _when_ stars were created by passing the following custom [media type](https://docs.github.com/rest/overview/media-types/) via the `Accept` header: `application/vnd.github.star+json`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        node_id: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                    },
                                    optionalProperties: ['html_url'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        organization: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                            },
                            optionalProperties: ['triage', 'maintain'],
                            restriction: 'open',
                        },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        allow_rebase_merge: { kind: 'boolean' },
                        template_repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'string' },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'string' },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'string' },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'string' },
                                        language: { kind: 'string' },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'string' },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        temp_clone_token: { kind: 'string' },
                        allow_squash_merge: { kind: 'boolean' },
                        allow_auto_merge: { kind: 'boolean' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        allow_update_branch: { kind: 'boolean' },
                        use_squash_pr_title_as_default: { kind: 'boolean' },
                        squash_merge_commit_title: { kind: 'string' },
                        squash_merge_commit_message: { kind: 'string' },
                        merge_commit_title: { kind: 'string' },
                        merge_commit_message: { kind: 'string' },
                        allow_merge_commit: { kind: 'boolean' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        master_branch: { kind: 'string' },
                        starred_at: { kind: 'string' },
                        anonymous_access_enabled: { kind: 'boolean' },
                    },
                    optionalProperties: [
                        'organization',
                        'permissions',
                        'is_template',
                        'topics',
                        'has_discussions',
                        'visibility',
                        'allow_rebase_merge',
                        'template_repository',
                        'temp_clone_token',
                        'allow_squash_merge',
                        'allow_auto_merge',
                        'delete_branch_on_merge',
                        'allow_update_branch',
                        'use_squash_pr_title_as_default',
                        'squash_merge_commit_title',
                        'squash_merge_commit_message',
                        'merge_commit_title',
                        'merge_commit_message',
                        'allow_merge_commit',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'subscribers_count',
                        'network_count',
                        'master_branch',
                        'starred_at',
                        'anonymous_access_enabled',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'starred'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a repository is starred by the authenticated user',
        name: 'Github: Check if a repository is starred by the authenticated user',
        description: 'Whether the authenticated user has starred the repository.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'starred', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Star a repository for the authenticated user',
        name: 'Github: Star a repository for the authenticated user',
        description:
            'Note that you\'ll need to set `Content-Length` to zero when calling out to this endpoint. For more information, see "[HTTP verbs](https://docs.github.com/rest/overview/resources-in-the-rest-api#http-verbs)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'starred', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'put',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Unstar a repository for the authenticated user',
        name: 'Github: Unstar a repository for the authenticated user',
        description: 'Unstar a repository that the authenticated user has previously starred.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            owner: { kind: 'string' },
            repo: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'starred', input.inPorts.owner, input.inPorts.repo].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories watched by the authenticated user',
        name: 'Github: List repositories watched by the authenticated user',
        description: 'Lists repositories the authenticated user is watching.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'subscriptions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List teams for the authenticated user',
        name: 'Github: List teams for the authenticated user',
        description:
            'List all of the teams across all of the organizations to which the authenticated user belongs. This method requires `user`, `repo`, or `read:org` [scope](https://docs.github.com/apps/building-oauth-apps/understanding-scopes-for-oauth-apps/) when authenticating via [OAuth](https://docs.github.com/apps/building-oauth-apps/). When using a fine-grained personal access token, the resource owner of the token [must be a single organization](https://docs.github.com/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token#fine-grained-personal-access-tokens), and have at least read-only member organization permissions. The response payload only contains the teams from a single organization when using a fine-grained personal access token.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        name: { kind: 'string' },
                        slug: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        privacy: { kind: 'string' },
                        notification_setting: { kind: 'string' },
                        permission: { kind: 'string' },
                        members_url: { kind: 'string' },
                        repositories_url: { kind: 'string' },
                        parent: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        members_url: { kind: 'string' },
                                        name: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permission: { kind: 'string' },
                                        privacy: { kind: 'string' },
                                        notification_setting: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        repositories_url: { kind: 'string' },
                                        slug: { kind: 'string' },
                                        ldap_dn: { kind: 'string' },
                                    },
                                    optionalProperties: ['privacy', 'notification_setting', 'ldap_dn'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        members_count: { kind: 'number' },
                        repos_count: { kind: 'number' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        organization: {
                            kind: 'object',
                            properties: {
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                hooks_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                members_url: { kind: 'string' },
                                public_members_url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                name: { kind: 'string' },
                                company: { kind: 'string' },
                                blog: { kind: 'string' },
                                location: { kind: 'string' },
                                email: { kind: 'string' },
                                twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                is_verified: { kind: 'boolean' },
                                has_organization_projects: { kind: 'boolean' },
                                has_repository_projects: { kind: 'boolean' },
                                public_repos: { kind: 'number' },
                                public_gists: { kind: 'number' },
                                followers: { kind: 'number' },
                                following: { kind: 'number' },
                                html_url: { kind: 'string' },
                                created_at: { kind: 'string' },
                                type: { kind: 'string' },
                                total_private_repos: { kind: 'number' },
                                owned_private_repos: { kind: 'number' },
                                private_gists: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                disk_usage: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                collaborators: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                                billing_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                plan: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'string' },
                                        space: { kind: 'number' },
                                        private_repos: { kind: 'number' },
                                        filled_seats: { kind: 'number' },
                                        seats: { kind: 'number' },
                                    },
                                    optionalProperties: ['filled_seats', 'seats'],
                                    restriction: 'open',
                                },
                                default_repository_permission: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                members_can_create_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                two_factor_requirement_enabled: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                members_allowed_repository_creation_type: { kind: 'string' },
                                members_can_create_public_repositories: { kind: 'boolean' },
                                members_can_create_private_repositories: { kind: 'boolean' },
                                members_can_create_internal_repositories: { kind: 'boolean' },
                                members_can_create_pages: { kind: 'boolean' },
                                members_can_create_public_pages: { kind: 'boolean' },
                                members_can_create_private_pages: { kind: 'boolean' },
                                members_can_fork_private_repositories: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                                web_commit_signoff_required: { kind: 'boolean' },
                                updated_at: { kind: 'string' },
                                archived_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: [
                                'name',
                                'company',
                                'blog',
                                'location',
                                'email',
                                'twitter_username',
                                'is_verified',
                                'total_private_repos',
                                'owned_private_repos',
                                'private_gists',
                                'disk_usage',
                                'collaborators',
                                'billing_email',
                                'plan',
                                'default_repository_permission',
                                'members_can_create_repositories',
                                'two_factor_requirement_enabled',
                                'members_allowed_repository_creation_type',
                                'members_can_create_public_repositories',
                                'members_can_create_private_repositories',
                                'members_can_create_internal_repositories',
                                'members_can_create_pages',
                                'members_can_create_public_pages',
                                'members_can_create_private_pages',
                                'members_can_fork_private_repositories',
                                'web_commit_signoff_required',
                            ],
                            restriction: 'open',
                        },
                        ldap_dn: { kind: 'string' },
                    },
                    optionalProperties: ['privacy', 'notification_setting', 'parent', 'ldap_dn'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'user', 'teams'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List users',
        name: 'Github: List users',
        description:
            'Lists all users, in the order that they signed up on GitHub. This list includes personal user accounts and organization accounts.\n\nNote: Pagination is powered exclusively by the `since` parameter. Use the [Link header](https://docs.github.com/rest/guides/using-pagination-in-the-rest-api#using-link-headers) to get the URL for the next page of users.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            since: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a user',
        name: 'Github: Get a user',
        description:
            'Provides publicly available information about someone with a GitHub account.\n\nGitHub Apps with the `Plan` user permission can use this endpoint to retrieve information about a user\'s GitHub plan. The GitHub App must be authenticated as a user. See "[Identifying and authorizing users for GitHub Apps](https://docs.github.com/apps/building-github-apps/identifying-and-authorizing-users-for-github-apps/)" for details about authentication. For an example response, see \'Response with GitHub plan information\' below"\n\nThe `email` key in the following response is the publicly visible email address from your GitHub [profile page](https://github.com/settings/profile). When setting up your profile, you can select a primary email address to be \u201Cpublic\u201D which provides an email entry for this endpoint. If you do not set a public email address for `email`, then it will have a value of `null`. You only see publicly visible email addresses when authenticated with GitHub. For more information, see [Authentication](https://docs.github.com/rest/overview/resources-in-the-rest-api#authentication).\n\nThe Emails API enables you to list all of your email addresses, and toggle a primary email to be visible publicly. For more information, see "[Emails API](https://docs.github.com/rest/reference/users#emails)".',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            private_gists: { kind: 'number' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            disk_usage: { kind: 'number' },
                            collaborators: { kind: 'number' },
                            two_factor_authentication: { kind: 'boolean' },
                            plan: {
                                kind: 'object',
                                properties: {
                                    collaborators: { kind: 'number' },
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            business_plus: { kind: 'boolean' },
                            ldap_dn: { kind: 'string' },
                        },
                        optionalProperties: ['twitter_username', 'plan', 'suspended_at', 'business_plus', 'ldap_dn'],
                        restriction: 'open',
                    },
                    {
                        kind: 'object',
                        properties: {
                            login: { kind: 'string' },
                            id: { kind: 'number' },
                            node_id: { kind: 'string' },
                            avatar_url: { kind: 'string' },
                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            url: { kind: 'string' },
                            html_url: { kind: 'string' },
                            followers_url: { kind: 'string' },
                            following_url: { kind: 'string' },
                            gists_url: { kind: 'string' },
                            starred_url: { kind: 'string' },
                            subscriptions_url: { kind: 'string' },
                            organizations_url: { kind: 'string' },
                            repos_url: { kind: 'string' },
                            events_url: { kind: 'string' },
                            received_events_url: { kind: 'string' },
                            type: { kind: 'string' },
                            site_admin: { kind: 'boolean' },
                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            company: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            blog: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            location: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            hireable: { kind: 'union', subTypes: [{ kind: 'boolean' }, { kind: 'null' }] },
                            bio: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            twitter_username: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            public_repos: { kind: 'number' },
                            public_gists: { kind: 'number' },
                            followers: { kind: 'number' },
                            following: { kind: 'number' },
                            created_at: { kind: 'string' },
                            updated_at: { kind: 'string' },
                            plan: {
                                kind: 'object',
                                properties: {
                                    collaborators: { kind: 'number' },
                                    name: { kind: 'string' },
                                    space: { kind: 'number' },
                                    private_repos: { kind: 'number' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            private_gists: { kind: 'number' },
                            total_private_repos: { kind: 'number' },
                            owned_private_repos: { kind: 'number' },
                            disk_usage: { kind: 'number' },
                            collaborators: { kind: 'number' },
                        },
                        optionalProperties: [
                            'twitter_username',
                            'plan',
                            'suspended_at',
                            'private_gists',
                            'total_private_repos',
                            'owned_private_repos',
                            'disk_usage',
                            'collaborators',
                        ],
                        restriction: 'open',
                    },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get list of conflicting packages during Docker migration for user',
        name: 'Github: Get list of conflicting packages during Docker migration for user',
        description:
            "Lists all packages that are in a specific user's namespace, that the requesting user has access to, and that encountered a conflict during Docker migration.\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        package_type: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        version_count: { kind: 'number' },
                        visibility: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: ['owner', 'repository'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'docker', 'conflicts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List events for the authenticated user',
        name: 'Github: List events for the authenticated user',
        description:
            "If you are authenticated as the given user, you will see your private events. Otherwise, you'll only see public events.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organization events for the authenticated user',
        name: 'Github: List organization events for the authenticated user',
        description: "This is the user's organization dashboard. You must be authenticated as the user to view this.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            org: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'events', 'orgs', input.inPorts.org].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public events for a user',
        name: 'Github: List public events for a user',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'events', 'public'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List followers of a user',
        name: 'Github: List followers of a user',
        description: 'Lists the people following the specified user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'followers'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List the people a user follows',
        name: 'Github: List the people a user follows',
        description: 'Lists the people who the specified user follows.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        followers_url: { kind: 'string' },
                        following_url: { kind: 'string' },
                        gists_url: { kind: 'string' },
                        starred_url: { kind: 'string' },
                        subscriptions_url: { kind: 'string' },
                        organizations_url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        received_events_url: { kind: 'string' },
                        type: { kind: 'string' },
                        site_admin: { kind: 'boolean' },
                        starred_at: { kind: 'string' },
                    },
                    optionalProperties: ['name', 'email', 'starred_at'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'following'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Check if a user follows another user',
        name: 'Github: Check if a user follows another user',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            target_user: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'following', input.inPorts.target_user].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List gists for a user',
        name: 'Github: List gists for a user',
        description: 'Lists public gists for the specified user:',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            since: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['since', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        id: { kind: 'string' },
                        node_id: { kind: 'string' },
                        git_pull_url: { kind: 'string' },
                        git_push_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        files: {
                            kind: 'object',
                            properties: {
                                type: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                properties: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        comments: { kind: 'number' },
                        user: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        comments_url: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        truncated: { kind: 'boolean' },
                        forks: { kind: 'array', subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
                        history: {
                            kind: 'array',
                            subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                        },
                    },
                    optionalProperties: ['owner', 'truncated', 'forks', 'history'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'gists'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ since: input.inPorts.since, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List GPG keys for a user',
        name: 'Github: List GPG keys for a user',
        description: 'Lists the GPG keys for a user. This information is accessible by anyone.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        primary_key_id: { kind: 'union', subTypes: [{ kind: 'number' }, { kind: 'null' }] },
                        key_id: { kind: 'string' },
                        public_key: { kind: 'string' },
                        emails: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        subkeys: {
                            kind: 'array',
                            subType: {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    primary_key_id: { kind: 'number' },
                                    key_id: { kind: 'string' },
                                    public_key: { kind: 'string' },
                                    emails: {
                                        kind: 'array',
                                        subType: {
                                            kind: 'object',
                                            properties: { email: { kind: 'string' }, verified: { kind: 'boolean' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    subkeys: {
                                        kind: 'array',
                                        subType: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' },
                                    },
                                    can_sign: { kind: 'boolean' },
                                    can_encrypt_comms: { kind: 'boolean' },
                                    can_encrypt_storage: { kind: 'boolean' },
                                    can_certify: { kind: 'boolean' },
                                    created_at: { kind: 'string' },
                                    expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    revoked: { kind: 'boolean' },
                                },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                        },
                        can_sign: { kind: 'boolean' },
                        can_encrypt_comms: { kind: 'boolean' },
                        can_encrypt_storage: { kind: 'boolean' },
                        can_certify: { kind: 'boolean' },
                        created_at: { kind: 'string' },
                        expires_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        revoked: { kind: 'boolean' },
                        raw_key: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['name'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'gpg_keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get contextual information for a user',
        name: 'Github: Get contextual information for a user',
        description:
            "Provides hovercard information when authenticated through basic auth or OAuth with the `repo` scope. You can find out more about someone in relation to their pull requests, issues, repositories, and organizations.\n\nThe `subject_type` and `subject_id` parameters provide context for the person's hovercard, which returns more information than without the parameters. For example, if you wanted to find out more about `octocat` who owns the `Spoon-Knife` repository via cURL, it would look like this:\n\n```shell\n curl -u username:token\n  https://api.github.com/users/octocat/hovercard?subject_type=repository&subject_id=1300192\n```",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            subject_type: { kind: 'string' },
            subject_id: { kind: 'string' },
        },
        optionalInPorts: ['subject_type', 'subject_id'],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    contexts: {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: { message: { kind: 'string' }, octicon: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'hovercard'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ subject_type: input.inPorts.subject_type, subject_id: input.inPorts.subject_id }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a user installation for the authenticated app',
        name: 'Github: Get a user installation for the authenticated app',
        description:
            'Enables an authenticated GitHub App to find the user\u2019s installation information.\n\nYou must use a [JWT](https://docs.github.com/apps/building-github-apps/authenticating-with-github-apps/#authenticating-as-a-github-app) to access this endpoint.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    account: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'union',
                                subTypes: [
                                    {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    {
                                        kind: 'object',
                                        properties: {
                                            description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            html_url: { kind: 'string' },
                                            website_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            name: { kind: 'string' },
                                            slug: { kind: 'string' },
                                            created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            avatar_url: { kind: 'string' },
                                        },
                                        optionalProperties: ['description', 'website_url'],
                                        restriction: 'open',
                                    },
                                ],
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository_selection: { kind: 'string' },
                    access_tokens_url: { kind: 'string' },
                    repositories_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    app_id: { kind: 'number' },
                    target_id: { kind: 'number' },
                    target_type: { kind: 'string' },
                    permissions: {
                        kind: 'object',
                        properties: {
                            actions: { kind: 'string' },
                            administration: { kind: 'string' },
                            checks: { kind: 'string' },
                            contents: { kind: 'string' },
                            deployments: { kind: 'string' },
                            environments: { kind: 'string' },
                            issues: { kind: 'string' },
                            metadata: { kind: 'string' },
                            packages: { kind: 'string' },
                            pages: { kind: 'string' },
                            pull_requests: { kind: 'string' },
                            repository_hooks: { kind: 'string' },
                            repository_projects: { kind: 'string' },
                            secret_scanning_alerts: { kind: 'string' },
                            secrets: { kind: 'string' },
                            security_events: { kind: 'string' },
                            single_file: { kind: 'string' },
                            statuses: { kind: 'string' },
                            vulnerability_alerts: { kind: 'string' },
                            workflows: { kind: 'string' },
                            members: { kind: 'string' },
                            organization_administration: { kind: 'string' },
                            organization_custom_roles: { kind: 'string' },
                            organization_announcement_banners: { kind: 'string' },
                            organization_hooks: { kind: 'string' },
                            organization_personal_access_tokens: { kind: 'string' },
                            organization_personal_access_token_requests: { kind: 'string' },
                            organization_plan: { kind: 'string' },
                            organization_projects: { kind: 'string' },
                            organization_packages: { kind: 'string' },
                            organization_secrets: { kind: 'string' },
                            organization_self_hosted_runners: { kind: 'string' },
                            organization_user_blocking: { kind: 'string' },
                            team_discussions: { kind: 'string' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                    events: { kind: 'array', subType: { kind: 'string' } },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    single_file_name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    has_multiple_single_files: { kind: 'boolean' },
                    single_file_paths: { kind: 'array', subType: { kind: 'string' } },
                    app_slug: { kind: 'string' },
                    suspended_by: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    suspended_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    contact_email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                },
                optionalProperties: ['has_multiple_single_files', 'single_file_paths', 'contact_email'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'installation'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public keys for a user',
        name: 'Github: List public keys for a user',
        description: 'Lists the _verified_ public SSH keys for a user. This is accessible by anyone.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: { id: { kind: 'number' }, key: { kind: 'string' } },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List organizations for a user',
        name: 'Github: List organizations for a user',
        description:
            'List [public organization memberships](https://docs.github.com/articles/publicizing-or-concealing-organization-membership) for the specified user.\n\nThis method only lists _public_ memberships, regardless of authentication. If you need to fetch all of the organization memberships (public and private) for the authenticated user, use the [List organizations for the authenticated user](https://docs.github.com/rest/orgs/orgs#list-organizations-for-the-authenticated-user) API instead.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        login: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        url: { kind: 'string' },
                        repos_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        hooks_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        members_url: { kind: 'string' },
                        public_members_url: { kind: 'string' },
                        avatar_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'orgs'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List packages for a user',
        name: 'Github: List packages for a user',
        description:
            'Lists all packages in a user\'s namespace for which the requesting user has access.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            package_type: { kind: 'string' },
            visibility: { kind: 'string' },
            page: { kind: 'number' },
            per_page: { kind: 'number' },
        },
        optionalInPorts: ['visibility', 'page', 'per_page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        package_type: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        version_count: { kind: 'number' },
                        visibility: { kind: 'string' },
                        owner: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        repository: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        role_name: { kind: 'string' },
                                        temp_clone_token: { kind: 'string' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        code_of_conduct: {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'string' },
                                                body: { kind: 'string' },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['body'],
                                            restriction: 'open',
                                        },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        spdx_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        security_and_analysis: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        advanced_security: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        dependabot_security_updates: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        secret_scanning_push_protection: {
                                                            kind: 'object',
                                                            properties: { status: { kind: 'string' } },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                    },
                                    optionalProperties: [
                                        'git_url',
                                        'ssh_url',
                                        'clone_url',
                                        'mirror_url',
                                        'svn_url',
                                        'homepage',
                                        'language',
                                        'forks_count',
                                        'stargazers_count',
                                        'watchers_count',
                                        'size',
                                        'default_branch',
                                        'open_issues_count',
                                        'is_template',
                                        'topics',
                                        'has_issues',
                                        'has_projects',
                                        'has_wiki',
                                        'has_pages',
                                        'has_downloads',
                                        'has_discussions',
                                        'archived',
                                        'disabled',
                                        'visibility',
                                        'pushed_at',
                                        'created_at',
                                        'updated_at',
                                        'permissions',
                                        'role_name',
                                        'temp_clone_token',
                                        'delete_branch_on_merge',
                                        'subscribers_count',
                                        'network_count',
                                        'code_of_conduct',
                                        'license',
                                        'forks',
                                        'open_issues',
                                        'watchers',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'security_and_analysis',
                                    ],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                    },
                    optionalProperties: ['owner', 'repository'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'packages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            package_type: input.inPorts.package_type,
                            visibility: input.inPorts.visibility,
                            page: input.inPorts.page,
                            per_page: input.inPorts.per_page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a package for a user',
        name: 'Github: Get a package for a user',
        description:
            'Gets a specific package metadata for a public package owned by a user.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    package_type: { kind: 'string' },
                    url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    version_count: { kind: 'number' },
                    visibility: { kind: 'string' },
                    owner: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    login: { kind: 'string' },
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    avatar_url: { kind: 'string' },
                                    gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    url: { kind: 'string' },
                                    html_url: { kind: 'string' },
                                    followers_url: { kind: 'string' },
                                    following_url: { kind: 'string' },
                                    gists_url: { kind: 'string' },
                                    starred_url: { kind: 'string' },
                                    subscriptions_url: { kind: 'string' },
                                    organizations_url: { kind: 'string' },
                                    repos_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    received_events_url: { kind: 'string' },
                                    type: { kind: 'string' },
                                    site_admin: { kind: 'boolean' },
                                    starred_at: { kind: 'string' },
                                },
                                optionalProperties: ['name', 'email', 'starred_at'],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    repository: {
                        kind: 'union',
                        subTypes: [
                            {
                                kind: 'object',
                                properties: {
                                    id: { kind: 'number' },
                                    node_id: { kind: 'string' },
                                    name: { kind: 'string' },
                                    full_name: { kind: 'string' },
                                    owner: {
                                        kind: 'object',
                                        properties: {
                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            login: { kind: 'string' },
                                            id: { kind: 'number' },
                                            node_id: { kind: 'string' },
                                            avatar_url: { kind: 'string' },
                                            gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            url: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                            followers_url: { kind: 'string' },
                                            following_url: { kind: 'string' },
                                            gists_url: { kind: 'string' },
                                            starred_url: { kind: 'string' },
                                            subscriptions_url: { kind: 'string' },
                                            organizations_url: { kind: 'string' },
                                            repos_url: { kind: 'string' },
                                            events_url: { kind: 'string' },
                                            received_events_url: { kind: 'string' },
                                            type: { kind: 'string' },
                                            site_admin: { kind: 'boolean' },
                                            starred_at: { kind: 'string' },
                                        },
                                        optionalProperties: ['name', 'email', 'starred_at'],
                                        restriction: 'open',
                                    },
                                    private: { kind: 'boolean' },
                                    html_url: { kind: 'string' },
                                    description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    fork: { kind: 'boolean' },
                                    url: { kind: 'string' },
                                    archive_url: { kind: 'string' },
                                    assignees_url: { kind: 'string' },
                                    blobs_url: { kind: 'string' },
                                    branches_url: { kind: 'string' },
                                    collaborators_url: { kind: 'string' },
                                    comments_url: { kind: 'string' },
                                    commits_url: { kind: 'string' },
                                    compare_url: { kind: 'string' },
                                    contents_url: { kind: 'string' },
                                    contributors_url: { kind: 'string' },
                                    deployments_url: { kind: 'string' },
                                    downloads_url: { kind: 'string' },
                                    events_url: { kind: 'string' },
                                    forks_url: { kind: 'string' },
                                    git_commits_url: { kind: 'string' },
                                    git_refs_url: { kind: 'string' },
                                    git_tags_url: { kind: 'string' },
                                    git_url: { kind: 'string' },
                                    issue_comment_url: { kind: 'string' },
                                    issue_events_url: { kind: 'string' },
                                    issues_url: { kind: 'string' },
                                    keys_url: { kind: 'string' },
                                    labels_url: { kind: 'string' },
                                    languages_url: { kind: 'string' },
                                    merges_url: { kind: 'string' },
                                    milestones_url: { kind: 'string' },
                                    notifications_url: { kind: 'string' },
                                    pulls_url: { kind: 'string' },
                                    releases_url: { kind: 'string' },
                                    ssh_url: { kind: 'string' },
                                    stargazers_url: { kind: 'string' },
                                    statuses_url: { kind: 'string' },
                                    subscribers_url: { kind: 'string' },
                                    subscription_url: { kind: 'string' },
                                    tags_url: { kind: 'string' },
                                    teams_url: { kind: 'string' },
                                    trees_url: { kind: 'string' },
                                    clone_url: { kind: 'string' },
                                    mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    hooks_url: { kind: 'string' },
                                    svn_url: { kind: 'string' },
                                    homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    forks_count: { kind: 'number' },
                                    stargazers_count: { kind: 'number' },
                                    watchers_count: { kind: 'number' },
                                    size: { kind: 'number' },
                                    default_branch: { kind: 'string' },
                                    open_issues_count: { kind: 'number' },
                                    is_template: { kind: 'boolean' },
                                    topics: { kind: 'array', subType: { kind: 'string' } },
                                    has_issues: { kind: 'boolean' },
                                    has_projects: { kind: 'boolean' },
                                    has_wiki: { kind: 'boolean' },
                                    has_pages: { kind: 'boolean' },
                                    has_downloads: { kind: 'boolean' },
                                    has_discussions: { kind: 'boolean' },
                                    archived: { kind: 'boolean' },
                                    disabled: { kind: 'boolean' },
                                    visibility: { kind: 'string' },
                                    pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                    permissions: {
                                        kind: 'object',
                                        properties: {
                                            admin: { kind: 'boolean' },
                                            maintain: { kind: 'boolean' },
                                            push: { kind: 'boolean' },
                                            triage: { kind: 'boolean' },
                                            pull: { kind: 'boolean' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                    role_name: { kind: 'string' },
                                    temp_clone_token: { kind: 'string' },
                                    delete_branch_on_merge: { kind: 'boolean' },
                                    subscribers_count: { kind: 'number' },
                                    network_count: { kind: 'number' },
                                    code_of_conduct: {
                                        kind: 'object',
                                        properties: {
                                            key: { kind: 'string' },
                                            name: { kind: 'string' },
                                            url: { kind: 'string' },
                                            body: { kind: 'string' },
                                            html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        },
                                        optionalProperties: ['body'],
                                        restriction: 'open',
                                    },
                                    license: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    key: { kind: 'string' },
                                                    name: { kind: 'string' },
                                                    spdx_id: { kind: 'string' },
                                                    url: { kind: 'string' },
                                                    node_id: { kind: 'string' },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                    forks: { kind: 'number' },
                                    open_issues: { kind: 'number' },
                                    watchers: { kind: 'number' },
                                    allow_forking: { kind: 'boolean' },
                                    web_commit_signoff_required: { kind: 'boolean' },
                                    security_and_analysis: {
                                        kind: 'union',
                                        subTypes: [
                                            {
                                                kind: 'object',
                                                properties: {
                                                    advanced_security: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    dependabot_security_updates: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    secret_scanning: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                    secret_scanning_push_protection: {
                                                        kind: 'object',
                                                        properties: { status: { kind: 'string' } },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                },
                                                optionalProperties: [],
                                                restriction: 'open',
                                            },
                                            { kind: 'null' },
                                        ],
                                    },
                                },
                                optionalProperties: [
                                    'git_url',
                                    'ssh_url',
                                    'clone_url',
                                    'mirror_url',
                                    'svn_url',
                                    'homepage',
                                    'language',
                                    'forks_count',
                                    'stargazers_count',
                                    'watchers_count',
                                    'size',
                                    'default_branch',
                                    'open_issues_count',
                                    'is_template',
                                    'topics',
                                    'has_issues',
                                    'has_projects',
                                    'has_wiki',
                                    'has_pages',
                                    'has_downloads',
                                    'has_discussions',
                                    'archived',
                                    'disabled',
                                    'visibility',
                                    'pushed_at',
                                    'created_at',
                                    'updated_at',
                                    'permissions',
                                    'role_name',
                                    'temp_clone_token',
                                    'delete_branch_on_merge',
                                    'subscribers_count',
                                    'network_count',
                                    'code_of_conduct',
                                    'license',
                                    'forks',
                                    'open_issues',
                                    'watchers',
                                    'allow_forking',
                                    'web_commit_signoff_required',
                                    'security_and_analysis',
                                ],
                                restriction: 'open',
                            },
                            { kind: 'null' },
                        ],
                    },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                },
                optionalProperties: ['owner', 'repository'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'packages', input.inPorts.package_type, input.inPorts.package_name].join(
                        '/',
                    ),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete a package for a user',
        name: 'Github: Delete a package for a user',
        description:
            'Deletes an entire package for a user. You cannot delete a public package if any version of the package has more than 5,000 downloads. In this scenario, contact GitHub support for further assistance.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `delete:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package you want to delete. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'packages', input.inPorts.package_type, input.inPorts.package_name].join(
                        '/',
                    ),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Restore a package for a user',
        name: 'Github: Restore a package for a user',
        description:
            'Restores an entire package for a user.\n\nYou can restore a deleted package under the following conditions:\n  - The package was deleted within the last 30 days.\n  - The same package namespace and version is still available and not reused for a new package. If the same package namespace is not available, you will not be able to restore your package. In this scenario, to restore the deleted package, you must delete the new package that uses the deleted package\'s namespace first.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `write:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package you want to restore. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            username: { kind: 'string' },
            token: { kind: 'string' },
        },
        optionalInPorts: ['token'],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'users',
                        input.inPorts.username,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'restore',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({ token: input.inPorts.token }).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List package versions for a package owned by a user',
        name: 'Github: List package versions for a package owned by a user',
        description:
            'Lists package versions for a public package owned by a specified user.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        name: { kind: 'string' },
                        url: { kind: 'string' },
                        package_html_url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        license: { kind: 'string' },
                        description: { kind: 'string' },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        deleted_at: { kind: 'string' },
                        metadata: {
                            kind: 'object',
                            properties: {
                                package_type: { kind: 'string' },
                                container: {
                                    kind: 'object',
                                    properties: { tags: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                docker: {
                                    kind: 'object',
                                    properties: { tag: { kind: 'array', subType: { kind: 'string' } } },
                                    optionalProperties: ['tag'],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: ['container', 'docker'],
                            restriction: 'open',
                        },
                    },
                    optionalProperties: ['html_url', 'license', 'description', 'deleted_at', 'metadata'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'users',
                        input.inPorts.username,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get a package version for a user',
        name: 'Github: Get a package version for a user',
        description:
            'Gets a specific package version for a public package owned by a specified user.\n\nAt this time, to use this endpoint, you must authenticate using an access token with the `read:packages` scope. If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of GitHub Packages registries that only support repository-scoped permissions, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            package_version_id: { kind: 'number' },
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    id: { kind: 'number' },
                    name: { kind: 'string' },
                    url: { kind: 'string' },
                    package_html_url: { kind: 'string' },
                    html_url: { kind: 'string' },
                    license: { kind: 'string' },
                    description: { kind: 'string' },
                    created_at: { kind: 'string' },
                    updated_at: { kind: 'string' },
                    deleted_at: { kind: 'string' },
                    metadata: {
                        kind: 'object',
                        properties: {
                            package_type: { kind: 'string' },
                            container: {
                                kind: 'object',
                                properties: { tags: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: [],
                                restriction: 'open',
                            },
                            docker: {
                                kind: 'object',
                                properties: { tag: { kind: 'array', subType: { kind: 'string' } } },
                                optionalProperties: ['tag'],
                                restriction: 'open',
                            },
                        },
                        optionalProperties: ['container', 'docker'],
                        restriction: 'open',
                    },
                },
                optionalProperties: ['html_url', 'license', 'description', 'deleted_at', 'metadata'],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'users',
                        input.inPorts.username,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                    ].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Delete package version for a user',
        name: 'Github: Delete package version for a user',
        description:
            'Deletes a specific package version for a user. If the package is public and the package version has more than 5,000 downloads, you cannot delete the package version. In this scenario, contact GitHub support for further assistance.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `delete:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package whose version you want to delete. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            username: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'users',
                        input.inPorts.username,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                    ].join('/'),
                    method: 'delete',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Restore package version for a user',
        name: 'Github: Restore package version for a user',
        description:
            'Restores a specific package version for a user.\n\nYou can restore a deleted package under the following conditions:\n  - The package was deleted within the last 30 days.\n  - The same package namespace and version is still available and not reused for a new package. If the same package namespace is not available, you will not be able to restore your package. In this scenario, to restore the deleted package, you must delete the new package that uses the deleted package\'s namespace first.\n\nTo use this endpoint, you must authenticate using an access token with the `read:packages` and `write:packages` scopes. In addition:\n- If the `package_type` belongs to a GitHub Packages registry that only supports repository-scoped permissions, your token must also include the `repo` scope. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#permissions-for-repository-scoped-packages)."\n- If the `package_type` belongs to a GitHub Packages registry that supports granular permissions, you must have admin permissions to the package whose version you want to restore. For the list of these registries, see "[About permissions for GitHub Packages](https://docs.github.com/packages/learn-github-packages/about-permissions-for-github-packages#granular-permissions-for-userorganization-scoped-packages)."',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            package_type: { kind: 'string' },
            package_name: { kind: 'string' },
            username: { kind: 'string' },
            package_version_id: { kind: 'number' },
        },
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: [
                        '',
                        'users',
                        input.inPorts.username,
                        'packages',
                        input.inPorts.package_type,
                        input.inPorts.package_name,
                        'versions',
                        input.inPorts.package_version_id,
                        'restore',
                    ].join('/'),
                    method: 'post',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List user projects',
        name: 'Github: List user projects',
        description: 'Lists projects for a user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            state: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['state', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        owner_url: { kind: 'string' },
                        url: { kind: 'string' },
                        html_url: { kind: 'string' },
                        columns_url: { kind: 'string' },
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        number: { kind: 'number' },
                        state: { kind: 'string' },
                        creator: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        created_at: { kind: 'string' },
                        updated_at: { kind: 'string' },
                        organization_permission: { kind: 'string' },
                        private: { kind: 'boolean' },
                    },
                    optionalProperties: ['organization_permission', 'private'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'projects'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ state: input.inPorts.state, per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(
                            ([_, v]) => v !== undefined,
                        ),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List events received by the authenticated user',
        name: 'Github: List events received by the authenticated user',
        description:
            "These are events that you've received by watching repos and following users. If you are authenticated as the given user, you will see private events. Otherwise, you'll only see public events.",
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'received_events'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List public events received by a user',
        name: 'Github: List public events received by a user',
        description: '',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'string' },
                        type: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        actor: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        repo: {
                            kind: 'object',
                            properties: { id: { kind: 'number' }, name: { kind: 'string' }, url: { kind: 'string' } },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        org: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                login: { kind: 'string' },
                                display_login: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                            },
                            optionalProperties: ['display_login'],
                            restriction: 'open',
                        },
                        payload: {
                            kind: 'object',
                            properties: {
                                action: { kind: 'string' },
                                issue: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        repository_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        number: { kind: 'number' },
                                        state: { kind: 'string' },
                                        state_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        title: { kind: 'string' },
                                        body: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        labels: {
                                            kind: 'array',
                                            subType: {
                                                kind: 'union',
                                                subTypes: [
                                                    { kind: 'string' },
                                                    {
                                                        kind: 'object',
                                                        properties: {
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            url: { kind: 'string' },
                                                            name: { kind: 'string' },
                                                            description: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            color: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            default: { kind: 'boolean' },
                                                        },
                                                        optionalProperties: [],
                                                        restriction: 'open',
                                                    },
                                                ],
                                            },
                                        },
                                        assignee: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        assignees: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'array',
                                                    subType: {
                                                        kind: 'object',
                                                        properties: {
                                                            name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                            login: { kind: 'string' },
                                                            id: { kind: 'number' },
                                                            node_id: { kind: 'string' },
                                                            avatar_url: { kind: 'string' },
                                                            gravatar_id: {
                                                                kind: 'union',
                                                                subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                            },
                                                            url: { kind: 'string' },
                                                            html_url: { kind: 'string' },
                                                            followers_url: { kind: 'string' },
                                                            following_url: { kind: 'string' },
                                                            gists_url: { kind: 'string' },
                                                            starred_url: { kind: 'string' },
                                                            subscriptions_url: { kind: 'string' },
                                                            organizations_url: { kind: 'string' },
                                                            repos_url: { kind: 'string' },
                                                            events_url: { kind: 'string' },
                                                            received_events_url: { kind: 'string' },
                                                            type: { kind: 'string' },
                                                            site_admin: { kind: 'boolean' },
                                                            starred_at: { kind: 'string' },
                                                        },
                                                        optionalProperties: ['name', 'email', 'starred_at'],
                                                        restriction: 'open',
                                                    },
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        milestone: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        number: { kind: 'number' },
                                                        state: { kind: 'string' },
                                                        title: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        creator: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        open_issues: { kind: 'number' },
                                                        closed_issues: { kind: 'number' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        due_on: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        locked: { kind: 'boolean' },
                                        active_lock_reason: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        comments: { kind: 'number' },
                                        pull_request: {
                                            kind: 'object',
                                            properties: {
                                                merged_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                diff_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                patch_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            },
                                            optionalProperties: ['merged_at'],
                                            restriction: 'open',
                                        },
                                        closed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        draft: { kind: 'boolean' },
                                        closed_by: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        body_html: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        timeline_url: { kind: 'string' },
                                        repository: {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                license: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                key: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                spdx_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                node_id: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['html_url'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                organization: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: {
                                                                    kind: 'union',
                                                                    subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                                starred_at: { kind: 'string' },
                                                            },
                                                            optionalProperties: ['name', 'email', 'starred_at'],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                forks: { kind: 'number' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: ['triage', 'maintain'],
                                                    restriction: 'open',
                                                },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                has_discussions: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                template_repository: {
                                                    kind: 'union',
                                                    subTypes: [
                                                        {
                                                            kind: 'object',
                                                            properties: {
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                name: { kind: 'string' },
                                                                full_name: { kind: 'string' },
                                                                owner: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: { kind: 'string' },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                private: { kind: 'boolean' },
                                                                html_url: { kind: 'string' },
                                                                description: { kind: 'string' },
                                                                fork: { kind: 'boolean' },
                                                                url: { kind: 'string' },
                                                                archive_url: { kind: 'string' },
                                                                assignees_url: { kind: 'string' },
                                                                blobs_url: { kind: 'string' },
                                                                branches_url: { kind: 'string' },
                                                                collaborators_url: { kind: 'string' },
                                                                comments_url: { kind: 'string' },
                                                                commits_url: { kind: 'string' },
                                                                compare_url: { kind: 'string' },
                                                                contents_url: { kind: 'string' },
                                                                contributors_url: { kind: 'string' },
                                                                deployments_url: { kind: 'string' },
                                                                downloads_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                forks_url: { kind: 'string' },
                                                                git_commits_url: { kind: 'string' },
                                                                git_refs_url: { kind: 'string' },
                                                                git_tags_url: { kind: 'string' },
                                                                git_url: { kind: 'string' },
                                                                issue_comment_url: { kind: 'string' },
                                                                issue_events_url: { kind: 'string' },
                                                                issues_url: { kind: 'string' },
                                                                keys_url: { kind: 'string' },
                                                                labels_url: { kind: 'string' },
                                                                languages_url: { kind: 'string' },
                                                                merges_url: { kind: 'string' },
                                                                milestones_url: { kind: 'string' },
                                                                notifications_url: { kind: 'string' },
                                                                pulls_url: { kind: 'string' },
                                                                releases_url: { kind: 'string' },
                                                                ssh_url: { kind: 'string' },
                                                                stargazers_url: { kind: 'string' },
                                                                statuses_url: { kind: 'string' },
                                                                subscribers_url: { kind: 'string' },
                                                                subscription_url: { kind: 'string' },
                                                                tags_url: { kind: 'string' },
                                                                teams_url: { kind: 'string' },
                                                                trees_url: { kind: 'string' },
                                                                clone_url: { kind: 'string' },
                                                                mirror_url: { kind: 'string' },
                                                                hooks_url: { kind: 'string' },
                                                                svn_url: { kind: 'string' },
                                                                homepage: { kind: 'string' },
                                                                language: { kind: 'string' },
                                                                forks_count: { kind: 'number' },
                                                                stargazers_count: { kind: 'number' },
                                                                watchers_count: { kind: 'number' },
                                                                size: { kind: 'number' },
                                                                default_branch: { kind: 'string' },
                                                                open_issues_count: { kind: 'number' },
                                                                is_template: { kind: 'boolean' },
                                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                                has_issues: { kind: 'boolean' },
                                                                has_projects: { kind: 'boolean' },
                                                                has_wiki: { kind: 'boolean' },
                                                                has_pages: { kind: 'boolean' },
                                                                has_downloads: { kind: 'boolean' },
                                                                archived: { kind: 'boolean' },
                                                                disabled: { kind: 'boolean' },
                                                                visibility: { kind: 'string' },
                                                                pushed_at: { kind: 'string' },
                                                                created_at: { kind: 'string' },
                                                                updated_at: { kind: 'string' },
                                                                permissions: {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        admin: { kind: 'boolean' },
                                                                        maintain: { kind: 'boolean' },
                                                                        push: { kind: 'boolean' },
                                                                        triage: { kind: 'boolean' },
                                                                        pull: { kind: 'boolean' },
                                                                    },
                                                                    optionalProperties: [],
                                                                    restriction: 'open',
                                                                },
                                                                allow_rebase_merge: { kind: 'boolean' },
                                                                temp_clone_token: { kind: 'string' },
                                                                allow_squash_merge: { kind: 'boolean' },
                                                                allow_auto_merge: { kind: 'boolean' },
                                                                delete_branch_on_merge: { kind: 'boolean' },
                                                                allow_update_branch: { kind: 'boolean' },
                                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                                squash_merge_commit_title: { kind: 'string' },
                                                                squash_merge_commit_message: { kind: 'string' },
                                                                merge_commit_title: { kind: 'string' },
                                                                merge_commit_message: { kind: 'string' },
                                                                allow_merge_commit: { kind: 'boolean' },
                                                                subscribers_count: { kind: 'number' },
                                                                network_count: { kind: 'number' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        { kind: 'null' },
                                                    ],
                                                },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                allow_forking: { kind: 'boolean' },
                                                web_commit_signoff_required: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                                open_issues: { kind: 'number' },
                                                watchers: { kind: 'number' },
                                                master_branch: { kind: 'string' },
                                                starred_at: { kind: 'string' },
                                                anonymous_access_enabled: { kind: 'boolean' },
                                            },
                                            optionalProperties: [
                                                'organization',
                                                'permissions',
                                                'is_template',
                                                'topics',
                                                'has_discussions',
                                                'visibility',
                                                'allow_rebase_merge',
                                                'template_repository',
                                                'temp_clone_token',
                                                'allow_squash_merge',
                                                'allow_auto_merge',
                                                'delete_branch_on_merge',
                                                'allow_update_branch',
                                                'use_squash_pr_title_as_default',
                                                'squash_merge_commit_title',
                                                'squash_merge_commit_message',
                                                'merge_commit_title',
                                                'merge_commit_message',
                                                'allow_merge_commit',
                                                'allow_forking',
                                                'web_commit_signoff_required',
                                                'subscribers_count',
                                                'network_count',
                                                'master_branch',
                                                'starred_at',
                                                'anonymous_access_enabled',
                                            ],
                                            restriction: 'open',
                                        },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        author_association: { kind: 'string' },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [
                                        'state_reason',
                                        'body',
                                        'assignees',
                                        'active_lock_reason',
                                        'pull_request',
                                        'draft',
                                        'closed_by',
                                        'body_html',
                                        'body_text',
                                        'timeline_url',
                                        'repository',
                                        'performed_via_github_app',
                                        'reactions',
                                    ],
                                    restriction: 'open',
                                },
                                comment: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        body: { kind: 'string' },
                                        body_text: { kind: 'string' },
                                        body_html: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        user: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        created_at: { kind: 'string' },
                                        updated_at: { kind: 'string' },
                                        issue_url: { kind: 'string' },
                                        author_association: { kind: 'string' },
                                        performed_via_github_app: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        slug: { kind: 'string' },
                                                        node_id: { kind: 'string' },
                                                        owner: {
                                                            kind: 'union',
                                                            subTypes: [
                                                                {
                                                                    kind: 'object',
                                                                    properties: {
                                                                        name: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        email: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        login: { kind: 'string' },
                                                                        id: { kind: 'number' },
                                                                        node_id: { kind: 'string' },
                                                                        avatar_url: { kind: 'string' },
                                                                        gravatar_id: {
                                                                            kind: 'union',
                                                                            subTypes: [{ kind: 'string' }, { kind: 'null' }],
                                                                        },
                                                                        url: { kind: 'string' },
                                                                        html_url: { kind: 'string' },
                                                                        followers_url: { kind: 'string' },
                                                                        following_url: { kind: 'string' },
                                                                        gists_url: { kind: 'string' },
                                                                        starred_url: { kind: 'string' },
                                                                        subscriptions_url: { kind: 'string' },
                                                                        organizations_url: { kind: 'string' },
                                                                        repos_url: { kind: 'string' },
                                                                        events_url: { kind: 'string' },
                                                                        received_events_url: { kind: 'string' },
                                                                        type: { kind: 'string' },
                                                                        site_admin: { kind: 'boolean' },
                                                                        starred_at: { kind: 'string' },
                                                                    },
                                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                                    restriction: 'open',
                                                                },
                                                                { kind: 'null' },
                                                            ],
                                                        },
                                                        name: { kind: 'string' },
                                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        external_url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                issues: { kind: 'string' },
                                                                checks: { kind: 'string' },
                                                                metadata: { kind: 'string' },
                                                                contents: { kind: 'string' },
                                                                deployments: { kind: 'string' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        events: { kind: 'array', subType: { kind: 'string' } },
                                                        installations_count: { kind: 'number' },
                                                        client_id: { kind: 'string' },
                                                        client_secret: { kind: 'string' },
                                                        webhook_secret: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        pem: { kind: 'string' },
                                                    },
                                                    optionalProperties: [
                                                        'slug',
                                                        'installations_count',
                                                        'client_id',
                                                        'client_secret',
                                                        'webhook_secret',
                                                        'pem',
                                                    ],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        reactions: {
                                            kind: 'object',
                                            properties: {
                                                url: { kind: 'string' },
                                                total_count: { kind: 'number' },
                                                '+1': { kind: 'number' },
                                                '-1': { kind: 'number' },
                                                laugh: { kind: 'number' },
                                                confused: { kind: 'number' },
                                                heart: { kind: 'number' },
                                                hooray: { kind: 'number' },
                                                eyes: { kind: 'number' },
                                                rocket: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: ['body', 'body_text', 'body_html', 'performed_via_github_app', 'reactions'],
                                    restriction: 'open',
                                },
                                pages: {
                                    kind: 'array',
                                    subType: {
                                        kind: 'object',
                                        properties: {
                                            page_name: { kind: 'string' },
                                            title: { kind: 'string' },
                                            summary: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                            action: { kind: 'string' },
                                            sha: { kind: 'string' },
                                            html_url: { kind: 'string' },
                                        },
                                        optionalProperties: [],
                                        restriction: 'open',
                                    },
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        public: { kind: 'boolean' },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                    },
                    optionalProperties: ['org'],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'received_events', 'public'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories for a user',
        name: 'Github: List repositories for a user',
        description:
            'Lists public repositories for the specified user. Note: For GitHub AE, this endpoint will list internal repositories for the specified user.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            type: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['type', 'sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'repos'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            type: input.inPorts.type,
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Actions billing for a user',
        name: 'Github: Get GitHub Actions billing for a user',
        description:
            'Gets the summary of the free and paid GitHub Actions minutes used.\n\nPaid minutes only apply to workflows in private repositories that use GitHub-hosted runners. Minutes used is listed for each GitHub-hosted runner operating system. Any job re-runs are also included in the usage. The usage returned includes any minute multipliers for macOS and Windows runners, and is rounded up to the nearest whole minute. For more information, see "[Managing billing for GitHub Actions](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-actions)".\n\nAccess tokens must have the `user` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_minutes_used: { kind: 'number' },
                    total_paid_minutes_used: { kind: 'number' },
                    included_minutes: { kind: 'number' },
                    minutes_used_breakdown: {
                        kind: 'object',
                        properties: {
                            UBUNTU: { kind: 'number' },
                            MACOS: { kind: 'number' },
                            WINDOWS: { kind: 'number' },
                            ubuntu_4_core: { kind: 'number' },
                            ubuntu_8_core: { kind: 'number' },
                            ubuntu_16_core: { kind: 'number' },
                            ubuntu_32_core: { kind: 'number' },
                            ubuntu_64_core: { kind: 'number' },
                            windows_4_core: { kind: 'number' },
                            windows_8_core: { kind: 'number' },
                            windows_16_core: { kind: 'number' },
                            windows_32_core: { kind: 'number' },
                            windows_64_core: { kind: 'number' },
                            macos_12_core: { kind: 'number' },
                            total: { kind: 'number' },
                        },
                        optionalProperties: [],
                        restriction: 'open',
                    },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'settings', 'billing', 'actions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get GitHub Packages billing for a user',
        name: 'Github: Get GitHub Packages billing for a user',
        description:
            'Gets the free and paid storage used for GitHub Packages in gigabytes.\n\nPaid minutes only apply to packages stored for private repositories. For more information, see "[Managing billing for GitHub Packages](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-packages)."\n\nAccess tokens must have the `user` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    total_gigabytes_bandwidth_used: { kind: 'number' },
                    total_paid_gigabytes_bandwidth_used: { kind: 'number' },
                    included_gigabytes_bandwidth: { kind: 'number' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'settings', 'billing', 'packages'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get shared storage billing for a user',
        name: 'Github: Get shared storage billing for a user',
        description:
            'Gets the estimated paid and estimated total storage used for GitHub Actions and GitHub Packages.\n\nPaid minutes only apply to packages stored for private repositories. For more information, see "[Managing billing for GitHub Packages](https://docs.github.com/github/setting-up-and-managing-billing-and-payments-on-github/managing-billing-for-github-packages)."\n\nAccess tokens must have the `user` scope.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
        },
        optionalInPorts: [],
        outPorts: {
            Response: {
                kind: 'object',
                properties: {
                    days_left_in_billing_cycle: { kind: 'number' },
                    estimated_paid_storage_for_month: { kind: 'number' },
                    estimated_storage_for_month: { kind: 'number' },
                },
                optionalProperties: [],
                restriction: 'open',
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'settings', 'billing', 'shared-storage'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List social accounts for a user',
        name: 'Github: List social accounts for a user',
        description: 'Lists social media accounts for a user. This endpoint is accessible by anyone.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: { provider: { kind: 'string' }, url: { kind: 'string' } },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'social_accounts'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List SSH signing keys for a user',
        name: 'Github: List SSH signing keys for a user',
        description: 'Lists the SSH signing keys for a user. This operation is accessible by anyone.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        key: { kind: 'string' },
                        id: { kind: 'number' },
                        title: { kind: 'string' },
                        created_at: { kind: 'string' },
                    },
                    optionalProperties: [],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'ssh_signing_keys'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories starred by a user',
        name: 'Github: List repositories starred by a user',
        description:
            'Lists repositories a user has starred.\n\nYou can also find out _when_ stars were created by passing the following custom [media type](https://docs.github.com/rest/overview/media-types/) via the `Accept` header: `application/vnd.github.star+json`.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            sort: { kind: 'string' },
            direction: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['sort', 'direction', 'per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                starred_at: { kind: 'string' },
                                repo: {
                                    kind: 'object',
                                    properties: {
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        name: { kind: 'string' },
                                        full_name: { kind: 'string' },
                                        license: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        key: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        node_id: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['html_url'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        organization: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                        starred_at: { kind: 'string' },
                                                    },
                                                    optionalProperties: ['name', 'email', 'starred_at'],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        forks: { kind: 'number' },
                                        permissions: {
                                            kind: 'object',
                                            properties: {
                                                admin: { kind: 'boolean' },
                                                pull: { kind: 'boolean' },
                                                triage: { kind: 'boolean' },
                                                push: { kind: 'boolean' },
                                                maintain: { kind: 'boolean' },
                                            },
                                            optionalProperties: ['triage', 'maintain'],
                                            restriction: 'open',
                                        },
                                        owner: {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        private: { kind: 'boolean' },
                                        html_url: { kind: 'string' },
                                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        fork: { kind: 'boolean' },
                                        url: { kind: 'string' },
                                        archive_url: { kind: 'string' },
                                        assignees_url: { kind: 'string' },
                                        blobs_url: { kind: 'string' },
                                        branches_url: { kind: 'string' },
                                        collaborators_url: { kind: 'string' },
                                        comments_url: { kind: 'string' },
                                        commits_url: { kind: 'string' },
                                        compare_url: { kind: 'string' },
                                        contents_url: { kind: 'string' },
                                        contributors_url: { kind: 'string' },
                                        deployments_url: { kind: 'string' },
                                        downloads_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        forks_url: { kind: 'string' },
                                        git_commits_url: { kind: 'string' },
                                        git_refs_url: { kind: 'string' },
                                        git_tags_url: { kind: 'string' },
                                        git_url: { kind: 'string' },
                                        issue_comment_url: { kind: 'string' },
                                        issue_events_url: { kind: 'string' },
                                        issues_url: { kind: 'string' },
                                        keys_url: { kind: 'string' },
                                        labels_url: { kind: 'string' },
                                        languages_url: { kind: 'string' },
                                        merges_url: { kind: 'string' },
                                        milestones_url: { kind: 'string' },
                                        notifications_url: { kind: 'string' },
                                        pulls_url: { kind: 'string' },
                                        releases_url: { kind: 'string' },
                                        ssh_url: { kind: 'string' },
                                        stargazers_url: { kind: 'string' },
                                        statuses_url: { kind: 'string' },
                                        subscribers_url: { kind: 'string' },
                                        subscription_url: { kind: 'string' },
                                        tags_url: { kind: 'string' },
                                        teams_url: { kind: 'string' },
                                        trees_url: { kind: 'string' },
                                        clone_url: { kind: 'string' },
                                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        hooks_url: { kind: 'string' },
                                        svn_url: { kind: 'string' },
                                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        forks_count: { kind: 'number' },
                                        stargazers_count: { kind: 'number' },
                                        watchers_count: { kind: 'number' },
                                        size: { kind: 'number' },
                                        default_branch: { kind: 'string' },
                                        open_issues_count: { kind: 'number' },
                                        is_template: { kind: 'boolean' },
                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                        has_issues: { kind: 'boolean' },
                                        has_projects: { kind: 'boolean' },
                                        has_wiki: { kind: 'boolean' },
                                        has_pages: { kind: 'boolean' },
                                        has_downloads: { kind: 'boolean' },
                                        has_discussions: { kind: 'boolean' },
                                        archived: { kind: 'boolean' },
                                        disabled: { kind: 'boolean' },
                                        visibility: { kind: 'string' },
                                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        allow_rebase_merge: { kind: 'boolean' },
                                        template_repository: {
                                            kind: 'union',
                                            subTypes: [
                                                {
                                                    kind: 'object',
                                                    properties: {
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        name: { kind: 'string' },
                                                        full_name: { kind: 'string' },
                                                        owner: {
                                                            kind: 'object',
                                                            properties: {
                                                                login: { kind: 'string' },
                                                                id: { kind: 'number' },
                                                                node_id: { kind: 'string' },
                                                                avatar_url: { kind: 'string' },
                                                                gravatar_id: { kind: 'string' },
                                                                url: { kind: 'string' },
                                                                html_url: { kind: 'string' },
                                                                followers_url: { kind: 'string' },
                                                                following_url: { kind: 'string' },
                                                                gists_url: { kind: 'string' },
                                                                starred_url: { kind: 'string' },
                                                                subscriptions_url: { kind: 'string' },
                                                                organizations_url: { kind: 'string' },
                                                                repos_url: { kind: 'string' },
                                                                events_url: { kind: 'string' },
                                                                received_events_url: { kind: 'string' },
                                                                type: { kind: 'string' },
                                                                site_admin: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        private: { kind: 'boolean' },
                                                        html_url: { kind: 'string' },
                                                        description: { kind: 'string' },
                                                        fork: { kind: 'boolean' },
                                                        url: { kind: 'string' },
                                                        archive_url: { kind: 'string' },
                                                        assignees_url: { kind: 'string' },
                                                        blobs_url: { kind: 'string' },
                                                        branches_url: { kind: 'string' },
                                                        collaborators_url: { kind: 'string' },
                                                        comments_url: { kind: 'string' },
                                                        commits_url: { kind: 'string' },
                                                        compare_url: { kind: 'string' },
                                                        contents_url: { kind: 'string' },
                                                        contributors_url: { kind: 'string' },
                                                        deployments_url: { kind: 'string' },
                                                        downloads_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        forks_url: { kind: 'string' },
                                                        git_commits_url: { kind: 'string' },
                                                        git_refs_url: { kind: 'string' },
                                                        git_tags_url: { kind: 'string' },
                                                        git_url: { kind: 'string' },
                                                        issue_comment_url: { kind: 'string' },
                                                        issue_events_url: { kind: 'string' },
                                                        issues_url: { kind: 'string' },
                                                        keys_url: { kind: 'string' },
                                                        labels_url: { kind: 'string' },
                                                        languages_url: { kind: 'string' },
                                                        merges_url: { kind: 'string' },
                                                        milestones_url: { kind: 'string' },
                                                        notifications_url: { kind: 'string' },
                                                        pulls_url: { kind: 'string' },
                                                        releases_url: { kind: 'string' },
                                                        ssh_url: { kind: 'string' },
                                                        stargazers_url: { kind: 'string' },
                                                        statuses_url: { kind: 'string' },
                                                        subscribers_url: { kind: 'string' },
                                                        subscription_url: { kind: 'string' },
                                                        tags_url: { kind: 'string' },
                                                        teams_url: { kind: 'string' },
                                                        trees_url: { kind: 'string' },
                                                        clone_url: { kind: 'string' },
                                                        mirror_url: { kind: 'string' },
                                                        hooks_url: { kind: 'string' },
                                                        svn_url: { kind: 'string' },
                                                        homepage: { kind: 'string' },
                                                        language: { kind: 'string' },
                                                        forks_count: { kind: 'number' },
                                                        stargazers_count: { kind: 'number' },
                                                        watchers_count: { kind: 'number' },
                                                        size: { kind: 'number' },
                                                        default_branch: { kind: 'string' },
                                                        open_issues_count: { kind: 'number' },
                                                        is_template: { kind: 'boolean' },
                                                        topics: { kind: 'array', subType: { kind: 'string' } },
                                                        has_issues: { kind: 'boolean' },
                                                        has_projects: { kind: 'boolean' },
                                                        has_wiki: { kind: 'boolean' },
                                                        has_pages: { kind: 'boolean' },
                                                        has_downloads: { kind: 'boolean' },
                                                        archived: { kind: 'boolean' },
                                                        disabled: { kind: 'boolean' },
                                                        visibility: { kind: 'string' },
                                                        pushed_at: { kind: 'string' },
                                                        created_at: { kind: 'string' },
                                                        updated_at: { kind: 'string' },
                                                        permissions: {
                                                            kind: 'object',
                                                            properties: {
                                                                admin: { kind: 'boolean' },
                                                                maintain: { kind: 'boolean' },
                                                                push: { kind: 'boolean' },
                                                                triage: { kind: 'boolean' },
                                                                pull: { kind: 'boolean' },
                                                            },
                                                            optionalProperties: [],
                                                            restriction: 'open',
                                                        },
                                                        allow_rebase_merge: { kind: 'boolean' },
                                                        temp_clone_token: { kind: 'string' },
                                                        allow_squash_merge: { kind: 'boolean' },
                                                        allow_auto_merge: { kind: 'boolean' },
                                                        delete_branch_on_merge: { kind: 'boolean' },
                                                        allow_update_branch: { kind: 'boolean' },
                                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                                        squash_merge_commit_title: { kind: 'string' },
                                                        squash_merge_commit_message: { kind: 'string' },
                                                        merge_commit_title: { kind: 'string' },
                                                        merge_commit_message: { kind: 'string' },
                                                        allow_merge_commit: { kind: 'boolean' },
                                                        subscribers_count: { kind: 'number' },
                                                        network_count: { kind: 'number' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                { kind: 'null' },
                                            ],
                                        },
                                        temp_clone_token: { kind: 'string' },
                                        allow_squash_merge: { kind: 'boolean' },
                                        allow_auto_merge: { kind: 'boolean' },
                                        delete_branch_on_merge: { kind: 'boolean' },
                                        allow_update_branch: { kind: 'boolean' },
                                        use_squash_pr_title_as_default: { kind: 'boolean' },
                                        squash_merge_commit_title: { kind: 'string' },
                                        squash_merge_commit_message: { kind: 'string' },
                                        merge_commit_title: { kind: 'string' },
                                        merge_commit_message: { kind: 'string' },
                                        allow_merge_commit: { kind: 'boolean' },
                                        allow_forking: { kind: 'boolean' },
                                        web_commit_signoff_required: { kind: 'boolean' },
                                        subscribers_count: { kind: 'number' },
                                        network_count: { kind: 'number' },
                                        open_issues: { kind: 'number' },
                                        watchers: { kind: 'number' },
                                        master_branch: { kind: 'string' },
                                        starred_at: { kind: 'string' },
                                        anonymous_access_enabled: { kind: 'boolean' },
                                    },
                                    optionalProperties: [
                                        'organization',
                                        'permissions',
                                        'is_template',
                                        'topics',
                                        'has_discussions',
                                        'visibility',
                                        'allow_rebase_merge',
                                        'template_repository',
                                        'temp_clone_token',
                                        'allow_squash_merge',
                                        'allow_auto_merge',
                                        'delete_branch_on_merge',
                                        'allow_update_branch',
                                        'use_squash_pr_title_as_default',
                                        'squash_merge_commit_title',
                                        'squash_merge_commit_message',
                                        'merge_commit_title',
                                        'merge_commit_message',
                                        'allow_merge_commit',
                                        'allow_forking',
                                        'web_commit_signoff_required',
                                        'subscribers_count',
                                        'network_count',
                                        'master_branch',
                                        'starred_at',
                                        'anonymous_access_enabled',
                                    ],
                                    restriction: 'open',
                                },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                    },
                    {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            properties: {
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                name: { kind: 'string' },
                                full_name: { kind: 'string' },
                                license: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                key: { kind: 'string' },
                                                name: { kind: 'string' },
                                                url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                spdx_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                node_id: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                            },
                                            optionalProperties: ['html_url'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                organization: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                login: { kind: 'string' },
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                avatar_url: { kind: 'string' },
                                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                                url: { kind: 'string' },
                                                html_url: { kind: 'string' },
                                                followers_url: { kind: 'string' },
                                                following_url: { kind: 'string' },
                                                gists_url: { kind: 'string' },
                                                starred_url: { kind: 'string' },
                                                subscriptions_url: { kind: 'string' },
                                                organizations_url: { kind: 'string' },
                                                repos_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                received_events_url: { kind: 'string' },
                                                type: { kind: 'string' },
                                                site_admin: { kind: 'boolean' },
                                                starred_at: { kind: 'string' },
                                            },
                                            optionalProperties: ['name', 'email', 'starred_at'],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                forks: { kind: 'number' },
                                permissions: {
                                    kind: 'object',
                                    properties: {
                                        admin: { kind: 'boolean' },
                                        pull: { kind: 'boolean' },
                                        triage: { kind: 'boolean' },
                                        push: { kind: 'boolean' },
                                        maintain: { kind: 'boolean' },
                                    },
                                    optionalProperties: ['triage', 'maintain'],
                                    restriction: 'open',
                                },
                                owner: {
                                    kind: 'object',
                                    properties: {
                                        name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        login: { kind: 'string' },
                                        id: { kind: 'number' },
                                        node_id: { kind: 'string' },
                                        avatar_url: { kind: 'string' },
                                        gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                        url: { kind: 'string' },
                                        html_url: { kind: 'string' },
                                        followers_url: { kind: 'string' },
                                        following_url: { kind: 'string' },
                                        gists_url: { kind: 'string' },
                                        starred_url: { kind: 'string' },
                                        subscriptions_url: { kind: 'string' },
                                        organizations_url: { kind: 'string' },
                                        repos_url: { kind: 'string' },
                                        events_url: { kind: 'string' },
                                        received_events_url: { kind: 'string' },
                                        type: { kind: 'string' },
                                        site_admin: { kind: 'boolean' },
                                        starred_at: { kind: 'string' },
                                    },
                                    optionalProperties: ['name', 'email', 'starred_at'],
                                    restriction: 'open',
                                },
                                private: { kind: 'boolean' },
                                html_url: { kind: 'string' },
                                description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                fork: { kind: 'boolean' },
                                url: { kind: 'string' },
                                archive_url: { kind: 'string' },
                                assignees_url: { kind: 'string' },
                                blobs_url: { kind: 'string' },
                                branches_url: { kind: 'string' },
                                collaborators_url: { kind: 'string' },
                                comments_url: { kind: 'string' },
                                commits_url: { kind: 'string' },
                                compare_url: { kind: 'string' },
                                contents_url: { kind: 'string' },
                                contributors_url: { kind: 'string' },
                                deployments_url: { kind: 'string' },
                                downloads_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                forks_url: { kind: 'string' },
                                git_commits_url: { kind: 'string' },
                                git_refs_url: { kind: 'string' },
                                git_tags_url: { kind: 'string' },
                                git_url: { kind: 'string' },
                                issue_comment_url: { kind: 'string' },
                                issue_events_url: { kind: 'string' },
                                issues_url: { kind: 'string' },
                                keys_url: { kind: 'string' },
                                labels_url: { kind: 'string' },
                                languages_url: { kind: 'string' },
                                merges_url: { kind: 'string' },
                                milestones_url: { kind: 'string' },
                                notifications_url: { kind: 'string' },
                                pulls_url: { kind: 'string' },
                                releases_url: { kind: 'string' },
                                ssh_url: { kind: 'string' },
                                stargazers_url: { kind: 'string' },
                                statuses_url: { kind: 'string' },
                                subscribers_url: { kind: 'string' },
                                subscription_url: { kind: 'string' },
                                tags_url: { kind: 'string' },
                                teams_url: { kind: 'string' },
                                trees_url: { kind: 'string' },
                                clone_url: { kind: 'string' },
                                mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                hooks_url: { kind: 'string' },
                                svn_url: { kind: 'string' },
                                homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                forks_count: { kind: 'number' },
                                stargazers_count: { kind: 'number' },
                                watchers_count: { kind: 'number' },
                                size: { kind: 'number' },
                                default_branch: { kind: 'string' },
                                open_issues_count: { kind: 'number' },
                                is_template: { kind: 'boolean' },
                                topics: { kind: 'array', subType: { kind: 'string' } },
                                has_issues: { kind: 'boolean' },
                                has_projects: { kind: 'boolean' },
                                has_wiki: { kind: 'boolean' },
                                has_pages: { kind: 'boolean' },
                                has_downloads: { kind: 'boolean' },
                                has_discussions: { kind: 'boolean' },
                                archived: { kind: 'boolean' },
                                disabled: { kind: 'boolean' },
                                visibility: { kind: 'string' },
                                pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                allow_rebase_merge: { kind: 'boolean' },
                                template_repository: {
                                    kind: 'union',
                                    subTypes: [
                                        {
                                            kind: 'object',
                                            properties: {
                                                id: { kind: 'number' },
                                                node_id: { kind: 'string' },
                                                name: { kind: 'string' },
                                                full_name: { kind: 'string' },
                                                owner: {
                                                    kind: 'object',
                                                    properties: {
                                                        login: { kind: 'string' },
                                                        id: { kind: 'number' },
                                                        node_id: { kind: 'string' },
                                                        avatar_url: { kind: 'string' },
                                                        gravatar_id: { kind: 'string' },
                                                        url: { kind: 'string' },
                                                        html_url: { kind: 'string' },
                                                        followers_url: { kind: 'string' },
                                                        following_url: { kind: 'string' },
                                                        gists_url: { kind: 'string' },
                                                        starred_url: { kind: 'string' },
                                                        subscriptions_url: { kind: 'string' },
                                                        organizations_url: { kind: 'string' },
                                                        repos_url: { kind: 'string' },
                                                        events_url: { kind: 'string' },
                                                        received_events_url: { kind: 'string' },
                                                        type: { kind: 'string' },
                                                        site_admin: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                private: { kind: 'boolean' },
                                                html_url: { kind: 'string' },
                                                description: { kind: 'string' },
                                                fork: { kind: 'boolean' },
                                                url: { kind: 'string' },
                                                archive_url: { kind: 'string' },
                                                assignees_url: { kind: 'string' },
                                                blobs_url: { kind: 'string' },
                                                branches_url: { kind: 'string' },
                                                collaborators_url: { kind: 'string' },
                                                comments_url: { kind: 'string' },
                                                commits_url: { kind: 'string' },
                                                compare_url: { kind: 'string' },
                                                contents_url: { kind: 'string' },
                                                contributors_url: { kind: 'string' },
                                                deployments_url: { kind: 'string' },
                                                downloads_url: { kind: 'string' },
                                                events_url: { kind: 'string' },
                                                forks_url: { kind: 'string' },
                                                git_commits_url: { kind: 'string' },
                                                git_refs_url: { kind: 'string' },
                                                git_tags_url: { kind: 'string' },
                                                git_url: { kind: 'string' },
                                                issue_comment_url: { kind: 'string' },
                                                issue_events_url: { kind: 'string' },
                                                issues_url: { kind: 'string' },
                                                keys_url: { kind: 'string' },
                                                labels_url: { kind: 'string' },
                                                languages_url: { kind: 'string' },
                                                merges_url: { kind: 'string' },
                                                milestones_url: { kind: 'string' },
                                                notifications_url: { kind: 'string' },
                                                pulls_url: { kind: 'string' },
                                                releases_url: { kind: 'string' },
                                                ssh_url: { kind: 'string' },
                                                stargazers_url: { kind: 'string' },
                                                statuses_url: { kind: 'string' },
                                                subscribers_url: { kind: 'string' },
                                                subscription_url: { kind: 'string' },
                                                tags_url: { kind: 'string' },
                                                teams_url: { kind: 'string' },
                                                trees_url: { kind: 'string' },
                                                clone_url: { kind: 'string' },
                                                mirror_url: { kind: 'string' },
                                                hooks_url: { kind: 'string' },
                                                svn_url: { kind: 'string' },
                                                homepage: { kind: 'string' },
                                                language: { kind: 'string' },
                                                forks_count: { kind: 'number' },
                                                stargazers_count: { kind: 'number' },
                                                watchers_count: { kind: 'number' },
                                                size: { kind: 'number' },
                                                default_branch: { kind: 'string' },
                                                open_issues_count: { kind: 'number' },
                                                is_template: { kind: 'boolean' },
                                                topics: { kind: 'array', subType: { kind: 'string' } },
                                                has_issues: { kind: 'boolean' },
                                                has_projects: { kind: 'boolean' },
                                                has_wiki: { kind: 'boolean' },
                                                has_pages: { kind: 'boolean' },
                                                has_downloads: { kind: 'boolean' },
                                                archived: { kind: 'boolean' },
                                                disabled: { kind: 'boolean' },
                                                visibility: { kind: 'string' },
                                                pushed_at: { kind: 'string' },
                                                created_at: { kind: 'string' },
                                                updated_at: { kind: 'string' },
                                                permissions: {
                                                    kind: 'object',
                                                    properties: {
                                                        admin: { kind: 'boolean' },
                                                        maintain: { kind: 'boolean' },
                                                        push: { kind: 'boolean' },
                                                        triage: { kind: 'boolean' },
                                                        pull: { kind: 'boolean' },
                                                    },
                                                    optionalProperties: [],
                                                    restriction: 'open',
                                                },
                                                allow_rebase_merge: { kind: 'boolean' },
                                                temp_clone_token: { kind: 'string' },
                                                allow_squash_merge: { kind: 'boolean' },
                                                allow_auto_merge: { kind: 'boolean' },
                                                delete_branch_on_merge: { kind: 'boolean' },
                                                allow_update_branch: { kind: 'boolean' },
                                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                                squash_merge_commit_title: { kind: 'string' },
                                                squash_merge_commit_message: { kind: 'string' },
                                                merge_commit_title: { kind: 'string' },
                                                merge_commit_message: { kind: 'string' },
                                                allow_merge_commit: { kind: 'boolean' },
                                                subscribers_count: { kind: 'number' },
                                                network_count: { kind: 'number' },
                                            },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        { kind: 'null' },
                                    ],
                                },
                                temp_clone_token: { kind: 'string' },
                                allow_squash_merge: { kind: 'boolean' },
                                allow_auto_merge: { kind: 'boolean' },
                                delete_branch_on_merge: { kind: 'boolean' },
                                allow_update_branch: { kind: 'boolean' },
                                use_squash_pr_title_as_default: { kind: 'boolean' },
                                squash_merge_commit_title: { kind: 'string' },
                                squash_merge_commit_message: { kind: 'string' },
                                merge_commit_title: { kind: 'string' },
                                merge_commit_message: { kind: 'string' },
                                allow_merge_commit: { kind: 'boolean' },
                                allow_forking: { kind: 'boolean' },
                                web_commit_signoff_required: { kind: 'boolean' },
                                subscribers_count: { kind: 'number' },
                                network_count: { kind: 'number' },
                                open_issues: { kind: 'number' },
                                watchers: { kind: 'number' },
                                master_branch: { kind: 'string' },
                                starred_at: { kind: 'string' },
                                anonymous_access_enabled: { kind: 'boolean' },
                            },
                            optionalProperties: [
                                'organization',
                                'permissions',
                                'is_template',
                                'topics',
                                'has_discussions',
                                'visibility',
                                'allow_rebase_merge',
                                'template_repository',
                                'temp_clone_token',
                                'allow_squash_merge',
                                'allow_auto_merge',
                                'delete_branch_on_merge',
                                'allow_update_branch',
                                'use_squash_pr_title_as_default',
                                'squash_merge_commit_title',
                                'squash_merge_commit_message',
                                'merge_commit_title',
                                'merge_commit_message',
                                'allow_merge_commit',
                                'allow_forking',
                                'web_commit_signoff_required',
                                'subscribers_count',
                                'network_count',
                                'master_branch',
                                'starred_at',
                                'anonymous_access_enabled',
                            ],
                            restriction: 'open',
                        },
                    },
                ],
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'starred'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({
                            sort: input.inPorts.sort,
                            direction: input.inPorts.direction,
                            per_page: input.inPorts.per_page,
                            page: input.inPorts.page,
                        }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: List repositories watched by a user',
        name: 'Github: List repositories watched by a user',
        description: 'Lists repositories a user is watching.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {
            username: { kind: 'string' },
            per_page: { kind: 'number' },
            page: { kind: 'number' },
        },
        optionalInPorts: ['per_page', 'page'],
        outPorts: {
            Response: {
                kind: 'array',
                subType: {
                    kind: 'object',
                    properties: {
                        id: { kind: 'number' },
                        node_id: { kind: 'string' },
                        name: { kind: 'string' },
                        full_name: { kind: 'string' },
                        owner: {
                            kind: 'object',
                            properties: {
                                name: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                email: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                login: { kind: 'string' },
                                id: { kind: 'number' },
                                node_id: { kind: 'string' },
                                avatar_url: { kind: 'string' },
                                gravatar_id: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                                url: { kind: 'string' },
                                html_url: { kind: 'string' },
                                followers_url: { kind: 'string' },
                                following_url: { kind: 'string' },
                                gists_url: { kind: 'string' },
                                starred_url: { kind: 'string' },
                                subscriptions_url: { kind: 'string' },
                                organizations_url: { kind: 'string' },
                                repos_url: { kind: 'string' },
                                events_url: { kind: 'string' },
                                received_events_url: { kind: 'string' },
                                type: { kind: 'string' },
                                site_admin: { kind: 'boolean' },
                                starred_at: { kind: 'string' },
                            },
                            optionalProperties: ['name', 'email', 'starred_at'],
                            restriction: 'open',
                        },
                        private: { kind: 'boolean' },
                        html_url: { kind: 'string' },
                        description: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        fork: { kind: 'boolean' },
                        url: { kind: 'string' },
                        archive_url: { kind: 'string' },
                        assignees_url: { kind: 'string' },
                        blobs_url: { kind: 'string' },
                        branches_url: { kind: 'string' },
                        collaborators_url: { kind: 'string' },
                        comments_url: { kind: 'string' },
                        commits_url: { kind: 'string' },
                        compare_url: { kind: 'string' },
                        contents_url: { kind: 'string' },
                        contributors_url: { kind: 'string' },
                        deployments_url: { kind: 'string' },
                        downloads_url: { kind: 'string' },
                        events_url: { kind: 'string' },
                        forks_url: { kind: 'string' },
                        git_commits_url: { kind: 'string' },
                        git_refs_url: { kind: 'string' },
                        git_tags_url: { kind: 'string' },
                        git_url: { kind: 'string' },
                        issue_comment_url: { kind: 'string' },
                        issue_events_url: { kind: 'string' },
                        issues_url: { kind: 'string' },
                        keys_url: { kind: 'string' },
                        labels_url: { kind: 'string' },
                        languages_url: { kind: 'string' },
                        merges_url: { kind: 'string' },
                        milestones_url: { kind: 'string' },
                        notifications_url: { kind: 'string' },
                        pulls_url: { kind: 'string' },
                        releases_url: { kind: 'string' },
                        ssh_url: { kind: 'string' },
                        stargazers_url: { kind: 'string' },
                        statuses_url: { kind: 'string' },
                        subscribers_url: { kind: 'string' },
                        subscription_url: { kind: 'string' },
                        tags_url: { kind: 'string' },
                        teams_url: { kind: 'string' },
                        trees_url: { kind: 'string' },
                        clone_url: { kind: 'string' },
                        mirror_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        hooks_url: { kind: 'string' },
                        svn_url: { kind: 'string' },
                        homepage: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        language: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        forks_count: { kind: 'number' },
                        stargazers_count: { kind: 'number' },
                        watchers_count: { kind: 'number' },
                        size: { kind: 'number' },
                        default_branch: { kind: 'string' },
                        open_issues_count: { kind: 'number' },
                        is_template: { kind: 'boolean' },
                        topics: { kind: 'array', subType: { kind: 'string' } },
                        has_issues: { kind: 'boolean' },
                        has_projects: { kind: 'boolean' },
                        has_wiki: { kind: 'boolean' },
                        has_pages: { kind: 'boolean' },
                        has_downloads: { kind: 'boolean' },
                        has_discussions: { kind: 'boolean' },
                        archived: { kind: 'boolean' },
                        disabled: { kind: 'boolean' },
                        visibility: { kind: 'string' },
                        pushed_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        created_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        updated_at: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                        permissions: {
                            kind: 'object',
                            properties: {
                                admin: { kind: 'boolean' },
                                maintain: { kind: 'boolean' },
                                push: { kind: 'boolean' },
                                triage: { kind: 'boolean' },
                                pull: { kind: 'boolean' },
                            },
                            optionalProperties: [],
                            restriction: 'open',
                        },
                        role_name: { kind: 'string' },
                        temp_clone_token: { kind: 'string' },
                        delete_branch_on_merge: { kind: 'boolean' },
                        subscribers_count: { kind: 'number' },
                        network_count: { kind: 'number' },
                        code_of_conduct: {
                            kind: 'object',
                            properties: {
                                key: { kind: 'string' },
                                name: { kind: 'string' },
                                url: { kind: 'string' },
                                body: { kind: 'string' },
                                html_url: { kind: 'union', subTypes: [{ kind: 'string' }, { kind: 'null' }] },
                            },
                            optionalProperties: ['body'],
                            restriction: 'open',
                        },
                        license: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        key: { kind: 'string' },
                                        name: { kind: 'string' },
                                        spdx_id: { kind: 'string' },
                                        url: { kind: 'string' },
                                        node_id: { kind: 'string' },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                        forks: { kind: 'number' },
                        open_issues: { kind: 'number' },
                        watchers: { kind: 'number' },
                        allow_forking: { kind: 'boolean' },
                        web_commit_signoff_required: { kind: 'boolean' },
                        security_and_analysis: {
                            kind: 'union',
                            subTypes: [
                                {
                                    kind: 'object',
                                    properties: {
                                        advanced_security: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        dependabot_security_updates: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                        secret_scanning_push_protection: {
                                            kind: 'object',
                                            properties: { status: { kind: 'string' } },
                                            optionalProperties: [],
                                            restriction: 'open',
                                        },
                                    },
                                    optionalProperties: [],
                                    restriction: 'open',
                                },
                                { kind: 'null' },
                            ],
                        },
                    },
                    optionalProperties: [
                        'git_url',
                        'ssh_url',
                        'clone_url',
                        'mirror_url',
                        'svn_url',
                        'homepage',
                        'language',
                        'forks_count',
                        'stargazers_count',
                        'watchers_count',
                        'size',
                        'default_branch',
                        'open_issues_count',
                        'is_template',
                        'topics',
                        'has_issues',
                        'has_projects',
                        'has_wiki',
                        'has_pages',
                        'has_downloads',
                        'has_discussions',
                        'archived',
                        'disabled',
                        'visibility',
                        'pushed_at',
                        'created_at',
                        'updated_at',
                        'permissions',
                        'role_name',
                        'temp_clone_token',
                        'delete_branch_on_merge',
                        'subscribers_count',
                        'network_count',
                        'code_of_conduct',
                        'license',
                        'forks',
                        'open_issues',
                        'watchers',
                        'allow_forking',
                        'web_commit_signoff_required',
                        'security_and_analysis',
                    ],
                    restriction: 'open',
                },
            },
        },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'users', input.inPorts.username, 'subscriptions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(
                        Object.entries({ per_page: input.inPorts.per_page, page: input.inPorts.page }).filter(([_, v]) => v !== undefined),
                    ),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get all API versions',
        name: 'Github: Get all API versions',
        description: 'Get all supported GitHub API versions.',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: { Response: { kind: 'array', subType: { kind: 'string' } } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'versions'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
    createNode({
        id: 'Github: Get the Zen of GitHub',
        name: 'Github: Get the Zen of GitHub',
        description: 'Get a random sentence from the Zen of GitHub',
        category: 'Github',
        color: '#000000',
        integration: GithubIntegration,
        inPorts: {},
        optionalInPorts: [],
        outPorts: { Response: { kind: 'object', properties: {}, optionalProperties: [], restriction: 'open' } },
        isAsync: true,
        execute: async function (input) {
            try {
                const response = await input.integration.callIntegration<any>({
                    baseURL: 'https://api.github.com',
                    url: ['', 'zen'].join('/'),
                    method: 'get',
                    params: Object.fromEntries(Object.entries({}).filter(([_, v]) => v !== undefined)),
                });
                return { output: { Response: response.data } };
            } catch (e) {
                return { output: { Response: e } };
            }
        },
    }),
];
